// scheduling/pages/SchedulerGrid.jsx
// Staff-centric timeline scheduler — the primary scheduling canvas.
// People on rows, dates on columns (5-week scrollable window).
// Features: click-to-create, job block stacking, drag-to-move, utilization bars,
// detail panel, department filters, zoom, conflict detection.

import { useState, useEffect, useRef, useCallback, useMemo, memo, forwardRef, useImperativeHandle, Fragment } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth, DEV_SUPERUSERS } from '../../auth/AuthContext';
// The initial grid load goes through fetchBootstrap, so staff/jobs/calendars/holidays/
// global_assignments/custom_jobs/pool_events no longer need individual imports here.
import { fetchBootstrap, fetchSchedule, fetchSavedFilters, fetchConflictDetails, fetchSuggestions, fetchPTO, fetchJobsWIP, fetchJobProgress, fetchJobScheduledStaff, fetchPlaceholders } from '../services/schedData';
import { fetchPoolAvailability } from '../services/poolApi';
import { POOL_META } from '../utils/poolEligibility';
// upsertEntry/deleteEntry are intentionally NOT imported: every call site here now goes
// through a batched action (bulkMove / bulkDeleteEntries). The single-entry service
// wrappers and their server actions still exist for other callers.
import { bulkDeleteEntries, bulkMove, bulkMoveMulti, bulkEntries, notifyRemoval, saveFilter as saveFilterAPI, updateFilter as updateFilterAPI, deleteFilter as deleteFilterAPI, getBatchId, approvePTO, rejectPTO, cancelPTO, upsertPlaceholder, convertToPlaceholder, setJobOnSite, deleteClosedJobFuture } from '../services/schedApi';
import PlaceholderAssignModal from '../components/PlaceholderAssignModal';
import ScheduleHistoryModal from './ScheduleHistoryModal';
import { useUrlState, csvOfNumbers, csvOfStrings } from '../../hooks/useUrlState';
import { BRAND, DEPT_GROUPS, STAFF_DEPT_GROUPS, JOB_TYPE_COLORS, SERVICE_TO_DEPT, jobDeptColor, jobDept, staffColor, LEVEL_BANDS, LEADERSHIP_LEVELS, CUSTOM_JOB_CATEGORY_COLORS, CUSTOM_JOB_DEFAULT_COLOR, SCHED_STATUS, computeSchedStatus, baseSuggestionFilter, isSchedulableStaff, RETURN_TYPES, RETURN_TYPE_OTHER, getReturnType, PLACEHOLDER_LEVELS, isAiJob } from '../utils/schedConstants';
import { buildAiJobSections, AI_RECENT_DAYS } from '../utils/aiJobPicker';
import SeniorityBars from '../components/SeniorityBars';
import ContractChip from '../components/ContractChip';
import NicheGapFlag from '../components/NicheGapFlag';
import GenTrackFlag from '../components/GenTrackFlag';
import OffOfficeFlag from '../components/OffOfficeFlag';
import OnSiteFilterNote from '../components/OnSiteFilterNote';
import { useGenTrackBands } from '../hooks/useGenTrackBands';
import { jobBucketOf } from '../utils/genTrack';
import JobProgressBar from '../components/JobProgressBar';
import HoursByStaffPopover from '../components/HoursByStaffPopover';
import OnSiteToggle from '../components/OnSiteToggle';
import { computeOptimizedHours, computeFillToCapacity, distributeEvenlyQuarters, spreadWithinCapacity, MIN_PLANNED_DAY_HOURS } from '../utils/optimizeHours';
import { useTourDrive, useTourDemo } from '../../components/tour/tourStore';
import { SENTINEL_JOB_ID, SENTINEL_MODAL_JOB_ID, buildDemoEntries, buildDemoPlaceholder, TOUR_JOB_SIDEBAR_OBJ, TOUR_SUGGESTION_CANDIDATES, TOUR_GRID_JOBS } from '../../components/tour/tourDemoData';
import { toISO, makeGetDayCap, makeGetContract, buildPtoByStaffDay, buildHolidayByDate, buildDayHoursByStaff, isEmployedOn, computeStaffWeeklyUtil } from '../utils/capacity';
import { normalizeSavedFilter, savedFilterKey, cacheableFilters } from '../utils/savedFilters';
import BeforeAfterSummary from '../components/distribution/BeforeAfterSummary';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import { recordEvent } from '../../utils/bugLog';

// " · combined with hours already on Oct 6, Oct 7" — appended to a save's toast when the
// server ADDED hours into days that already had them (bulk_move / bulk_move_multi /
// bulk_entries return merged[]: a day the client couldn't see as occupied, or a reassign onto
// a day the candidate already had on that job). Empty string when nothing was combined.
function mergeNote(res) {
  const m = Array.isArray(res?.merged) ? res.merged : [];
  if (!m.length) return '';
  const days = [...new Set(m.map(x => x.date).filter(Boolean))].sort();
  const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const shown = days.slice(0, 3).map(fmt).join(', ') + (days.length > 3 ? ` +${days.length - 3} more` : '');
  return ` · combined with hours already on ${shown || `${m.length} day${m.length === 1 ? '' : 's'}`}`;
}

// Leadership levels hidden by the People-view "Mgr & below" toggle + placeholder-row
// eligibility. Sourced from schedConstants so it stays in sync with the suggestion
// filter (Consultant is treated as leadership for these filters — not for rank/sort).
const LEADERSHIP_LEVEL_SET = new Set(LEADERSHIP_LEVELS);

// Item 4 — two-way toggle atop the modal staff picklist: ranked Suggestions vs staff
// who've logged WIP on the picked job. The "Staff with WIP" half greys out (disabled)
// when no one has logged time yet.
function PicklistToggle({ mode, onMode, hasWip, wipCount }) {
  return (
    <div className="sched-modal-picklist-toggle">
      <button type="button"
        className={`sched-modal-picklist-tab${mode === 'suggest' ? ' sched-modal-picklist-tab--on' : ''}`}
        onClick={(e) => { e.stopPropagation(); onMode('suggest'); }}>
        ★ Suggested
      </button>
      <button type="button"
        className={`sched-modal-picklist-tab${mode === 'wip' ? ' sched-modal-picklist-tab--on' : ''}${hasWip ? '' : ' sched-modal-picklist-tab--disabled'}`}
        disabled={!hasWip}
        title={hasWip ? 'Staff who have logged time on this job' : 'No staff with WIP logged yet'}
        onClick={(e) => { e.stopPropagation(); if (hasWip) onMode('wip'); }}>
        Staff with WIP{hasWip ? ` (${wipCount})` : ''}
      </button>
    </div>
  );
}

// One row in either picklist mode (★ Suggested / Staff with WIP). Both the
// multi-staff and person-mode picklists render this — they were byte-identical copies
// differing only in the click handler and the --selected class, so the Gen Track chip
// would have had to be added (and kept in sync) twice.
//
// Module-level for a stable component identity across the grid's frequent re-renders.
// Kept in this file rather than components/ because it is coupled to these picklist CSS
// classes and nothing else uses it.
//
// NOTE both text lines are `white-space: nowrap; overflow: hidden; text-overflow:
// ellipsis` (Scheduler.css) — an inline chip on either line gets CLIPPED, not wrapped.
// That is why `badge` renders on its own third line.
function PicklistStaffRow({ staff, mode, right, rightColor, selected, badge, onClick }) {
  return (
    <div
      className={`sched-modal-job-option sched-modal-job-option--suggest${selected ? ' sched-modal-job-option--selected' : ''}`}
      onClick={onClick}>
      <div className="sched-modal-job-option-client" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{staff.Name}</span>
        {mode === 'suggest' && (
          <span className="sched-modal-suggest-fit" title="Match score (experience + interest)">
            {Math.round(staff.totalScore || 0)}
          </span>
        )}
      </div>
      <div className="sched-modal-job-option-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>
          {staff.Level}
          <span className="sched-modal-job-option-type">{mode === 'wip' ? (staff.Department || staff.DepartmentCode || '') : (staff.DepartmentCode || '')}</span>
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: rightColor }}>{right}</span>
      </div>
      <div className="sched-picklist-badges">{badge}</div>
    </div>
  );
}

// ── Date Helpers ────────────────────────────────────────────────────────────

function getMonday(d = new Date()) {
  const dt = new Date(d);
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - (dow === 0 ? 6 : dow - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isToday(d) {
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

function isWeekend(d) {
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

// If `d` lands on Sat/Sun, advance to the following Monday. Returns a new Date.
// Used when a placeholder DND lands on a weekend cell — snap forward so the
// placeholder always starts on a workday.
function snapForwardToWorkday(d) {
  const x = new Date(d);
  while (isWeekend(x)) x.setDate(x.getDate() + 1);
  return x;
}

// Count weekdays (Mon-Fri) between `start` and `end` inclusive.
function countWeekdaysInclusive(start, end) {
  let n = 0;
  const c = new Date(start);
  const stop = new Date(end);
  while (c <= stop) {
    if (!isWeekend(c)) n++;
    c.setDate(c.getDate() + 1);
  }
  return n;
}

// Walk forward from `start` (assumed to be a workday) until we've counted
// `n` workdays inclusive; return that date. Used to compute a placeholder's
// new end date when shifting by drop, preserving workday count.
function addNWorkdaysInclusive(start, n) {
  if (n <= 0) return new Date(start);
  const d = new Date(start);
  let counted = 0;
  // Safety cap to avoid infinite loops on bad input.
  for (let i = 0; i < 365 * 5; i++) {
    if (!isWeekend(d)) {
      counted++;
      if (counted === n) return d;
    }
    d.setDate(d.getDate() + 1);
  }
  return d;
}

// Shift a single date by N workdays (skip Sat + Sun). Positive n walks
// forward, negative walks backward. Returns a new local Date.
// Used by the edit-modal stepper buttons (< > and << >>) and for rekeying
// dailyHours objects when the whole range shifts.
function shiftWorkdays(d, n) {
  if (!n) return new Date(d);
  const out = new Date(d);
  const step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  for (let i = 0; i < 365 * 5 && remaining > 0; i++) {
    out.setDate(out.getDate() + step);
    if (!isWeekend(out)) remaining--;
  }
  return out;
}

// Parse an ISO date string ('YYYY-MM-DD') as a local-midnight Date. Avoids
// the `new Date('YYYY-MM-DD')` UTC-parse pitfall that shifts to the prior
// day in CST/CDT.
function parseLocalISODate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

// ?week=YYYY-MM-DD (the Monday) ↔ internal integer weekOffset. The URL carries
// an absolute Monday date so a shared/bookmarked link points to the same
// calendar week forever; the offset is recomputed relative to whoever opens it.
function offsetToWeekParam(offset) {
  return toISO(addDays(getMonday(), (offset || 0) * 7));
}
function weekParamToOffset(iso) {
  const target = parseLocalISODate(iso);
  if (!target) return 0;
  const diff = getMonday(target) - getMonday();
  return Math.round(diff / (7 * 24 * 60 * 60 * 1000));
}

// Build a new dailyHours object for a [start, end] ISO range:
//   - Days that exist in oldDaily AND fall in the new range → keep old hours
//   - New weekdays in range not in oldDaily → 0h
//   - Sundays always excluded; Saturdays only if staff has Sat capacity
// Returns { dailyHours, totalHours }.
function rebuildDailyHoursForRange(oldDaily, startISO, endISO, staffId, getDayCap) {
  const out = {};
  const start = parseLocalISODate(startISO);
  const end = parseLocalISODate(endISO);
  if (!start || !end || end < start) {
    return { dailyHours: { ...oldDaily }, totalHours: Object.values(oldDaily || {}).reduce((s, h) => s + (Number(h) || 0), 0) };
  }
  for (const iso of workdaysInRange(startISO, endISO, staffId, getDayCap)) {
    out[iso] = oldDaily && oldDaily[iso] != null ? oldDaily[iso] : 0;
  }
  const totalHours = Object.values(out).reduce((s, h) => s + (Number(h) || 0), 0);
  return { dailyHours: out, totalHours };
}

// Enumerate the schedulable workdays in [startISO, endISO] for a staffer:
// skip Sundays; include Saturday only when the staffer has Saturday capacity.
// Shared by the edit modal's proposed-distribution helper + the save so the
// preview and the write always agree on the day set.
function workdaysInRange(startISO, endISO, staffId, getDayCap) {
  const out = [];
  const start = parseLocalISODate(startISO);
  const end = parseLocalISODate(endISO || startISO);
  if (!start || !end || end < start) return out;
  const c = new Date(start);
  while (c <= end) {
    const iso = toISO(c);
    const dow = c.getDay();
    if (dow !== 0) {
      const includeSat = dow !== 6 || (getDayCap && getDayCap(staffId, iso) > 0);
      if (includeSat) out.push(iso);
    }
    c.setDate(c.getDate() + 1);
  }
  return out;
}

// Extend a base workday list before/after with up to 5 extra workdays each side
// (capped at 14 calendar days back/forward, never earlier than today). Skips
// Sundays + Saturdays without capacity, same rule as workdaysInRange. Returns a
// NEW array; the base is not mutated. Shared by single-staff Optimize and the
// multi-staff candidate builder so "Extend before/after" behaves identically.
function extendOverflowDates(dates, staffId, overflowStart, overflowEnd, getDayCap) {
  const out = [...(dates || [])];
  if (overflowStart && out.length > 0) {
    const first = new Date(out[0] + 'T12:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let added = 0;
    for (let i = 1; added < 5 && i <= 14; i++) {
      const prev = new Date(first); prev.setDate(first.getDate() - i);
      if (prev < today) break;
      const dow = prev.getDay();
      if (dow === 0) continue;
      if (dow === 6 && getDayCap(staffId, toISO(prev)) <= 0) continue;
      out.unshift(toISO(prev)); added++;
    }
  }
  if (overflowEnd && out.length > 0) {
    const last = new Date(out[out.length - 1] + 'T12:00:00');
    let added = 0;
    for (let i = 1; added < 5 && i <= 14; i++) {
      const next = new Date(last); next.setDate(last.getDate() + i);
      const dow = next.getDay();
      if (dow === 0) continue;
      if (dow === 6 && getDayCap(staffId, toISO(next)) <= 0) continue;
      out.push(toISO(next)); added++;
    }
  }
  return out;
}

// Single source of truth for the modal's PROPOSED daily distribution — used by
// BOTH the Before/After preview and the save, so they can never diverge:
//   - By Day mode (an explicit per-day map) → that map verbatim (custom /
//     Optimize / Fill hours preserved; new days already 0; shrink already
//     dropped the cut days).
//   - else (Total mode / new entry) → the total split evenly across the range's
//     workdays (0.25h-quantized), which is exactly what "Total" mode means.
// Returns { iso: hours } (may include 0-hour days, like the preview; the save
// filters those out when building inserts).
function computeProposedDaily({ startISO, endISO, total, allocType, dailyMap, staffId, getDayCap }) {
  if (allocType === 'daily' && dailyMap && Object.keys(dailyMap).length > 0) {
    return { ...dailyMap };
  }
  const days = workdaysInRange(startISO, endISO, staffId, getDayCap);
  const arr = distributeEvenlyQuarters(Number(total) || 0, days.length);
  const out = {};
  days.forEach((iso, i) => { out[iso] = arr[i]; });
  return out;
}

// Single per-day capacity/conflict predicate — the one place the "does this
// proposed allocation fit under the staffer's remaining capacity that day"
// rule lives. Callers pass already-looked-up scalars so the predicate stays
// pure and each site controls its own inputs:
//   proposedHrs      – hours being placed on the day (<=0 is never a conflict)
//   cap              – that day's capacity (from getDayCap)
//   totalExistingHrs – all hours already scheduled that day for this staffer
//   ownPreEditHrs    – this entry's own pre-edit hours already counted in
//                      totalExistingHrs (subtracted so the entry isn't compared
//                      against itself — pass 0 when the staffer is changing)
//   ptoHrs           – approved PTO hours that day (only consulted when ptoAsConflict)
//   ptoAsConflict    – when true, any hours on an approved-PTO day is a conflict
function dayIsConflict({ proposedHrs, cap, totalExistingHrs = 0, ownPreEditHrs = 0, ptoHrs = 0, ptoAsConflict = false }) {
  const proposed = Number(proposedHrs) || 0;
  if (proposed <= 0) return false;
  if (ptoAsConflict && (Number(ptoHrs) || 0) > 0) return true;
  const otherExisting = Math.max(0, (Number(totalExistingHrs) || 0) - (Number(ownPreEditHrs) || 0));
  return proposed > (Number(cap) || 0) - otherExisting;
}

const DOW_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function utilColor(pct) {
  if (pct > 100) return '#EF4444';   // over capacity — red
  if (pct === 100) return '#F59E0B'; // exactly at capacity — yellow
  return '#22C55E';                   // under capacity — green
}

// Split a staff+job's entries into contiguous blocks (gap > 3 days = new block) and
// return the block containing weekIso (or the first block). Window-INDEPENDENT — feed it
// a full unbounded fetch. Shared by the conflict "entire block" display + reassign action.
function findBlockContaining(allEntries, weekIso) {
  if (!allEntries || !allEntries.length) return [];
  const sorted = [...allEntries].sort((a, b) => String(a.EntryDate).localeCompare(String(b.EntryDate)));
  const blocks = []; let cur = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prevD = new Date(String(sorted[i - 1].EntryDate).slice(0, 10) + 'T12:00:00');
    const curD = new Date(String(sorted[i].EntryDate).slice(0, 10) + 'T12:00:00');
    if ((curD - prevD) / 86400000 > 3) { blocks.push(cur); cur = []; }
    cur.push(sorted[i]);
  }
  blocks.push(cur);
  if (!weekIso) return blocks[0] || [];
  const wkStart = new Date(weekIso + 'T12:00:00').getTime();
  const wkEnd = wkStart + 7 * 86400000;
  return blocks.find(bl => bl.some(e => {
    const t = new Date(String(e.EntryDate).slice(0, 10) + 'T12:00:00').getTime();
    return t >= wkStart && t < wkEnd;
  })) || [];
}

// ── Layout Constants ────────────────────────────────────────────────────────

// DW/WW are now derived from zoom inside the component
const ROW_MIN = 60;    // minimum row height
const BLOCK_H = 26;    // job block height
const BLOCK_GAP = 4;   // gap between stacked blocks
const BLOCK_TOP = 20;  // top offset (below util bar + padding)
// Custom-job rows (Jobs view, "Custom" scope) stack one track per staffer, and the big
// firm-internal jobs are an order of magnitude wider than any client job — EBP Team runs
// ~19 concurrent staff against a 10-staff maximum for the busiest real job. Uncapped that
// is a ~600px row, so they collapse to this many tracks with a "+N more" expander.
const CUSTOM_ROW_TRACK_CAP = 6;
// Width of a continuation marker. It is carved OUT of the bar rather than drawn on top of
// it, so the bar's own label, hours and badges can never collide with the glyph.
const MARKER_W = 10;
// Rendered widths of the block's trailing, flex-shrink:0 items. Everything after the name
// refuses to shrink, so when the children overflow, .sched-grid-block's overflow:hidden
// eats the LAST child — the hours. Charging these to the fit test is what keeps a "104h"
// from rendering as "104". Deliberately rounded UP: over-estimating hides the hours a
// pixel early, under-estimating slices them.
const BADGE_W = { staffGone: 14, capWarn: 16, ptoWarn: 26, offOffice: 72 };
// .sched-grid-block is CONTENT-BOX — there is no global border-box reset, and
// .sched-grid-block--placeholder has to opt in explicitly (see its box-sizing comment).
// So `width` is the content width and this padding renders OUTSIDE it: a bar styled
// width:W actually occupies W + 12px. Marker placement has to add it back or the glyph
// lands inside the bar.
const BLOCK_PAD_X = 6;
const MARKER_GAP = 2;   // breathing room between a bar and its marker
const SENIORITY_W = 21;   // 4 bars + gaps + its 6/4 margins
const HOURS_MIN_W = 90;   // the long-standing "is this bar worth labelling" threshold
// Widening stops here on the first click. 60 of 65 real continuations fit inside a quarter,
// so this resolves almost all of them in one go without ever dumping someone from a 5-week
// view into an unreadable 52-week one. The marker survives a partial widen, so a second
// click walks further out.
const WIDEN_SOFT_CAP_WEEKS = 13;
// Height of the full-width expand/collapse band drawn on a collapsible custom row's
// divider. Only rows that actually have hidden tracks get one, so the band's mere
// presence is the "there is more here" signal.
const EXPAND_BAND_H = 16;


// Isolated free-text search box. Keeping the text in its OWN local state means a
// keystroke re-renders only this tiny input — not the whole SchedulerGrid (hundreds
// of rows × ~35 columns) — so typing feels native. The committed value is debounced
// up to onChange (which writes ?q and drives the grid re-filter) and synced back down
// when ?q changes externally (the × clear, back/forward nav, or a programmatic reset).
const StaffSearchInput = memo(function StaffSearchInput({ value, onChange, debounceMs = 250 }) {
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync down when the committed value changes from outside this input.
  useEffect(() => { setText(value); }, [value]);

  // Debounce up: commit the typed text after a pause (not on every keystroke).
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChangeRef.current(text), debounceMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <>
      <input
        type="text"
        placeholder="Filter staff..."
        value={text}
        onChange={e => setText(e.target.value)}
        className="sched-mgr-search"
        style={{ width: 140, fontSize: 11, padding: '4px 8px', marginLeft: 0 }}
      />
      {text.trim() && (
        <button style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
          onClick={() => { setText(''); onChangeRef.current(''); }}
          title="Clear search"
        >&times;</button>
      )}
    </>
  );
});

// Client name/code filter (?cq). Same isolated/debounced pattern as StaffSearchInput
// so keystrokes re-render only the input, not the whole grid. Filters blocks/jobs by
// client name or PE client code (rprs.Clients.Code, e.g. "12345-001"); composes with
// the staff search + the dept/office filters in both views.
const ClientSearchInput = memo(function ClientSearchInput({ value, onChange, debounceMs = 250 }) {
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChangeRef.current(text), debounceMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <>
      <input
        type="text"
        placeholder="Filter by client name or code..."
        value={text}
        onChange={e => setText(e.target.value)}
        className="sched-mgr-search"
        style={{ width: 180, fontSize: 11, padding: '4px 8px', marginLeft: 0 }}
      />
      {text.trim() && (
        <button style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
          onClick={() => { setText(''); onChangeRef.current(''); }}
          title="Clear client filter"
        >&times;</button>
      )}
    </>
  );
});

// The little "New start: Mon 14" label that follows the cursor during a drag.
//
// It owns its own position state and is driven imperatively, because onDragOver fires
// continuously — many times per second, per pixel of movement. Holding {x, y, label}
// in SchedulerGrid state meant every one of those events re-rendered the ENTIRE grid
// (every staff row × every day column) purely to move a tooltip a few pixels. Dragging
// is the primary interaction on this page, so that was the single most expensive
// avoidable render in the app.
//
// Imperative handle rather than props so the drag handlers can push updates without
// the parent re-rendering at all.
const DragHoverTooltip = forwardRef(function DragHoverTooltip(_props, ref) {
  const [hover, setHover] = useState(null);
  useImperativeHandle(ref, () => ({
    show: (next) => setHover(next),
    hide: () => setHover(prev => (prev === null ? prev : null)),
  }), []);
  if (!hover) return null;
  return (
    <div style={{
      position: 'fixed', left: hover.x + 14, top: hover.y - 30,
      background: '#003D4C', color: '#fff', fontSize: 11, fontWeight: 600,
      padding: '4px 10px', borderRadius: 6, pointerEvents: 'none', zIndex: 300,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)', whiteSpace: 'nowrap',
    }}>
      {hover.label}
    </div>
  );
});

// Generic version of the StaffSearchInput trick above, for search boxes whose
// committed value has to stay in SchedulerGrid state because other logic reads it
// (the edit modal's job + staff pickers use it to decide whether to show the picker
// or the selected-item chip, so it can't simply be localized away).
//
// Typing updates only this component; the value is debounced upward. That keeps the
// caret responsive AND stops the expensive list rebuild from running per keystroke —
// the job picker filters all ~8,500 jobs, so at 200ms it runs once per pause instead
// of once per character.
const DebouncedSearchInput = memo(function DebouncedSearchInput({
  value, onChange, placeholder, className, style, debounceMs = 200, autoFocus = false,
}) {
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => { setText(value); }, [value]);

  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChangeRef.current(text), debounceMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <input
      type="text"
      placeholder={placeholder}
      value={text}
      onChange={e => setText(e.target.value)}
      className={className}
      style={style}
      autoFocus={autoFocus}
    />
  );
});

// The jump-to-week date field.
//
// Holds a DRAFT: nothing moves the grid until you press Enter or leave the field.
// Driving the grid straight off `change` meant paging from August to November
// moved it three times on the way to the month you actually wanted — three full
// grid re-renders and three URL writes, none of them asked for.
//
// An earlier attempt kept the instant jump for a deliberate day-click and drafted
// only keyboard edits, telling them apart by whether a keystroke preceded the
// change. That does not work: in Chromium the calendar popup's month arrows
// AUTO-SELECT a date and fire `change` with no keystroke, so month paging is
// indistinguishable from picking a day. The popup is browser chrome — it emits no
// open/close events and nothing else separates the two. Hence one rule for every
// path, which is also the only rule that behaves the same in every browser.
//
// Blur commits rather than reverts: the field visibly shows the staged date, so
// committing what is on screen is less surprising than silently discarding it, and
// it rescues anyone who picks a date and clicks straight back onto the grid.
const WeekJumpPicker = memo(function WeekJumpPicker({ value, onCommit }) {
  const [draft, setDraft] = useState(value);

  // Follow the grid whenever it moves for some other reason (‹ ›, Today, a
  // deeplink) so the field keeps working as a position indicator.
  useEffect(() => { setDraft(value); }, [value]);

  // "Pending" means committing would actually land on a different week — not
  // merely that the text differs. Nudging the DAY within the current week reads as
  // a change but is a no-op, and prompting for Enter there would be a lie.
  const pending = !!draft && weekParamToOffset(draft) !== weekParamToOffset(value);

  const commit = useCallback((iso) => {
    if (!iso) return;   // field cleared — leave the grid where it is
    if (weekParamToOffset(iso) === weekParamToOffset(value)) return;
    onCommit(iso);
  }, [value, onCommit]);

  return (
    <input
      type="date"
      className={`sched-grid-week-picker${pending ? ' sched-grid-week-picker--pending' : ''}`}
      value={draft}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
        if (e.key === 'Escape') { e.preventDefault(); setDraft(value); }
      }}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      title={pending
        ? 'Press Enter to jump to this week'
        : 'Jump to any week — page to the date you want, then press Enter'}
      aria-label="Jump to week"
    />
  );
});

// ── Filter-modal picker rows ───────────────────────────────────────────────
//
// Each row is its OWN memoized component, and that is the whole point: the rows
// used to be inline JSX inside the list's .map, so ticking one checkbox
// re-rendered every row in the list. A staff row is ~13 elements (checkbox,
// avatar, name, level line, a 5-span SeniorityBars) — at the 100-row cap that is
// ~1,300 elements reconciled, with fresh inline style objects on every one, to
// change a single checkbox. Now only the row you clicked re-renders; the rest
// bail out on `selected` being an unchanged primitive.
//
// `selected` is passed as a BOOLEAN rather than the selection Set/array on
// purpose — a Set or array is a new identity on every toggle, which would defeat
// memo for all 100 rows and put us right back where we started.
const PICK_ROW = {
  base: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' },
  cb: { flexShrink: 0 },
  avatar: { width: 26, height: 26, fontSize: 9, flexShrink: 0 },
  body: { flex: 1, minWidth: 0 },
  name: { fontSize: 12, fontWeight: 600, color: '#111827' },
  meta: { fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 },
  nameEllipsis: { fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontSize: 10, color: '#6B7280' },
  on: { background: '#EFF6FF' },
  off: { background: '#fff' },
  onGreen: { background: '#F0FDF4' },
};

const StaffPickRow = memo(function StaffPickRow({ staff, selected, onToggle }) {
  const deptColor = staffColor(staff.DepartmentCode);
  return (
    <div style={{ ...PICK_ROW.base, ...(selected ? PICK_ROW.on : PICK_ROW.off) }}
      onClick={() => onToggle(staff.StaffId)}>
      <input type="checkbox" checked={selected} readOnly style={PICK_ROW.cb} />
      <div className="sched-grid-avatar" style={{ ...PICK_ROW.avatar, background: deptColor }}>
        {staff.Initials || '??'}
      </div>
      <div style={PICK_ROW.body}>
        <div style={PICK_ROW.name}>{staff.Name}</div>
        <div style={PICK_ROW.meta}>
          <span>{staff.Level} &middot; {staff.Department || staff.DepartmentCode}{staff.Homeroom && <> &middot; <span style={{ color: '#0EA5E9' }}>{staff.Homeroom}</span></>}</span>
          <SeniorityBars level={staff.Level} color={deptColor} />
        </div>
      </div>
    </div>
  );
});

const ClientPickRow = memo(function ClientPickRow({ client, selected, onToggle }) {
  return (
    <div style={{ ...PICK_ROW.base, ...(selected ? PICK_ROW.on : PICK_ROW.off) }}
      onClick={() => onToggle(client.clientId)}>
      <input type="checkbox" checked={selected} readOnly style={PICK_ROW.cb} />
      <div style={PICK_ROW.body}>
        <div style={PICK_ROW.nameEllipsis}>{client.name}</div>
        <div style={PICK_ROW.sub}>{client.code}</div>
      </div>
    </div>
  );
});

const SharePickRow = memo(function SharePickRow({ staff, selected, onToggle }) {
  return (
    <div style={{ ...PICK_ROW.base, padding: '4px 8px', gap: 7, ...(selected ? PICK_ROW.onGreen : PICK_ROW.off) }}
      onClick={() => onToggle(staff.StaffId)}>
      <input type="checkbox" checked={selected} readOnly style={PICK_ROW.cb} />
      <span style={{ fontSize: 11, color: '#111827' }}>{staff.Name}</span>
      <span style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 'auto' }}>{staff.Level}</span>
    </div>
  );
});

// Search box + checklist for the Filter Staff Groups modal.
//
// Extracted and memoized because typing here used to lag badly. `filterSearch` lived
// in SchedulerGrid state, so every keystroke re-rendered the ENTIRE grid behind the
// modal (hundreds of staff rows × ~35 day columns) before it could repaint the
// dropdown. Same root cause StaffSearchInput above was created to solve — this modal
// just never got the treatment.
//
// Owning the query internally means a keystroke re-renders only this subtree. No
// debounce needed as a result: the filter runs against ~300 staff and 100 rendered
// rows, which is trivial once the grid isn't re-rendering with it. The modal is
// conditionally mounted, so local state resets on each open for free.
//
// One row here is far heavier than the edit modal's plain-text equivalent (avatar +
// SeniorityBars + level line), which is why this one felt slower than that picker
// despite both binding raw state — 100 rows vs 50, and ~5 elements per row vs 2.
const StaffFilterPicker = memo(function StaffFilterPicker({ staffList, selectedIds, onToggle }) {
  const [q, setQ] = useState('');
  const query = q.toLowerCase().trim();
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredList = useMemo(() => (
    staffList.filter(s => !query
      || (s.Name || '').toLowerCase().includes(query)
      || (s.Level || '').toLowerCase().includes(query)
      || (s.DepartmentCode || '').toLowerCase().includes(query)
      || (s.Homeroom || '').toLowerCase().includes(query)
    ).slice(0, 100)
  ), [staffList, query]);

  return (
    <>
      <input type="text" placeholder="Search by name, level, department, or homeroom..."
        value={q} onChange={e => setQ(e.target.value)}
        className="sched-modal-job-search" style={{ marginBottom: 8 }} />
      <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
        {filteredList.map(s => (
          <StaffPickRow key={s.StaffId} staff={s} selected={selectedSet.has(s.StaffId)} onToggle={onToggle} />
        ))}
        {filteredList.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No staff match your search.</div>
        )}
      </div>
    </>
  );
});

// Search + checklist for the Filter Client Groups modal.
//
// Deliberately NOT shaped like StaffFilterPicker. There are ~300 staff but 7,368
// clients with active jobs — 25x — so the staff modal's "render the first 100 and
// let you browse" pattern is useless here: the first 100 of 7,368 alphabetically
// is never the client you want. This is search-first, and shows nothing until you
// type, so the list is always a deliberate answer to a query.
//
// Two characters minimum: one character matches thousands of rows and the result
// would be an arbitrary 100-row slice presented as if it meant something.
const CLIENT_SEARCH_MIN = 2;
const CLIENT_SEARCH_LIMIT = 100;

const ClientFilterPicker = memo(function ClientFilterPicker({ clients, selectedIds, onToggle }) {
  const [q, setQ] = useState('');
  const query = q.toLowerCase().trim();
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Matches are counted before slicing so the "showing first N" line can be
  // honest about how much was left out.
  const { shown, total } = useMemo(() => {
    if (query.length < CLIENT_SEARCH_MIN) return { shown: [], total: 0 };
    const hits = clients.filter(c =>
      c.name.toLowerCase().includes(query) || c.code.toLowerCase().includes(query));
    return { shown: hits.slice(0, CLIENT_SEARCH_LIMIT), total: hits.length };
  }, [clients, query]);

  return (
    <>
      <input type="text"
        placeholder={`Search ${clients.length.toLocaleString()} clients by name or code...`}
        value={q} onChange={e => setQ(e.target.value)}
        className="sched-modal-job-search" style={{ marginBottom: 8 }} />
      <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
        {query.length < CLIENT_SEARCH_MIN ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
            Type at least {CLIENT_SEARCH_MIN} characters to find clients.
          </div>
        ) : shown.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
            No clients match &ldquo;{q.trim()}&rdquo;.
          </div>
        ) : (
          <>
            {shown.map(c => (
              <ClientPickRow key={c.clientId} client={c} selected={selectedSet.has(c.clientId)} onToggle={onToggle} />
            ))}
            {total > shown.length && (
              <div style={{ padding: '8px 10px', textAlign: 'center', color: '#9CA3AF', fontSize: 11 }}>
                Showing {shown.length} of {total.toLocaleString()} matches — keep typing to narrow.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
});

// Visual language for a saved-filter chip. Four states worth telling apart at a
// glance, because they differ in who controls the filter:
//   system   — generated (My Homeroom), can't be edited or deleted
//   global   — a super-user published it firm-wide
//   shared   — visible to a named list
//   private  — the default, mine alone
function filterChipStyle(g) {
  if (g.isSystem)               return { background: '#E0F2FE', border: '1px solid #7DD3FC', color: '#0369A1' };
  if (g.visibility === 'global') return { background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E' };
  if (g.visibility === 'shared') return { background: '#DCFCE7', border: '1px solid #86EFAC', color: '#166534' };
  return { background: '#F3F4F6', border: '1px solid transparent', color: '#374151' };
}

// Hover text for a saved-filter chip: who owns it, who can see it, and what is in
// it. `nameOf` resolves members (staff or clients, depending on the panel);
// `staffNameOf` resolves the share list, which is always people.
function filterChipTitle(g, nameOf, staffNameOf) {
  const ids = g.memberIds || [];
  // Capped: a 200-member group would produce a tooltip taller than the screen.
  const members = ids.slice(0, 25).map(nameOf).join(', ')
    + (ids.length > 25 ? `, +${ids.length - 25} more` : '');
  const lines = [];
  if (g.isMine === false) lines.push(`Shared by ${g.ownerName || g.ownerEmail || 'someone else'}`);
  if (g.visibility === 'global') lines.push('Visible to everyone at BMSS');
  else if (g.visibility === 'shared') {
    const who = (g.sharedWith || []).map(staffNameOf).join(', ');
    lines.push(who ? `Shared with: ${who}` : 'Shared');
  }
  // Recipients can't edit the original, so say what they CAN do rather than
  // leaving the missing delete control unexplained.
  if (g.isMine === false) lines.push('Click to use it — save under a new name to keep your own copy');
  lines.push(`Members (${ids.length}): ${members}`);
  return lines.join('\n');
}

// Who a filter is shared with. Separate from StaffFilterPicker because it answers
// a different question (who may SEE this, not who is IN it) and has to sit inside
// an already-tall modal — hence a short list and no avatars.
const SharedWithPicker = memo(function SharedWithPicker({ staffList, staffById, selectedIds, onToggle }) {
  const [q, setQ] = useState('');
  const query = q.toLowerCase().trim();
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const matches = useMemo(() => (
    staffList.filter(s => !query || (s.Name || '').toLowerCase().includes(query)).slice(0, 50)
  ), [staffList, query]);

  return (
    <div style={{ marginTop: 6, padding: 8, border: '1px solid #E5E7EB', borderRadius: 8, background: '#F9FAFB' }}>
      {selectedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {selectedIds.map(sid => (
            <span key={sid} style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#DCFCE7', color: '#166534', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>
              {staffById[sid]?.Name || `#${sid}`}
              <button style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: 11, padding: 0, lineHeight: 1 }}
                onClick={() => onToggle(sid)}>&times;</button>
            </span>
          ))}
        </div>
      )}
      <input type="text" placeholder="Search people to share with..."
        value={q} onChange={e => setQ(e.target.value)}
        style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 11, boxSizing: 'border-box' }} />
      <div style={{ maxHeight: 132, overflowY: 'auto', marginTop: 6, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6 }}>
        {matches.map(s => (
          <SharePickRow key={s.StaffId} staff={s} selected={selectedSet.has(s.StaffId)} onToggle={onToggle} />
        ))}
        {matches.length === 0 && (
          <div style={{ padding: 10, textAlign: 'center', color: '#9CA3AF', fontSize: 11 }}>No one matches.</div>
        )}
      </div>
    </div>
  );
});

// Name box + Save button for the filter modal.
//
// The name used to live in SchedulerGrid state, so it had exactly the problem
// StaffFilterPicker above was extracted to fix: every keystroke re-rendered the
// whole grid before it could repaint one text input. Owning the text here means
// typing touches this subtree only.
//
// It also stops needing to be reset from outside -- the modal is conditionally
// mounted, so the field starts empty on each open for free.
const SaveFilterRow = memo(function SaveFilterRow({
  disabled, onSave, placeholder = 'Filter name...',
  canShareGlobally = false, shareStaffList = [], staffById = {},
}) {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [sharedWith, setSharedWith] = useState([]);
  const toggleShare = useCallback((sid) => {
    setSharedWith(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  }, []);

  // 'Specific people' with nobody chosen would save as a filter no one can see,
  // including its own creator's intent. The API rejects it too; blocking here
  // means the user finds out before the round trip.
  const needsAudience = visibility === 'shared' && sharedWith.length === 0;
  const canSave = !disabled && name.trim().length > 0 && !needsAudience;

  const commit = () => {
    if (!canSave) return;
    onSave(name.trim(), {
      visibility,
      sharedWith: visibility === 'shared' ? sharedWith : [],
    });
    setName('');
    setVisibility('private');
    setSharedWith([]);
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="text" placeholder={placeholder} value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, minWidth: 0 }} />
        <select value={visibility} onChange={e => setVisibility(e.target.value)}
          title="Who can see this saved filter?"
          style={{ padding: '6px 6px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 11, background: '#fff', cursor: 'pointer' }}>
          <option value="private">Only me</option>
          <option value="shared">Specific people</option>
          {/* Firm-wide is super-user only — mirrored on the server, which is what
              actually enforces it. Hidden rather than disabled: there is nothing
              a non-super-user could do to earn it. */}
          {canShareGlobally && <option value="global">Everyone</option>}
        </select>
        <button className="sched-mgr-pill" style={{ fontSize: 11, whiteSpace: 'nowrap' }}
          disabled={!canSave} onClick={commit}
          title={needsAudience ? 'Pick at least one person to share with' : 'Save this filter'}>Save</button>
      </div>
      {visibility === 'shared' && (
        <SharedWithPicker staffList={shareStaffList} staffById={staffById}
          selectedIds={sharedWith} onToggle={toggleShare} />
      )}
      {visibility === 'global' && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 6, padding: '5px 8px' }}>
          Everyone at BMSS will see this filter. Only you can edit or delete it.
        </div>
      )}
    </div>
  );
});

// Shown when the saved-filter sync failed, in BOTH filter modals.
//
// This exists because the failure used to be invisible. The sync was
// `.catch(() => {})`, so a broken read looked exactly like "you have no saved
// filters" — and when the legacy StaffIds column was dropped out from under the
// query on 2026-09-01, 27 users silently lost the filters shared with them and
// nobody reported it for a day. Anything the user could mistake for empty state
// has to say so.
const SavedFilterSyncWarning = memo(function SavedFilterSyncWarning({ onRetry }) {
  const [retrying, setRetrying] = useState(false);
  return (
    <div style={{ marginBottom: 10, padding: '6px 9px', borderRadius: 6, background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E', fontSize: 11, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ flex: 1 }}>
        Couldn&rsquo;t load your saved groups from the server. You may be seeing only a
        cached copy, and groups shared with you are missing.
      </span>
      <button style={{ background: '#fff', border: '1px solid #FCD34D', borderRadius: 5, color: '#92400E', fontSize: 11, fontWeight: 700, padding: '3px 9px', cursor: 'pointer', whiteSpace: 'nowrap' }}
        disabled={retrying}
        onClick={() => { setRetrying(true); Promise.resolve(onRetry?.()).finally(() => setRetrying(false)); }}>
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
});

// Everything inside the Filter Staff / Filter Client modals below the header.
//
// These own the in-progress SELECTION. It used to live in SchedulerGrid state,
// which meant ticking one checkbox re-rendered the entire grid behind the modal
// before the checkbox could repaint — the same root cause already fixed for
// typing (StaffFilterPicker, SaveFilterRow), just reached via a different event.
// Picking ten clients in a row was ten full grid renders.
//
// The selection is lifted only on Apply or Save, so nothing outside the modal
// needs to watch it. `initialSelected` is read once on mount, which is correct
// because these modals are conditionally rendered and remount on every open.
// Human label for a filter's audience, used on the manage rows.
function visibilityLabel(g) {
  if (g.visibility === 'global') return 'Everyone';
  if (g.visibility === 'shared') {
    const n = (g.sharedWith || []).length;
    return `${n} ${n === 1 ? 'person' : 'people'}`;
  }
  return 'Only me';
}

// The manage view — a list with per-row actions, swapped in over the picker
// rather than opened as a second modal (no backdrop stacking, no z-index).
//
// It exists for exactly one thing the rest of the UI cannot do: RENAME. Deleting,
// duplicating and editing membership all already worked via the chips — clicking a
// chip loads its members, and saving the same name upserts — they were just
// undiscoverable. Renaming was genuinely impossible: saving under a new name left
// the original behind as a duplicate.
//
// Scoped to one kind, because it is reached from that kind's modal and 'Edit
// members' hands the ids back to that modal's picker.
const ManageFilterList = memo(function ManageFilterList({
  kind, filters, nameOf, staffById, canShareGlobally, shareStaffList,
  onRename, onDuplicate, onDelete, onUseMembers, onSetSharing, onBack,
}) {
  const [renamingId, setRenamingId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [sharingId, setSharingId] = useState(null);
  const [draftVis, setDraftVis] = useState('private');
  const [draftShared, setDraftShared] = useState([]);
  const toggleDraftShared = useCallback((sid) => {
    setDraftShared(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  }, []);

  const noun = kind === 'client' ? 'client group' : 'filter';

  const startRename = (g) => { setSharingId(null); setRenamingId(g.id); setDraftName(g.name); };
  const commitRename = (g) => {
    const next = draftName.trim();
    setRenamingId(null);
    if (next && next !== g.name) onRename(g, next);
  };
  const startSharing = (g) => {
    setRenamingId(null);
    setSharingId(g.id);
    setDraftVis(g.visibility || 'private');
    setDraftShared(g.sharedWith || []);
  };
  const commitSharing = (g) => {
    if (draftVis === 'shared' && draftShared.length === 0) return;
    setSharingId(null);
    onSetSharing(g, { visibility: draftVis, sharedWith: draftVis === 'shared' ? draftShared : [] });
  };

  const btn = {
    background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer',
    fontSize: 11, padding: '2px 4px', whiteSpace: 'nowrap',
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button className="sched-mgr-pill" style={{ fontSize: 11 }} onClick={onBack}>&larr; Back</button>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
          Manage saved {kind === 'client' ? 'client groups' : 'filters'}
        </span>
      </div>

      {filters.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 8 }}>
          You haven&rsquo;t saved any {noun}s yet.
        </div>
      ) : (
        <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
          {filters.map(g => {
            const ids = g.memberIds || [];
            const mine = g.isMine !== false && !g.isSystem;
            const cs = filterChipStyle(g);
            return (
              <div key={g.id || g.name} style={{ borderBottom: '1px solid #F3F4F6', padding: '7px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {renamingId === g.id ? (
                    <input autoFocus type="text" value={draftName}
                      onChange={e => setDraftName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename(g); }
                        if (e.key === 'Escape') { e.preventDefault(); setRenamingId(null); }
                      }}
                      style={{ flex: 1, minWidth: 0, padding: '4px 8px', borderRadius: 6, border: '1px solid #93C5FD', fontSize: 12 }} />
                  ) : (
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={filterChipTitle(g, nameOf, (sid) => staffById[sid]?.Name || `#${sid}`)}>
                      {g.name}
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: '#6B7280', whiteSpace: 'nowrap' }}>{ids.length}</span>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: cs.background, border: cs.border, color: cs.color, whiteSpace: 'nowrap' }}>
                    {g.isSystem ? 'Automatic' : visibilityLabel(g)}
                  </span>
                </div>

                {g.isMine === false && (
                  <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>
                    Shared by {g.ownerName || g.ownerEmail || 'someone else'} &middot; you can use or copy it
                  </div>
                )}

                <div style={{ display: 'flex', gap: 2, marginTop: 4, flexWrap: 'wrap' }}>
                  {renamingId === g.id ? (
                    <>
                      <button style={{ ...btn, fontWeight: 700 }} onClick={() => commitRename(g)}>Save name</button>
                      <button style={{ ...btn, color: '#6B7280' }} onClick={() => setRenamingId(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button style={btn} onClick={() => onUseMembers(g)}
                        title="Load these members back into the picker to add or remove some">Edit members</button>
                      {mine && <button style={btn} onClick={() => startRename(g)}>Rename</button>}
                      {!g.isSystem && (
                        <button style={btn} onClick={() => onDuplicate(g)}
                          title={mine ? 'Make a copy you can change independently' : 'Make your own copy — the original stays theirs'}>Duplicate</button>
                      )}
                      {mine && <button style={btn} onClick={() => startSharing(g)}>Sharing</button>}
                      {mine && (
                        <button style={{ ...btn, color: '#DC2626', marginLeft: 'auto' }} onClick={() => onDelete(g)}>Delete</button>
                      )}
                    </>
                  )}
                </div>

                {sharingId === g.id && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select value={draftVis} onChange={e => setDraftVis(e.target.value)}
                        style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 11, background: '#fff', cursor: 'pointer' }}>
                        <option value="private">Only me</option>
                        <option value="shared">Specific people</option>
                        {canShareGlobally && <option value="global">Everyone</option>}
                      </select>
                      <button style={{ ...btn, fontWeight: 700 }}
                        disabled={draftVis === 'shared' && draftShared.length === 0}
                        onClick={() => commitSharing(g)}>Apply</button>
                      <button style={{ ...btn, color: '#6B7280' }} onClick={() => setSharingId(null)}>Cancel</button>
                    </div>
                    {draftVis === 'shared' && (
                      <SharedWithPicker staffList={shareStaffList} staffById={staffById}
                        selectedIds={draftShared} onToggle={toggleDraftShared} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
});

const StaffFilterPanel = memo(function StaffFilterPanel({
  initialSelected, staffList, staffById, savedGroups, canShareGlobally,
  syncFailed, onRetrySync,
  onApply, onSaveGroup, onDeleteGroup, onClose,
  onRenameGroup, onDuplicateGroup, onSetGroupSharing,
}) {
  const [selected, setSelected] = useState(initialSelected);
  const [managing, setManaging] = useState(false);
  const toggle = useCallback((id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);
  const nameOf = (sid) => staffById[sid]?.Name || `#${sid}`;

  // Read from a ref so these stay identity-stable across selection changes.
  // As plain inline arrows they were new functions on every toggle, which
  // re-rendered SaveFilterRow (and its recipient picker) for a checkbox click.
  const selRef = useRef(selected);
  selRef.current = selected;
  const saveGroup = useCallback((name, opts) => onSaveGroup(name, selRef.current, opts), [onSaveGroup]);
  const applyNow = useCallback(() => onApply(selRef.current), [onApply]);

  if (managing) {
    return (
      <ManageFilterList
        kind="staff" filters={savedGroups} nameOf={nameOf} staffById={staffById}
        canShareGlobally={canShareGlobally} shareStaffList={staffList}
        onRename={onRenameGroup} onDuplicate={onDuplicateGroup}
        onDelete={onDeleteGroup} onSetSharing={onSetGroupSharing}
        // Editing membership hands the ids straight back to the picker behind
        // this view — that is the flow that already worked, just findable now.
        onUseMembers={(g) => { setSelected(g.memberIds || []); setManaging(false); }}
        onBack={() => setManaging(false)}
      />
    );
  }

  return (
    <>
      {/* Outside the savedGroups guard on purpose — the case that most needs the
          warning is the one where the list came back empty. */}
      {syncFailed && <SavedFilterSyncWarning onRetry={onRetrySync} />}
      {savedGroups.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Saved Filters</span>
            <button style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 11, padding: 0 }}
              onClick={() => setManaging(true)}>Manage</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {savedGroups.map(g => {
              const ids = g.memberIds || [];
              const cs = filterChipStyle(g);
              return (
                <div key={g.id || g.name} title={filterChipTitle(g, nameOf, nameOf)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: cs.background, border: cs.border, borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
                  onClick={() => setSelected(ids)}>
                  <span style={{ fontWeight: 600, color: cs.color }}>{g.name}</span>
                  <span style={{ color: '#9CA3AF' }}>({ids.length})</span>
                  {g.isMine === false && <span style={{ fontSize: 9, color: cs.color, opacity: 0.8 }}>· {g.ownerName || 'shared'}</span>}
                  {!g.isSystem && g.isMine !== false && (
                    <button style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 11, padding: 0, lineHeight: 1 }}
                      onClick={e => { e.stopPropagation(); onDeleteGroup(g); }}
                      title="Delete saved filter">&times;</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, maxHeight: 120, overflowY: 'auto' }}>
          {selected.map(sid => (
            <span key={sid} style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#DBEAFE', color: '#1D4ED8', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>
              {nameOf(sid)}
              <button style={{ background: 'none', border: 'none', color: '#1D4ED8', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}
                onClick={() => setSelected(prev => prev.filter(id => id !== sid))}>&times;</button>
            </span>
          ))}
          <button style={{ fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setSelected([])}>Clear all</button>
        </div>
      )}

      <StaffFilterPicker staffList={staffList} selectedIds={selected} onToggle={toggle} />

      <SaveFilterRow disabled={selected.length === 0}
        onSave={saveGroup}
        canShareGlobally={canShareGlobally}
        shareStaffList={staffList} staffById={staffById} />

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="sched-assign-btn" style={{ flex: 1 }}
          disabled={selected.length === 0}
          onClick={applyNow}>
          Apply Filter ({selected.length} staff)
        </button>
        <button className="sched-mgr-pill" style={{ padding: '8px 16px' }} onClick={onClose}>Cancel</button>
      </div>
    </>
  );
});

const ClientFilterPanel = memo(function ClientFilterPanel({
  initialSelected, clients, clientById, savedGroups, canShareGlobally,
  shareStaffList, staffById, syncFailed, onRetrySync,
  onApply, onSaveGroup, onDeleteGroup, onClose,
  onRenameGroup, onDuplicateGroup, onSetGroupSharing,
}) {
  const [selected, setSelected] = useState(initialSelected);
  const [managing, setManaging] = useState(false);
  const toggle = useCallback((id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);
  const nameOf = (cid) => clientById[cid]?.name || `#${cid}`;

  // See the note in StaffFilterPanel — stable so a toggle doesn't re-render the
  // save row and its recipient picker.
  const selRef = useRef(selected);
  selRef.current = selected;
  const saveGroup = useCallback((name, opts) => onSaveGroup(name, selRef.current, opts), [onSaveGroup]);
  const applyNow = useCallback(() => onApply(selRef.current), [onApply]);

  if (managing) {
    return (
      <ManageFilterList
        kind="client" filters={savedGroups} nameOf={nameOf} staffById={staffById}
        canShareGlobally={canShareGlobally} shareStaffList={shareStaffList}
        onRename={onRenameGroup} onDuplicate={onDuplicateGroup}
        onDelete={onDeleteGroup} onSetSharing={onSetGroupSharing}
        onUseMembers={(g) => { setSelected(g.memberIds || []); setManaging(false); }}
        onBack={() => setManaging(false)}
      />
    );
  }

  return (
    <>
      {syncFailed && <SavedFilterSyncWarning onRetry={onRetrySync} />}
      {savedGroups.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>Saved Client Groups</span>
            <button style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 11, padding: 0 }}
              onClick={() => setManaging(true)}>Manage</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {savedGroups.map(g => {
              const ids = g.memberIds || [];
              const cs = filterChipStyle(g);
              // Members are clients; the share list is people — two lookups.
              const staffNameOf = (sid) => staffById[sid]?.Name || `#${sid}`;
              return (
                <div key={g.id || g.name} title={filterChipTitle(g, nameOf, staffNameOf)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: cs.background, border: cs.border, borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
                  onClick={() => setSelected(ids)}>
                  <span style={{ fontWeight: 600, color: cs.color }}>{g.name}</span>
                  <span style={{ color: '#9CA3AF' }}>({ids.length})</span>
                  {g.isMine === false && <span style={{ fontSize: 9, color: cs.color, opacity: 0.8 }}>· {g.ownerName || 'shared'}</span>}
                  {g.isMine !== false && (
                    <button style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: 11, padding: 0, lineHeight: 1 }}
                      onClick={e => { e.stopPropagation(); onDeleteGroup(g); }}
                      title="Delete saved client group">&times;</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected chips are NAMED, not just counted — in a 7,692-client namespace
          "12 clients" is impossible to verify at a glance. */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, maxHeight: 120, overflowY: 'auto' }}>
          {selected.map(cid => (
            <span key={cid} title={clientById[cid]?.code || ''}
              style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#DBEAFE', color: '#1D4ED8', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>
              {nameOf(cid)}
              <button style={{ background: 'none', border: 'none', color: '#1D4ED8', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}
                onClick={() => setSelected(prev => prev.filter(id => id !== cid))}>&times;</button>
            </span>
          ))}
          <button style={{ fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setSelected([])}>Clear all</button>
        </div>
      )}

      <ClientFilterPicker clients={clients} selectedIds={selected} onToggle={toggle} />

      <SaveFilterRow disabled={selected.length === 0}
        onSave={saveGroup}
        placeholder="Client group name..."
        canShareGlobally={canShareGlobally}
        shareStaffList={shareStaffList} staffById={staffById} />

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="sched-assign-btn" style={{ flex: 1 }}
          disabled={selected.length === 0}
          onClick={applyNow}>
          Apply Filter ({selected.length} client{selected.length === 1 ? '' : 's'})
        </button>
        <button className="sched-mgr-pill" style={{ padding: '8px 16px' }} onClick={onClose}>Cancel</button>
      </div>
    </>
  );
});

// ── Main Component ──────────────────────────────────────────────────────────

export default function SchedulerGrid() {
  const { username, isSuperUser, isDevSuperUser } = useAuth();
  const navigate = useNavigate();
  const { refreshNotifications } = useOutletContext() || {};

  // Data
  const [staff, setStaff] = useState([]);
  const [entries, setEntries] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [allCustomJobs, setAllCustomJobs] = useState([]); // Item 2 — active custom jobs (lazy-loaded when modal opens)
  // ── Job Pools read-only visuals (Trello #257) ──
  // poolEvents: one row per claim/assign, anchored to ClaimedLocalDate — a day
  //   STAMP, not a schedule entry. Zero hours, zero duration, and deliberately
  //   invisible to capacity/utilization/conflict math.
  // poolAvailable: who is currently checked in as available in the pools.
  // Both exist so the grid isn't blank about pool work; neither changes a number.
  const [poolEvents, setPoolEvents] = useState([]);
  const [poolAvailable, setPoolAvailable] = useState([]);
  const [globalCalendars, setGlobalCalendars] = useState([]); // global calendar templates
  const [ptoData, setPtoData] = useState([]); // PTO from PE (approved + pending)
  const [holidays, setHolidays] = useState([]); // global holiday definitions
  const [globalAssignments, setGlobalAssignments] = useState([]); // firm-wide events
  const [placeholders, setPlaceholders] = useState([]); // Item 1 — (dept, level)-keyed job assignments awaiting staffer
  const [placeholderAssignTarget, setPlaceholderAssignTarget] = useState(null); // open placeholder in the assign modal
  // Job View item 6 — embedded staff suggestions for the schedule modal: top-5
  // ranked candidates for the selected real job (+ availability for the modal's week).
  const [modalSuggestions, setModalSuggestions] = useState({ jobId: null, list: [], loading: false });
  const [modalStaffFromSuggestion, setModalStaffFromSuggestion] = useState(false); // chip badge when picked from suggestions (single)
  const [modalSuggestedStaffIds, setModalSuggestedStaffIds] = useState(new Set()); // multi-staff chips added from a suggestion (★)
  const [modalPicklistMode, setModalPicklistMode] = useState('suggest'); // Item 4 — picklist toggle: 'suggest' | 'wip'
  const [modalWipStaff, setModalWipStaff] = useState({ jobId: null, list: [], wipByStaff: [], schedByStaff: [], loading: false }); // WIP-on-job: picklist list + by-staff popovers for the bar
  const [progressByJob, setProgressByJob] = useState({}); // Item 4 — per-job { BudgetHours, WipTotal, FutureScheduled } for the mini-bar + chips
  const [detailWip, setDetailWip] = useState({ jobId: null, byStaff: [] }); // on-demand per-staff WIP for the open jobs detail panel
  const [detailSched, setDetailSched] = useState({ jobId: null, byStaff: [] }); // on-demand per-staff scheduled hours (today onward, ALL dates) for the open job
  const [sidebarStaffMode, setSidebarStaffMode] = useState('scheduled'); // detail-sidebar by-staff list: 'scheduled' (today-on) | 'wip' (logged)
  const [scheduleSpans, setScheduleSpans] = useState([]); // (staff, job) groups that spill outside the fetched window
  // Custom-job roster for the "Custom" scope sidebar. Deliberately NOT folded into
  // detailSched: JobId and CustomJobId are separate id namespaces (CustomJobId 1 = EBP
  // Team, JobId 1 is a real job), so a shared `jobId` guard would cross-render one
  // job's roster onto the other. No WIP twin — PE has no custom jobs, so it is always
  // empty by construction.
  const [detailCustomSched, setDetailCustomSched] = useState({ customJobId: null, byStaff: [] });
  // The dept filter carries over on People↔Jobs switch (like office/search) — see the
  // toggle handler. The one-time Jobs default-landing is tracked by jobsDefaultedRef
  // (declared after viewMode below).
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore grid state from session storage
  const GRID_STATE_KEY = 'rprs_grid_state';
  const savedState = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(GRID_STATE_KEY) || '{}'); } catch { return {}; }
  }, []);

  // UI — initialized from saved session state
  // View state lives in the URL for shareable / navbot deeplinks; sessionStorage
  // (savedState) supplies the fallback default so a bare /schedule/grid still
  // restores the user's last view. See docs/navigation.md for the vocabulary.
  // Multi-select departments (e.g. Audit + General). Scope modes ('mine'/'all')
  // stay exclusive — see selectDeptScope. Normalize old single-string saved state.
  const initialDept = Array.isArray(savedState.deptFilter)
    ? savedState.deptFilter
    : (savedState.deptFilter ? [savedState.deptFilter] : ['mine']);
  const [deptFilter, setDeptFilter] = useUrlState('dept', initialDept, csvOfStrings);
  const [staffFilter, setStaffFilter] = useUrlState('staff', savedState.staffFilter || [], csvOfNumbers);
  const [staffSearch, setStaffSearch] = useUrlState('q', savedState.staffSearch || '');
  const [clientSearch, setClientSearch] = useUrlState('cq', savedState.clientSearch || ''); // client name/code filter (both views)
  // Explicit client selection from the Filter Client Groups modal (?client=ids).
  // The id-list counterpart to ?cq, exactly as ?staff is to ?q.
  const [clientFilter, setClientFilter] = useUrlState('client', savedState.clientFilter || [], csvOfNumbers);

  // ── "More filters" (suggestion #17) ────────────────────────────────────────
  // Job-shaped filters that don't fit the toolbar. URL-backed like every other
  // filter so a narrowed view stays shareable.
  //   ?ret=1040,1120S  — return types (RETURN_TYPES ids, plus 'other')
  //   ?prep=15         — minimum budgeted PREP hours from PE (0 = off)
  const [returnTypes, setReturnTypes] = useUrlState('ret', [], csvOfStrings);
  const [minPrepHours, setMinPrepHours] = useUrlState('prep', 0, {
    serialize: v => String(v || 0),
    deserialize: s => { const n = parseFloat(s); return Number.isFinite(n) && n > 0 ? n : 0; },
  });
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const moreFiltersRef = useRef(null);
  // Close the popover on an outside click, matching the notification bell + legend.
  useEffect(() => {
    if (!showMoreFilters) return undefined;
    const onDown = (e) => {
      if (moreFiltersRef.current && !moreFiltersRef.current.contains(e.target)) setShowMoreFilters(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showMoreFilters]);
  const [highlightStaffIds, setHighlightStaffIds] = useState(new Set());
  const [highlightOldCells, setHighlightOldCells] = useState(new Set()); // amber — old/removed cells
  const [highlightNewCells, setHighlightNewCells] = useState(new Set()); // blue — new/changed cells
  const [highlightConflictCells, setHighlightConflictCells] = useState(null);
  const [weekOffset, setWeekOffset] = useUrlState('week', savedState.weekOffset ?? 0, { serialize: offsetToWeekParam, deserialize: weekParamToOffset });
  const [zoom, setZoom] = useUrlState('zoom', savedState.zoom ?? 5);
  const [viewMode, setViewMode] = useUrlState('view', savedState.viewMode || 'people', { validate: ['people', 'jobs'] });
  const jobsDefaultedRef = useRef(viewMode === 'jobs'); // one-time Jobs default-landing guard
  // "Custom" is an exclusive dept scope (see SCOPE_MODES) that swaps the Jobs view's rows
  // from client jobs to firm-internal custom jobs.
  const customScope = viewMode === 'jobs' && deptFilter.includes('custom');
  const [hideEmpty, setHideEmpty] = useUrlState('hideEmpty', savedState.hideEmpty ?? false);
  const [showFreeOnly, setShowFreeOnly] = useUrlState('freeOnly', savedState.showFreeOnly ?? false);
  const [hideLeadership, setHideLeadership] = useUrlState('hideLeadership', savedState.hideLeadership ?? true);
  const [officeFilter, setOfficeFilter] = useUrlState('office', savedState.officeFilter || 'all');
  const [jobLead, setJobLead] = useUrlState('jobLead', savedState.jobLead || '');            // JM/JP filter: selected StaffId (string) or ''
  const [jobLeadRole, setJobLeadRole] = useUrlState('jobLeadRole', savedState.jobLeadRole || 'both', { validate: ['both', 'jm', 'jp'] });
  const [showLegend, setShowLegend] = useState(false);

  // Dynamic column widths based on zoom level
  const DW = zoom === 1 ? 120 : 38;  // weekday column width
  const WW = zoom === 1 ? 40 : 22;   // weekend column width

  const [selectedBlock, setSelectedBlock] = useState(null); // restored from savedState.selectedBlockKey after data loads
  // ?job=<id> deeplink — source of truth for the jobs-mode job sidebar (persistent + Back-safe).
  const [jobParam, setJobParam] = useUrlState('job', null, { replace: false });
  const focusJobId = jobParam ? parseInt(jobParam, 10) : null;
  const jobRowRefs = useRef({});
  const consumedJobParam = useRef(undefined);
  // ?cjob=<id> — the custom-job twin of ?job=. A separate param rather than a shared one
  // for the same reason detailCustomSched is separate: the two id spaces overlap.
  const [customJobParam, setCustomJobParam] = useUrlState('cjob', null, { replace: false });
  const focusCustomJobId = customJobParam ? parseInt(customJobParam, 10) : null;
  const customJobRowRefs = useRef({});
  const consumedCustomJobParam = useRef(undefined);
  // Custom-job rows collapse to CUSTOM_ROW_TRACK_CAP tracks; this holds the ids the user
  // has expanded.
  const [expandedCustomRows, setExpandedCustomRows] = useState(() => new Set());
  const [entryHistOpen, setEntryHistOpen] = useState(false); // entry sidebar: daily-breakdown histogram collapsed by default
  const [tooltip, setTooltip] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalPerson, setModalPerson] = useState(null);
  const [dragging, setDragging] = useState(null);
  // Drag tooltip lives in its own component and is driven imperatively — see
  // DragHoverTooltip. Deliberately NOT state here: onDragOver fires per pixel.
  const dragTipRef = useRef(null);
  const showDragTip = useCallback((x, y, label) => dragTipRef.current?.show({ x, y, label }), []);
  const hideDragTip = useCallback(() => dragTipRef.current?.hide(), []);
  const [ptoCellPopover, setPtoCellPopover] = useState(null); // { x, y, req } for admin PTO approve/reject
  const [myPtoPopover, setMyPtoPopover] = useState(null); // { x, y, req, person, dayIdx } for self-cancel
  const [ptoPopoverActioning, setPtoPopoverActioning] = useState(false);
  const [toast, setToast] = useState(null); // { message, type: 'info'|'success'|'error' }
  // Closed-job cleanup: clearing the future-dated time stranded on a job PE has completed.
  // Holds the server's preview ({ entries, hours, staff[] }) between the button press and
  // the confirm, so the count shown is the count the server will actually delete.
  const [closedDelete, setClosedDelete] = useState(null); // { jobId, scope, preview, busy }
  // URL vocabulary uses ?tab=calendar|conflicts; internal token stays 'alerts'.
  const [tab, setTab] = useUrlState('tab', savedState.tab || 'calendar', {
    serialize: v => (v === 'alerts' ? 'conflicts' : 'calendar'),
    deserialize: s => (s === 'conflicts' ? 'alerts' : 'calendar'),
    validate: ['calendar', 'alerts'],
  });
  const [showDailyGrid, setShowDailyGrid] = useState(false); // collapsed by default in edit modal

  // Modal form
  const [modalJob, setModalJob] = useState('');
  const [modalJobSearch, setModalJobSearch] = useState('');
  // Item 2 — picker toggle: 'real' (existing client/job dropdown) | 'custom' (firm-internal jobs)
  const [modalJobKind, setModalJobKind] = useState('real');
  // 'all' | 'ai' — a FILTER on the real-job picker (the toggle's "AI" segment). AI jobs are
  // ordinary real jobs, so modalJobKind stays 'real' and every save/edit path is unchanged.
  const [modalJobScope, setModalJobScope] = useState('all');
  const [aiJobWip, setAiJobWip] = useState(null);   // rprs.JobsWIP rows for the AI client, loaded once on first use
  const [modalCustomJobId, setModalCustomJobId] = useState('');
  const [modalStaffSearch, setModalStaffSearch] = useState('');
  // Item 1 — placeholder mode for the new-entry modal (Person | Dept+Level).
  const [modalMode, setModalMode] = useState('person'); // 'person' | 'placeholder'
  const [modalDept, setModalDept] = useState('');
  const [modalLevel, setModalLevel] = useState('');
  const [modalStart, setModalStart] = useState('');
  const [modalEnd, setModalEnd] = useState('');
  const [modalHours, setModalHours] = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [modalEditBlock, setModalEditBlock] = useState(null); // non-null = editing existing
  const [modalAllocType, setModalAllocType] = useState('total'); // 'total' | 'daily'
  const [modalDailyHours, setModalDailyHours] = useState({}); // { 'Mon': 8, 'Tue': 8, ... }
  const [modalSource, setModalSource] = useState('ui'); // tracks origin: 'ui', 'suggestion', 'schedule-btn'
  const [modalOrigDailyHours, setModalOrigDailyHours] = useState({}); // snapshot of original daily hours
  const [modalOrigStart, setModalOrigStart] = useState(''); // ISO snapshot for Reset-to-original
  const [modalOrigEnd, setModalOrigEnd] = useState('');
  const [modalOrigAllocType, setModalOrigAllocType] = useState('total'); // classified mode on open — Reset restores this
  const [modalCapacityWarning, setModalCapacityWarning] = useState(null); // string or null
  const [modalOverflowStart, setModalOverflowStart] = useState(false);
  const [modalOverflowEnd, setModalOverflowEnd] = useState(false);
  const [optimizeHasOverCap, setOptimizeHasOverCap] = useState(false);
  // Multi-staff mode (create-only). Each tab is an INDEPENDENT single-staff editor:
  // modalStaffList holds the team; modalActiveStaffId is the focused tab; and
  // modalSettingsByStaff keeps every NON-active tab's full editor state
  // { start, end, hours, allocType, dailyHours, fillMode, overflowStart, overflowEnd }.
  // The ACTIVE tab's truth is the scalar modal state (modalStart/End/Hours/…) — the
  // whole single-staff editor drives it — and is flushed back into the map on tab
  // switch (see buildActiveSettings / hydrateScalarsFrom / switchActiveStaff).
  const [modalIsMultiStaff, setModalIsMultiStaff] = useState(false);
  const [modalStaffList, setModalStaffList] = useState([]);
  const [modalActiveStaffId, setModalActiveStaffId] = useState(null);
  const [modalSettingsByStaff, setModalSettingsByStaff] = useState({});
  const [modalFillMode, setModalFillMode] = useState(false);
  const modalNotesRef = useRef('');
  const staffTabScrollRef = useRef(null);
  // Snapshot of pre-Fill state so toggling Fill mode OFF restores prior values.
  // Captured at Fill ON, cleared at Fill OFF.
  const modalFillSnapshotRef = useRef(null);
  // Per-staff pre-fill snapshot for the multi-staff "Fill all" toggle.
  const modalFillAllSnapshotRef = useRef(null);
  // Transient flag: pulse the non-active tabs after "Copy active → all".
  const [copyPulsing, setCopyPulsing] = useState(false);
  // Multi-staff create confirm-gate: shows a per-staff gantt preview before saving.
  const [showMultiPreview, setShowMultiPreview] = useState(false);
  // Chooser-originated placeholders (dept+level) staged for review in the confirm-gate,
  // created via upsertPlaceholder on Confirm. [{ dept, level, hours, startDate, endDate }]
  const [pendingPlaceholders, setPendingPlaceholders] = useState([]);

  // Drag-drop preview modal
  const [dropPreview, setDropPreview] = useState(null);
  const dropNotesRef = useRef('');
  const [dropOverflowStart, setDropOverflowStart] = useState(false); // allow overflow before start date
  const [dropOverflowEnd, setDropOverflowEnd] = useState(false); // allow overflow after end date
  // { block, targetStaffId, targetStaffName, dayShift, mode: 'shift'|'optimize', dailyHours: {iso: hours}, warnings: [], newDates: ['iso',...] }

  // Convert-to-placeholder preview modal (staff block dropped onto the
  // (dept, level) placeholder row at the top of the group).
  // { block, departmentCode, level, totalHours, minDate, maxDate, willMergeWith }
  const [convertPreview, setConvertPreview] = useState(null);
  const [convertSaving, setConvertSaving] = useState(false);

  // Conflict resolution state
  const [fixConflict, setFixConflict] = useState(null); // { staffId, weekIdx, weekStart } — which conflict is being resolved
  const [optimizeConflict, setOptimizeConflict] = useState(null); // { staffId, weekIdx } — which conflict's optimize panel is open
  const [fixEntries, setFixEntries] = useState([]); // jobs grouped by jobId for the conflicted week
  const [fixSelectedJob, setFixSelectedJob] = useState(null); // jobId selected for reassignment
  const [fixCandidates, setFixCandidates] = useState([]); // replacement candidates from querySuggestions
  const [fixLoading, setFixLoading] = useState(false);
  const [fixNicheMissing, setFixNicheMissing] = useState(false); // fix-job's client has no real niche
  // The fix-job's on-site restriction. Previously computed as a local const inside the
  // async candidate loader, so the hard office filter at the bottom of that function
  // had NOTHING rendering it -- the panel just showed a short list, and when the office
  // eliminated everyone it fell through to "No candidates at a similar level with
  // capacity this week", naming the wrong reason entirely.
  const [fixOnSite, setFixOnSite] = useState({ isOnSite: false, office: null, officeCode: null });
  const [fixReassigning, setFixReassigning] = useState(false);
  const [fixExactLevel, setFixExactLevel] = useState(false); // true = exact level match, false = +-1 level

  // ── Gen Track balance chip (Trello #309) ─────────────────────────────────
  // One cached fetch shared with the Suggestions page, the placeholder modal and the
  // schedule chooser — gentrack_bands aggregates 6.8M rows of pe.TranWIP, so it must
  // not run once per surface. See hooks/useGenTrackBands.js.
  //
  // Two buckets because this file has two independent job contexts: the edit/create
  // modal (modalJob) and the conflict-resolution panel (fixSelectedJob). The chip only
  // fires when the job at hand is the side the candidate is short of, so mixing them up
  // would flag the wrong people.
  const { bandByStaff: genTrackBands, lists: genTrackLists } = useGenTrackBands();

  const modalJobBucket = useMemo(() => {
    const j = allJobs.find(x => x.JobId === parseInt(modalJob, 10));
    return jobBucketOf(j, genTrackLists);
  }, [allJobs, modalJob, genTrackLists]);

  const fixJobBucket = useMemo(() => {
    const j = allJobs.find(x => x.JobId === fixSelectedJob);
    return jobBucketOf(j, genTrackLists);
  }, [allJobs, fixSelectedJob, genTrackLists]);

  const genTrackFlagFor = useCallback((c) => (
    <GenTrackFlag band={genTrackBands[c.StaffId]}
      deptCode={c.DepartmentCode || c.Department} jobBucket={modalJobBucket} />
  ), [genTrackBands, modalJobBucket]);

  const fixGenTrackFlagFor = useCallback((c) => (
    <GenTrackFlag band={genTrackBands[c.StaffId]}
      deptCode={c.DepartmentCode || c.Department} jobBucket={fixJobBucket} />
  ), [genTrackBands, fixJobBucket]);
  const [reassignScope, setReassignScope] = useState('week'); // 'week' | 'all'
  const [conflictSelectedJob, setConflictSelectedJob] = useState(null); // { jobId, jobName, clientName, ... } for sidebar
  const [conflictJobWip, setConflictJobWip] = useState([]); // per-staff WIP for selected job
  const [conflictJobSched, setConflictJobSched] = useState([]); // per-staff scheduled today-on (window-independent) for selected conflict job
  const [fixFullBlock, setFixFullBlock] = useState(null); // { totalHours, startISO, endISO, days[] } — true contiguous block for conflicted staff+job (window-independent)
  // Every entry on the selected conflict job, all dates (window-independent). Lets each
  // candidate card say up front which days they ALREADY have on this job — those hours are
  // combined on reassign rather than colliding.
  const [fixJobEntries, setFixJobEntries] = useState([]);
  // In-place History modal: { jobId, jobName, clientName } or { staffId, staffName }.
  const [historyModal, setHistoryModal] = useState(null);

  // Staff filter modal state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showClientFilterModal, setShowClientFilterModal] = useState(false);
  // Set when the saved-filter read fails, so both modals can say so rather than
  // rendering an empty list that looks identical to having none.
  const [savedFiltersSyncFailed, setSavedFiltersSyncFailed] = useState(false);
  // No filterSearch here on purpose — the modal's query lives inside StaffFilterPicker.
  // Holding it at this level meant every keystroke re-rendered the whole grid.
  // The in-progress selection lives inside StaffFilterPanel / ClientFilterPanel,
  // not here — see the note on those components. The grid only supplies the seed
  // (the currently-applied filter) and receives the result on Apply.
  const [savedFilters, setSavedFilters] = useState(() => {
    // Normalized on read: the cache predates `memberIds` and still holds `staffIds`.
    try {
      const raw = JSON.parse(localStorage.getItem(`sched-grid-filters-${username}`) || '[]');
      return Array.isArray(raw) ? raw.map(normalizeSavedFilter).filter(Boolean) : [];
    } catch { return []; }
  });

  const gridRef = useRef(null);
  const gridBodyRef = useRef(null);
  const leftRef = useRef(null);
  const rowRefs = useRef({}); // staffId → DOM element for scroll-to
  const pendingConflictHighlight = useRef(null); // key to highlight after conflicts re-render
  const sidebarRef = useRef(null);
  const modalBackdropRef = useRef(null);
  const dndModalRef = useRef(null);
  const filterModalRef = useRef(null);
  const clientFilterModalRef = useRef(null);

  // ── Persist grid state to sessionStorage ──────────────────────────────
  useEffect(() => {
    sessionStorage.setItem(GRID_STATE_KEY, JSON.stringify({
      deptFilter, staffFilter, staffSearch, clientSearch, clientFilter, weekOffset, zoom, viewMode, hideEmpty, showFreeOnly, hideLeadership, officeFilter, jobLead, jobLeadRole, tab,
      selectedBlockKey: selectedBlock?.key || null,
    }));
  }, [deptFilter, staffFilter, staffSearch, clientSearch, clientFilter, weekOffset, zoom, viewMode, hideEmpty, showFreeOnly, hideLeadership, officeFilter, jobLead, jobLeadRole, tab, selectedBlock]);

  // ── Computed date window ──────────────────────────────────────────────
  // Today's local ISO, computed once per render rather than per staff row — the
  // NOT-STARTED chip is evaluated for every person in the grid.
  const todayISO = useMemo(() => toISO(new Date()), []);

  const baseMonday = useMemo(() => {
    const m = getMonday();
    return addDays(m, weekOffset * 7);
  }, [weekOffset]);

  const totalDays = zoom * 7;
  const allDays = useMemo(() => {
    return Array.from({ length: totalDays }, (_, i) => {
      const d = addDays(baseMonday, i);
      return {
        idx: i,
        date: d,
        iso: toISO(d),
        dow: d.getDay(),
        isWE: isWeekend(d),
        today: isToday(d),
        w: isWeekend(d) ? WW : DW,
        label: DOW_LABELS[d.getDay()],
        dn: d.getDate(),
        mn: d.toLocaleDateString('en-US', { month: 'short' }),
      };
    });
  }, [baseMonday, totalDays, DW, WW]);

  // Pre-compute x positions
  const dayX = useMemo(() => {
    const xs = [0];
    for (let i = 1; i < allDays.length; i++) {
      xs.push(xs[i-1] + allDays[i-1].w);
    }
    return xs;
  }, [allDays]);

  const totalW = useMemo(() =>
    allDays.reduce((s, d) => s + d.w, 0), [allDays]);

  // Week groupings for headers
  const weeks = useMemo(() => {
    const result = [];
    for (let wi = 0; wi < zoom; wi++) {
      const wDays = allDays.slice(wi * 7, (wi + 1) * 7);
      const wkDays = wDays.filter(d => !d.isWE);
      result.push({
        idx: wi,
        days: wDays,
        label: `${wkDays[0]?.mn} ${wkDays[0]?.dn}\u2013${wkDays[wkDays.length-1]?.mn} ${wkDays[wkDays.length-1]?.dn}`,
        w: wDays.reduce((s, d) => s + d.w, 0),
      });
    }
    return result;
  }, [allDays, zoom]);

  const endDate = useMemo(() => addDays(baseMonday, totalDays - 1), [baseMonday, totalDays]);

  // The { startDate, endDate } the loaded schedule/PTO actually cover. Not the same as
  // baseMonday..endDate: right after a week jump there is a render with the NEW window
  // and the OLD data, before the load effect flips `loading`. Anything that must not run
  // on stale data (the chooser hand-off) checks this instead.
  const loadedRangeRef = useRef(null);
  // Set once the chooser hand-off has moved the grid, so a render in between can't jump again.
  const handoffJumpRef = useRef(false);

  // ── Load data ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const range = { startDate: toISO(baseMonday), endDate: toISO(endDate) };
        // Ten of these eleven reads are the same Function with a different `type`, so
        // they go out as ONE bootstrap request rather than ten. Ten separate fetches
        // meant ten concurrent invocations, and SWA managed functions share one small
        // worker pool across every /api/* route — so a grid load held ~10 workers and
        // made everything else (including saves) queue behind it.
        //
        // Pool availability stays separate: it lives on a different Function (rprsPool).
        const [boot, poolAvailData] = await Promise.all([
          fetchBootstrap([
            // includeScheduledInactive: keep departed staff who still hold future work on
            // the grid so it stays visible/reassignable (see scheduledInactiveIds below).
            { type: 'staff', args: { includeScheduledInactive: true } },
            { type: 'schedule', args: range },
            // Continuation info for blocks that run past the window edge. Optional — the
            // grid renders fine without it, just with no truncation markers.
            { type: 'schedule_spans', args: range },
            { type: 'jobs', args: {} },
            { type: 'calendars', args: {} }, // global templates (no staffId = globals only)
            { type: 'pto', args: range },
            { type: 'holidays', args: {} },
            { type: 'global_assignments', args: {} },
            { type: 'placeholders', args: {} },
            { type: 'custom_jobs', args: { activeOnly: true } },
            // Job Pools (Trello #257) — READ-ONLY VISUAL. Pool work never touches the
            // calendar, so it must not enter capacity, block stacking, utilization or
            // conflict math. Decoration over the grid, nothing more.
            { type: 'pool_events', args: range },
          ]),
          fetchPoolAvailability().then(r => r.available || []).catch(() => []),
        ]);
        if (cancelled) return;
        // Bootstrap isolates per-part failures, so an optional dataset that errored is
        // simply absent — same effect the old per-fetch .catch(() => []) had.
        const d = boot?.data || {};
        setStaff(d.staff || []);
        setEntries(d.schedule || []);
        loadedRangeRef.current = range;
        setScheduleSpans(d.schedule_spans || []);
        setAllJobs(d.jobs || []);
        setGlobalCalendars(d.calendars || []);
        setPtoData(d.pto || []);
        setHolidays(d.holidays || []);
        setGlobalAssignments(d.global_assignments || []);
        setPlaceholders(d.placeholders || []);
        setAllCustomJobs(d.custom_jobs || []);
        setPoolEvents(d.pool_events || []);
        setPoolAvailable(poolAvailData || []);
        // Surface a failed REQUIRED dataset rather than rendering a silently empty grid.
        // The optional ones (placeholders, custom_jobs, pool_events, schedule_spans) previously swallowed
        // their own errors, so they stay non-fatal.
        const fatal = ['staff', 'schedule', 'jobs'].filter(k => boot?.errors?.[k]);
        if (fatal.length) throw new Error(fatal.map(k => `${k}: ${boot.errors[k]}`).join('; '));
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Restore scroll position from session (retry until grid has scrollable content)
          const savedTop = parseInt(sessionStorage.getItem('rprs_grid_scrollTop') || '0');
          const savedLeft = parseInt(sessionStorage.getItem('rprs_grid_scrollLeft') || '0');
          if (savedTop || savedLeft) {
            const restoreScroll = (attempts) => {
              if (!gridRef.current || attempts <= 0) return;
              gridRef.current.scrollTop = savedTop;
              gridRef.current.scrollLeft = savedLeft;
              // Verify it took — if scrollHeight is too small, content hasn't rendered yet
              if (gridRef.current.scrollTop < savedTop - 5 && attempts > 1) {
                setTimeout(() => restoreScroll(attempts - 1), 100);
              }
            };
            setTimeout(() => restoreScroll(5), 50);
          }
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [baseMonday, endDate]);

  // ── Resolve logged-in user's StaffId ──────────────────────────────────
  const myStaffId = useMemo(() => {
    if (!username || !staff.length) return null;
    const uLower = (username || '').toLowerCase();
    // Match by email or by username prefix of email
    const match = staff.find(s => {
      const email = (s.Email || '').toLowerCase();
      return email === uLower + '@bmss.com' || email === uLower;
    });
    return match ? match.StaffId : null;
  }, [staff, username]);

  // ── Guided-tour demo data ──
  // While the tour runs, append a VIEW-ONLY sample block (sentinel JobId -1) to the
  // signed-in user's row so the tour can spotlight "your schedule", drag-drop, and
  // the entry sidebar. demoEntries feeds blocksByPerson/blocksByJob; nothing persists.
  const tourDemo = useTourDemo();
  const myStaffName = useMemo(
    () => (staff.find(s => s.StaffId === myStaffId)?.Name) || null,
    [staff, myStaffId],
  );
  const demoEntries = useMemo(() => {
    if (!tourDemo || !myStaffId) return entries;
    return [...entries, ...buildDemoEntries(myStaffId, myStaffName, toISO(baseMonday))];
  }, [entries, tourDemo, myStaffId, myStaffName, baseMonday]);
  // Sentinel jobs added to the job list so the modal's picker + jobInfoMap resolve
  // their names during the tour. Passthrough — returns `allJobs` verbatim when off.
  const demoAllJobs = useMemo(
    () => (tourDemo ? [...allJobs, ...TOUR_GRID_JOBS] : allJobs),
    [allJobs, tourDemo],
  );

  // ── Edit-modal picker lists ───────────────────────────────────────────────
  // Hoisted out of the modal's render IIFE (which can't hold hooks, so they were
  // recomputed on EVERY grid render — including drags, hour edits and week nav, not
  // just typing). The job scan touches ~8,500 rows; it now runs only when the
  // committed query or the job list actually changes.
  //
  // `q` is lifted out of the predicate too: it used to call toLowerCase() inside the
  // filter callback, i.e. once per job rather than once per search.
  const modalFilteredJobs = useMemo(() => {
    const q = modalJobSearch.trim().toLowerCase();
    if (!q) return [];
    return demoAllJobs.filter(j =>
      (j.ClientName || '').toLowerCase().includes(q) ||
      (j.Name || '').toLowerCase().includes(q) ||
      (j.Code || '').toLowerCase().includes(q)
    ).slice(0, 100);
  }, [demoAllJobs, modalJobSearch]);

  // AI picker. The segment follows the SELECTED job, so every way into the modal (edit, a
  // Jobs-view row, a suggestion draft) lands on the right one without each path setting it.
  // Keyed on the job + modal open only: clicking Real with an AI job selected must not snap back.
  useEffect(() => {
    if (!showModal || !modalJob) return;
    const j = demoAllJobs.find(x => String(x.JobId) === modalJob);
    if (j) setModalJobScope(isAiJob(j) ? 'ai' : 'all');
  }, [modalJob, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Who charged to which AI project: one small read the first time the AI segment opens.
  // Hourly-synced data, so once per grid session is fresh enough.
  const aiWipRequested = useRef(false);
  useEffect(() => {
    if (!showModal || modalJobKind !== 'real' || modalJobScope !== 'ai' || aiWipRequested.current) return;
    const aiClientId = demoAllJobs.find(isAiJob)?.ClientId;
    if (!aiClientId) return;
    aiWipRequested.current = true;
    fetchJobsWIP({ clientId: aiClientId }).then(d => setAiJobWip(d || [])).catch(() => setAiJobWip([]));
  }, [showModal, modalJobKind, modalJobScope, demoAllJobs]);

  const modalAiJobSections = useMemo(() => {
    if (modalJobKind !== 'real' || modalJobScope !== 'ai') return [];
    const people = modalMode === 'placeholder' ? []
      : modalIsMultiStaff ? modalStaffList : (modalPerson ? [modalPerson] : []);
    const staffLabel = people.length === 1 ? (people[0].Name || '').split(' ')[0] || 'this person' : 'selected staff';
    return buildAiJobSections({
      jobs: demoAllJobs,
      query: modalJobSearch,
      staffIds: people.map(p => p.StaffId),
      staffLabel,
      wipRows: aiJobWip || [],
      cutoffISO: toISO(addDays(new Date(), -AI_RECENT_DAYS)),
    });
  }, [modalJobKind, modalJobScope, modalMode, modalIsMultiStaff, modalStaffList, modalPerson, demoAllJobs, modalJobSearch, aiJobWip]);

  const modalFilteredCustomJobs = useMemo(() => {
    const q = modalJobSearch.trim().toLowerCase();
    if (!q) return allCustomJobs;
    return allCustomJobs.filter(c =>
      (c.Name || '').toLowerCase().includes(q) ||
      (c.Category || '').toLowerCase().includes(q) ||
      (c.Description || '').toLowerCase().includes(q)
    );
  }, [allCustomJobs, modalJobSearch]);
  // Sample placeholder shown on the grid (Jobs view) during the tour. Passthrough —
  // returns real placeholders verbatim when off.
  const demoPlaceholders = useMemo(
    () => (tourDemo ? [...placeholders, buildDemoPlaceholder(toISO(baseMonday))] : placeholders),
    [placeholders, tourDemo, baseMonday],
  );

  // ── On-site flag: gating + persist/patch ──────────────────────────────
  // JM/JP of the job (or a superuser) may toggle. Field casing differs by source
  // (queryJobs → ManagerId/PartnerId; grid baseJobs → managerId/partnerId).
  const canSetOnSite = useCallback((job) => {
    if (!job) return false;
    if (isSuperUser) return true;
    const mgr = job.ManagerId ?? job.managerId;
    const ptr = job.PartnerId ?? job.partnerId;
    return (mgr != null && mgr === myStaffId) || (ptr != null && ptr === myStaffId);
  }, [isSuperUser, myStaffId]);

  const handleSetOnSite = useCallback(async (jobId, next) => {
    // Optimistic patch of allJobs → jobInfoMap/baseJobs re-derive, so badges + the
    // off-office checks update without a reload.
    setAllJobs(prev => prev.map(j => (j.JobId === jobId ? { ...j, IsOnSite: next ? 1 : 0 } : j)));
    try {
      await setJobOnSite({ jobId, isOnSite: next });
      showToast(next ? 'Marked on-site' : 'On-site cleared', 'success');
    } catch (e) {
      setAllJobs(prev => prev.map(j => (j.JobId === jobId ? { ...j, IsOnSite: next ? 0 : 1 } : j)));
      showToast('Failed to update on-site: ' + (e?.message || e), 'error');
    }
    // showToast is defined later in the component (stable) — referenced at call time, kept out of deps to avoid a TDZ on this line.
  }, [isSuperUser, myStaffId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Off-office advisory: a staffer assigned to an on-site job from a different office.
  const staffOfficeById = useMemo(() => {
    const m = {};
    for (const s of staff) m[s.StaffId] = s.OfficeCode || '';
    return m;
  }, [staff]);
  // Unknown staffer office also flags (same-office can't be confirmed); OUTSOURCE flags.
  const offOffice = useCallback(
    (staffId, onSite, officeCode) => !!onSite && !!officeCode && staffOfficeById[staffId] !== officeCode,
    [staffOfficeById]
  );

  // ── Homeroom system filter (auto-generated for homeroom leaders) ──────
  const myHomeroomFilter = useMemo(() => {
    if (!myStaffId || !staff.length) return null;
    const me = staff.find(s => s.StaffId === myStaffId);
    if (!me?.IsHomeRoomLeader || !me.Homeroom) return null;
    // ScheduledInactive is allowed through so a homeroom leader's filter doesn't hide a
    // departed report's leftover work. visibleStaff does the final per-window gate.
    const ids = staff.filter(s => (s.IsActive || s.ScheduledInactive) && s.Homeroom === me.Homeroom).map(s => s.StaffId);
    return ids.length ? { id: '__system_homeroom__', name: 'My Homeroom', kind: 'staff', memberIds: ids, isSystem: true, isMine: true, visibility: 'private' } : null;
  }, [staff, myStaffId]);

  // Staff-kind only: this list feeds the Filter Staff modal. Client groups are a
  // separate list on the client picker, so a client group must never surface here
  // as if its ids were people.
  const displayFilters = useMemo(() => {
    const staffKind = savedFilters.filter(f => (f.kind || 'staff') === 'staff');
    return [...(myHomeroomFilter ? [myHomeroomFilter] : []), ...staffKind];
  }, [myHomeroomFilter, savedFilters]);

  // No system equivalent of the homeroom filter here — there is no client grouping
  // the firm already agrees on, which is exactly why Kate asked to build her own.
  const clientDisplayFilters = useMemo(
    () => savedFilters.filter(f => f.kind === 'client'),
    [savedFilters]);

  // ── Window-truncation ─────────────────────────────────────────────────────
  // A block is built only from entries inside the fetched window, so one that continues
  // past an edge looks exactly like one that ends there. `schedule_spans` supplies the
  // missing half: the group's true first/last date, its true total, and the nearest entry
  // just outside each edge.
  const spanKey = (staffId, jobId, customJobId) => (
    customJobId ? `${staffId}_C${customJobId}` : `${staffId}_${jobId}`
  );

  const spansByGroup = useMemo(() => {
    const m = {};
    for (const s of (scheduleSpans || [])) {
      m[spanKey(s.StaffId, s.JobId, s.CustomJobId)] = s;
    }
    return m;
  }, [scheduleSpans]);

  // Classify what is hidden beyond each edge for one group.
  //
  // FUTURE-ONLY, both edges: work already in the past is not actionable, and marking it
  // would put an arrow on nearly every bar in a back-dated window. Keying on "is the hidden
  // work itself >= today" rather than "is the window in the future" gets this exactly right
  // in the one case that matters — a window navigated forward, where work between today and
  // the window start IS future work and DOES deserve a left marker.
  //
  // 'run'  = resumes within the same 3-day gap the block splitter uses, i.e. this very bar
  //          keeps going. 'later' = more work on the job, but a separate block.
  const windowEdges = useMemo(() => {
    if (!allDays.length) return { startISO: null, endISO: null };
    return { startISO: allDays[0].iso, endISO: allDays[allDays.length - 1].iso };
  }, [allDays]);

  const continuationFor = useCallback((staffId, jobId, customJobId) => {
    const s = spansByGroup[spanKey(staffId, jobId, customJobId)];
    if (!s || !windowEdges.endISO) return null;
    const todayISO = toISO(new Date());
    const iso = v => (v ? String(v).slice(0, 10) : null);
    const days = (a, b) => Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000);

    const nextAfter = iso(s.NextAfter);
    const prevBefore = iso(s.PrevBefore);
    const right = (nextAfter && nextAfter >= todayISO)
      ? { kind: days(windowEdges.endISO, nextAfter) <= 3 ? 'run' : 'later', date: nextAfter }
      : null;
    const left = (prevBefore && prevBefore >= todayISO)
      ? { kind: days(prevBefore, windowEdges.startISO) <= 3 ? 'run' : 'later', date: prevBefore }
      : null;
    return {
      right, left,
      fullMin: iso(s.FullMin), fullMax: iso(s.FullMax),
      fullHours: Math.round((s.FullHours || 0) * 100) / 100,
      // Clipping is reported regardless of past/future: an END of "Oct 4" for work that
      // actually runs to Nov 21 is wrong whichever side of today it falls on, and the
      // sidebar is where people read exact figures.
      clippedLeft: !!prevBefore,
      clippedRight: !!nextAfter,
    };
  }, [spansByGroup, windowEdges]);

  // Click a continuation marker → grow the window until the assignment fits.
  // Right-hand growth is just more weeks. Left-hand growth has to MOVE the window start
  // back (weekOffset) and add the same number of weeks to zoom, or pulling the start back
  // would push the current right edge out of view.
  const widenToShow = useCallback((b, side) => {
    const span = b?.span;
    if (!span) return;
    const target = side === 'right' ? span.fullMax : span.fullMin;
    if (!target) return;
    const day = 86400000;
    const t = new Date(target + 'T12:00:00');

    if (side === 'right') {
      const weeksNeeded = Math.ceil(((t - baseMonday) / day + 1) / 7);
      const next = weeksNeeded <= WIDEN_SOFT_CAP_WEEKS
        ? weeksNeeded
        : (zoom < WIDEN_SOFT_CAP_WEEKS ? WIDEN_SOFT_CAP_WEEKS : Math.min(52, weeksNeeded));
      setZoom(Math.min(52, Math.max(next, zoom + 1)));
      return;
    }
    const weeksBack = Math.max(1, Math.ceil((baseMonday - t) / day / 7));
    // Cap the same way as the right side: one click never takes you past a quarter.
    const headroom = Math.max(1, (zoom < WIDEN_SOFT_CAP_WEEKS ? WIDEN_SOFT_CAP_WEEKS : 52) - zoom);
    const shift = Math.min(weeksBack, headroom);
    setWeekOffset(weekOffset - shift);
    setZoom(Math.min(52, zoom + shift));
  }, [baseMonday, zoom, setZoom, weekOffset, setWeekOffset]);

  // Bar geometry + marker placement, carved out of the block's ORIGINAL footprint so a
  // marked bar occupies exactly the same span as an unmarked one.
  //
  // The box model is the whole game here. A regular .sched-grid-block is content-box, so
  // its drawn extent is `width + 2*BLOCK_PAD_X`; .sched-grid-block--placeholder opts into
  // border-box, so its drawn extent IS its width. Placing the right marker off the nominal
  // width instead of the drawn right edge is what put the glyph on top of the "h".
  //
  // contentW is what the fit tests use. It equals barW, which is the same quantity the
  // long-standing `w > 90` gates were tuned against (both are the styled width), so the
  // thresholds keep their original meaning.
  const barGeom = useCallback((b, x, w, borderBox = false) => {
    const pad = borderBox ? 0 : BLOCK_PAD_X * 2;
    const l = b?.contLeft ? MARKER_W + MARKER_GAP : 0;
    const r = b?.contRight ? MARKER_W + MARKER_GAP : 0;
    const barW = Math.max(6, w - l - r);
    const barX = x + l;
    return {
      barX, barW, contentW: barW,
      markerL: x,
      markerR: barX + barW + pad + MARKER_GAP,
    };
  }, []);

  const fmtShort = (iso) => (iso
    ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '');

  // One continuation marker. Same colour as its bar so it reads as the bar carrying on;
  // a solid arrow means this very block resumes within the splitter's 3-day gap, a faded
  // dot means there is more work on the job but as a separate block later.
  const contMarker = useCallback((b, side, color, geom, top) => {
    const cont = side === 'left' ? b.contLeft : b.contRight;
    if (!cont) return null;
    const isRun = cont.kind === 'run';
    const span = b.span || {};
    const totals = span.fullHours === b.totalHours
      ? `${span.fullHours}h`
      : `${span.fullHours}h total, ${b.totalHours}h in view`;
    const title = side === 'right'
      ? (isRun
        ? `Continues past this window — runs to ${fmtShort(span.fullMax)} · ${totals}. Click to widen.`
        : `More work on this later — next ${fmtShort(cont.date)}, through ${fmtShort(span.fullMax)} · ${totals}. Click to widen.`)
      : (isRun
        ? `Started before this window — begins ${fmtShort(span.fullMin)} · ${totals}. Click to widen.`
        : `Earlier work on this — last ${fmtShort(cont.date)}, from ${fmtShort(span.fullMin)} · ${totals}. Click to widen.`);
    return (
      <div key={`${b.key}_cont_${side}`}
        className={`sched-grid-cont sched-grid-cont--${side}${isRun ? '' : ' sched-grid-cont--later'}`}
        style={{ left: side === 'left' ? geom.markerL : geom.markerR, top, background: color }}
        title={title}
        onClick={e => { e.stopPropagation(); widenToShow(b, side); }}>
        {isRun ? (side === 'right' ? '\u25B8' : '\u25C2') : '\u00B7'}
      </div>
    );
  }, [widenToShow]);

  // Placeholders are fetched with NO date filter, so unlike schedule entries they already
  // know their true span — the render just silently clamps it to the window (safeS/safeE).
  // Shape one like a block so contMarker can treat it identically. A placeholder is a single
  // flat span with no per-day breakdown, so it is always a contiguous 'run', never a 'later'.
  const phContBlock = useCallback((ph) => {
    const { startISO, endISO } = windowEdges;
    if (!startISO || !endISO) return null;
    const s = String(ph.StartDate).slice(0, 10);
    const e = String(ph.EndDate).slice(0, 10);
    const todayISO = toISO(new Date());
    // Same future-only rule as schedule entries: only flag hidden work that is still ahead.
    const left = (s < startISO && startISO > todayISO) ? { kind: 'run', date: s } : null;
    const right = (e > endISO && e >= todayISO) ? { kind: 'run', date: e } : null;
    if (!left && !right) return null;
    return {
      key: `ph_${ph.PlaceholderId}`,
      contLeft: left, contRight: right,
      totalHours: Number(ph.Hours),
      span: { fullMin: s, fullMax: e, fullHours: Number(ph.Hours) },
    };
  }, [windowEdges]);

  // ── Build job blocks per person ───────────────────────────────────────
  const blocksByPerson = useMemo(() => {
    const map = {};
    // Group entries by StaffId + (JobId | custom-CustomJobId), then split into
    // separate blocks when there's a gap > 2 days. The custom-job branch uses
    // a distinct key prefix so real and custom entries never collide.
    const grouped = {};
    for (const e of demoEntries) {
      const gKey = e.CustomJobId
        ? `${e.StaffId}_C${e.CustomJobId}`
        : `${e.StaffId}_${e.JobId}`;
      if (!grouped[gKey]) grouped[gKey] = { ...e, dates: [] };
      const dateStr = typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) : e.EntryDate;
      grouped[gKey].dates.push({ date: new Date(dateStr + 'T12:00:00'), hours: e.Hours, entryId: e.EntryId });
    }

    for (const [gKey, block] of Object.entries(grouped)) {
      const sid = block.StaffId;
      if (!map[sid]) map[sid] = [];
      // Only the FIRST run of a group can be cut on the left and only the LAST on the right.
      // Keyed on run index, not on "does sIdx/eIdx touch the window edge" — the last entry is
      // usually a Friday while the window ends on a Sunday, so an edge test would miss almost
      // every truncated block.
      const cont = continuationFor(sid, block.JobId, block.CustomJobId);
      block.dates.sort((a, b) => a.date - b.date);

      // Split into contiguous runs: gap > 2 days = new block (weekend = 2 day gap, OK)
      const runs = [];
      let currentRun = [block.dates[0]];
      for (let i = 1; i < block.dates.length; i++) {
        const gap = (block.dates[i].date - block.dates[i - 1].date) / 86400000;
        if (gap > 3) {
          runs.push(currentRun);
          currentRun = [];
        }
        currentRun.push(block.dates[i]);
      }
      runs.push(currentRun);

      for (let ri = 0; ri < runs.length; ri++) {
        const run = runs[ri];
        const totalHours = run.reduce((s, d) => s + d.hours, 0);
        const startDate = run[0].date;
        const endDate = run[run.length - 1].date;
        const sIdx = allDays.findIndex(d => d.iso === toISO(startDate));
        const eIdx = allDays.findIndex(d => d.iso === toISO(endDate));
        if (sIdx < 0 || eIdx < 0) continue;

        // Item 2 — custom-job blocks pull from cj.* columns; jobType holds the
        // category name (consumed by the renderer via CUSTOM_JOB_CATEGORY_COLORS).
        const isCustom = !!block.CustomJobId;
        map[sid].push({
          key: runs.length > 1 ? `${gKey}_r${ri}` : gKey,
          staffId: sid,
          jobId: isCustom ? null : block.JobId,
          customJobId: isCustom ? block.CustomJobId : null,
          isCustom,
          clientName: isCustom ? '' : (block.ClientName || ''),
          clientCode: isCustom ? '' : (block.ClientCode || ''),
          jobName: isCustom ? (block.CustomJobName || '') : (block.JobName || ''),
          jobCode: isCustom ? '' : (block.JobCode || ''),
          jobType: isCustom ? (block.CustomJobCategory || 'Other') : (block.ServiceDesc || block.JobType || block.JobClassDesc || ''),
          customJobCategory: isCustom ? (block.CustomJobCategory || 'Other') : null,
          customJobDescription: isCustom ? (block.CustomJobDescription || '') : null,
          entryType: block.EntryType || 'billable',
          totalHours: Math.round(totalHours * 100) / 100,
          sIdx, eIdx,
          dates: run,
          staffName: block.StaffName,
          managerName: isCustom ? '' : (block.JobManagerName || ''),
          serviceDesc: isCustom ? '' : (block.ServiceDesc || ''),
          notes: block.Notes || null,
          contLeft: ri === 0 ? (cont?.left || null) : null,
          contRight: ri === runs.length - 1 ? (cont?.right || null) : null,
          span: cont || null,
          statusDetail: isCustom ? '' : (block.JobStatusDetail || ''),
          peJobIndex: isCustom ? null : (block.PEJobIndex || null),
          budgetHours: isCustom ? 0 : (block.JobBudgetHours || 0),
          clientId: isCustom ? null : (block.ClientId || null),
          isEmpire: !isCustom && (block.CreatedBy === 'empire-sync' || block.CreatedBy === 'seed-script'),
          empireSyncedAt: (!isCustom && block.CreatedBy === 'empire-sync') ? block.UpdatedAt : null,
          isClosedInPE: !isCustom && !!block.IsClosedInPE,
          // Closed-job metadata rides along on the entry because queryJobs filters closed
          // jobs out, so allJobs has no row to look these up in.
          closedAt: isCustom ? null : (block.JobClosedAt || null),
          jobManagerId: isCustom ? null : (block.JobManagerId ?? null),
          jobPartnerId: isCustom ? null : (block.JobPartnerId ?? null),
          clientStatus: isCustom ? null : (block.ClientStatus || null),
        });
      }
    }

    // Inject global assignment blocks as synthetic entries for each affected staff
    for (const ga of globalAssignments) {
      const iso = typeof ga.AssignmentDate === 'string' ? ga.AssignmentDate.slice(0, 10) : '';
      if (!iso) continue;
      const dayIdx = allDays.findIndex(d => d.iso === iso);
      if (dayIdx < 0 || allDays[dayIdx].isWE) continue;
      const depts = ga.Departments ? ga.Departments.split(',') : null;
      for (const sid of Object.keys(map)) {
        const person = staff.find(s => String(s.StaffId) === sid);
        if (depts && person && !depts.includes(person.DepartmentCode)) continue;
        // Don't book departed staff into firm-wide events. Matches the !person.IsActive
        // skip in the "not yet in map" loop below and in buildDayHoursByStaff
        // (utils/capacity.js) — an inactive row should show only real remaining work.
        if (person && !person.IsActive) continue;
        if (!map[sid]) map[sid] = [];
        map[sid].push({
          key: `ga_${ga.AssignmentId}_${sid}`,
          staffId: parseInt(sid),
          jobId: null,
          clientName: ga.Name,
          jobName: ga.Description || 'Firm-wide Event',
          jobCode: '',
          jobType: 'Global',
          entryType: 'global',
          totalHours: ga.Hours,
          sIdx: dayIdx, eIdx: dayIdx,
          dates: [{ date: new Date(iso + 'T12:00:00'), hours: ga.Hours }],
          staffName: person?.Name || '',
          isGlobal: true,
        });
      }
      // Also add for staff not yet in map (no other entries)
      for (const person of staff) {
        const sid = String(person.StaffId);
        if (map[sid]) continue; // already handled above
        if (depts && !depts.includes(person.DepartmentCode)) continue;
        if (!person.IsActive) continue;
        map[sid] = [{
          key: `ga_${ga.AssignmentId}_${sid}`,
          staffId: person.StaffId,
          jobId: null,
          clientName: ga.Name,
          jobName: ga.Description || 'Firm-wide Event',
          jobCode: '',
          jobType: 'Global',
          entryType: 'global',
          totalHours: ga.Hours,
          sIdx: dayIdx, eIdx: dayIdx,
          dates: [{ date: new Date(iso + 'T12:00:00'), hours: ga.Hours }],
          staffName: person.Name,
          isGlobal: true,
        }];
      }
    }

    // Assign tracks (stacking overlapping blocks)
    for (const sid of Object.keys(map)) {
      const blocks = map[sid].sort((a, b) => a.sIdx - b.sIdx);
      const tracks = [];
      for (const b of blocks) {
        let placed = false;
        for (let t = 0; t < tracks.length; t++) {
          if (tracks[t] < b.sIdx) {
            b.track = t;
            tracks[t] = b.eIdx;
            placed = true;
            break;
          }
        }
        if (!placed) {
          b.track = tracks.length;
          tracks.push(b.eIdx);
        }
      }
    }
    return map;
  }, [demoEntries, allDays, globalAssignments, staff, continuationFor]);

  // ── Inactive-but-still-scheduled staff ────────────────────────────────
  // fetchStaff({ includeScheduledInactive: true }) returns departed staff who still
  // hold scheduled hours today-onward, so their leftover work stays visible and
  // reassignable instead of silently vanishing the moment peSync flips IsActive=0.
  // Keyed off the server's explicit ScheduledInactive tag, NOT !IsActive: schedData
  // also appends the caller's own row when they're inactive (contractors with
  // StaffIncludeInReports=0), and those users must keep working normally.
  const scheduledInactiveIds = useMemo(
    () => new Set(staff.filter(s => s.ScheduledInactive).map(s => s.StaffId)),
    [staff],
  );

  // A staffer earns a grid row when they're active, or they're inactive-but-scheduled
  // AND actually have blocks in the CURRENT window. Without the window test, someone
  // whose remaining work is months out would sit as an empty FREE row until then;
  // this way their row surfaces exactly when you pan to where the work is.
  const isGridEligible = useCallback(
    (s) => !!s.IsActive
      || (scheduledInactiveIds.has(s.StaffId) && (blocksByPerson[s.StaffId] || []).length > 0),
    [scheduledInactiveIds, blocksByPerson],
  );

  // Candidate list for the Filter Staff Groups modal. Deliberately looser than
  // isGridEligible: no current-window test, so you can filter TO a departed staffer
  // and go find their leftover work. Memoized so StaffFilterPicker's props stay
  // stable and it doesn't re-render with the rest of the grid.
  const gridEligibleStaff = useMemo(
    () => staff.filter(s => s.IsActive || s.ScheduledInactive),
    [staff],
  );

  // Read-only: departed staff can't be scheduled onto. Their existing blocks stay
  // fully editable (drag off, delete) — only NEW assignment is blocked.
  const isAssignableStaffId = useCallback(
    (staffId) => !scheduledInactiveIds.has(staffId),
    [scheduledInactiveIds],
  );

  // Edit-modal staff search results. Same reasoning as modalFilteredJobs — hoisted
  // out of the render IIFE so it isn't rebuilt on unrelated grid renders, and the
  // query is lowercased once instead of per staffer. Declared after
  // isAssignableStaffId because it depends on it.
  const modalFilteredStaff = useMemo(() => {
    const q = modalStaffSearch.trim().toLowerCase();
    if (!q) return [];
    return staff.filter(s => isAssignableStaffId(s.StaffId) && (
      (s.Name || '').toLowerCase().includes(q) || (s.Level || '').toLowerCase().includes(q)
    )).slice(0, 50);
  }, [staff, modalStaffSearch, isAssignableStaffId]);

  // Tooltip payload for the inactive marker: hours still on the books + last date.
  // Derived from the entries already loaded — no extra read.
  const inactiveNoteFor = useCallback((staffId) => {
    const blocks = blocksByPerson[staffId] || [];
    const hours = blocks.reduce((sum, b) => sum + (b.totalHours || 0), 0);
    let last = null;
    for (const b of blocks) {
      for (const d of (b.dates || [])) {
        if (!last || d.date > last) last = d.date;
      }
    }
    const hrs = Math.round(hours * 100) / 100;
    return `Inactive staff — no longer active in PE. ${hrs}h still scheduled`
      + (last ? ` through ${last.toLocaleDateString()}` : '')
      + `. Reassign this work; new entries are blocked.`;
  }, [blocksByPerson]);

  // Restore selectedBlock from session after blocks are computed
  const restoredBlock = useRef(false);
  useEffect(() => {
    if (restoredBlock.current || !savedState.selectedBlockKey) return;
    if (focusJobId) { restoredBlock.current = true; return; } // a ?job= deeplink owns the selection
    for (const blocks of Object.values(blocksByPerson)) {
      const match = blocks.find(b => b.key === savedState.selectedBlockKey);
      if (match) { setSelectedBlock(match); restoredBlock.current = true; return; }
    }
  }, [blocksByPerson, savedState.selectedBlockKey]);

  // ── Job View: group entries by Job ────────────────────────────────────
  // Build a quick lookup from allJobs for department/service info
  const jobInfoMap = useMemo(() => {
    const m = {};
    for (const j of demoAllJobs) m[j.JobId] = j;
    return m;
  }, [demoAllJobs]);

  // Both per-candidate chips for the modal picklists, in one call so the row
  // component has a single badge slot. In "suggest" mode OffOfficeFlag always
  // renders null (that list is hard office-filtered); in "Staff with WIP" mode it
  // can fire, which is the point -- that list is deliberately NOT filtered, because
  // its members have already charged time to this job and hiding them would hide a
  // fact rather than prevent a mistake.
  const picklistBadgesFor = useCallback((c) => {
    const mJob = jobInfoMap[parseInt(modalJob, 10)];
    return (
      <>
        {genTrackFlagFor(c)}
        <OffOfficeFlag staffOffice={staffOfficeById[c.StaffId]} isOnSite={!!mJob?.IsOnSite}
          jobOfficeCode={mJob?.OfficeCode} jobOffice={mJob?.Office} staffName={c.Name} />
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genTrackFlagFor, staffOfficeById, modalJob, jobInfoMap[parseInt(modalJob, 10)]?.IsOnSite]);

  // ── Client groups (Kate Fluker: "Job/Client group filters the way we can with
  //    staff. We had a similar option in Empire") ────────────────────────────
  //
  // Derived from jobs already in memory rather than fetched: every job carries
  // ClientId/ClientName/ClientCode, so the client list is free. It is also the
  // RIGHT list — clients with no active job cannot appear on the grid, so offering
  // them in the picker would only produce filters that match nothing.
  const gridClients = useMemo(() => {
    const m = new Map();
    for (const j of demoAllJobs) {
      if (!j.ClientId || m.has(j.ClientId)) continue;
      m.set(j.ClientId, { clientId: j.ClientId, name: j.ClientName || '', code: j.ClientCode || '' });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [demoAllJobs]);

  // Chip labels used to do staff.find(...) inside a .map over the selection,
  // which is quadratic on a 200-person group.
  const staffById = useMemo(() => {
    const m = {};
    for (const s of staff) m[s.StaffId] = s;
    return m;
  }, [staff]);

  const clientById = useMemo(() => {
    const m = {};
    for (const c of gridClients) m[c.clientId] = c;
    return m;
  }, [gridClients]);

  // Set, not array: this runs per job and per block on every render pass.
  const clientFilterSet = useMemo(() => new Set(clientFilter), [clientFilter]);
  const clientGroupActive = clientFilterSet.size > 0;

  // Keyed on jobId, like jobMatchesMoreFilters, so both views apply one rule and
  // cannot drift: Jobs view drops non-matching job rows, People view drops
  // non-matching blocks.
  const jobMatchesClientFilter = useCallback((jobId) => {
    if (!clientGroupActive) return true;
    const meta = jobInfoMap[jobId];
    return !!meta && clientFilterSet.has(meta.ClientId);
  }, [clientGroupActive, clientFilterSet, jobInfoMap]);

  // ── "More filters" predicate (suggestion #17) ──────────────────────────────
  // One rule, applied in BOTH views so they can't disagree about what a filter
  // means: Jobs view drops non-matching job rows, People view drops non-matching
  // blocks — mirroring how the client search already behaves.
  //
  // Reads the whole job record: return type is parsed from the NAME (PE has no
  // field for it) and prep hours come from PrepBudgetHours on schedData queryJobs.
  // Returns true when nothing is filtered so callers can apply it unconditionally.
  const moreFiltersActive = returnTypes.length > 0 || minPrepHours > 0;
  const jobMatchesMoreFilters = useCallback((jobId) => {
    if (!moreFiltersActive) return true;
    const meta = jobInfoMap[jobId];
    if (!meta) return false;   // a job we know nothing about can't satisfy a filter
    if (returnTypes.length > 0 && !returnTypes.includes(getReturnType(meta.Name))) return false;
    if (minPrepHours > 0 && !((meta.PrepBudgetHours || 0) >= minPrepHours)) return false;
    return true;
  }, [moreFiltersActive, returnTypes, minPrepHours, jobInfoMap]);

  const blocksByJob = useMemo(() => {
    const map = {};
    const grouped = {};
    for (const e of demoEntries) {
      // Custom-job entries have JobId NULL, so they used to all collapse into a single
      // `null` bucket that baseJobs then dropped (Number("null") = NaN, no jobInfoMap
      // hit). Harmless output, but it grouped + track-packed ~2,400 rows on every
      // entries change for nothing. They get their own map below.
      if (e.CustomJobId) continue;
      const gKey = `${e.JobId}_${e.StaffId}`;
      if (!grouped[gKey]) grouped[gKey] = { ...e, dates: [] };
      const dateStr = typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) : e.EntryDate;
      grouped[gKey].dates.push({ date: new Date(dateStr + 'T12:00:00'), hours: e.Hours, entryId: e.EntryId });
    }
    for (const [gKey, block] of Object.entries(grouped)) {
      const jid = block.JobId;
      const jobMeta = jobInfoMap[jid] || {};
      if (!map[jid]) map[jid] = {
        blocks: [], clientName: block.ClientName || '', clientCode: block.ClientCode || '', jobName: block.JobName || '', jobCode: block.JobCode || '',
        jobType: block.ServiceDesc || block.JobType || jobMeta.ServiceDesc || '',
        departmentCode: jobMeta.DepartmentCode || '',
        budgetHours: block.JobBudgetHours || jobMeta.BudgetHours || 0,
        clientId: block.ClientId || null, managerName: block.JobManagerName || '', peJobIndex: block.PEJobIndex || null,
        nicheName: jobMeta.NicheName || '', clientIndustry: jobMeta.ClientIndustry || '',
        isClosedInPE: !!block.IsClosedInPE,
        closedAt: block.JobClosedAt || null,
        jobManagerId: block.JobManagerId ?? null,
        jobPartnerId: block.JobPartnerId ?? null,
        clientStatus: block.ClientStatus || null,
      };
      const cont = continuationFor(block.StaffId, block.JobId, null);
      block.dates.sort((a, b) => a.date - b.date);

      // Split into contiguous runs (same logic as blocksByPerson)
      const runs = [];
      let currentRun = [block.dates[0]];
      for (let i = 1; i < block.dates.length; i++) {
        const gap = (block.dates[i].date - block.dates[i - 1].date) / 86400000;
        if (gap > 3) { runs.push(currentRun); currentRun = []; }
        currentRun.push(block.dates[i]);
      }
      runs.push(currentRun);

      const person = staff.find(s => s.StaffId === block.StaffId);
      for (let ri = 0; ri < runs.length; ri++) {
        const run = runs[ri];
        const totalHours = run.reduce((s, d) => s + d.hours, 0);
        const sIdx = allDays.findIndex(d => d.iso === toISO(run[0].date));
        const eIdx = allDays.findIndex(d => d.iso === toISO(run[run.length - 1].date));
        if (sIdx < 0 || eIdx < 0) continue;
        map[jid].blocks.push({
          key: runs.length > 1 ? `${gKey}_r${ri}` : gKey,
          staffId: block.StaffId, staffName: block.StaffName || person?.Name || '',
          initials: person?.Initials || '??', level: person?.Level || '', department: person?.DepartmentCode || '',
          jobId: jid, totalHours: Math.round(totalHours * 100) / 100, sIdx, eIdx, dates: run,
          entryType: block.EntryType || 'billable',
          notes: block.Notes || null,
          contLeft: ri === 0 ? (cont?.left || null) : null,
          contRight: ri === runs.length - 1 ? (cont?.right || null) : null,
          span: cont || null,
          isEmpire: (block.CreatedBy === 'empire-sync' || block.CreatedBy === 'seed-script'),
          empireSyncedAt: block.CreatedBy === 'empire-sync' ? block.UpdatedAt : null,
        });
      }
    }
    for (const jid of Object.keys(map)) {
      const blocks = map[jid].blocks.sort((a, b) => a.sIdx - b.sIdx);
      const tracks = [];
      for (const b of blocks) {
        let placed = false;
        for (let t = 0; t < tracks.length; t++) {
          if (tracks[t] < b.sIdx) { b.track = t; tracks[t] = b.eIdx; placed = true; break; }
        }
        if (!placed) { b.track = tracks.length; tracks.push(b.eIdx); }
      }
      map[jid].maxTrack = tracks.length;
    }
    return map;
  }, [demoEntries, allDays, staff, jobInfoMap, continuationFor]);

  // ── Build blocks per CUSTOM job (Jobs view, "Custom" scope) ───────────────
  // The twin of blocksByJob, keyed on CustomJobId. Same grouping, contiguous-run split
  // and track packer — a custom job is just a job whose identity lives in
  // rprs.CustomJobs instead of rprs.Jobs. All the row metadata (name, category,
  // description) rides along on the entry: the schedule read LEFT JOINs CustomJobs, so
  // no second fetch and no dependency on allCustomJobs (which is activeOnly).
  const blocksByCustomJob = useMemo(() => {
    const map = {};
    const grouped = {};
    for (const e of demoEntries) {
      if (!e.CustomJobId) continue;
      const gKey = `${e.CustomJobId}_${e.StaffId}`;
      if (!grouped[gKey]) grouped[gKey] = { ...e, dates: [] };
      const dateStr = typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) : e.EntryDate;
      grouped[gKey].dates.push({ date: new Date(dateStr + 'T12:00:00'), hours: e.Hours, entryId: e.EntryId });
    }
    for (const [gKey, block] of Object.entries(grouped)) {
      const cid = block.CustomJobId;
      if (!map[cid]) map[cid] = {
        customJobId: cid,
        name: block.CustomJobName || 'Custom job',
        category: block.CustomJobCategory || 'Other',
        description: block.CustomJobDescription || '',
        blocks: [],
      };
      const cont = continuationFor(block.StaffId, null, cid);
      block.dates.sort((a, b) => a.date - b.date);

      // Split into contiguous runs (same gap > 3 rule as blocksByJob / blocksByPerson)
      const runs = [];
      let currentRun = [block.dates[0]];
      for (let i = 1; i < block.dates.length; i++) {
        const gap = (block.dates[i].date - block.dates[i - 1].date) / 86400000;
        if (gap > 3) { runs.push(currentRun); currentRun = []; }
        currentRun.push(block.dates[i]);
      }
      runs.push(currentRun);

      const person = staff.find(s => s.StaffId === block.StaffId);
      for (let ri = 0; ri < runs.length; ri++) {
        const run = runs[ri];
        const totalHours = run.reduce((s, d) => s + d.hours, 0);
        const sIdx = allDays.findIndex(d => d.iso === toISO(run[0].date));
        const eIdx = allDays.findIndex(d => d.iso === toISO(run[run.length - 1].date));
        if (sIdx < 0 || eIdx < 0) continue;
        map[cid].blocks.push({
          key: runs.length > 1 ? `${gKey}_r${ri}` : gKey,
          staffId: block.StaffId, staffName: block.StaffName || person?.Name || '',
          initials: person?.Initials || '??', level: person?.Level || '', department: person?.DepartmentCode || '',
          // Mirrors the blocksByPerson custom shape so the entry sidebar's existing
          // isCustom branches (no on-site toggle, no budget bar, no closed banner) work
          // unchanged when one of these blocks is clicked.
          jobId: null, customJobId: cid, isCustom: true,
          clientName: '', clientCode: '', clientId: null,
          jobName: map[cid].name,
          jobType: map[cid].category,
          customJobCategory: map[cid].category,
          customJobDescription: map[cid].description,
          totalHours: Math.round(totalHours * 100) / 100, sIdx, eIdx, dates: run,
          entryType: block.EntryType || 'billable',
          notes: block.Notes || null,
          contLeft: ri === 0 ? (cont?.left || null) : null,
          contRight: ri === runs.length - 1 ? (cont?.right || null) : null,
          span: cont || null,
        });
      }
    }
    for (const cid of Object.keys(map)) {
      const blocks = map[cid].blocks.sort((a, b) => a.sIdx - b.sIdx);
      const tracks = [];
      for (const b of blocks) {
        let placed = false;
        for (let t = 0; t < tracks.length; t++) {
          if (tracks[t] < b.sIdx) { b.track = t; tracks[t] = b.eIdx; placed = true; break; }
        }
        if (!placed) { b.track = tracks.length; tracks.push(b.eIdx); }
      }
      map[cid].maxTrack = tracks.length;
    }
    return map;
  }, [demoEntries, allDays, staff, continuationFor]);

  // Placeholder hours per job, today-onward — the client-side twin of the
  // PlaceholderScheduled column on schedData job_progress / jobs. Same rule as the
  // server: a placeholder has no per-day breakdown, so its whole span total counts
  // until the span has fully passed. Window-INDEPENDENT (not scoped to the visible
  // grid range) so it matches the bar totals, which are also all-dates-from-today.
  const placeholderHoursByJob = useMemo(() => {
    const todayISO = toISO(new Date());
    const map = {};
    for (const ph of (demoPlaceholders || [])) {
      if (ph.JobId == null) continue;
      if (String(ph.EndDate).slice(0, 10) < todayISO) continue;
      map[ph.JobId] = (map[ph.JobId] || 0) + (ph.Hours || 0);
    }
    return map;
  }, [demoPlaceholders]);

  // Job View: group placeholders by Job so they render as blocks on the job row
  // (mirrors blocksByJob). Each gets sIdx/eIdx from its date range + a stacking
  // track; jobRowHeight adds these tracks below the staff blocks.
  const placeholdersByJob = useMemo(() => {
    const map = {};
    for (const ph of (demoPlaceholders || [])) {
      const jid = ph.JobId;
      if (jid == null) continue;
      const sIdx = allDays.findIndex(d => d.iso === String(ph.StartDate).slice(0, 10));
      const eIdx = allDays.findIndex(d => d.iso === String(ph.EndDate).slice(0, 10));
      if (!map[jid]) map[jid] = { phBlocks: [], maxTrack: 0 };
      map[jid].phBlocks.push({ ph, sIdx, eIdx });
    }
    for (const jid of Object.keys(map)) {
      const phBlocks = map[jid].phBlocks.sort((a, b) => (a.sIdx < 0 ? 0 : a.sIdx) - (b.sIdx < 0 ? 0 : b.sIdx));
      const tracks = [];
      for (const pb of phBlocks) {
        const s = pb.sIdx < 0 ? 0 : pb.sIdx;
        const e = pb.eIdx < 0 ? allDays.length - 1 : pb.eIdx;
        let placed = false;
        for (let t = 0; t < tracks.length; t++) {
          if (tracks[t] < s) { pb.track = t; tracks[t] = e; placed = true; break; }
        }
        if (!placed) { pb.track = tracks.length; tracks.push(e); }
      }
      map[jid].maxTrack = tracks.length;
    }
    return map;
  }, [demoPlaceholders, allDays]);

  // Custom-job twins of placeholderHoursByJob / placeholdersByJob. PlaceholderAssignments
  // went polymorphic in migrate-074 (JobId nullable + CustomJobId, XOR-checked), but both
  // job-side maps skip rows with a null JobId — so a custom placeholder rendered nowhere
  // in the Jobs view. Same today-onward rule as the job version: a placeholder has no
  // per-day breakdown, so its whole span total counts until the span has fully passed.
  const placeholderHoursByCustomJob = useMemo(() => {
    const todayISO = toISO(new Date());
    const map = {};
    for (const ph of (demoPlaceholders || [])) {
      if (ph.CustomJobId == null) continue;
      if (String(ph.EndDate).slice(0, 10) < todayISO) continue;
      map[ph.CustomJobId] = (map[ph.CustomJobId] || 0) + (ph.Hours || 0);
    }
    return map;
  }, [demoPlaceholders]);

  const placeholdersByCustomJob = useMemo(() => {
    const map = {};
    for (const ph of (demoPlaceholders || [])) {
      const cid = ph.CustomJobId;
      if (cid == null) continue;
      const sIdx = allDays.findIndex(d => d.iso === String(ph.StartDate).slice(0, 10));
      const eIdx = allDays.findIndex(d => d.iso === String(ph.EndDate).slice(0, 10));
      if (!map[cid]) map[cid] = { phBlocks: [], maxTrack: 0 };
      map[cid].phBlocks.push({ ph, sIdx, eIdx });
    }
    for (const cid of Object.keys(map)) {
      const phBlocks = map[cid].phBlocks.sort((a, b) => (a.sIdx < 0 ? 0 : a.sIdx) - (b.sIdx < 0 ? 0 : b.sIdx));
      const tracks = [];
      for (const pb of phBlocks) {
        const s = pb.sIdx < 0 ? 0 : pb.sIdx;
        const e = pb.eIdx < 0 ? allDays.length - 1 : pb.eIdx;
        let placed = false;
        for (let t = 0; t < tracks.length; t++) {
          if (tracks[t] < s) { pb.track = t; tracks[t] = e; placed = true; break; }
        }
        if (!placed) { pb.track = tracks.length; tracks.push(e); }
      }
      map[cid].maxTrack = tracks.length;
    }
    return map;
  }, [demoPlaceholders, allDays]);

  // Active staff matching the free-text staff search (?q). Computed at render
  // time so the URL stays clean (?q=name) rather than expanding to a staff-id
  // list. Preserves the prior search behaviour (active staff, name contains).
  const searchStaffIds = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return null;
    // ScheduledInactive is searchable so you can find a departed staffer's leftover work
    // by name. visibleStaff still applies isGridEligible (needs blocks in the window).
    return new Set(staff.filter(s => (s.IsActive || s.ScheduledInactive) && (s.Name || '').toLowerCase().includes(q)).map(s => s.StaffId));
  }, [staffSearch, staff]);

  const baseJobs = useMemo(() => {
    // Start with jobs that have entries (from blocksByJob)
    const jobsWithEntries = new Set(Object.keys(blocksByJob).map(Number));

    // Also include all jobs from allJobs when hideEmpty is off (so empty jobs are schedulable).
    // When a lead filter is active (e.g. "Me"), only materialize that lead's unscheduled
    // jobs — keeps the default "my led jobs" view fast instead of building all ~2,000.
    const allJobIds = new Set(jobsWithEntries);
    if (!hideEmpty) {
      const leadId = jobLead ? Number(jobLead) : null;
      const wantJM = !leadId || jobLeadRole === 'both' || jobLeadRole === 'jm';
      const wantJP = !leadId || jobLeadRole === 'both' || jobLeadRole === 'jp';
      for (const j of allJobs) {
        if (leadId && !((wantJM && j.ManagerId === leadId) || (wantJP && j.PartnerId === leadId))) continue;
        allJobIds.add(j.JobId);
      }
    } else {
      // "Scheduled only" is on, so the bulk materialize above is skipped — but a
      // placeholder-only job has no blocks and would never make it into the set to be
      // filtered. Seed those explicitly; they now count as scheduled, so the filter is
      // meant to KEEP them. Bounded by the placeholder count (single digits), not by
      // allJobs, so this cannot reintroduce the ~2,000-job materialize this branch avoids.
      for (const jid of Object.keys(placeholderHoursByJob)) allJobIds.add(Number(jid));
    }

    let jobList = [...allJobIds].map(jid => {
      const fromBlocks = blocksByJob[jid];
      const fromMeta = jobInfoMap[jid];
      if (fromBlocks) return { jobId: jid, ...fromBlocks };
      // Empty job — build stub from allJobs metadata
      if (fromMeta) return {
        jobId: jid, blocks: [], maxTrack: 0,
        clientName: fromMeta.ClientName || '', clientCode: fromMeta.ClientCode || '', jobName: fromMeta.Name || '', jobCode: fromMeta.Code || '',
        jobType: fromMeta.ServiceDesc || fromMeta.JobType || '',
        departmentCode: fromMeta.DepartmentCode || '',
        budgetHours: fromMeta.BudgetHours || 0, clientId: fromMeta.ClientId || null,
        managerName: fromMeta.ManagerName || '', peJobIndex: fromMeta.PEJobIndex || null,
        nicheName: fromMeta.NicheName || '', clientIndustry: fromMeta.ClientIndustry || '',
      };
      return null;
    }).filter(Boolean);

    // Attach lead ids/names from job metadata (drives the JM/JP filter; matched by id)
    jobList = jobList.map(j => {
      const meta = jobInfoMap[j.jobId];
      return {
        ...j,
        managerId: meta?.ManagerId ?? null,
        managerName: j.managerName || meta?.ManagerName || '',
        partnerId: meta?.PartnerId ?? null,
        partnerName: meta?.PartnerName || '',
        isOnSite: !!meta?.IsOnSite,
        office: meta?.Office || '',
        officeCode: meta?.OfficeCode || '',
      };
    });

    // Department filter via ServiceDesc → SERVICE_TO_DEPT. ("Jobs I lead" is now the
    // composable jobLead='me' filter applied in visibleJobs, not a dept scope.)
    if (deptFilter.includes('mine')) {
      jobList = jobList.filter(j => j.blocks?.some(b => b.staffId === myStaffId));
    } else if (!deptFilter.includes('all')) {
      jobList = jobList.filter(j => deptFilter.includes(SERVICE_TO_DEPT[j.jobType]));
    }

    // Staff filter
    if (staffFilter.length > 0) {
      jobList = jobList.filter(j => j.blocks?.some(b => staffFilter.includes(b.staffId)));
    }
    if (searchStaffIds) {
      jobList = jobList.filter(j => j.blocks?.some(b => searchStaffIds.has(b.staffId)));
    }
    // Client name/code filter (?cq) — composes with the dept/staff scope above.
    if (clientSearch.trim()) {
      const cq = clientSearch.trim().toLowerCase();
      jobList = jobList.filter(j =>
        (j.clientName || '').toLowerCase().includes(cq) ||
        (j.clientCode || '').toLowerCase().includes(cq));
    }

    // Client group (?client) — composes with the free-text client search above.
    if (clientGroupActive) {
      jobList = jobList.filter(j => jobMatchesClientFilter(j.jobId));
    }

    // Return type / prep-budget filters (suggestion #17).
    if (moreFiltersActive) {
      jobList = jobList.filter(j => jobMatchesMoreFilters(j.jobId));
    }

    // "Scheduled only": a placeholder counts as scheduled. The calendar space for the
    // job is allotted and the JM's scheduling work is done — naming the staffer is the
    // production committee's step. Hiding placeholder-only jobs here made them look
    // unscheduled on the very filter used to find work that still needs scheduling.
    if (hideEmpty) {
      jobList = jobList.filter(j => (j.blocks || []).length > 0 || (placeholderHoursByJob[j.jobId] || 0) > 0);
    }
    // "Free only" (Jobs view): the exact inverse — only jobs with nothing on them at
    // all. Placeholders were previously ignored here (a placeholder-only job counted as
    // "free"); they now disqualify a job, so the two filters stay complements.
    if (showFreeOnly) {
      jobList = jobList.filter(j => (j.blocks || []).length === 0 && (placeholderHoursByJob[j.jobId] || 0) === 0);
    }

    // Client name, then job name, then id. Without the tiebreakers, two jobs for the same
    // client kept whatever order the entries happened to arrive in, so moving a block from
    // one to the other could SWAP the rows instead of visibly moving the block (manual test
    // A7, 2026-10-06: RLBTest 1040 <-> 1041).
    return jobList.sort((a, b) => (a.clientName || '').localeCompare(b.clientName || '')
      || (a.jobName || '').localeCompare(b.jobName || '')
      || (a.jobId - b.jobId));
  }, [blocksByJob, deptFilter, myStaffId, staffFilter, searchStaffIds, clientSearch, hideEmpty, showFreeOnly, allJobs, jobInfoMap, jobLead, jobLeadRole, placeholderHoursByJob, moreFiltersActive, jobMatchesMoreFilters, clientGroupActive, jobMatchesClientFilter]);

  // Distinct Job Managers / Partners in the current (pre-lead-filter) job set — drives the
  // JM/JP picklist so it only lists leads available under the active dept/staff filters.
  const jobLeadOptions = useMemo(() => {
    const m = new Map();
    for (const j of baseJobs) {
      if (j.managerId) {
        const e = m.get(j.managerId) || { id: j.managerId, name: j.managerName || `#${j.managerId}`, isJM: false, isJP: false };
        e.isJM = true; m.set(j.managerId, e);
      }
      if (j.partnerId) {
        const e = m.get(j.partnerId) || { id: j.partnerId, name: j.partnerName || `#${j.partnerId}`, isJM: false, isJP: false };
        e.isJP = true; m.set(j.partnerId, e);
      }
    }
    return [...m.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [baseJobs]);

  // Apply the JM/JP filter last so the option list stays stable when one is selected.
  const visibleJobs = useMemo(() => {
    let list = baseJobs;
    if (jobLead) {
      const id = Number(jobLead);
      const jm = jobLeadRole === 'both' || jobLeadRole === 'jm';
      const jp = jobLeadRole === 'both' || jobLeadRole === 'jp';
      list = baseJobs.filter(j => (jm && j.managerId === id) || (jp && j.partnerId === id));
    }
    // Deeplink focus: ensure the ?job= target is present regardless of dept/JM scope (focus ≠ filter).
    if (focusJobId && !list.some(j => j.jobId === focusJobId)) {
      const blk = blocksByJob[focusJobId];
      const meta = jobInfoMap[focusJobId];
      if (blk || meta) {
        const base = blk
          ? { jobId: focusJobId, ...blk }
          : { jobId: focusJobId, blocks: [], maxTrack: 0,
              clientName: meta.ClientName || '', clientCode: meta.ClientCode || '', jobName: meta.Name || '', jobCode: meta.Code || '',
              jobType: meta.ServiceDesc || meta.JobType || '', departmentCode: meta.DepartmentCode || '',
              budgetHours: meta.BudgetHours || 0, clientId: meta.ClientId || null,
              managerName: meta.ManagerName || '', peJobIndex: meta.PEJobIndex || null,
              nicheName: meta.NicheName || '', clientIndustry: meta.ClientIndustry || '' };
        list = [{
          ...base,
          managerId: meta?.ManagerId ?? base.managerId ?? null,
          managerName: base.managerName || meta?.ManagerName || '',
          partnerId: meta?.PartnerId ?? null,
          partnerName: meta?.PartnerName || '',
          isOnSite: !!meta?.IsOnSite, office: meta?.Office || '', officeCode: meta?.OfficeCode || '',
        }, ...list];
      }
    }
    return list;
  }, [baseJobs, jobLead, jobLeadRole, focusJobId, blocksByJob, jobInfoMap]);

  // ── Jobs view, "Custom" scope: one row per firm-internal custom job ────────
  // Seeded from allCustomJobs (already in the bootstrap read, activeOnly) and unioned
  // with any custom job that still HOLDS entries but has since been deactivated —
  // otherwise its scheduled hours would silently vanish from the grid. Those rows carry
  // their own name/category on the entries, so the union needs no extra fetch.
  //
  // Only the filters that mean something here are applied. There is no client, no JM/JP
  // and no office on a custom job, so clientSearch / clientGroupActive / moreFiltersActive
  // / jobLead / officeFilter are all deliberately skipped rather than silently emptying
  // the list. Sorted by future-scheduled hours (EBP Team first) — the job list's
  // client-name sort has nothing to sort on.
  const visibleCustomJobs = useMemo(() => {
    const byId = new Map();
    for (const c of (allCustomJobs || [])) {
      byId.set(c.CustomJobId, {
        customJobId: c.CustomJobId,
        name: c.Name || 'Custom job',
        category: c.Category || 'Other',
        description: c.Description || '',
        isActive: c.IsActive !== false,
      });
    }
    for (const [cid, blk] of Object.entries(blocksByCustomJob)) {
      const id = Number(cid);
      if (byId.has(id)) continue;
      byId.set(id, { customJobId: id, name: blk.name, category: blk.category, description: blk.description, isActive: false });
    }

    let list = [...byId.values()].map(c => {
      const blk = blocksByCustomJob[c.customJobId];
      const blocks = blk?.blocks || [];
      return {
        ...c,
        blocks,
        maxTrack: blk?.maxTrack || 0,
        phMaxTrack: placeholdersByCustomJob[c.customJobId]?.maxTrack || 0,
        placeholderHours: Math.round((placeholderHoursByCustomJob[c.customJobId] || 0) * 100) / 100,
      };
    });

    // Staff scope — same semantics as the job list: keep rows this staffer appears on.
    if (staffFilter.length > 0) list = list.filter(c => c.blocks.some(b => staffFilter.includes(b.staffId)));
    if (searchStaffIds) list = list.filter(c => c.blocks.some(b => searchStaffIds.has(b.staffId)));

    // "Scheduled only" / "Free only" stay complements, and a placeholder counts as
    // scheduled — same rule baseJobs applies.
    if (hideEmpty) list = list.filter(c => c.blocks.length > 0 || c.placeholderHours > 0);
    if (showFreeOnly) list = list.filter(c => c.blocks.length === 0 && c.placeholderHours === 0);

    return list.sort((a, b) => {
      const ah = a.blocks.reduce((s, x) => s + x.totalHours, 0) + a.placeholderHours;
      const bh = b.blocks.reduce((s, x) => s + x.totalHours, 0) + b.placeholderHours;
      if (bh !== ah) return bh - ah;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [allCustomJobs, blocksByCustomJob, placeholdersByCustomJob, placeholderHoursByCustomJob,
      staffFilter, searchStaffIds, hideEmpty, showFreeOnly]);

  // ?job= deeplink: open the jobs-mode sidebar when the param is set, close it when cleared
  // (Back button). consumedJobParam makes this idempotent so a manually-closed sidebar isn't
  // resurrected by a stale param, and re-running on data/view changes stays cheap.
  useEffect(() => {
    if (loading || !allJobs.length) return;
    if (focusJobId === consumedJobParam.current) return;
    consumedJobParam.current = focusJobId;
    if (!focusJobId) {
      if (selectedBlock?._viewMode === 'jobs') setSelectedBlock(null);
      return;
    }
    if (viewMode !== 'jobs') setViewMode('jobs');
    const job = visibleJobs.find(j => j.jobId === focusJobId);
    if (!job) return;
    setSelectedBlock({ key: `job_${focusJobId}`, _viewMode: 'jobs', _job: job });
    setTimeout(() => { jobRowRefs.current[focusJobId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 80);
  }, [focusJobId, loading, allJobs, viewMode, visibleJobs, selectedBlock]);

  // 'custom' is a Jobs-view-only scope: custom jobs have no staff rows of their own (the
  // People view already shows them per-person, on each staffer's row). Landing on People
  // while it is active — via the view toggle OR a shared ?view=people&dept=custom URL —
  // matches no DepartmentCode and would render an empty grid. One guard covers both.
  // Job-only dept groups (AI) are the same story, so they are dropped from the set too.
  useEffect(() => {
    if (viewMode === 'jobs') return;
    if (deptFilter.includes('custom')) { setDeptFilter(['all']); return; }
    const kept = deptFilter.filter(d => !DEPT_GROUPS.some(g => g.jobOnly && g.id === d));
    if (kept.length !== deptFilter.length) setDeptFilter(kept.length ? kept : ['all']);
  }, [viewMode, deptFilter, setDeptFilter]);

  // ?cjob= deeplink — the custom-job twin of the ?job= effect below. Forces the Jobs view
  // and the Custom scope, since that is the only place these rows exist.
  useEffect(() => {
    if (loading) return;
    if (focusCustomJobId === consumedCustomJobParam.current) return;
    consumedCustomJobParam.current = focusCustomJobId;
    if (!focusCustomJobId) {
      if (selectedBlock?._viewMode === 'customjob') setSelectedBlock(null);
      return;
    }
    if (viewMode !== 'jobs') setViewMode('jobs');
    if (!deptFilter.includes('custom')) setDeptFilter(['custom']);
    const c = visibleCustomJobs.find(x => x.customJobId === focusCustomJobId);
    if (!c) return;
    setSelectedBlock({ key: `cjob_${focusCustomJobId}`, _viewMode: 'customjob', _customJob: c });
    setTimeout(() => { customJobRowRefs.current[focusCustomJobId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 80);
  }, [focusCustomJobId, loading, viewMode, deptFilter, visibleCustomJobs, selectedBlock]); // eslint-disable-line react-hooks/exhaustive-deps

  // A tooltip is hover state bound to ONE block. If the view, week, or filtered block
  // set changes, that block may no longer exist and its onMouseLeave will never fire,
  // so the tooltip would hang. Clearing on any such change is always correct: a
  // re-layout means the pointer is no longer meaningfully "on" the old block.
  useEffect(() => { setTooltip(null); }, [viewMode, weekOffset, zoom, blocksByPerson, blocksByJob, blocksByCustomJob]);

  const jobRowHeight = useCallback((jobId) => {
    const mt = blocksByJob[jobId]?.maxTrack || 0;
    const pt = placeholdersByJob[jobId]?.maxTrack || 0; // placeholder tracks stack below the staff blocks
    return Math.max(80, BLOCK_TOP + (mt + pt) * (BLOCK_H + BLOCK_GAP) + 8); // taller min for job rows (service pill + util bar)
  }, [blocksByJob, placeholdersByJob]);

  // Custom-job row geometry. Staff tracks are capped at CUSTOM_ROW_TRACK_CAP unless the
  // row is expanded; placeholder tracks stack below whatever the staff tracks resolve to
  // and are never hidden (there are only ever a handful, and hiding the unassigned work
  // is exactly backwards).
  const customShownTracks = useCallback((c) => (
    expandedCustomRows.has(c.customJobId) ? c.maxTrack : Math.min(c.maxTrack, CUSTOM_ROW_TRACK_CAP)
  ), [expandedCustomRows]);

  const customHasBand = useCallback((c) => c.maxTrack > CUSTOM_ROW_TRACK_CAP, []);

  const customRowHeight = useCallback((c) => (
    Math.max(80, BLOCK_TOP + (customShownTracks(c) + (c.phMaxTrack || 0)) * (BLOCK_H + BLOCK_GAP) + 8
      + (customHasBand(c) ? EXPAND_BAND_H : 0))
  ), [customShownTracks, customHasBand]);

  const toggleCustomRow = useCallback((customJobId) => {
    setExpandedCustomRows(prev => {
      const next = new Set(prev);
      if (next.has(customJobId)) next.delete(customJobId); else next.add(customJobId);
      return next;
    });
  }, []);

  // Fallback used until the bulk job_progress read lands (and for jobs missing from it).
  // Carries `placeholder` separately so jobProgressProps can fold it into scheduled the
  // same way the server path does — otherwise the bar would jump as job_progress arrives.
  const jobBudgetUtil = useCallback((jobId) => {
    const job = blocksByJob[jobId];
    const placeholder = Math.round((placeholderHoursByJob[jobId] || 0) * 100) / 100;
    if (!job) return { scheduled: 0, budget: 0, pct: 0, placeholder };
    const scheduled = job.blocks.reduce((s, b) => s + b.totalHours, 0);
    const budget = job.budgetHours || 0;
    return { scheduled: Math.round(scheduled * 100) / 100, budget, pct: budget > 0 ? Math.round((scheduled / budget) * 100) : 0, placeholder };
  }, [blocksByJob, placeholderHoursByJob]);

  // ── Resolve weekly capacity per staff from CalendarTemplates ─────────
  // Returns a function: (staffId, weekMondayISO) => number (capacity hours)
  const getStaffWeekCap = useCallback((staffId, weekMondayISO) => {
    const person = staff.find(s => s.StaffId === staffId);
    if (!person) return 40;
    // TODO: per-staff overrides would be fetched separately; for now globals only
    const applicable = globalCalendars.filter(t => {
      if (!t.IsActive) return false;
      if (t.DepartmentCode && t.DepartmentCode !== person.DepartmentCode) return false;
      const start = t.EffectiveStart ? t.EffectiveStart.slice(0, 10) : null;
      const end = t.EffectiveEnd ? t.EffectiveEnd.slice(0, 10) : null;
      if (start && weekMondayISO < start) return false;
      if (end && weekMondayISO > end) return false;
      return true;
    });
    if (applicable.length === 0) return 40;
    // Highest priority wins
    applicable.sort((a, b) => (b.Priority || 0) - (a.Priority || 0));
    const t = applicable[0];
    return (Number(t.MonHours)||0) + (Number(t.TueHours)||0) + (Number(t.WedHours)||0) +
           (Number(t.ThuHours)||0) + (Number(t.FriHours)||0) + (Number(t.SatHours)||0) + (Number(t.SunHours)||0);
  }, [staff, globalCalendars]);

  // PTO hours by staff by day — approved only (affects capacity + conflicts)
  const ptoByStaffDay = useMemo(() => buildPtoByStaffDay(ptoData), [ptoData]);

  // Pending PTO by staff by day — visual only (no capacity impact)
  // Non-admins see only their own pending; admins see all
  const pendingPtoByStaffDay = useMemo(() => {
    const map = {};
    for (const p of ptoData) {
      if (p.Status !== 'pending') continue;
      if (!isSuperUser && p.StaffId !== myStaffId) continue;
      const d = (typeof p.PTODate === 'string' ? p.PTODate : '').slice(0, 10);
      if (!d) continue;
      if (!map[p.StaffId]) map[p.StaffId] = {};
      map[p.StaffId][d] = (map[p.StaffId][d] || 0) + (p.Hours || 0);
    }
    return map;
  }, [ptoData, isSuperUser, myStaffId]);

  // Pending PTO lookup: cellKey → full request info (for admin grid popover)
  const pendingPtoLookup = useMemo(() => {
    if (!isSuperUser) return {};
    const byReq = {};
    for (const p of ptoData) {
      if (p.Status !== 'pending' || !p.LeaveReqID) continue;
      const d = (typeof p.PTODate === 'string' ? p.PTODate : '').slice(0, 10);
      if (!d) continue;
      if (!byReq[p.LeaveReqID]) {
        const s = staff.find(st => st.StaffId === p.StaffId);
        byReq[p.LeaveReqID] = {
          leaveReqId: p.LeaveReqID, staffId: p.StaffId,
          staffName: s?.Name || `Staff #${p.StaffId}`,
          notes: p.RequestNotes, totalHours: 0, days: [],
        };
      }
      byReq[p.LeaveReqID].totalHours += (p.Hours || 0);
      byReq[p.LeaveReqID].days.push(d);
    }
    // Build cell→request map; compute date range per request
    const map = {};
    for (const req of Object.values(byReq)) {
      req.days.sort();
      req.startDate = req.days[0];
      req.endDate = req.days[req.days.length - 1];
      for (const d of req.days) map[`${req.staffId}_${d}`] = req;
    }
    return map;
  }, [ptoData, isSuperUser, staff]);

  // Own PTO lookup: cellKey → request, powering the self-cancel popover.
  // Covers both statuses that actually render on the grid — 'approved' (drives
  // capacity + the hatched cell) and 'pending' (the blue cell). 'rejected' never
  // renders, and cancelled rows are hard-deleted by syncPTO's MERGE rather than
  // being restatused, so neither is reachable from a cell click.
  //
  // Keyed on LeaveReqID because PE has no partial cancel: clicking Wednesday of a
  // Mon–Fri request cancels the whole request, so the popover has to show the full
  // range, not just the clicked day.
  const myPtoLookup = useMemo(() => {
    if (!myStaffId) return {};
    const byReq = {};
    for (const p of ptoData) {
      if (p.StaffId !== myStaffId || !p.LeaveReqID) continue;
      if (p.Status !== 'approved' && p.Status !== 'pending') continue;
      const d = (typeof p.PTODate === 'string' ? p.PTODate : '').slice(0, 10);
      if (!d) continue;
      if (!byReq[p.LeaveReqID]) {
        byReq[p.LeaveReqID] = {
          leaveReqId: p.LeaveReqID, staffId: p.StaffId, status: p.Status,
          notes: p.RequestNotes, totalHours: 0, days: [],
        };
      }
      byReq[p.LeaveReqID].totalHours += (p.Hours || 0);
      byReq[p.LeaveReqID].days.push(d);
    }
    const map = {};
    for (const req of Object.values(byReq)) {
      req.days.sort();
      req.startDate = req.days[0];
      req.endDate = req.days[req.days.length - 1];
      // Cancelling reverses posted PTO in PE, so past-dated requests are read-only
      // here — see the same guard on EmployeeProfile's canCancel.
      req.isPast = req.endDate < todayISO;
      for (const d of req.days) map[`${req.staffId}_${d}`] = req;
    }
    return map;
  }, [ptoData, myStaffId, todayISO]);

  // ── Holiday lookup by date (computed from definitions + visible years) ─
  const holidayByDate = useMemo(() => {
    if (!holidays.length || !allDays.length) return {};
    const visibleYears = new Set(allDays.map(d => d.date.getFullYear()));
    return buildHolidayByDate(holidays, visibleYears);
  }, [holidays, allDays]);

  // ── Global assignment lookup by date ──────────────────────────────────
  const globalAssignmentByDate = useMemo(() => {
    const map = {};
    for (const ga of globalAssignments) {
      const iso = typeof ga.AssignmentDate === 'string' ? ga.AssignmentDate.slice(0, 10) : '';
      if (!iso) continue;
      if (!map[iso]) map[iso] = [];
      map[iso].push({ name: ga.Name, hours: ga.Hours, description: ga.Description, departments: ga.Departments });
    }
    return map;
  }, [globalAssignments]);

  // ── Job Pools lookups (Trello #257) ───────────────────────────────────
  // Keyed `${staffId}|${YYYY-MM-DD}` off ClaimedLocalDate. Using the stored LOCAL
  // date is load-bearing: the server runs UTC, so a claim made at 8pm Central has a
  // next-day UTC timestamp and keying off ClaimedAt would stamp the wrong day.
  const poolEventsByStaffDay = useMemo(() => {
    const map = {};
    for (const e of poolEvents) {
      if (!e.ClaimedByStaffId || !e.ClaimedLocalDate) continue;
      const iso = typeof e.ClaimedLocalDate === 'string'
        ? e.ClaimedLocalDate.slice(0, 10)
        : toISO(new Date(e.ClaimedLocalDate));
      const key = `${e.ClaimedByStaffId}|${iso}`;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    }
    return map;
  }, [poolEvents]);

  // Who is checked in as available right now → staffId : { capacityHours, since }.
  const poolAvailByStaff = useMemo(() => {
    const map = {};
    for (const a of poolAvailable) {
      if (a.StaffId) map[a.StaffId] = { hours: a.CapacityHours, since: a.CheckedInAt };
    }
    return map;
  }, [poolAvailable]);

  // Jobs currently sitting in a pool → JobId : { code, pooledAt }. Comes free from
  // the jobs read (queryJobs carries live pool membership), so the Jobs view can
  // chip them without another fetch.
  const poolJobsById = useMemo(() => {
    const map = {};
    for (const j of allJobs) {
      if (j.PoolJobId) map[j.JobId] = { code: j.PoolCode, pooledAt: j.PooledAt };
    }
    return map;
  }, [allJobs]);

  // Helper: get total global assignment hours for a staff member on a date
  const getGlobalHours = useCallback((staffId, dateISO) => {
    const entries = globalAssignmentByDate[dateISO];
    if (!entries?.length) return 0;
    const person = staff.find(s => s.StaffId === staffId);
    const deptCode = person?.DepartmentCode || '';
    return entries.reduce((sum, ga) => {
      if (ga.departments && !ga.departments.split(',').includes(deptCode)) return sum;
      return sum + (ga.hours || 0);
    }, 0);
  }, [globalAssignmentByDate, staff]);

  // Helper: get first matching global assignment name for a staff member on a date
  const getGlobalEntry = useCallback((staffId, dateISO) => {
    const entries = globalAssignmentByDate[dateISO];
    if (!entries?.length) return null;
    const person = staff.find(s => s.StaffId === staffId);
    const deptCode = person?.DepartmentCode || '';
    return entries.find(ga => !ga.departments || ga.departments.split(',').includes(deptCode)) || null;
  }, [globalAssignmentByDate, staff]);

  // ── Per-day capacity for a staff member on a specific date ──────────
  // Precedence (holiday > per-staff override > Default+dept-global pool > 8h),
  // with PTO subtracted, now lives in utils/capacity.js — the single source of
  // truth shared with the suggestions heatmap, placeholder modal, and the
  // profile/dashboard utilization views. Returns a stable (staffId, dateISO) => hours fn.
  const getDayCap = useMemo(
    () => makeGetDayCap({ staff, globalCalendars, ptoByStaffDay, holidayByDate }),
    [staff, globalCalendars, ptoByStaffDay, holidayByDate]
  );
  // Contract chip on each People row (PT / Flex / Leave), from the same templates.
  const getContract = useMemo(
    () => makeGetContract({ staff, globalCalendars }),
    [staff, globalCalendars]
  );

  // ── Per-day total scheduled hours by staff (for daily indicators) ─────
  const dayHoursByStaff = useMemo(
    () => buildDayHoursByStaff(entries, globalAssignments, staff),
    [entries, globalAssignments, staff]
  );

  // ── Utilization per person per week ───────────────────────────────────
  // Uses actual per-day entry hours (not block averaging) for accuracy with
  // uneven distributions and Saturday scheduling.
  const weekUtil = useCallback((staffId, wi) => {
    const weekDays = allDays.slice(wi * 7, (wi + 1) * 7);
    const staffDayHrs = dayHoursByStaff[staffId] || {};
    let sch = 0, cap = 0;
    for (const d of weekDays) {
      sch += staffDayHrs[d.iso] || 0;
      cap += getDayCap(staffId, d.iso); // PTO-aware daily capacity
    }
    return { sch: Math.round(sch * 100) / 100, cap };
  }, [allDays, dayHoursByStaff, getDayCap]);

  const overallUtil = useCallback((staffId) => {
    // Average utilization across all visible weeks
    let totalSch = 0, totalCap = 0;
    for (let wi = 0; wi < zoom; wi++) {
      const u = weekUtil(staffId, wi);
      totalSch += u.sch;
      totalCap += u.cap;
    }
    if (totalCap === 0) return 0;
    return Math.round((totalSch / totalCap) * 100);
  }, [weekUtil, zoom]);

  // Dynamic row height based on max track count
  const rowHeight = useCallback((staffId) => {
    const blocks = blocksByPerson[staffId] || [];
    const maxTrack = blocks.reduce((m, b) => Math.max(m, (b.track || 0) + 1), 0);
    const base = Math.max(ROW_MIN, BLOCK_TOP + maxTrack * (BLOCK_H + BLOCK_GAP) + 8);
    // Add extra space for PTO/overday labels at the bottom
    const hasPTOLabels = ptoByStaffDay[staffId] && Object.keys(ptoByStaffDay[staffId]).length > 0;
    return hasPTOLabels ? base + 14 : base;
  }, [blocksByPerson, ptoByStaffDay]);

  // Job Pools (#257): hovering a pool-available person's TODAY cell pulses their POOL
  // chip on the staff card, so it's obvious what the highlighted cell is about.
  //
  // Done imperatively on purpose. The chip lives in the frozen label column — a
  // different DOM subtree from the day cells — so CSS :hover can't reach across, and
  // routing a hover through React state would re-render this whole grid on every
  // mouse move. Same classList approach the drag handlers already use.
  const setPoolChipPulse = useCallback((staffId, on) => {
    const el = document.querySelector(`[data-pool-chip="${staffId}"]`);
    if (el) el.classList.toggle('sched-grid-person-poolavail--pulse', on);
  }, []);

  // Because that class is applied outside React, a hovered cell that unmounts on a
  // data refresh would never fire mouseleave and could leave a chip pulsing forever.
  // Clearing on every availability refresh is enough to make that unreachable.
  useEffect(() => {
    document.querySelectorAll('.sched-grid-person-poolavail--pulse')
      .forEach(el => el.classList.remove('sched-grid-person-poolavail--pulse'));
  }, [poolAvailByStaff]);

  // Dev-superuser testing visibility: when a dev is logged in, surface ALL dev-superuser
  // rows (not just their own) even though these are admin/leadership staff normally excluded
  // — so devs can move assignments between each other to test cross-user behavior
  // (e.g. Outlook sync appearing on a second dev's calendar). No effect for non-dev viewers.
  const devSuperuserStaffIds = useMemo(() => {
    const emails = new Set(DEV_SUPERUSERS.map(e => e.toLowerCase()));
    return new Set(staff.filter(s => emails.has((s.Email || '').toLowerCase())).map(s => s.StaffId));
  }, [staff]);

  // ── Filter staff ──────────────────────────────────────────────────────
  const visibleStaff = useMemo(() => {
    let list = staff.filter(s => isGridEligible(s) && (
      deptFilter.includes('ADMIN') ||
      (isDevSuperUser && devSuperuserStaffIds.has(s.StaffId)) ||   // dev-superusers see each other (admin/leadership) for cross-user testing
      !(s.DepartmentCode || '').startsWith('ADMIN')
    ));
    if (deptFilter.includes('mine') || deptFilter.includes('mine-lead')) {
      // 'mine-lead' is a Jobs-view-only scope; in People view treat it as my own row.
      if (myStaffId) list = list.filter(s => s.StaffId === myStaffId);
      else list = []; // no match found
    } else if (deptFilter.includes('ADMIN')) {
      list = list.filter(s => (s.DepartmentCode || '').startsWith('ADMIN'));
    } else if (!deptFilter.includes('all')) {
      // ADVISORY tab should also match staff with DepartmentCode 'CONSULT' (PE legacy name)
      list = list.filter(s => deptFilter.includes(s.DepartmentCode) || (deptFilter.includes('ADVISORY') && s.DepartmentCode === 'CONSULT'));
    }
    if (staffFilter.length > 0) {
      list = list.filter(s => staffFilter.includes(s.StaffId));
    }
    if (searchStaffIds) {
      list = list.filter(s => searchStaffIds.has(s.StaffId));
    }
    if (hideLeadership && !deptFilter.includes('mine')) {
      // Keep dev-superusers visible to a dev viewer even when hiding leadership (testing).
      list = list.filter(s => !LEADERSHIP_LEVEL_SET.has(s.Level) || (isDevSuperUser && devSuperuserStaffIds.has(s.StaffId)));
    }
    if (officeFilter !== 'all') {
      list = list.filter(s => s.OfficeCode === officeFilter);
    }
    // Client name/code filter (?cq) — keep staff with at least one block on a matching client.
    // The per-block filter at render time hides the non-matching blocks; this prunes empty rows.
    if (clientSearch.trim()) {
      const cq = clientSearch.trim().toLowerCase();
      list = list.filter(s => (blocksByPerson[s.StaffId] || []).some(b =>
        (b.clientName || '').toLowerCase().includes(cq) ||
        (b.clientCode || '').toLowerCase().includes(cq)));
    }

    // Client group (?client) — same shape as the client search above: keep staff
    // with at least one block on a selected client; the per-block filter at render
    // time hides the rest.
    if (clientGroupActive) {
      list = list.filter(s => (blocksByPerson[s.StaffId] || [])
        .some(b => b.jobId && jobMatchesClientFilter(b.jobId)));
    }

    // Return type / prep budget (suggestion #17) — keep only staff holding at least
    // one matching block. Custom-job blocks have no jobId and are never client work,
    // so they can't satisfy a job filter.
    if (moreFiltersActive) {
      list = list.filter(s => (blocksByPerson[s.StaffId] || [])
        .some(b => b.jobId && jobMatchesMoreFilters(b.jobId)));
    }
    if (hideEmpty) {
      // Keep dev-superuser rows for a dev viewer even when empty — you need an empty
      // target row to drop an assignment onto when testing cross-user (Outlook) sync.
      list = list.filter(s => (blocksByPerson[s.StaffId] || []).length > 0 || (isDevSuperUser && devSuperuserStaffIds.has(s.StaffId)));
    }
    if (showFreeOnly) {
      list = list.filter(s => (blocksByPerson[s.StaffId] || []).length === 0);
    }
    // Sort: department alpha, then level descending (highest first), then name alpha
    const LEVEL_RANK = { 'Member': 8, 'Principal': 7, 'Senior Manager': 7, 'Manager': 6, 'Supervisor': 5, 'Senior Accountant': 4, 'Staff B': 3, 'Staff A': 2, 'Intern': 1, 'Consultant': 1 };
    list.sort((a, b) => {
      const deptCmp = (a.DepartmentCode || '').localeCompare(b.DepartmentCode || '');
      if (deptCmp !== 0) return deptCmp;
      const levelA = LEVEL_RANK[a.Level] || 0;
      const levelB = LEVEL_RANK[b.Level] || 0;
      if (levelA !== levelB) return levelB - levelA; // descending level
      return (a.Name || '').localeCompare(b.Name || '');
    });

    // Item 1 — always inject a synthetic placeholder row above each (dept, level)
    // group of visible staff. The row serves as a level divider AND a place to
    // park placeholder job blocks. Clicking an empty cell on a placeholder row
    // opens the new-entry modal pre-filled with that dept + level.
    const phByKey = {};
    for (const ph of placeholders) {
      const k = `${ph.DepartmentCode}|${ph.Level}`;
      if (!phByKey[k]) phByKey[k] = [];
      phByKey[k].push(ph);
    }
    // Assign tracks to each (dept, level) group so overlapping placeholders
    // stack vertically like regular blocks. Uses millisecond timestamps so the
    // algorithm works for ranges entirely outside the visible window too.
    const phMaxTrackByKey = {};
    for (const k of Object.keys(phByKey)) {
      const phs = phByKey[k];
      phs.sort((a, b) => String(a.StartDate).localeCompare(String(b.StartDate)));
      const trackEnds = [];
      for (const ph of phs) {
        const sNum = new Date(String(ph.StartDate).slice(0, 10) + 'T12:00:00').getTime();
        const eNum = new Date(String(ph.EndDate).slice(0, 10) + 'T12:00:00').getTime();
        let placed = false;
        for (let t = 0; t < trackEnds.length; t++) {
          if (trackEnds[t] < sNum) {
            ph._track = t;
            trackEnds[t] = eNum;
            placed = true;
            break;
          }
        }
        if (!placed) {
          ph._track = trackEnds.length;
          trackEnds.push(eNum);
        }
      }
      phMaxTrackByKey[k] = trackEnds.length;
    }

    const visibleDeptLevels = new Set();
    for (const s of list) visibleDeptLevels.add(`${s.DepartmentCode}|${s.Level}`);

    // Placeholder-row eligibility — a placeholder for (D, L) should appear only
    // when the current dept + level filters would have allowed staff at (D, L)
    // to be in the list. Staff-specific filters (hideEmpty, showFreeOnly,
    // staffFilter, officeFilter) don't affect this — those hide individual
    // staffers but not the (dept, level) section itself.
    const isEligibleDeptLevel = (dept, level) => {
      if (deptFilter.includes('mine') || deptFilter.includes('mine-lead')) return false; // 'mine'/'mine-lead' are staff-specific; placeholders not meaningful
      if (deptFilter.includes('custom')) return false; // Jobs-view-only scope; never a People-view (dept, level) row
      if (deptFilter.includes('ADMIN')) {
        if (!(dept || '').startsWith('ADMIN')) return false;
      } else if (!deptFilter.includes('all')) {
        const match = deptFilter.includes(dept) || (deptFilter.includes('ADVISORY') && dept === 'CONSULT');
        if (!match) return false;
      } else if ((dept || '').startsWith('ADMIN')) {
        // Same exclusion the staff filter applies for non-ADMIN tabs
        return false;
      }
      if (hideLeadership && LEADERSHIP_LEVEL_SET.has(level)) return false;
      return true;
    };

    const withPlaceholders = [];
    const emittedPhKeys = new Set(); // defensive: prevents the same (dept, level) row from being injected twice
    let lastKey = null;
    for (const s of list) {
      const k = `${s.DepartmentCode}|${s.Level}`;
      if (k !== lastKey && !emittedPhKeys.has(k)) {
        emittedPhKeys.add(k);
        withPlaceholders.push({
          StaffId: `ph-${s.DepartmentCode}-${s.Level}`,
          Name: s.Level,
          Initials: 'PH',
          Level: s.Level,
          DepartmentCode: s.DepartmentCode,
          Department: s.Department,
          IsActive: true,
          isPlaceholder: true,
          placeholders: phByKey[k] || [],
          phMaxTrack: phMaxTrackByKey[k] || 0,
        });
      }
      withPlaceholders.push(s);
      lastKey = k;
    }
    // Also inject rows for (dept, level) pairs that have placeholders but no
    // visible staff (e.g. all staff in that group are filtered out by
    // hideEmpty/showFreeOnly/staffFilter). Filter by the SAME dept + leadership
    // rules so we don't surface placeholders for hidden departments or hidden
    // leadership levels.
    for (const k of Object.keys(phByKey)) {
      if (visibleDeptLevels.has(k)) continue;
      if (emittedPhKeys.has(k)) continue;
      const [dept, level] = k.split('|');
      if (!isEligibleDeptLevel(dept, level)) continue;
      emittedPhKeys.add(k);
      const deptLabel = (DEPT_GROUPS.find(g => g.id === dept) || {}).label || dept;
      withPlaceholders.push({
        StaffId: `ph-${dept}-${level}`,
        Name: level,
        Initials: 'PH',
        Level: level,
        DepartmentCode: dept,
        Department: deptLabel,
        IsActive: true,
        isPlaceholder: true,
        placeholders: phByKey[k],
        phMaxTrack: phMaxTrackByKey[k] || 0,
      });
    }
    return withPlaceholders;
  }, [staff, deptFilter, staffFilter, searchStaffIds, clientSearch, myStaffId, isDevSuperUser, devSuperuserStaffIds, hideEmpty, showFreeOnly, hideLeadership, officeFilter, blocksByPerson, placeholders, isGridEligible, moreFiltersActive, jobMatchesMoreFilters, clientGroupActive, jobMatchesClientFilter]);

  // ── Conflict detection ─────────────────────────────────────────────────
  const conflicts = useMemo(() => {
    const result = [];
    for (const person of visibleStaff) {
      for (let wi = 0; wi < zoom; wi++) {
        const u = weekUtil(person.StaffId, wi);
        const weekDays = allDays.slice(wi * 7, (wi + 1) * 7);
        const ptoDays = weekDays.filter(d => (ptoByStaffDay[person.StaffId]?.[d.iso] || 0) > 0);
        const ptoHoursInWeek = ptoDays.reduce((sum, d) => sum + (ptoByStaffDay[person.StaffId]?.[d.iso] || 0), 0);
        const holidayDays = weekDays.filter(d => !d.isWE && holidayByDate[d.iso]);
        const holidayConflictDays = holidayDays.filter(d => (dayHoursByStaff[person.StaffId]?.[d.iso] || 0) > 0);
        const globalDays = weekDays.filter(d => !d.isWE && getGlobalEntry(person.StaffId, d.iso));
        const globalConflictDays = globalDays.filter(d => (dayHoursByStaff[person.StaffId]?.[d.iso] || 0) > 0);

        if (u.sch > u.cap) {
          // Weekly capacity exceeded
          result.push({
            staffId: person.StaffId, staffName: person.Name, level: person.Level,
            weekIdx: wi, weekLabel: weeks[wi]?.label || `Week ${wi + 1}`,
            scheduled: u.sch, capacity: u.cap, overage: u.sch - u.cap,
            hasPTO: ptoDays.length > 0, ptoDays: ptoDays.map(d => d.iso), ptoHours: ptoHoursInWeek,
            hasHoliday: holidayConflictDays.length > 0, holidayDays: holidayConflictDays.map(d => d.iso),
            holidayNames: holidayConflictDays.map(d => holidayByDate[d.iso]?.name).filter(Boolean),
            hasGlobalAssignment: globalConflictDays.length > 0,
            globalAssignmentNames: globalConflictDays.map(d => getGlobalEntry(person.StaffId, d.iso)?.name).filter(Boolean),
          });
        } else {
          // Weekly total is OK — but check for daily violations (e.g., hours on PTO/holiday days)
          const violatingDays = weekDays.filter(d => {
            if (d.isWE) return false;
            const sched = dayHoursByStaff[person.StaffId]?.[d.iso] || 0;
            const cap = getDayCap(person.StaffId, d.iso);
            return sched > 0 && sched > cap;
          });
          if (violatingDays.length > 0) {
            const ptoViolations = violatingDays.filter(d => (ptoByStaffDay[person.StaffId]?.[d.iso] || 0) > 0);
            const holidayViolations = violatingDays.filter(d => !!holidayByDate[d.iso]);
            const violatingHrs = violatingDays.reduce((s, d) => s + (dayHoursByStaff[person.StaffId]?.[d.iso] || 0), 0);
            result.push({
              staffId: person.StaffId, staffName: person.Name, level: person.Level,
              weekIdx: wi, weekLabel: weeks[wi]?.label || `Week ${wi + 1}`,
              scheduled: u.sch, capacity: u.cap, overage: 0,
              hasPTO: ptoViolations.length > 0, ptoDays: ptoViolations.map(d => d.iso),
              ptoHours: ptoViolations.reduce((s, d) => s + (ptoByStaffDay[person.StaffId]?.[d.iso] || 0), 0),
              hasDailyViolation: true, violatingDays: violatingDays.map(d => d.iso), violatingHours: violatingHrs,
              hasHoliday: holidayViolations.length > 0, holidayDays: holidayViolations.map(d => d.iso),
              holidayNames: holidayViolations.map(d => holidayByDate[d.iso]?.name).filter(Boolean),
              hasGlobalAssignment: globalConflictDays.length > 0,
              globalAssignmentNames: globalConflictDays.map(d => getGlobalEntry(person.StaffId, d.iso)?.name).filter(Boolean),
            });
          }
        }
      }
    }
    result.sort((a, b) => a.weekIdx - b.weekIdx || b.overage - a.overage);
    return result;
  }, [visibleStaff, zoom, weekUtil, weeks, allDays, ptoByStaffDay, dayHoursByStaff, getDayCap, holidayByDate, getGlobalEntry]);

  const conflictStaffIds = useMemo(() => new Set(conflicts.map(c => c.staffId)), [conflicts]);

  // After conflicts re-render, check for pending highlight from dashboard navigation
  useEffect(() => {
    const key = pendingConflictHighlight.current;
    if (!key || conflicts.length === 0) return;
    // Small delay to let DOM render the conflict cards
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-conflict-key="${key}"]`);
      if (el) {
        pendingConflictHighlight.current = null;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s, background 0.3s';
        el.style.boxShadow = '0 0 0 3px #3B82F6';
        el.style.background = '#EFF6FF';
        el.style.borderRadius = '10px';
        setTimeout(() => { el.style.boxShadow = ''; el.style.background = ''; }, 3500);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [conflicts]); // fires when conflicts array updates (after zoom/offset change)

  // ── Conflict resolution helpers ───────────────────────────────────────
  const getBlockInfo = useCallback((staffId, jobId) => {
    const block = (blocksByPerson[staffId] || []).find(b => b.jobId === jobId);
    if (!block) return { weekCount: 1, isStandalone: true, totalHours: 0, startISO: null, endISO: null };
    const weekMondays = new Set(block.dates.map(d => {
      const dt = d.date instanceof Date ? d.date : new Date(d.date);
      const mon = new Date(dt); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
      return toISO(mon);
    }));
    const startISO = block.dates.length > 0 ? toISO(block.dates[0].date instanceof Date ? block.dates[0].date : new Date(block.dates[0].date)) : null;
    const endISO = block.dates.length > 0 ? toISO(block.dates[block.dates.length - 1].date instanceof Date ? block.dates[block.dates.length - 1].date : new Date(block.dates[block.dates.length - 1].date)) : null;
    return { weekCount: weekMondays.size, isStandalone: weekMondays.size <= 1, totalHours: block.totalHours, startISO, endISO };
  }, [blocksByPerson]);

  const fmtBlockDate = (iso) => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  const LEVEL_ORDER = ['Intern', 'Consultant', 'Staff A', 'Staff B', 'Senior Accountant', 'Supervisor', 'Manager', 'Senior Manager', 'Principal', 'Member'];

  const handleSuggestFix = useCallback(async (conflict) => {
    const weekStartDate = weeks[conflict.weekIdx]?.days?.[0]?.date;
    if (!weekStartDate) return;
    const weekStartISO = toISO(weekStartDate);

    // Toggle off if same conflict
    if (fixConflict?.staffId === conflict.staffId && fixConflict?.weekIdx === conflict.weekIdx) {
      setFixConflict(null); setFixEntries([]); setFixSelectedJob(null); setFixCandidates([]); setConflictSelectedJob(null); setConflictJobWip([]);
      return;
    }

    setFixConflict({ staffId: conflict.staffId, weekIdx: conflict.weekIdx, weekStart: weekStartISO });
    setFixEntries([]); setFixSelectedJob(null); setFixCandidates([]); setFixLoading(true); setConflictSelectedJob(null); setConflictJobWip([]);

    try {
      const details = await fetchConflictDetails(conflict.staffId, weekStartISO);
      setFixEntries(details || []);
    } catch { setFixEntries([]); }
    setFixLoading(false);
  }, [weeks, fixConflict]);

  const handleSelectFixJob = useCallback(async (jobId) => {
    setFixSelectedJob(jobId);
    setFixCandidates([]); setFixLoading(true); setFixNicheMissing(false);
    // Show job detail sidebar
    const entry = fixEntries.find(e => e.jobId === jobId);
    if (entry) {
      setConflictSelectedJob({ jobId, jobName: entry.jobName, clientName: entry.clientName, staffName: fixConflict?.staffId ? staff.find(s => s.StaffId === fixConflict.staffId)?.Name : '', weekEntries: entry.entries });
      fetchJobsWIP({ jobId }).then(d => setConflictJobWip(d || [])).catch(() => setConflictJobWip([]));
      // today-on per-staff scheduled (window-independent) for the panel's Staff list + SCHEDULED header
      setConflictJobSched([]);
      fetchJobScheduledStaff({ jobId, today: toISO(new Date()) }).then(d => setConflictJobSched(d || [])).catch(() => setConflictJobSched([]));
      // true contiguous block for THIS staff+job (window-independent) for the "Entire block" label
      setFixFullBlock(null);
      fetchSchedule({ staffId: fixConflict?.staffId, jobId }).then(all => {
        const blk = findBlockContaining(all || [], fixConflict?.weekStart || null);
        const dates = blk.map(e => String(e.EntryDate).slice(0, 10)).sort();
        setFixFullBlock({
          totalHours: blk.reduce((s, e) => s + (e.Hours || 0), 0), startISO: dates[0] || null, endISO: dates[dates.length - 1] || null,
          days: blk.map(e => ({ date: String(e.EntryDate).slice(0, 10), hours: Number(e.Hours) || 0 })),
        });
      }).catch(() => setFixFullBlock(null));
      // Everyone's entries on this job (all dates) — drives the "already on this job" note.
      setFixJobEntries([]);
      fetchSchedule({ jobId }).then(rows => setFixJobEntries(rows || [])).catch(() => setFixJobEntries([]));
    }

    try {
      // Get suggestions for this job (reuses the scoring engine)
      const sugData = await fetchSuggestions(jobId);
      setFixNicheMissing(!!sugData?.job?.nicheMissing);
      setFixOnSite({
        isOnSite: !!sugData?.job?.isOnSite,
        office: sugData?.job?.office || null,
        officeCode: sugData?.job?.officeCode || null,
      });
      const cands = sugData?.candidates || [];
      // On-site job → only staff from the job's office can take the reassignment.
      const onSiteOffice = (sugData?.job?.isOnSite && sugData?.job?.officeCode) ? sugData.job.officeCode : null;

      // Availability for the top candidates in the conflict week, computed from the
      // SAME getDayCap / dayHoursByStaff this grid uses everywhere else — so a proposed
      // replacement can never disagree with the very conflict we are resolving.
      //
      // This used to call schedData `suggestion_availability`, which reads
      // rprs.vw_StaffUtilization -> rprs.fn_WeeklyCapacity: a parallel SQL capacity path
      // that ignores PTO and holidays outright (and drops inactive staff, and has no
      // IsDefault tie-break). It would happily offer someone with 24h of approved PTO as
      // the fix for another person's overbooking — e.g. week of 2026-09-14 read 39%
      // utilized there vs 156% in reality. capacity.js is the single capacity source.
      //
      // No fetch needed: `conflicts` is derived client-side over the loaded grid window,
      // so weekStart is always a Monday these memos already cover.
      const weekStart = fixConflict?.weekStart;
      const topIds = cands.slice(0, 50).map(c => c.StaffId);
      let availMap = {};
      if (topIds.length > 0 && weekStart) {
        const availRows = computeStaffWeeklyUtil({
          staffIds: topIds, weekMondays: [weekStart], getDayCap, dayHoursByStaff,
        });
        for (const r of availRows) availMap[r.staffId] = r.weeks?.[0] || null;
      }

      // Filter: similar level (+-1), matching department, and has capacity
      const conflictStaff = staff.find(s => s.StaffId === fixConflict?.staffId);
      const conflictLevelIdx = LEVEL_ORDER.indexOf(conflictStaff?.Level || '');
      const entry = fixEntries.find(e => e.jobId === jobId);
      const entryHours = entry?.totalHours || 0;

      // Department filter — same logic as StaffSuggestions allowedDepts
      const jobDept = entry?.serviceDesc ? SERVICE_TO_DEPT[entry.serviceDesc] : null;
      let allowedDepts = null;
      if (jobDept === 'AUDIT') allowedDepts = new Set(['AUDIT', 'GENERAL']);
      else if (jobDept === 'TAX' || jobDept === 'ESTATE') allowedDepts = new Set(['TAX', 'ESTATE', 'GENERAL']);
      else if (jobDept === 'ADVISORY') allowedDepts = new Set(['ADVISORY', 'CONSULT', 'GENERAL']);
      else if (jobDept === 'GOVCON') allowedDepts = new Set(['GOVCON', 'GENERAL']);
      else if (jobDept === 'CAS') allowedDepts = new Set(['CAS', 'GENERAL']);

      // Deduplicate candidates by StaffId (SQL JOINs can produce duplicates)
      const seenIds = new Set();
      const dedupedCands = cands.filter(c => { if (seenIds.has(c.StaffId)) return false; seenIds.add(c.StaffId); return true; });
      const filtered = dedupedCands
        .filter(c => {
          if (c.StaffId === fixConflict?.staffId) return false;
          // Department filter
          if (allowedDepts && !allowedDepts.has(c.DepartmentCode)) return false;
          // On-site job → office must match (hard filter, like the modal quick-pick).
          if (onSiteOffice && c.OfficeCode !== onSiteOffice) return false;
          // Level filter — exact match or +-1 depending on toggle
          const cLevelIdx = LEVEL_ORDER.indexOf(c.Level || '');
          if (conflictLevelIdx >= 0 && cLevelIdx >= 0) {
            const diff = Math.abs(cLevelIdx - conflictLevelIdx);
            if (fixExactLevel ? diff > 0 : diff > 1) return false;
          }
          return true;
        })
        .map(c => {
          const avail = availMap[c.StaffId];
          const capacity = avail?.capacity || 40;
          const scheduled = avail?.scheduled || 0;
          const freeHours = Math.max(0, capacity - scheduled);
          return { ...c, freeHours, hasCapacity: freeHours >= entryHours };
        })
        .slice(0, 10);

      setFixCandidates(filtered);
    } catch { setFixCandidates([]); }
    setFixLoading(false);
  }, [fixConflict, fixEntries, staff, fixExactLevel, getDayCap, dayHoursByStaff]);

  // Re-fetch candidates when exact level toggle changes
  useEffect(() => {
    if (fixSelectedJob) handleSelectFixJob(fixSelectedJob);
  }, [fixExactLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Not a useCallback — needs refreshEntries/showToast which are defined later.
  // Using a ref-based approach to avoid the TDZ issue.
  const handleReassignRef = useRef(null);
  handleReassignRef.current = async (candidateStaffId, jobId) => {
    const entry = fixEntries.find(e => e.jobId === jobId);
    if (!entry) return;

    if (reassignScope === 'all') {
      // Entire block: fetch ALL entries for this staff+job, find the contiguous block, move, then open edit modal for optimization
      setFixReassigning(true);
      try {
        const allEntries = await fetchSchedule({ staffId: fixConflict?.staffId, jobId });
        if (!allEntries?.length) { showToast('No entries found', 'error'); setFixReassigning(false); return; }

        // Find the contiguous block containing this conflict week (window-independent).
        const weekStart = weeks[fixConflict?.weekIdx]?.days?.[0]?.date;
        const weekIso = weekStart ? toISO(weekStart) : (fixConflict?.weekStart || null);
        const blockEntries = findBlockContaining(allEntries, weekIso);

        const batchResult = await getBatchId();
        const batchId = batchResult.batchId;
        // ONE atomic save (it used to be one move_entry call per day, so a collision partway
        // left a half-reassigned block). Days the candidate already has on this job are
        // combined server-side; bulk_move notifies both people, as a drag-drop reassign does.
        const res = await bulkMove({
          jobId, staffId: fixConflict?.staffId, batchId, source: 'conflict-resolve', movedBy: username,
          moves: blockEntries.map(e => ({ entryId: e.EntryId, newStaffId: candidateStaffId })),
          deletes: [], inserts: [],
        });
        await refreshEntries();
        const cand = fixCandidates.find(ca => ca.StaffId === candidateStaffId);
        showToast(`Block reassigned to ${cand?.Name || 'new staff'} (${blockEntries.length} entries)${mergeNote(res)}. Opening to optimize...`, 'success');

        // Calculate zoom needed to show the full block + set week offset
        const blockStart = String(blockEntries[0].EntryDate).slice(0, 10);
        const blockEnd = String(blockEntries[blockEntries.length - 1].EntryDate).slice(0, 10);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const startDate = new Date(blockStart + 'T12:00:00');
        const endDate = new Date(blockEnd + 'T12:00:00');
        const startOffset = Math.floor((startDate - today) / 86400000 / 7);
        const blockWeeks = Math.max(1, Math.ceil((endDate - startDate) / 86400000 / 7) + 2);
        setWeekOffset(startOffset);
        if (blockWeeks > zoom) setZoom(Math.min(52, blockWeeks));

        // Navigate to grid and open edit modal on the new staff's block
        sessionStorage.setItem('pendingReassign', JSON.stringify({
          blockStaffId: candidateStaffId,
          blockJobId: jobId,
          newStaffId: candidateStaffId,
          // The candidate may already have had other blocks on this job (and some days were
          // just combined into them), so name the block by a date inside it.
          anchorDate: blockStart,
        }));

        setFixConflict(null); setFixEntries([]); setFixSelectedJob(null); setFixCandidates([]); setConflictSelectedJob(null); setConflictJobWip([]); setReassignScope('week');
        refreshNotifications?.();
        setTab('calendar');
      } catch (err) {
        showToast('Reassign failed: ' + err.message, 'error');
      }
      setFixReassigning(false);
      return;
    }

    // Week only: direct move
    setFixReassigning(true);
    try {
      const batchResult = await getBatchId();
      const batchId = batchResult.batchId;
      // One atomic save \u2014 see the entire-block branch above.
      const res = await bulkMove({
        jobId, staffId: fixConflict?.staffId, batchId, source: 'conflict-resolve', movedBy: username,
        moves: entry.entries.map(e => ({ entryId: e.entryId, newStaffId: candidateStaffId })),
        deletes: [], inserts: [],
      });
      await refreshEntries();
      const cand = fixCandidates.find(ca => ca.StaffId === candidateStaffId);
      const overCap = cand && !cand.hasCapacity;
      showToast(overCap
        ? `Reassigned to ${cand.Name}${mergeNote(res)}. Note: they are now over capacity \u2014 consider optimizing their schedule.`
        : `Entries reassigned successfully (this week)${mergeNote(res)}`, overCap ? 'info' : 'success');
      setFixConflict(null); setFixEntries([]); setFixSelectedJob(null); setFixCandidates([]); setConflictSelectedJob(null); setConflictJobWip([]); setReassignScope('week');
      refreshNotifications?.();
    } catch (err) {
      showToast('Reassign failed: ' + err.message, 'error');
    }
    setFixReassigning(false);
  };

  // Days in the chosen reassign scope that the candidate ALREADY has on this job. Shown on the
  // candidate card before anyone clicks Reassign: the server combines those hours (it used to
  // fail on them partway through), so the user sees exactly what will happen.
  const fixOverlapsFor = (candStaffId) => {
    if (!fixSelectedJob) return [];
    const scopeDays = reassignScope === 'week'
      ? ((fixEntries.find(e => e.jobId === fixSelectedJob)?.entries) || []).map(e => ({ date: String(e.date).slice(0, 10), hours: Number(e.hours) || 0 }))
      : (fixFullBlock?.days || []);
    if (!scopeDays.length) return [];
    const theirs = {};
    for (const e of fixJobEntries) {
      if (e.StaffId === candStaffId && e.JobId === fixSelectedJob) theirs[String(e.EntryDate).slice(0, 10)] = Number(e.Hours) || 0;
    }
    return scopeDays
      .filter(d => theirs[d.date] != null)
      .map(d => ({
        date: d.date,
        label: new Date(`${d.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }),
        existing: Math.round(theirs[d.date] * 100) / 100,
        combined: Math.round((theirs[d.date] + d.hours) * 100) / 100,
      }));
  };

  // Animated close helpers — add closing class, wait for animation, then set state
  const closeSidebar = useCallback(() => {
    setJobParam(null);
    setCustomJobParam(null);
    if (sidebarRef.current) {
      sidebarRef.current.classList.add('sched-grid-detail--closing');
      setTimeout(() => setSelectedBlock(null), 150);
    } else setSelectedBlock(null);
  }, [setJobParam, setCustomJobParam]);

  const closeModal = useCallback(() => {
    setShowMultiPreview(false);
    setPendingPlaceholders([]);
    if (modalBackdropRef.current) {
      modalBackdropRef.current.classList.add('sched-mgr-modal-backdrop--closing');
      setTimeout(() => setShowModal(false), 150);
    } else setShowModal(false);
  }, []);

  const closeFilterModal = useCallback(() => {
    if (filterModalRef.current) {
      filterModalRef.current.classList.add('sched-mgr-modal-backdrop--closing');
      setTimeout(() => setShowFilterModal(false), 150);
    } else setShowFilterModal(false);
  }, []);

  // Stable so WeekJumpPicker's memo holds; it re-renders on its own draft anyway.
  const jumpToWeek = useCallback((iso) => {
    setWeekOffset(weekParamToOffset(iso));
  }, [setWeekOffset]);

  const closeClientFilterModal = useCallback(() => {
    if (clientFilterModalRef.current) {
      clientFilterModalRef.current.classList.add('sched-mgr-modal-backdrop--closing');
      setTimeout(() => setShowClientFilterModal(false), 150);
    } else setShowClientFilterModal(false);
  }, []);

  const closeDndModal = useCallback(() => {
    if (dndModalRef.current) {
      dndModalRef.current.classList.add('sched-mgr-modal-backdrop--closing');
      setTimeout(() => setDropPreview(null), 150);
    } else setDropPreview(null);
  }, []);

  // Backdrop dismissal that ignores a drag that merely ENDS on the backdrop —
  // selecting text in the notes field or overshooting an hours input used to close
  // the modal mid-edit, because `click` fires on the common ancestor of mousedown
  // and mouseup. See hooks/useBackdropClose (bug report #12).
  const modalBackdrop = useBackdropClose(closeModal);
  const filterBackdrop = useBackdropClose(closeFilterModal);
  const clientFilterBackdrop = useBackdropClose(closeClientFilterModal);
  const dndBackdrop = useBackdropClose(closeDndModal);

  const scrollToStaff = useCallback((staffId, weekIdx) => {
    // Focus the grid on just this staffer in PEOPLE view. Conflict rows only exist in
    // people view (arriving in jobs view is why the scroll seemed to do nothing —
    // rowRefs has no entry), and a single-staff filter guarantees the row is visible
    // without rendering all staff across a wide zoom.
    setViewMode('people');
    setStaffFilter([staffId]);
    setDeptFilter(['all']);   // ARRAY — a bare string serializes to ?dept= and reads back as []
    setHideLeadership(false);
    setHideEmpty(false);
    setShowFreeOnly(false);
    setTab('calendar');

    // Highlight the staff row + the specific conflict-week cells.
    setHighlightStaffIds(new Set([staffId]));
    if (weekIdx != null) {
      setHighlightConflictCells({ staffId, weekIdx });
    }

    setTimeout(() => {
      const el = rowRefs.current[staffId];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Bring the conflict week horizontally into view too (not just the row).
      if (weekIdx != null && gridRef.current && dayX[weekIdx * 7] != null) {
        gridRef.current.scrollTo({ left: Math.max(0, dayX[weekIdx * 7] - 40), behavior: 'smooth' });
      }
    }, 150);

    // Clear highlights after animation completes
    setTimeout(() => {
      setHighlightStaffIds(new Set());
      setHighlightConflictCells(null);
    }, 3500);
  }, [dayX]);

  // ── Sync scroll ───────────────────────────────────────────────────────
  const handleGridScroll = useCallback(() => {
    if (leftRef.current && gridRef.current) {
      leftRef.current.scrollTop = gridRef.current.scrollTop;
    }
    // Persist scroll position (separate keys for performance — avoids JSON parse/stringify on every scroll)
    if (gridRef.current) {
      sessionStorage.setItem('rprs_grid_scrollTop', String(gridRef.current.scrollTop));
      sessionStorage.setItem('rprs_grid_scrollLeft', String(gridRef.current.scrollLeft));
    }
  }, []);

  // ── Click empty cell → new entry modal ────────────────────────────────
  const openNewModal = useCallback((person, dayIdx, placeholderContext) => {
    if (dayIdx != null && allDays[dayIdx]?.isWE) return;
    // Departed staff are view-only — one guard here covers every empty-cell click on an
    // inactive person's row.
    if (person?.StaffId != null && !isAssignableStaffId(person.StaffId)) {
      showToast(`${person.Name || 'This staffer'} is inactive — reassign their work instead of adding to it`, 'error');
      return;
    }
    setModalEditBlock(null);
    setModalSource('ui');
    setModalPerson(person);
    const startDate = dayIdx != null ? allDays[dayIdx]?.date : baseMonday;
    setModalStart(dayIdx != null ? (allDays[dayIdx]?.iso || '') : toISO(baseMonday));
    // Default end date: Friday or Saturday depending on calendar capacity
    // If Saturday has capacity (e.g., busy season), include it
    const d = startDate || baseMonday;
    const dow = d.getDay();
    const friday = addDays(d, dow === 0 ? 5 : dow === 6 ? 6 : 5 - dow);
    const saturday = addDays(friday, 1);
    const satCap = person ? getDayCap(person.StaffId, toISO(saturday)) : 0;
    setModalEnd(toISO(satCap > 0 ? saturday : friday));
    setModalJob('');
    setModalJobSearch('');
    setModalJobKind('real');         // Item 2 — default new modals to the real-job picker
    setModalJobScope('all');         // the AI segment is re-picked (or follows a preselected job)
    setModalCustomJobId('');
    setModalStaffSearch('');
    modalNotesRef.current = '';
    setModalHours('8');
    setModalAllocType('total');
    setModalDailyHours({});
    setModalOrigDailyHours({});
    setModalOrigStart('');
    setModalOrigEnd('');
    setModalCapacityWarning(null);
    setModalOverflowStart(false);
    setModalOverflowEnd(false);
    setOptimizeHasOverCap(false); // clear any stale "extend the range" hint
    // Multi-staff state reset (create-only — never carried into edit/placeholder)
    setModalIsMultiStaff(false);
    setModalStaffList([]);
    setModalActiveStaffId(null);
    setModalSettingsByStaff({});
    setModalFillMode(false);
    setModalSuggestedStaffIds(new Set());
    // Item 1 — placeholderContext = { dept, level } pre-fills the modal in
    // placeholder mode (e.g., clicking an empty cell on a placeholder row).
    if (placeholderContext && placeholderContext.dept && placeholderContext.level) {
      setModalMode('placeholder');
      setModalDept(placeholderContext.dept);
      setModalLevel(placeholderContext.level);
    } else {
      setModalMode('person');
      setModalDept('');
      setModalLevel('');
    }
    setShowMultiPreview(false);
    setShowModal(true);
    // showToast is declared further down (line ~3014); naming it here would TDZ.
  }, [allDays, baseMonday, getDayCap, isAssignableStaffId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Jobs view — click an empty day-cell to schedule for that job (staff TBD).
  // Reuses openNewModal's full reset (person mode, dates from the clicked day),
  // then anchors the modal to the job so the user only picks staff + hours.
  const openNewModalForJob = useCallback((jobId, dayIdx, deptCode) => {
    if (dayIdx != null && allDays[dayIdx]?.isWE) return;
    openNewModal(null, dayIdx);
    setModalJob(String(jobId));
    // Pre-select the job's implied department so placeholder mode is ready out of the gate
    // (the dept dropdown only renders in placeholder mode; harmless while in person mode).
    if (deptCode) setModalDept(deptCode);
  }, [openNewModal, allDays]);

  // Custom-job twin: click an empty day-cell on a Custom-scope row. openNewModal resets to
  // the 'real' picker, so flip the kind AFTER it and pre-select the custom job — the user
  // only picks staff + hours. No department pre-fill: a custom job has a Category, which is
  // not a DepartmentCode, and guessing one would pre-fill placeholder mode wrongly.
  const openNewModalForCustomJob = useCallback((customJobId, dayIdx) => {
    if (dayIdx != null && allDays[dayIdx]?.isWE) return;
    openNewModal(null, dayIdx);
    setModalJobKind('custom');
    setModalCustomJobId(String(customJobId));
  }, [openNewModal, allDays]);

  // Per-day map of a staffer's existing hours on a given job (excluding the given
  // entry ids) — feeds the capacity view's "this job" segment so existing same-job
  // hours aren't mis-classified as "other jobs".
  const sameJobHoursFor = useCallback((staffId, jobId, excludeIds) => {
    if (!staffId || !jobId) return {};
    const ex = excludeIds instanceof Set ? excludeIds : new Set(excludeIds || []);
    const m = {};
    for (const e of entries) {
      if (e.StaffId !== staffId || e.JobId !== jobId || ex.has(e.EntryId)) continue;
      const iso = typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) : toISO(new Date(e.EntryDate));
      m[iso] = (m[iso] || 0) + (e.Hours || 0);
    }
    return m;
  }, [entries]);

  // Sibling of sameJobHoursFor that returns the existing entry's { entryId, hours }
  // per day (not just summed hours), excluding the given entry ids. The uniqueness
  // indexes guarantee at most one row per (staff, job, day) / (staff, customJob, day),
  // so one entry per ISO. Used to merge an incoming block additively into an existing
  // out-of-block entry instead of duplicating that key.
  //
  // Polymorphic: pass EITHER jobId (real job) or customJobId (custom job). Custom-job
  // blocks used to be skipped entirely here, which meant resolveOverlapMerges had
  // nothing to work with and a custom-job drag onto an occupied day 500'd on the
  // uniqueness index instead of merging (see migrate-063/064).
  const sameJobEntriesFor = useCallback((staffId, jobId, excludeIds, customJobId) => {
    if (!staffId || (!jobId && !customJobId)) return {};
    const ex = excludeIds instanceof Set ? excludeIds : new Set(excludeIds || []);
    const m = {};
    for (const e of entries) {
      if (e.StaffId !== staffId || ex.has(e.EntryId)) continue;
      // Match on whichever reference the caller asked for. Guarding the *other* column
      // matters: without it a null jobId would match every custom row and vice versa.
      const matches = customJobId
        ? (e.CustomJobId === customJobId)
        : (e.JobId === jobId && e.CustomJobId == null);
      if (!matches) continue;
      const iso = typeof e.EntryDate === 'string' ? e.EntryDate.slice(0, 10) : toISO(new Date(e.EntryDate));
      m[iso] = { entryId: e.EntryId, hours: e.Hours || 0 };
    }
    return m;
  }, [entries]);

  // Resolve a bulk_move batch's collisions with EXISTING out-of-batch entries for the
  // RESULT (staff, job) or (staff, customJob). An insert — or a move that lands (by
  // newDate, else its current date) on a day already holding a same-reference entry — is
  // merged ADDITIVELY into that existing entry (absolute total → idempotent on re-send)
  // instead of duplicating the key, which threw a uniqueness violation and rolled back
  // the whole batch. Merge is always within a single (staff, job) or (staff, customJob).
  // oldByEntryId maps entryId → { iso, hours } so a date-preserving reassign move can
  // find its landing day + incoming hours.
  //
  // resultCustomJobId is the custom-job equivalent of resultJobId — pass exactly one.
  // Callers that omit it (every real-job path) behave exactly as before.
  const resolveOverlapMerges = useCallback((batch, resultStaffId, resultJobId, batchEntryIds, oldByEntryId = {}, resultCustomJobId = null) => {
    const moves = batch.moves || [];
    const inserts = batch.inserts || [];
    const deletes = batch.deletes || [];
    const existing = sameJobEntriesFor(resultStaffId, resultJobId, batchEntryIds, resultCustomJobId);
    if (!Object.keys(existing).length) return { moves, inserts, deletes };
    const r2 = (x) => Math.round((x || 0) * 100) / 100;
    const acc = {}; // existing entryId → { base, added }
    const bump = (ov, hrs) => {
      if (!acc[ov.entryId]) acc[ov.entryId] = { base: ov.hours, added: 0 };
      acc[ov.entryId].added += (hrs || 0);
    };

    const outInserts = [];
    for (const ins of inserts) {
      const ov = existing[ins.date];
      if (ov) bump(ov, ins.hours);
      else outInserts.push(ins);
    }
    const outDeletes = [...deletes];
    const outMoves = [];
    for (const m of moves) {
      const landing = m.newDate || oldByEntryId[m.entryId]?.iso || null;
      const ov = landing ? existing[landing] : null;
      if (ov) {
        const incoming = m.newHours != null ? m.newHours : (oldByEntryId[m.entryId]?.hours || 0);
        bump(ov, incoming);
        outDeletes.push(m.entryId); // absorbed into the existing entry
      } else {
        outMoves.push(m);
      }
    }
    for (const [entryId, { base, added }] of Object.entries(acc)) {
      outMoves.push({ entryId: Number(entryId), newHours: r2(base + added) });
    }
    return { moves: outMoves, inserts: outInserts, deletes: outDeletes };
  }, [sameJobEntriesFor]);

  // Detail panel (job mode + entry mode) — click outside closes it. Clicks on grid
  // blocks / cells / row labels are ignored here so their own onClick switches/toggles
  // the selection directly (avoids racing the animated closeSidebar timeout).
  useEffect(() => {
    if (!selectedBlock) return;
    const onDown = (e) => {
      if (sidebarRef.current && sidebarRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.sched-grid-block, .sched-grid-cell-bg, .sched-grid-label-row')) return;
      closeSidebar();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selectedBlock, closeSidebar]);

  // On-demand per-staff WIP + scheduled hours for the open Jobs-view detail panel. The
  // bulk job_progress read carries totals only; these by-staff reads are window-INDEPENDENT
  // (all dates from today on) so the sidebar's lists/popovers sum to the bar total.
  useEffect(() => {
    // Job mode → the clicked job; entry mode → the entry's real job (drives the entry
    // sidebar's JobProgressBar by-staff popovers). Skip custom/global entries (no jobId).
    const jid = (selectedBlock && selectedBlock._viewMode === 'jobs' && selectedBlock._job)
      ? selectedBlock._job.jobId
      : (selectedBlock && !selectedBlock._viewMode && !selectedBlock.isCustom && !selectedBlock.isGlobal)
        ? selectedBlock.jobId
        : null;
    // Custom-job sidebar: same today-onward roster, from the customJobId arm of
    // job_scheduled_staff. No WIP fetch — PE has no custom jobs, so it is empty by
    // construction (the Ad-Hoc report words this the same way).
    if (selectedBlock?._viewMode === 'customjob' && selectedBlock._customJob) {
      const cid = selectedBlock._customJob.customJobId;
      let cancelledC = false;
      fetchJobScheduledStaff({ customJobId: cid, today: toISO(new Date()) })
        .then(rows => { if (!cancelledC) setDetailCustomSched({ customJobId: cid, byStaff: (rows || []).map(r => ({ staffId: r.StaffId, name: r.StaffName || 'Unknown', initials: r.Initials || '??', level: r.Level || '', department: r.DepartmentCode || '', hours: r.TotalHours || 0 })) }); })
        .catch(() => { if (!cancelledC) setDetailCustomSched({ customJobId: cid, byStaff: [] }); });
      return () => { cancelledC = true; };
    }
    if (!jid) return;
    let cancelled = false;
    fetchJobsWIP({ jobId: jid })
      .then(rows => { if (!cancelled) setDetailWip({ jobId: jid, byStaff: (rows || []).map(w => ({ staffId: w.StaffId, name: w.StaffName || 'Unknown', initials: w.StaffInitials || '??', level: w.StaffLevel || '', hours: w.TotalHours || 0 })) }); })
      .catch(() => { if (!cancelled) setDetailWip({ jobId: jid, byStaff: [] }); });
    // Scheduled (today onward, ALL dates) by staff — not scoped to the loaded grid window.
    fetchJobScheduledStaff({ jobId: jid, today: toISO(new Date()) })
      .then(rows => { if (!cancelled) setDetailSched({ jobId: jid, byStaff: (rows || []).map(r => ({ staffId: r.StaffId, name: r.StaffName || 'Unknown', initials: r.Initials || '??', level: r.Level || '', department: r.DepartmentCode || '', hours: r.TotalHours || 0 })) }); })
      .catch(() => { if (!cancelled) setDetailSched({ jobId: jid, byStaff: [] }); });
    return () => { cancelled = true; };
  }, [selectedBlock]);

  // Enrich an entry block with its job's manager/partner/niche (the entry sidebar shows
  // JM + JP). People blocks carry only managerName; pull the rest from jobInfoMap. Jobs
  // blocks are enriched from their `job` object at click time, so this is a no-op for them.
  const entryWithJobMeta = (b) => {
    if (!b || b.isCustom || b.isGlobal || !b.jobId) return b;
    const meta = jobInfoMap[b.jobId];
    if (!meta) return b;
    return {
      ...b,
      managerName: b.managerName || meta.ManagerName || '',
      managerId: b.managerId ?? meta.ManagerId ?? null,
      partnerName: b.partnerName || meta.PartnerName || '',
      partnerId: b.partnerId ?? meta.PartnerId ?? null,
      nicheName: b.nicheName || meta.NicheName || '',
      isOnSite: !!meta.IsOnSite,
      office: meta.Office || '',
      officeCode: meta.OfficeCode || '',
    };
  };

  // Pure per-job budget/progress derivation, shared by the detail-sidebar bar
  // (renderJobProgress) and the edit/create modal's budget bar (Item 1). WIP +
  // future-scheduled come from the live progressByJob map (refetched on [entries]);
  // falls back to the scheduled-only jobBudgetUtil when a job isn't in the map yet.
  const jobProgressProps = useCallback((jobId) => {
    const util = jobBudgetUtil(jobId);
    const prog = progressByJob[jobId];
    const budget = prog?.BudgetHours ?? util.budget;
    const noBudget = !(budget > 0);
    const wipTotal = prog?.WipTotal ?? 0;
    // Placeholder hours count as scheduled: the calendar space for the job is
    // allotted, only the name is outstanding. Folded into scheduledTotal (rather than
    // a fourth bar segment) so every consumer — bar, status chip, over-budget maths —
    // agrees without each having to know placeholders exist. `placeholderHours` is
    // passed alongside purely so the bar's popover can itemise it.
    const placeholderHours = prog?.PlaceholderScheduled ?? util.placeholder;
    const scheduledTotal = Math.round(((prog?.FutureScheduled ?? util.scheduled) + placeholderHours) * 100) / 100;
    const used = Math.round((wipTotal + scheduledTotal) * 100) / 100;
    const remaining = Math.max(0, Math.round((budget - used) * 100) / 100);
    const overBudget = used > budget;
    const overage = overBudget ? Math.round((used - budget) * 100) / 100 : 0;
    const pctComplete = budget > 0 ? Math.round((used / budget) * 100) : 0;
    // The job is completed in PE, so its WIP came from rprs.JobsHistoryWIP (the live table
    // is pruned on close) and will not move again. Callers label the figure "final" rather
    // than presenting it as progress still in flight.
    const isFinal = !!prog?.IsFinal;
    return { budget, noBudget, wipTotal, scheduledTotal, placeholderHours, used, remaining, overBudget, overage, pctComplete, isFinal };
  }, [progressByJob, jobBudgetUtil]);

  // Shared job-progress block for the detail sidebar (job mode + entry mode): WIP logged
  // + future-scheduled vs budget, status chip, by-staff popovers. By-staff WIP + scheduled
  // come from the on-demand, window-INDEPENDENT reads (detailWip / detailSched).
  function renderJobProgress(jobId) {
    const { budget: pBudget, noBudget, wipTotal: pWip, scheduledTotal: pFutureSched, placeholderHours: pPlaceholder, remaining, overBudget, overage, pctComplete, isFinal } = jobProgressProps(jobId);
    const st = noBudget ? null : SCHED_STATUS[computeSchedStatus(pBudget, pFutureSched, pWip)];
    const wipByStaff = detailWip.jobId === jobId ? detailWip.byStaff : [];
    const scheduledByStaff = detailSched.jobId === jobId ? detailSched.byStaff : [];
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span className="sched-grid-detail-label">{isFinal ? 'PROGRESS · FINAL' : 'PROGRESS'}</span>
          <span style={{ fontSize: 11, color: '#6B7280' }}>{noBudget ? 'no budget' : `${pBudget}h budget`}</span>
          {noBudget ? (
            <span className="sched-grid-job-chip" style={{ marginLeft: 'auto', background: '#F3F4F6', color: '#6B7280' }}><span className="sched-grid-job-chip-dot" style={{ background: '#9CA3AF' }} />No budget</span>
          ) : st && <span className="sched-grid-job-chip" style={{ marginLeft: 'auto', background: st.bg, color: st.text }}><span className="sched-grid-job-chip-dot" style={{ background: st.dot }} />{st.label}</span>}
        </div>
        {noBudget ? (
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            <HoursByStaffPopover label="WIP logged" hours={pWip} byStaff={wipByStaff} emptyText="No WIP detail" />
            {' '}&middot;{' '}
            <HoursByStaffPopover label="scheduled" hours={pFutureSched} byStaff={scheduledByStaff} align="right" accent="#BBF7D0" emptyText="No future entries" />
            {' '}(today on){pPlaceholder > 0 ? <span style={{ color: '#B45309' }}> &middot; incl. {Math.round(pPlaceholder * 100) / 100}h placeholder</span> : null}
          </div>
        ) : (
          <JobProgressBar budget={pBudget} wipTotal={pWip} scheduledTotal={pFutureSched} placeholderHours={pPlaceholder} remaining={remaining} overBudget={overBudget} overage={overage} pctComplete={pctComplete} wipByStaff={wipByStaff} scheduledByStaff={scheduledByStaff} tipDirection="down" />
        )}
      </div>
    );
  }

  // Shared closed-job banner for the detail sidebar (job mode + entry mode).
  //
  // A job PE has completed now KEEPS its rprs.Jobs row so its schedule entries survive the
  // close (peSync used to delete both). That means real future hours can sit on a job nobody
  // is working any more, which is what this banner is for — and why it offers to clear them.
  //
  // The delete is deliberately job-wide and server-resolved, not "this block": the grid only
  // holds its visible window, so far-future entries outside it would otherwise be missed.
  // Preview first, so the confirm shows the true blast radius.
  //
  // meta comes from the schedule entry, not allJobs: queryJobs filters closed jobs out by
  // default, so the client's job map has no row for them.
  function renderClosedJobBanner(jobId, meta = {}) {
    if (!jobId) return null;
    const closedAt = meta.closedAt
      ? new Date(meta.closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null;
    const isJMJP = myStaffId != null
      && (meta.jobManagerId === myStaffId || meta.jobPartnerId === myStaffId);
    // Only JM/JP + super-users can clear the whole job, for everyone on it.
    const canClearAll = isSuperUser || isJMJP;

    // The single-staffer button targets the staffer whose BLOCK is selected, not the viewer.
    // A People-view block belongs to exactly one person, so scoping it to myStaffId meant a
    // super-user looking at someone else's Ergon block got "no future-dated entries" — their
    // own id has nothing on that job. The Jobs-view banner is job-level and passes no
    // staffId, so it only offers the whole-job button.
    const targetStaffId = meta.staffId ?? null;
    const targetStaffName = meta.staffName || 'this person';
    const isSelf = targetStaffId != null && targetStaffId === myStaffId;
    // Mirrors the server rule in deleteClosedJobFuture: clearing someone else's rows needs
    // JM/JP or super-user; clearing your own is always allowed.
    const canClearStaff = targetStaffId != null && (canClearAll || isSelf);

    const active = closedDelete?.jobId === jobId ? closedDelete : null;
    const scopeArgs = (scope) => (scope === 'staff' ? { staffId: targetStaffId } : {});

    const startPreview = async (scope) => {
      setClosedDelete({ jobId, scope, preview: null, busy: true });
      try {
        const p = await deleteClosedJobFuture(jobId, { preview: true, ...scopeArgs(scope) });
        setClosedDelete({ jobId, scope, preview: p, busy: false });
      } catch (e) {
        setClosedDelete(null);
        showToast(e.message || 'Could not read the closed-job schedule', 'error');
      }
    };

    const confirmDelete = async () => {
      setClosedDelete(s => ({ ...s, busy: true }));
      try {
        const res = await deleteClosedJobFuture(jobId, scopeArgs(active.scope));
        const who = active.scope === 'staff' ? (isSelf ? 'your' : `${targetStaffName}'s`) : 'all';
        setClosedDelete(null);
        await refreshEntries();
        showToast(`Cleared ${res.deleted} of ${who} future ${res.deleted === 1 ? 'entry' : 'entries'} on this completed job`, 'success');
      } catch (e) {
        setClosedDelete(s => ({ ...s, busy: false }));
        showToast(e.message || 'Could not clear the closed-job schedule', 'error');
      }
    };

    return (
      <div style={{ marginBottom: 10, padding: '6px 8px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 14 }}>&#9888;&#65039;</span>
          <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>
            Job is completed/closed in PE{closedAt ? ` · ${closedAt}` : ''}
          </span>
        </div>

        {!active && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {canClearAll && (
              <button type="button" className="sched-closed-btn" onClick={() => startPreview('all')}
                title="Delete every future-dated entry on this job, for all staff — including dates outside the visible window">
                Clear future time (all staff)&hellip;
              </button>
            )}
            {canClearStaff && (
              <button type="button" className="sched-closed-btn" onClick={() => startPreview('staff')}
                title={`Delete only ${isSelf ? 'your' : `${targetStaffName}'s`} future-dated entries on this job — including dates outside the visible window`}>
                {isSelf ? 'Clear mine' : `Clear ${targetStaffName}'s`}&hellip;
              </button>
            )}
          </div>
        )}

        {active?.busy && !active.preview && (
          <div style={{ fontSize: 11, color: '#92400E', marginTop: 6 }}>Checking&hellip;</div>
        )}

        {active?.preview && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#92400E' }}>
            {active.preview.entries === 0 ? (
              <>
                <div>
                  No future-dated entries to clear
                  {active.scope === 'staff' ? ` for ${isSelf ? 'you' : targetStaffName}` : ''}.
                </div>
                <button type="button" className="sched-closed-btn" style={{ marginTop: 4 }}
                  onClick={() => setClosedDelete(null)}>Close</button>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700 }}>
                  Delete {active.preview.entries} {active.preview.entries === 1 ? 'entry' : 'entries'}
                  {' '}({active.preview.hours}h)
                  {active.scope === 'all'
                    ? ` across ${active.preview.staff.length} ${active.preview.staff.length === 1 ? 'person' : 'people'}`
                    : ` for ${isSelf ? 'you' : targetStaffName}`}?
                </div>
                <div style={{ marginTop: 2 }}>
                  {String(active.preview.firstDate).slice(0, 10)} &ndash; {String(active.preview.lastDate).slice(0, 10)}
                  {' '}&middot; past-dated entries are kept
                </div>
                {active.scope === 'all' && active.preview.staff.length > 1 && (
                  <div style={{ marginTop: 3, maxHeight: 84, overflowY: 'auto' }}>
                    {active.preview.staff.map(s => (
                      <div key={s.staffId}>{s.staffName} &middot; {s.entries} &middot; {s.hours}h</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button type="button" className="sched-closed-btn sched-closed-btn--danger" disabled={active.busy}
                    onClick={confirmDelete}>{active.busy ? 'Deleting…' : 'Delete'}</button>
                  <button type="button" className="sched-closed-btn" disabled={active.busy}
                    onClick={() => setClosedDelete(null)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Shared by-staff list for the detail sidebar (entry + job modes): clickable names
  // + a Scheduled (today-on) / WIP (logged) toggle. Both datasets are loaded on-demand
  // by the sidebar effect; we just switch which one renders. Hidden if both are empty.
  function renderJobStaffList(jobId) {
    if (!jobId) return null;
    const schedRows = detailSched.jobId === jobId ? detailSched.byStaff : [];
    const wipRows = detailWip.jobId === jobId ? detailWip.byStaff : [];
    if (!schedRows.length && !wipRows.length) return null;
    const rows = sidebarStaffMode === 'wip' ? wipRows : schedRows;
    const label = sidebarStaffMode === 'wip' ? 'WIP LOGGED' : 'SCHEDULED, TODAY ON';
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className="sched-grid-detail-label" style={{ margin: 0 }}>{label} ({rows.length})</span>
          <div className="sched-grid-staff-toggle" style={{ marginLeft: 'auto' }}>
            {[{ id: 'scheduled', label: 'Scheduled' }, { id: 'wip', label: 'WIP' }].map(m => (
              <button key={m.id} type="button"
                className={`sched-grid-staff-toggle-btn${sidebarStaffMode === m.id ? ' sched-grid-staff-toggle-btn--active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSidebarStaffMode(m.id); }}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {rows.length === 0 ? (
            <div style={{ fontSize: 11, color: '#9CA3AF', padding: '6px 0' }}>
              No {sidebarStaffMode === 'wip' ? 'WIP logged' : 'scheduled (today on)'} staff.
            </div>
          ) : rows.map(sb => {
            const dept = sb.department || (staff.find(s => s.StaffId === sb.staffId)?.DepartmentCode) || '';
            const deptColor = staffColor(dept);
            return (
              <div key={sb.staffId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div className="sched-grid-avatar" style={{ width: 26, height: 26, fontSize: 9, background: deptColor }}>{sb.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {sb.staffId
                      ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${sb.staffId}`); }}>{sb.name}</span>
                      : sb.name}
                  </div>
                  <div style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 }}>{sb.level}<SeniorityBars level={sb.level} color={deptColor} /></div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{Math.round((sb.hours || 0) * 100) / 100}h</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Scope modes are exclusive; department ids multi-select (toggle).
  // 'custom' rides the same exclusive path: it replaces the Jobs view's row set wholesale,
  // so composing it with a department would be meaningless (a custom job has a Category,
  // not a ServiceDesc, and SERVICE_TO_DEPT would never match it).
  const SCOPE_MODES = ['mine', 'all', 'custom'];
  const selectDeptScope = useCallback((id) => {
    let next;
    if (SCOPE_MODES.includes(id)) {
      next = [id];                                   // exclusive scope mode
    } else {
      const inScope = deptFilter.some(d => SCOPE_MODES.includes(d));
      const base = inScope ? [] : deptFilter.slice(); // leaving a scope mode → fresh dept set
      next = base.includes(id) ? base.filter(d => d !== id) : [...base, id];
      if (next.length === 0) next = ['all'];          // deselected the last dept → All
    }
    setDeptFilter(next);
    // Only landing on 'All' forces Jobs-view "scheduled only" ON (avoid loading
    // ~2,000 firm jobs). Toggling individual depts/scopes leaves the toggle as
    // the user set it — switching depts shouldn't reset their scheduled-only choice.
    if (viewMode === 'jobs' && next.length === 1 && next[0] === 'all') setHideEmpty(true);
  }, [viewMode, deptFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Job View item 6 — fetch ranked staff suggestions for the modal's selected
  // real job (person mode) so the staff picker defaults to the top 5, with
  // availability for the modal's week. Refetches when the job changes.
  useEffect(() => {
    if (!showModal || modalMode !== 'person' || modalJobKind !== 'real' || !modalJob) return;
    const jobId = parseInt(modalJob, 10);
    if (!jobId) return;
    // Guided-tour: any sentinel (negative) job shows curated sample candidates (no API call).
    if (jobId < 0) {
      setModalSuggestions({ jobId, list: TOUR_SUGGESTION_CANDIDATES.slice(0, 5), loading: false });
      return;
    }
    let cancelled = false;
    setModalSuggestions({ jobId, list: [], loading: true });
    (async () => {
      try {
        const sugData = await fetchSuggestions(jobId);
        const seen = new Set();
        const deduped = (sugData?.candidates || []).filter(c => { if (seen.has(c.StaffId)) return false; seen.add(c.StaffId); return true; });
        // Match the Staff Suggestions page's default filters (department + leadership
        // hidden) so the modal's top-5 == that page's no-filter top-5. On-site jobs
        // also hard-restrict to the job's office (no override in the quick-pick).
        const onSite = !!sugData?.job?.isOnSite && !!sugData?.job?.officeCode;
        const filtered = baseSuggestionFilter(deduped, sugData?.job?.serviceDesc, {
          onSiteOfficeCode: onSite ? sugData.job.officeCode : null,
        });
        // Free-hours per staffer is computed live + range-exact by the
        // modalSuggestionFreeHours memo (Item C), not fetched here.
        const list = filtered.slice(0, 10);
        if (!cancelled) setModalSuggestions({ jobId, list, loading: false, onSiteOffice: onSite ? (sugData.job.office || sugData.job.officeCode) : null, nicheMissing: !!sugData?.job?.nicheMissing });
      } catch {
        if (!cancelled) setModalSuggestions({ jobId, list: [], loading: false });
      }
    })();
    return () => { cancelled = true; };
    // Re-fetch when the job's on-site flag flips (toggled in-modal) so the top-10 re-filters.
  }, [showModal, modalMode, modalJobKind, modalJob, jobInfoMap[parseInt(modalJob, 10)]?.IsOnSite]); // eslint-disable-line react-hooks/exhaustive-deps

  // Item 4 — WIP + scheduled by staff for the modal's selected real job. Drives both the
  // picklist "Staff with WIP" toggle (list: joined to the pickable `staff` set +
  // schedulable-filtered) and the budget bar's click-to-expand by-staff popovers
  // (wipByStaff/schedByStaff: ALL contributors, mirroring the detail sidebar). Not gated
  // on person mode so the bar's popovers work in placeholder mode too.
  useEffect(() => {
    if (!showModal || modalJobKind !== 'real' || !modalJob) return undefined;
    const jobId = parseInt(modalJob, 10);
    if (!jobId || jobId < 0) { setModalWipStaff({ jobId, list: [], wipByStaff: [], schedByStaff: [], loading: false }); return undefined; }
    let cancelled = false;
    setModalWipStaff({ jobId, list: [], wipByStaff: [], schedByStaff: [], loading: true });
    Promise.all([
      fetchJobsWIP({ jobId }).catch(() => []),
      fetchJobScheduledStaff({ jobId, today: toISO(new Date()) }).catch(() => []),
    ]).then(([wipRows, schedRows]) => {
      if (cancelled) return;
      // Picklist list — schedulable staff who logged WIP, joined to the pickable set.
      const byId = new Map();
      for (const w of (wipRows || [])) {
        if (!w.StaffId) continue;
        const s = staff.find(st => st.StaffId === w.StaffId);
        if (!s || !isSchedulableStaff(s.Level)) continue;
        const cur = byId.get(w.StaffId) || { ...s, wipHours: 0 };
        cur.wipHours += w.TotalHours || 0;
        byId.set(w.StaffId, cur);
      }
      const list = [...byId.values()].sort((a, b) => b.wipHours - a.wipHours);
      // Bar popovers — ALL contributors by name+hours (unfiltered), mirroring the sidebar.
      const wipByStaff = (wipRows || []).map(w => ({ name: w.StaffName || 'Unknown', hours: w.TotalHours || 0 }));
      const schedByStaff = (schedRows || []).map(r => ({ name: r.StaffName || 'Unknown', hours: r.TotalHours || 0 }));
      setModalWipStaff({ jobId, list, wipByStaff, schedByStaff, loading: false });
    });
    return () => { cancelled = true; };
  }, [showModal, modalJobKind, modalJob, staff]);

  // Default the picklist back to Suggestions whenever the modal's job changes.
  useEffect(() => { setModalPicklistMode('suggest'); }, [modalJob]);

  // Item 4 — bulk per-job progress (budget / total WIP / future-scheduled today-onward)
  // for the Jobs-view mini-bar + status chips. Refreshes when schedule entries change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchJobProgress(toISO(new Date()));
        if (cancelled) return;
        const map = {};
        for (const r of (rows || [])) map[r.JobId] = r;
        setProgressByJob(map);
      } catch { /* non-blocking — mini-bar falls back to scheduled/budget */ }
    })();
    return () => { cancelled = true; };
  }, [entries]);

  const openEditModal = useCallback((block) => {
    const person = staff.find(s => s.StaffId === block.staffId);
    setModalEditBlock(block);
    setModalPerson(person || null);
    // Item 1 — editing an existing block defaults to person mode, but the
    // user can flip to placeholder mode to convert (provided this is a real
    // job, not a custom-job block). Pre-fill dept/level from the current
    // staff so a one-click "Convert" works without extra picks.
    setModalMode('person');
    setModalDept(person?.DepartmentCode || '');
    setModalLevel(person?.Level || '');
    // Item 2 — block is either a real-job or custom-job edit. Pre-fill the
    // picker kind + the relevant id so the modal renders the matching dropdown.
    if (block.customJobId) {
      setModalJobKind('custom');
      setModalCustomJobId(String(block.customJobId));
      setModalJob('');
    } else {
      setModalJobKind('real');
      setModalCustomJobId('');
      setModalJob(String(block.jobId));
    }
    setModalJobSearch('');
    setModalStaffSearch('');
    modalNotesRef.current = block.notes || '';
    setModalStart(block.dates?.[0]?.date ? toISO(block.dates[0].date) : '');
    setModalEnd(block.dates?.[block.dates.length - 1]?.date ? toISO(block.dates[block.dates.length - 1].date) : '');
    setModalHours(String(block.totalHours));
    // Pre-fill daily hours from existing entries
    const daily = {};
    for (const d of (block.dates || [])) {
      daily[toISO(d.date)] = d.hours;
    }
    // Classify the block's edit mode. It's a clean "Total" (even-split) block
    // ONLY if its actual per-day hours exactly reproduce an even split of its
    // total across the range's workdays. Uneven / gapped / Optimize-frontloaded
    // blocks open in "By Day" so their real distribution is preserved (and
    // previewed) rather than flattened to even on save. See computeProposedDaily —
    // this keeps the Before/After preview == the write in every case.
    const bStartISO = block.dates?.[0]?.date ? toISO(block.dates[0].date) : '';
    const bEndISO = block.dates?.[block.dates.length - 1]?.date ? toISO(block.dates[block.dates.length - 1].date) : '';
    const actualTotal = Object.values(daily).reduce((s, h) => s + (Number(h) || 0), 0);
    const evenMap = computeProposedDaily({
      startISO: bStartISO, endISO: bEndISO, total: actualTotal,
      allocType: 'total', dailyMap: null, staffId: block.staffId, getDayCap,
    });
    const evenKeys = Object.keys(evenMap).filter(k => (Number(evenMap[k]) || 0) > 0);
    const actualKeys = Object.keys(daily).filter(k => (Number(daily[k]) || 0) > 0);
    const isEvenBlock = evenKeys.length === actualKeys.length
      && evenKeys.every(k => Math.abs((Number(evenMap[k]) || 0) - (Number(daily[k]) || 0)) < 0.01);
    const openAlloc = isEvenBlock ? 'total' : 'daily';
    setModalAllocType(openAlloc);
    setModalOrigAllocType(openAlloc);
    setShowDailyGrid(openAlloc === 'daily'); // By-Day grid starts expanded when the block opens in daily mode
    setModalDailyHours(daily);
    setModalOrigDailyHours({ ...daily });
    setModalOrigStart(block.dates?.[0]?.date ? toISO(block.dates[0].date) : '');
    setModalOrigEnd(block.dates?.[block.dates.length - 1]?.date ? toISO(block.dates[block.dates.length - 1].date) : '');
    setModalCapacityWarning(null);
    setModalOverflowStart(false);
    setModalOverflowEnd(false);
    setOptimizeHasOverCap(false); // clear any stale "extend the range" hint from a prior open
    // Multi-staff is create-only — clear any lingering state from a previous open.
    setModalIsMultiStaff(false);
    setModalStaffList([]);
    setModalActiveStaffId(null);
    setModalSettingsByStaff({});
    setModalFillMode(false);
    setModalSuggestedStaffIds(new Set());
    setShowMultiPreview(false);
    setShowModal(true);
  }, [staff, getDayCap]);

  // Run the optimize redistributor — flips to By-Day allocation if needed
  // and runs computeOptimizedHours against the current date range. Takes
  // explicit overflow flags so the caller can pass in fresh state (avoiding
  // the stale-closure problem when called from a checkbox onChange).
  const runOptimizeWith = useCallback((overflowStart, overflowEnd) => {
    if (!modalPerson || !modalStart) return;
    setModalAllocType('daily');
    // Base workdays (skip Sun + no-cap Sat) + optional Extend before/after —
    // same shared helpers the multi-staff builder uses, so single-staff Optimize
    // no longer drops hours on a Saturday the staffer doesn't work.
    const base = workdaysInRange(modalStart, modalEnd || modalStart, modalPerson.StaffId, getDayCap);
    const dates = extendOverflowDates(base, modalPerson.StaffId, overflowStart, overflowEnd, getDayCap);
    if (dates.length === 0) return;
    const editEntryIds = new Set((modalEditBlock?.dates || []).map(dd => dd.entryId));
    // Distribute the CURRENT total the user sees (modalHours) so editing the total
    // — then clicking Optimize — honors the edit. Fall back to the original block
    // total only when the field is empty/0.
    const origTotal = Object.values(modalOrigDailyHours).reduce((sv, v) => sv + (v || 0), 0);
    const total = parseFloat(modalHours) || origTotal;
    const result = computeOptimizedHours({
      staffId: modalPerson.StaffId, candidateDates: dates, totalHours: total,
      excludeEntryIds: editEntryIds, getDayCapFn: getDayCap, entriesArr: entries, dayHoursMap: dayHoursByStaff,
    });
    setModalDailyHours(result.dailyHours);
    setModalHours(String(Math.round(result.totalAssigned * 100) / 100));
    setOptimizeHasOverCap(result.unassigned > 0.01 || (result.warnings || []).length > 0);
  }, [modalPerson, modalStart, modalEnd, modalEditBlock, modalOrigDailyHours, modalHours, getDayCap, entries, dayHoursByStaff]);

  // Default Optimize button call — uses current overflow state.
  const runOptimize = useCallback(() => {
    runOptimizeWith(modalOverflowStart, modalOverflowEnd);
  }, [runOptimizeWith, modalOverflowStart, modalOverflowEnd]);

  const pendingOptimizeRef = useRef(false);
  const openOptimizeModal = useCallback((block) => {
    openEditModal(block);
    pendingOptimizeRef.current = true; // signal: run optimize after state settles
  }, [openEditModal]);

  // Effect: run optimize once modal state is ready (after openEditModal sets state + React re-renders)
  useEffect(() => {
    if (pendingOptimizeRef.current && modalPerson && modalStart) {
      pendingOptimizeRef.current = false;
      runOptimize();
    }
  }, [modalPerson, modalStart, runOptimize]);

  // ── Multi-staff helpers ────────────────────────────────────────────────────
  // Build the candidate-date list for a given staff in [modalStart, modalEnd].
  // Skips Sundays + Saturdays without capacity. Used by runOptimizeAll /
  // runFillAll so each staff can include their own Saturday eligibility.
  const buildCandidateDatesForStaff = useCallback((staffId, overflowStart, overflowEnd) => {
    if (!modalStart) return [];
    const base = workdaysInRange(modalStart, modalEnd || modalStart, staffId, getDayCap);
    return extendOverflowDates(base, staffId, overflowStart, overflowEnd, getDayCap);
  }, [modalStart, modalEnd, getDayCap]);

  // The modal's capacity cues ("Xh free", Fill, Optimize, over-capacity outlines) read the
  // schedule + PTO the grid has loaded — only the weeks on screen. Dates outside them read
  // as empty, overstating free hours and hiding double-bookings, so the modal says so and
  // offers to bring those weeks on screen (the modal stays open across the reload).
  const modalOutsideLoaded = useMemo(() => {
    if (!showModal || !modalStart) return null;
    const s = modalStart;
    const e = modalEnd && modalEnd > modalStart ? modalEnd : modalStart;
    if (s >= toISO(baseMonday) && e <= toISO(endDate)) return null;
    return { start: s, end: e };
  }, [showModal, modalStart, modalEnd, baseMonday, endDate]);
  const showModalRangeOnGrid = useCallback(() => {
    if (!modalOutsideLoaded) return;
    const startMon = getMonday(parseLocalISODate(modalOutsideLoaded.start));
    const weeks = Math.ceil((parseLocalISODate(modalOutsideLoaded.end) - startMon) / (7 * 86400000)) + 1;
    setWeekOffset(weekParamToOffset(modalOutsideLoaded.start));
    if (weeks > zoom) setZoom(Math.min(52, weeks));
  }, [modalOutsideLoaded, zoom, setZoom, setWeekOffset]);

  // Build the ACTIVE tab's full editor state from the live scalars, for flushing
  // into the per-staff settings map on tab switch / batch ops.
  const buildActiveSettings = useCallback(() => ({
    start: modalStart, end: modalEnd, hours: modalHours,
    allocType: modalAllocType, dailyHours: { ...modalDailyHours },
    fillMode: modalFillMode, overflowStart: modalOverflowStart, overflowEnd: modalOverflowEnd,
    overCap: optimizeHasOverCap, // per-tab "some days over cap — extend the range" hint
  }), [modalStart, modalEnd, modalHours, modalAllocType, modalDailyHours, modalFillMode, modalOverflowStart, modalOverflowEnd, optimizeHasOverCap]);

  // Load a per-staff settings entry into the live scalars (hydrate the active tab).
  const hydrateScalarsFrom = useCallback((st) => {
    setModalStart(st?.start || '');
    setModalEnd(st?.end || '');
    setModalHours(st?.hours != null ? String(st.hours) : '0');
    setModalAllocType(st?.allocType || 'total');
    setModalDailyHours({ ...(st?.dailyHours || {}) });
    setModalFillMode(!!st?.fillMode);
    setModalOverflowStart(!!st?.overflowStart);
    setModalOverflowEnd(!!st?.overflowEnd);
    setShowDailyGrid((st?.allocType || 'total') === 'daily');
    setOptimizeHasOverCap(!!st?.overCap); // the hint is per-tab — restore this tab's
  }, []);

  // Switch the active tab: flush the outgoing tab's live scalars into the settings
  // map, then hydrate the incoming tab's saved settings into the scalars. Each tab
  // is thus an independent single-staff editor (its own range/total/distribution).
  const switchActiveStaff = useCallback((newStaffId) => {
    if (newStaffId === modalActiveStaffId) return;
    const next = modalStaffList.find(s => s.StaffId === newStaffId);
    if (!next) return;
    const flushed = buildActiveSettings();
    const incoming = modalSettingsByStaff[newStaffId];
    setModalSettingsByStaff(prev => ({ ...prev, [modalActiveStaffId]: flushed }));
    setModalActiveStaffId(newStaffId);
    setModalPerson(next);
    hydrateScalarsFrom(incoming); // restores this tab's own overCap hint too
    setModalCapacityWarning(null);
  }, [modalActiveStaffId, modalStaffList, modalSettingsByStaff, buildActiveSettings, hydrateScalarsFrom]);

  // Add a staff to the team. Seeds their settings from the CURRENT range/total
  // (Fill mode → fill to their capacity; else → even-split the total) so a new tab
  // starts aligned with the active one, then can be edited independently. The
  // first-added staffer also becomes the active tab (mirrored into the scalars).
  const addStaffToModal = useCallback((staffObj) => {
    if (!staffObj || modalStaffList.some(s => s.StaffId === staffObj.StaffId)) return;
    const nextList = [...modalStaffList, staffObj];
    setModalStaffList(nextList);
    const candidates = buildCandidateDatesForStaff(staffObj.StaffId, false, false);
    let map = {};
    let hoursStr = modalHours;
    let alloc = modalAllocType;
    if (modalFillMode && candidates.length > 0) {
      const result = computeFillToCapacity({
        staffId: staffObj.StaffId, candidateDates: candidates, getDayCapFn: getDayCap,
        ptoByStaffDay, dayHoursMap: dayHoursByStaff, entriesArr: entries, excludeEntryIds: new Set(),
      });
      map = result.dailyHours;
      hoursStr = String(Math.round(result.totalAssigned * 100) / 100);
      alloc = 'daily';
    } else {
      const totalHrs = parseFloat(modalHours) || 0;
      if (totalHrs > 0 && candidates.length > 0) {
        const dailyArr = distributeEvenlyQuarters(totalHrs, candidates.length);
        candidates.forEach((iso, i) => { if (dailyArr[i] > 0) map[iso] = dailyArr[i]; });
      }
    }
    const seeded = {
      start: modalStart, end: modalEnd, hours: hoursStr, allocType: alloc,
      dailyHours: map, fillMode: modalFillMode, overflowStart: false, overflowEnd: false,
    };
    setModalSettingsByStaff(prev => ({ ...prev, [staffObj.StaffId]: seeded }));
    if (nextList.length === 1) {
      setModalActiveStaffId(staffObj.StaffId);
      setModalPerson(staffObj);
      setModalDailyHours(map);
      setModalAllocType(alloc);
      setModalHours(hoursStr);
      setShowDailyGrid(alloc === 'daily');
    }
  }, [modalStaffList, modalHours, modalAllocType, modalFillMode, modalStart, modalEnd, buildCandidateDatesForStaff, getDayCap, ptoByStaffDay, dayHoursByStaff, entries]);

  // Remove a staff from the team. If they were the active tab, hydrate the first
  // remaining tab into the scalars (or clear when the list goes empty).
  const removeStaffFromModal = useCallback((staffId) => {
    const nextList = modalStaffList.filter(s => s.StaffId !== staffId);
    setModalStaffList(nextList);
    setModalSettingsByStaff(prev => {
      const n = { ...prev };
      delete n[staffId];
      return n;
    });
    setModalSuggestedStaffIds(prev => { const n = new Set(prev); n.delete(staffId); return n; });
    if (modalActiveStaffId === staffId) {
      const newActive = nextList[0] || null;
      setModalActiveStaffId(newActive?.StaffId || null);
      setModalPerson(newActive || null);
      if (newActive) hydrateScalarsFrom(modalSettingsByStaff[newActive.StaffId]);
    }
  }, [modalStaffList, modalActiveStaffId, modalSettingsByStaff, hydrateScalarsFrom]);

  // ── Batch "All staff" ops (multi-staff) ─────────────────────────────────────
  // Optimize EVERY tab, each against its OWN range / total / overflow. Flushes the
  // active tab's live scalars first so it's optimized alongside the rest, then
  // re-hydrates the active tab from its freshly-optimized settings.
  const runOptimizeAll = useCallback(() => {
    if (!modalStaffList.length) return;
    const merged = { ...modalSettingsByStaff, [modalActiveStaffId]: buildActiveSettings() };
    const nextByStaff = {};
    for (const s of modalStaffList) {
      const st = merged[s.StaffId] || {};
      const start = st.start || modalStart;
      const end = st.end || start;
      const cand = extendOverflowDates(
        workdaysInRange(start, end, s.StaffId, getDayCap),
        s.StaffId, !!st.overflowStart, !!st.overflowEnd, getDayCap
      );
      if (cand.length === 0) { nextByStaff[s.StaffId] = { ...st, allocType: 'daily', dailyHours: {}, overCap: false }; continue; }
      const result = computeOptimizedHours({
        staffId: s.StaffId, candidateDates: cand, totalHours: parseFloat(st.hours) || 0,
        excludeEntryIds: new Set(), getDayCapFn: getDayCap, entriesArr: entries, dayHoursMap: dayHoursByStaff,
      });
      // overCap is stored PER TAB so the "extend the range" hint appears on the
      // staffer that actually overflowed, not just whichever tab is active.
      nextByStaff[s.StaffId] = {
        ...st, allocType: 'daily', dailyHours: result.dailyHours,
        hours: String(Math.round(result.totalAssigned * 100) / 100),
        overCap: result.unassigned > 0.01 || (result.warnings || []).length > 0,
      };
    }
    setModalSettingsByStaff(nextByStaff);
    hydrateScalarsFrom(nextByStaff[modalActiveStaffId]); // sets the active tab's overCap hint
  }, [modalStaffList, modalActiveStaffId, modalSettingsByStaff, buildActiveSettings, hydrateScalarsFrom, modalStart, getDayCap, entries, dayHoursByStaff]);

  // Fill mode for ONE staff — fills each day to that staff's available capacity.
  // Used in single-staff Fill mode and as the inner loop of runFillAll.
  const runFillForStaff = useCallback((staffId) => {
    const candidates = buildCandidateDatesForStaff(staffId, false, false);
    if (candidates.length === 0) return { dailyHours: {}, totalAssigned: 0 };
    // Edit mode: subtract the editing entry's pre-edit contribution from "existing"
    // so Fill doesn't see the entry's own hours as already-scheduled. Only applies
    // when this staff is the entry's owner (always true in single-staff edit;
    // multi-staff doesn't enter edit mode at all).
    const excludeEntryIds = new Set(
      (modalEditBlock?.staffId === staffId ? (modalEditBlock.dates || []) : [])
        .map(d => d.entryId).filter(Boolean)
    );
    const result = computeFillToCapacity({
      staffId, candidateDates: candidates, getDayCapFn: getDayCap,
      ptoByStaffDay, dayHoursMap: dayHoursByStaff, entriesArr: entries,
      excludeEntryIds,
    });
    return result;
  }, [buildCandidateDatesForStaff, getDayCap, ptoByStaffDay, dayHoursByStaff, entries, modalEditBlock]);

  // Item C — range-exact "free hours" for each suggested staffer, via the same
  // capacity path as Fill mode (PTO + holidays + existing + global assignments).
  // Recomputes whenever the modal's date range changes (runFillForStaff depends
  // on buildCandidateDatesForStaff → modalStart/modalEnd).
  const modalSuggestionFreeHours = useMemo(() => {
    const map = {};
    for (const s of modalSuggestions.list) {
      map[s.StaffId] = Math.round((runFillForStaff(s.StaffId).totalAssigned || 0) * 100) / 100;
    }
    return map;
  }, [modalSuggestions, runFillForStaff]);

  // Fill trigger — fills the ACTIVE tab to its capacity (single-staff logic; each
  // multi-staff tab fills independently). "Fill all" is the batch equivalent.
  const runFillTrigger = useCallback(() => {
    if (!modalPerson || !modalStart) return;
    setModalAllocType('daily');
    const result = runFillForStaff(modalPerson.StaffId);
    setModalDailyHours(result.dailyHours);
    setModalHours(String(Math.round(result.totalAssigned * 100) / 100));
    setOptimizeHasOverCap(false);
  }, [modalPerson, modalStart, runFillForStaff]);

  // Batch "Fill all" TOGGLE — a team-wide version of the single-staff Fill toggle.
  // OFF→ON: snapshot every tab's pre-fill state, then fill each to its own capacity
  //         over its own range and mark every tab Fill-on (so per-tab checkboxes read on).
  // ON→OFF: restore every tab from the snapshot and clear Fill-on.
  // "All on" means EVERY tab is Fill-on; individually toggling one tab off flips this
  // back to the OFF state (next click re-fills all).
  const toggleFillAll = useCallback(() => {
    if (!modalStaffList.length) return;
    const merged = { ...modalSettingsByStaff, [modalActiveStaffId]: buildActiveSettings() };
    const allFilled = modalStaffList.every(s => !!merged[s.StaffId]?.fillMode);
    const next = {};
    if (allFilled) {
      // Toggle OFF — restore each tab's pre-fill snapshot.
      const snap = modalFillAllSnapshotRef.current || {};
      for (const s of modalStaffList) {
        const st = merged[s.StaffId] || {};
        const pre = snap[s.StaffId];
        next[s.StaffId] = pre
          ? { ...st, fillMode: false, allocType: pre.allocType, dailyHours: pre.dailyHours, hours: pre.hours, overCap: false }
          : { ...st, fillMode: false, overCap: false };
      }
      modalFillAllSnapshotRef.current = null;
      modalFillSnapshotRef.current = null;
    } else {
      // Toggle ON — snapshot pre-fill state, then fill each tab.
      const snap = {};
      for (const s of modalStaffList) {
        const st = merged[s.StaffId] || {};
        snap[s.StaffId] = { allocType: st.allocType || 'total', dailyHours: { ...(st.dailyHours || {}) }, hours: st.hours };
        const start = st.start || modalStart;
        const end = st.end || start;
        const cand = extendOverflowDates(
          workdaysInRange(start, end, s.StaffId, getDayCap),
          s.StaffId, !!st.overflowStart, !!st.overflowEnd, getDayCap
        );
        const result = cand.length === 0
          ? { dailyHours: {}, totalAssigned: 0 }
          : computeFillToCapacity({
              staffId: s.StaffId, candidateDates: cand, getDayCapFn: getDayCap,
              ptoByStaffDay, dayHoursMap: dayHoursByStaff, entriesArr: entries, excludeEntryIds: new Set(),
            });
        next[s.StaffId] = {
          ...st, fillMode: true, allocType: 'daily', dailyHours: result.dailyHours,
          hours: String(Math.round(result.totalAssigned * 100) / 100), overCap: false,
        };
      }
      modalFillAllSnapshotRef.current = snap;
      // Mirror the active tab's pre-fill snapshot into the single-toggle ref so the
      // per-tab Fill checkbox can also restore it if toggled off individually.
      const activePre = snap[modalActiveStaffId];
      modalFillSnapshotRef.current = activePre
        ? { dailyHours: { ...activePre.dailyHours }, hours: activePre.hours, allocType: activePre.allocType }
        : null;
    }
    setModalSettingsByStaff(next);
    hydrateScalarsFrom(next[modalActiveStaffId]); // sets the active tab's fill + overCap
  }, [modalStaffList, modalActiveStaffId, modalSettingsByStaff, buildActiveSettings, hydrateScalarsFrom, modalStart, getDayCap, ptoByStaffDay, dayHoursByStaff, entries]);

  // Batch: copy the ACTIVE tab's range + total + distribution onto every OTHER tab
  // (the "align all" convenience — the active tab is left untouched). Pulses the
  // non-active tabs briefly to emphasize the copy.
  const copyActiveToAll = useCallback(() => {
    if (!modalActiveStaffId || modalStaffList.length < 2) return;
    const active = buildActiveSettings();
    setModalSettingsByStaff(prev => {
      const nextByStaff = { ...prev };
      for (const s of modalStaffList) {
        if (s.StaffId === modalActiveStaffId) continue;
        // Don't propagate the active tab's overCap hint — capacity differs per staffer.
        nextByStaff[s.StaffId] = { ...active, dailyHours: { ...active.dailyHours }, overCap: false };
      }
      return nextByStaff;
    });
    setCopyPulsing(true);
    setTimeout(() => setCopyPulsing(false), 750);
  }, [modalActiveStaffId, modalStaffList, buildActiveSettings]);

  // Recompute the over-capacity warning against a given dailyHours snapshot.
  // Called from staff-picker change, date-input change, and stepper clicks.
  // Subtracts this entry's PRE-EDIT contribution from `existing` so we don't
  // double-count it: dayHoursByStaff still reflects the not-yet-saved entry
  // on its original dates. For shifted/new isos, modalOrigDailyHours[iso] is
  // undefined → no subtraction (and no false positive).
  const recomputeCapacityWarning = useCallback((person, dailyMap) => {
    if (!person || !dailyMap || Object.keys(dailyMap).length === 0) {
      setModalCapacityWarning(null);
      return;
    }
    let overDays = 0;
    for (const [iso, hrs] of Object.entries(dailyMap)) {
      if (dayIsConflict({
        proposedHrs: hrs,
        cap: getDayCap(person.StaffId, iso),
        totalExistingHrs: dayHoursByStaff[person.StaffId]?.[iso] || 0,
        ownPreEditHrs: Number(modalOrigDailyHours[iso]) || 0,
      })) overDays++;
    }
    setModalCapacityWarning(overDays > 0
      ? `${person.Name} has less availability on ${overDays} day${overDays > 1 ? 's' : ''}. Click Optimize to redistribute.`
      : null);
  }, [getDayCap, dayHoursByStaff, modalOrigDailyHours]);

  // The modal's proposed per-day distribution from current state — the SINGLE
  // source of truth shared by the Before/After preview and the save (see
  // computeProposedDaily). Guarantees the preview always matches what's written.
  const getProposedDailyHours = useCallback(() => computeProposedDaily({
    startISO: modalStart,
    endISO: modalEnd,
    total: parseFloat(modalHours) || 0,
    allocType: modalAllocType,
    dailyMap: modalDailyHours,
    staffId: modalPerson?.StaffId,
    getDayCap,
  }), [modalStart, modalEnd, modalHours, modalAllocType, modalDailyHours, modalPerson, getDayCap]);

  // Keep the By-Day grid expanded whenever the modal enters By Day mode (open
  // classified-daily, Total→By Day toggle, Optimize, Fill, job-change auto-switch).
  // Fires only on a mode *change*, so a manual collapse sticks until the mode
  // changes again.
  useEffect(() => {
    if (modalAllocType === 'daily') setShowDailyGrid(true);
  }, [modalAllocType]);

  // Start date input handler — independent of end in edit mode (no auto-shift).
  // New entries still get the Friday-default convenience on first pick.
  const handleStartChange = useCallback((newStart) => {
    if (!newStart) { setModalStart(''); return; }
    const isEditing = !!modalEditBlock;
    // Clamp: if newStart > current end, push end to match (single-day range).
    let nextEnd = modalEnd;
    if (modalEnd && newStart > modalEnd) nextEnd = newStart;

    if (isEditing) {
      setModalStart(newStart);
      if (nextEnd !== modalEnd) setModalEnd(nextEnd);
      if (modalFillMode) {
        // Edit + Fill: re-fill against the new range instead of rebuilding from
        // the existing per-day distribution.
        setTimeout(() => runFillTrigger(), 0);
      } else {
        // Regenerate dailyHours for the new [newStart, nextEnd] range —
        // preserve existing day values, fill new days with 0, drop days
        // outside range. Recompute total from the rebuilt map.
        const { dailyHours, totalHours } = rebuildDailyHoursForRange(
          modalDailyHours, newStart, nextEnd || newStart, modalPerson?.StaffId, getDayCap
        );
        setModalDailyHours(dailyHours);
        setModalHours(String(Math.round(totalHours * 100) / 100));
        // Warn against the PROPOSED distribution (Total mode → the even-split the
        // preview/save use), not the raw 0-filled rebuild — else a Total-mode
        // extend false-warns on the old days' pre-respread hours.
        recomputeCapacityWarning(modalPerson, computeProposedDaily({
          startISO: newStart, endISO: nextEnd || newStart, total: totalHours,
          allocType: modalAllocType, dailyMap: dailyHours, staffId: modalPerson?.StaffId, getDayCap,
        }));
      }
    } else {
      // New entry — keep Friday-default convenience.
      setModalStart(newStart);
      const d = parseLocalISODate(newStart);
      if (d) {
        const dow = d.getDay();
        const daysToFri = dow === 0 ? 5 : dow === 6 ? 6 : 5 - dow;
        const fri = new Date(d);
        fri.setDate(fri.getDate() + daysToFri);
        const sat = new Date(fri); sat.setDate(sat.getDate() + 1);
        const satCap = modalPerson ? getDayCap(modalPerson.StaffId, toISO(sat)) : 0;
        setModalEnd(toISO(satCap > 0 ? sat : fri));
      }
      // Fill mode wins — re-fill the ACTIVE tab against the new range. Multi-staff
      // tabs are independent, so changing this tab's range never touches the others.
      if (modalFillMode) {
        setTimeout(() => runFillTrigger(), 0);
      }
    }
  }, [modalEditBlock, modalEnd, modalDailyHours, modalPerson, getDayCap, modalAllocType, recomputeCapacityWarning, modalFillMode, runFillTrigger]);

  // End date input handler — independent of start in edit mode.
  const handleEndChange = useCallback((newEnd) => {
    if (!newEnd) { setModalEnd(''); return; }
    const isEditing = !!modalEditBlock;
    // Clamp: if newEnd < current start, pull start to match.
    let nextStart = modalStart;
    if (modalStart && newEnd < modalStart) nextStart = newEnd;

    setModalEnd(newEnd);
    if (nextStart !== modalStart) setModalStart(nextStart);
    if (isEditing) {
      if (modalFillMode) {
        setTimeout(() => runFillTrigger(), 0);
      } else {
        const { dailyHours, totalHours } = rebuildDailyHoursForRange(
          modalDailyHours, nextStart || newEnd, newEnd, modalPerson?.StaffId, getDayCap
        );
        setModalDailyHours(dailyHours);
        setModalHours(String(Math.round(totalHours * 100) / 100));
        // Warn against the PROPOSED distribution (Total mode → the even-split the
        // preview/save use), not the raw 0-filled rebuild — else a Total-mode
        // extend false-warns on the old days' pre-respread hours.
        recomputeCapacityWarning(modalPerson, computeProposedDaily({
          startISO: nextStart || newEnd, endISO: newEnd, total: totalHours,
          allocType: modalAllocType, dailyMap: dailyHours, staffId: modalPerson?.StaffId, getDayCap,
        }));
      }
    } else if (modalFillMode) {
      // Create + Fill: re-fill the ACTIVE tab against the new range.
      setTimeout(() => runFillTrigger(), 0);
    }
  }, [modalEditBlock, modalStart, modalDailyHours, modalPerson, getDayCap, modalAllocType, recomputeCapacityWarning, modalFillMode, runFillTrigger]);

  // Stepper: shift BOTH endpoints by n workdays (skip weekends). Rekey
  // dailyHours by the same shift so the per-day distribution stays intact.
  // Total hours preserved through shift.
  const shiftBy = useCallback((n) => {
    if (!n) return;
    const startD = parseLocalISODate(modalStart);
    const endD = parseLocalISODate(modalEnd);
    if (!startD && !endD) return;
    const newStart = startD ? toISO(shiftWorkdays(startD, n)) : '';
    const newEnd = endD ? toISO(shiftWorkdays(endD, n)) : '';
    if (newStart) setModalStart(newStart);
    if (newEnd) setModalEnd(newEnd);
    // Rekey daily hours by the same workday shift.
    const rekeyed = {};
    for (const [iso, hrs] of Object.entries(modalDailyHours || {})) {
      const d = parseLocalISODate(iso);
      if (!d) continue;
      const newIso = toISO(shiftWorkdays(d, n));
      rekeyed[newIso] = hrs;
    }
    setModalDailyHours(rekeyed);
    // Total unchanged through shift; per-day distribution preserved (rekeyed).
    recomputeCapacityWarning(modalPerson, rekeyed);
    // Fill mode: re-fill the ACTIVE tab against the shifted range. Multi-staff tabs
    // are independent, so shifting this tab preserves its distribution and never
    // touches the others (this was the old re-even-split bug).
    if (modalFillMode) {
      setTimeout(() => runFillTrigger(), 0);
    }
  }, [modalStart, modalEnd, modalDailyHours, modalPerson, recomputeCapacityWarning, modalFillMode, runFillTrigger]);

  // Shift-nudge: a NEW entry whose start isn't a Monday gets an amber pulse on the
  // date steppers + an inline "Starts mid-week" hint. Live — recomputes each render
  // and clears the moment the start lands on a Monday (no dismiss state).
  const shiftNudgeDow = modalStart ? (parseLocalISODate(modalStart)?.getDay?.() ?? null) : null;
  const shiftNudge = !modalEditBlock && shiftNudgeDow != null && shiftNudgeDow !== 1;

  // Reset edit modal back to the original snapshot from openEditModal.
  const resetToOriginal = useCallback(() => {
    if (!modalEditBlock) return;
    setModalStart(modalOrigStart);
    setModalEnd(modalOrigEnd);
    setModalDailyHours({ ...modalOrigDailyHours });
    const sum = Object.values(modalOrigDailyHours).reduce((s, v) => s + (Number(v) || 0), 0);
    setModalHours(String(Math.round(sum * 100) / 100));
    setModalOverflowStart(false);
    setModalOverflowEnd(false);
    setOptimizeHasOverCap(false); // Reset clears the "extend the range" hint too
    // Also restore the mode + Fill state so Reset returns the modal to its exact
    // open state (e.g. clicking Fill then Reset must turn Fill back off).
    setModalFillMode(false);
    setModalAllocType(modalOrigAllocType);
    setShowDailyGrid(modalOrigAllocType === 'daily');
    recomputeCapacityWarning(modalPerson, modalOrigDailyHours);
  }, [modalEditBlock, modalOrigStart, modalOrigEnd, modalOrigDailyHours, modalOrigAllocType, modalPerson, recomputeCapacityWarning]);

  // ── Helpers ────────────────────────────────────────────────────────────
  const refreshEntries = useCallback(async () => {
    const schedData = await fetchSchedule({
      startDate: toISO(baseMonday),
      endDate: toISO(endDate),
    });
    setEntries(schedData || []);
  }, [baseMonday, endDate]);

  const toastTimer = useRef(null);
  const showToast = useCallback((message, type = 'info', sticky = false) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    if (!sticky) toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // Check for pending assignment from StaffSuggestions
  useEffect(() => {
    if (loading || !allJobs.length || !staff.length) return;
    const raw = sessionStorage.getItem('pendingAssignment');
    if (!raw) return;
    // A chooser hand-off names the window being planned. The grid only loads the weeks on
    // screen, so seeding a January plan from a November grid would read January as empty —
    // free hours overstated, double-bookings invisible. Move the grid there first and leave
    // the payload in place; this effect runs again once that window's data has loaded.
    let win = null;
    try { win = JSON.parse(raw)?.window || null; } catch { /* handled below */ }
    if (win?.start && win?.end) {
      const loaded = loadedRangeRef.current;
      const covered = loaded && win.start >= loaded.startDate && win.end <= loaded.endDate;
      if (!covered) {
        if (!handoffJumpRef.current) {
          handoffJumpRef.current = true;
          setWeekOffset(weekParamToOffset(win.start));
          if ((win.weeks || 1) > zoom) setZoom(Math.min(52, win.weeks));
        }
        return;
      }
    }
    handoffJumpRef.current = false;
    sessionStorage.removeItem('pendingAssignment');
    try {
      const data = JSON.parse(raw);
      setModalEditBlock(null);
      // jobId is optional — the "Schedule Another Job" entry-point on the
      // staff backlog has no specific job in mind, just opens the picker blank.
      setModalJob(data.jobId != null ? String(data.jobId) : '');
      setModalJobSearch('');
      setModalStaffSearch('');
      setModalSource('suggestion');

      // ── Chooser-originated payload (per-staff budget allocation) ──────────
      // The backlog Schedule chooser hands in perStaff = [{ staffId, hours }].
      // Seed each staffer an Optimize-shaped near-term block that hits their exact
      // share (capacity-front-loaded, partial final day) so the allocation "sticks"
      // — allocType 'daily' + a real dailyHours map, which the create-preview gantt
      // and the multi-save both consume verbatim (computeProposedDaily short-circuits).
      const allPer = Array.isArray(data.perStaff) ? data.perStaff.filter(Boolean) : [];
      const per = allPer.filter(p => p.staffId != null);
      const phPer = allPer.filter(p => p.placeholder && p.placeholder.dept && p.placeholder.level);
      if (per.length || phPer.length) {
        const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
        const nowISO = toISO(todayD);
        // With a window: plan inside it (from today on). Without (older payloads): the
        // original 4-week near-term window, front-loaded.
        const startISO = win?.start && win.start > nowISO ? win.start : nowISO;
        const endISO = win ? win.end : toISO(addDays(todayD, 27));
        const phEndISO = win ? win.end : toISO(addDays(todayD, 13)); // placeholder window
        const seeded = {};
        for (const p of per) {
          const id = p.staffId;
          const share = Number(p.hours) || 0;
          const cand = workdaysInRange(startISO, endISO, id, getDayCap);
          let res;
          if (win) {
            // Level the share across the window around existing work, PTO and holidays.
            // What doesn't fit still lands (evenly, on workable days) so the manager's
            // allocation is kept and the over-capacity days show red — same contract as
            // Optimize's overflow — rather than hours quietly going missing.
            res = spreadWithinCapacity({
              staffId: id, candidateDates: cand, totalHours: share,
              getDayCapFn: getDayCap, entriesArr: entries, dayHoursMap: dayHoursByStaff,
              minDayHours: MIN_PLANNED_DAY_HOURS,
            });
            const workable = cand.filter(iso => getDayCap(id, iso) > 0);
            if (res.unassigned > 0 && workable.length) {
              const add = distributeEvenlyQuarters(res.unassigned, workable.length);
              const daily = { ...res.dailyHours };
              workable.forEach((iso, i) => { if (add[i]) daily[iso] = (daily[iso] || 0) + add[i]; });
              res = { ...res, dailyHours: daily, totalAssigned: res.totalAssigned + res.unassigned };
            }
          } else {
            res = computeOptimizedHours({
              staffId: id, candidateDates: cand, totalHours: share,
              excludeEntryIds: new Set(), getDayCapFn: getDayCap, entriesArr: entries, dayHoursMap: dayHoursByStaff,
            });
          }
          const nz = cand.filter(iso => (res.dailyHours[iso] || 0) > 0);
          seeded[id] = {
            start: nz[0] || startISO, end: nz[nz.length - 1] || nz[0] || startISO,
            hours: String(Math.round((res.totalAssigned || 0) * 100) / 100),
            allocType: 'daily', dailyHours: res.dailyHours,
            fillMode: false, overflowStart: false, overflowEnd: false,
            overCap: (res.unassigned || 0) > 0.01,
          };
        }
        // Chooser placeholders → staged for the confirm-gate (flat total over the planned
        // window, or a default near-term range; created via upsertPlaceholder on Confirm).
        setPendingPlaceholders(phPer.map(p => ({
          dept: p.placeholder.dept, level: p.placeholder.level,
          hours: Math.round((Number(p.hours) || 0) * 100) / 100,
          startDate: startISO, endDate: phEndISO,
        })));
        const objs = per.map(p => staff.find(s => s.StaffId === p.staffId)).filter(Boolean);
        const firstId = objs[0]?.StaffId ?? null;
        const firstSeed = firstId != null ? seeded[firstId] : null;
        setModalSettingsByStaff(seeded);
        // Mirror the first (active) tab into the scalar modal state.
        setModalStart(firstSeed?.start || startISO);
        setModalEnd(firstSeed?.end || startISO);
        setModalHours(firstSeed ? firstSeed.hours : '0');
        setModalAllocType('daily');
        setModalDailyHours(firstSeed ? { ...firstSeed.dailyHours } : {});
        setShowDailyGrid(true);
        setModalFillMode(false);
        setOptimizeHasOverCap(!!firstSeed?.overCap);
        // Force multi mode whenever placeholders are present so the confirm-gate (which
        // hosts the placeholder review) is reachable even with 0–1 real staff.
        if (per.length > 1 || phPer.length > 0) {
          setModalIsMultiStaff(true);
          setModalStaffList(objs);
          setModalActiveStaffId(firstId);
          setModalPerson(objs[0] || null);
        } else {
          setModalIsMultiStaff(false);
          setModalPerson(objs[0] || null);
        }
        setShowModal(true);
        // Placeholders-only (no real staff to schedule) → land straight on the review.
        if (!per.length && phPer.length) setShowMultiPreview(true);
        return;
      }

      // ── Legacy payload (staffIds + shared suggestedHours, Total mode) ─────
      const ids = Array.isArray(data.staffIds) ? data.staffIds : [];
      const isMulti = ids.length > 1;
      setModalStart(toISO(baseMonday));
      setModalEnd(toISO(addDays(baseMonday, 4)));
      setModalHours(String(data.suggestedHours || 40));
      setModalAllocType('total');
      setModalDailyHours({});
      if (isMulti) {
        // Dashboard multi-assign: open in multi mode with all selected staff. No
        // daily template in this payload → empty maps; the multi-save path rebuilds
        // each staffer's distribution from the total at save.
        const objs = ids.map(id => staff.find(s => s.StaffId === id)).filter(Boolean);
        setModalIsMultiStaff(true);
        setModalStaffList(objs);
        setModalActiveStaffId(objs[0]?.StaffId ?? null);
        setModalPerson(objs[0] || null);
        // Seed each tab's settings from the shared draft range/total (Total mode,
        // empty daily map → save even-splits per staffer). Tabs are then independent.
        setModalSettingsByStaff(Object.fromEntries(objs.map(o => [o.StaffId, {
          start: toISO(baseMonday), end: toISO(addDays(baseMonday, 4)),
          hours: String(data.suggestedHours || 40), allocType: 'total',
          dailyHours: {}, fillMode: false, overflowStart: false, overflowEnd: false,
        }])));
      } else {
        setModalPerson(ids[0] ? (staff.find(s => s.StaffId === ids[0]) || null) : (staff[0] || null));
      }
      setShowModal(true);
    } catch { /* ignore */ }
    // getDayCap/entries/dayHoursByStaff feed the perStaff Optimize-shaped seed; the
    // sessionStorage removeItem above makes any extra run a no-op, so widening the
    // deps only affects the (guarded) first read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allJobs, staff, baseMonday, getDayCap, entries, dayHoursByStaff]);

  // ── Guided-tour drive ──
  // The tour asks the grid to open the New Entry modal (blank) so it can spotlight
  // the distribution controls. Handled once per directive change; the null branch
  // (tour moved off a grid-modal step) closes a tour-opened modal. Outside a tour
  // the directive stays null, so this never fires on normal use.
  const tourDrive = useTourDrive();
  const tourDriveHandledRef = useRef(null);
  useEffect(() => {
    if (tourDrive === tourDriveHandledRef.current) return;
    tourDriveHandledRef.current = tourDrive;
    const findDemoBlock = () => (blocksByPerson[myStaffId] || []).find(b => b.jobId === SENTINEL_JOB_ID);
    // Close the grid surfaces the target step doesn't use, so only one is open at a
    // time (prevents a leftover modal from stealing a shared `.sched-mgr-modal` spotlight).
    const closeSidebar = () => setSelectedBlock(null);
    const closeModal = () => setShowModal(false);
    const closePlaceholder = () => setPlaceholderAssignTarget(null);
    const closePreview = () => setDropPreview(null);
    switch (tourDrive) {
      case 'grid-new-entry': {
        closeSidebar(); closePlaceholder(); closePreview();
        // Fresh "New Schedule Entry" with the signed-in user prepopulated (like clicking
        // a day in your own row) so the person-gated controls (Optimize/Extend/Fill/
        // multi-staff) render, plus a sample job (distinct from the block already on the
        // week) pre-picked so the picker isn't blank.
        openNewModal(staff.find(s => s.StaffId === myStaffId) || null, 0);
        setModalJob(String(SENTINEL_MODAL_JOB_ID));
        break;
      }
      case 'grid-edit-existing': {
        closeSidebar(); closePlaceholder(); closePreview();
        // Open the sample entry already on the user's week for EDITING, so the modal
        // shows its real job + populated hours (32h across Mon–Thu) — not a blank form.
        const b = findDemoBlock();
        if (b) openEditModal(b);
        break;
      }
      case 'grid-entry-detail': {
        closeModal(); closePlaceholder(); closePreview();
        const b = findDemoBlock();
        if (b) setSelectedBlock(entryWithJobMeta(b));
        break;
      }
      case 'grid-job-detail':
        closeModal(); closePlaceholder(); closePreview();
        setViewMode('jobs');
        setSelectedBlock({ key: `job_${SENTINEL_JOB_ID}`, _viewMode: 'jobs', _job: TOUR_JOB_SIDEBAR_OBJ });
        break;
      case 'grid-modal-suggest':
        closeSidebar(); closePlaceholder(); closePreview();
        // Fresh modal, NO staff picked → the picker defaults to ranked suggestions for
        // the chosen job (person mode + real job → embedded suggestions load, demo-guarded).
        openNewModal(null, null);
        setModalJob(String(SENTINEL_JOB_ID));
        break;
      case 'grid-new-placeholder':
        // New entry in PLACEHOLDER mode (dept + level instead of a person) — available
        // to everyone, so the tour shows how anyone can reserve work as a placeholder.
        closeSidebar(); closePlaceholder(); closePreview();
        openNewModal(null, 0, { dept: 'AUDIT', level: 'Senior Accountant' });
        setModalJob(String(SENTINEL_MODAL_JOB_ID));
        break;
      case 'grid-placeholder-block':
        // Show the demo placeholder ON the grid (Jobs view, under the sentinel job row)
        // so the tour can point it out before opening the assign modal.
        closeModal(); closeSidebar(); closePreview(); closePlaceholder();
        setViewMode('jobs');
        break;
      case 'grid-placeholder':
        closeModal(); closeSidebar(); closePreview();
        setPlaceholderAssignTarget(buildDemoPlaceholder(toISO(baseMonday)));
        break;
      case 'grid-drag-demo': {
        // Can't script a real HTML5 drag; drive the actual drop-PREVIEW modal with a
        // synthetic one-week shift so the tour shows the real before/after + capacity.
        closeModal(); closeSidebar(); closePlaceholder();
        const b = findDemoBlock();
        if (b) {
          const shifted = b.dates.map(d => { const nd = new Date(d.date); nd.setDate(nd.getDate() + 7); return nd; });
          const dailyHours = {};
          shifted.forEach((nd, i) => { dailyHours[toISO(nd)] = b.dates[i].hours; });
          setDropPreview({
            block: b, targetStaffId: b.staffId, targetStaffName: b.staffName,
            sameStaff: true, dayShift: 7, mode: 'shift', dailyHours,
            oldEntries: b.dates.map(d => ({ date: d.date, hours: d.hours, entryId: d.entryId })),
            newDates: shifted.map(toISO), originalNewDates: shifted.map(toISO), warnings: [],
          });
        }
        break;
      }
      case null:
      case undefined:
        // Tour moved off a grid surface (or ended) — close anything it opened.
        setShowModal(false);
        setSelectedBlock(null);
        setPlaceholderAssignTarget(null);
        setDropPreview(null);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourDrive]);

  // Restore the schedule modal after a round-trip to the full StaffSuggestions
  // page (Job View item 6). Reopens with the saved draft; if a staffer was picked
  // on the page, populates them + flags the "Suggested" badge.
  useEffect(() => {
    if (loading || !allJobs.length || !staff.length) return;
    const raw = sessionStorage.getItem('pendingScheduleDraft');
    if (!raw) return;
    sessionStorage.removeItem('pendingScheduleDraft');
    try {
      const d = JSON.parse(raw);
      const isMulti = !!d.isMultiStaff && Array.isArray(d.pickedStaffIds) && d.pickedStaffIds.length > 0;
      setModalEditBlock(null);
      setModalSource('suggestion');
      setModalJobKind(d.jobKind || 'real');
      setModalJobScope('all');
      setModalJob(d.jobId != null ? String(d.jobId) : '');
      setModalJobSearch('');
      setModalStaffSearch('');
      setModalMode(d.mode || 'person');
      setModalStart(d.start || toISO(baseMonday));
      setModalEnd(d.end || toISO(addDays(baseMonday, 4)));
      setModalHours(String(d.hours != null ? d.hours : 8));
      setModalAllocType(d.allocType || 'total');
      setModalDailyHours(d.dailyHours || {});
      if (isMulti) {
        // Multi round-trip: rebuild the team in multi mode (the grid unmounted, so
        // modalStaffList was cleared). Prime each staffer's daily map from the draft
        // template; the multi-save path rebuilds total-mode maps anyway.
        const objs = d.pickedStaffIds.map(id => staff.find(s => s.StaffId === id)).filter(Boolean);
        setModalIsMultiStaff(true);
        setModalStaffList(objs);
        setModalActiveStaffId(objs[0]?.StaffId ?? null);
        setModalPerson(objs[0] || null);
        // Seed each tab's settings from the draft (shared range/total/distribution);
        // each tab is then independently editable.
        setModalSettingsByStaff(Object.fromEntries(objs.map(o => [o.StaffId, {
          start: d.start || toISO(baseMonday), end: d.end || toISO(addDays(baseMonday, 4)),
          hours: String(d.hours != null ? d.hours : 8), allocType: d.allocType || 'total',
          dailyHours: { ...(d.dailyHours || {}) }, fillMode: false, overflowStart: false, overflowEnd: false,
        }])));
        // ★ only suggestion-sourced staff: those starred before leaving (still present)
        // + anyone newly picked on the page (not in the prior team) (F1).
        const prevStars = new Set(d.priorStarIds || []);
        const prevTeam = new Set(d.priorTeamIds || []);
        setModalSuggestedStaffIds(new Set(d.pickedStaffIds.filter(id => prevStars.has(id) || !prevTeam.has(id))));
        setModalStaffFromSuggestion(false);
      } else {
        const pickedId = d.pickedStaffId || d.staffId || null;
        setModalPerson(pickedId ? (staff.find(s => s.StaffId === pickedId) || null) : null);
        setModalStaffFromSuggestion(!!d.pickedStaffId);
      }
      setShowModal(true);
    } catch { /* ignore */ }
  }, [loading, allJobs, staff, baseMonday]);

  // Check for gridViewContext (filter + highlight from notifications/alerts)
  useEffect(() => {
    if (loading || !staff.length) return;
    const raw = sessionStorage.getItem('gridViewContext');
    if (!raw) return;
    sessionStorage.removeItem('gridViewContext');
    try {
      const ctx = JSON.parse(raw);
      // Filter to specific staff
      if (ctx.weekOffset != null) setWeekOffset(ctx.weekOffset);
      if (ctx.zoom) setZoom(ctx.zoom);
      if (ctx.tab) setTab(ctx.tab);
      if (ctx.deptFilter) setDeptFilter([ctx.deptFilter]); // external writers send a single dept string
      if (ctx.highlightConflictKey) {
        // Store the key in a ref — a useEffect watching conflicts will pick it up after re-render
        pendingConflictHighlight.current = ctx.highlightConflictKey;
      }
      if (ctx.staffIds?.length > 0) {
        setViewMode('people');   // staff rows only render in people view
        setStaffFilter(ctx.staffIds);
        setDeptFilter(['all']);
        // Clear hide-filters so an affected staffer isn't hidden by a
        // level/empty/free filter when we jump to them from a notification.
        setHideLeadership(false);
        setHideEmpty(false);
        setShowFreeOnly(false);
      } else if (ctx.clearStaffFilter) {
        // Resolve from the dashboard: drop any stale staff/hide filters so the
        // conflicted staffer is actually in view on the conflicts tab.
        setStaffFilter([]);
        setHideLeadership(false);
        setHideEmpty(false);
        setShowFreeOnly(false);
      }
      if (ctx.message) showToast(ctx.message, 'info');

      // Cell-level highlighting from notification/PTO metadata (old=amber, new=blue)
      {
        const hasOld = ctx.oldCells?.length > 0;
        const hasNew = ctx.newCells?.length > 0;
        if (hasOld || hasNew) {
          const extractDates = (cells) => (cells || []).map(c => c.split('_')[1]).filter(Boolean).sort();
          const oldDates = extractDates(ctx.oldCells);
          const newDates = extractDates(ctx.newCells);
          const allAffectedDates = [...oldDates, ...newDates].sort();
          const earliest = allAffectedDates[0];
          const latest = allAffectedDates[allAffectedDates.length - 1];

          if (earliest) {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const startDate = new Date(earliest + 'T12:00:00');
            const endDate = latest ? new Date(latest + 'T12:00:00') : startDate;
            const startOffset = Math.floor((startDate - today) / 86400000 / 7);
            // Set zoom wide enough to see both old and new dates
            const spanWeeks = Math.max(1, Math.ceil((endDate - startDate) / 86400000 / 7) + 2);
            if (spanWeeks > zoom) setZoom(Math.min(52, spanWeeks));
            setWeekOffset(startOffset);
          }

          if (hasOld) { setHighlightOldCells(new Set(ctx.oldCells)); setTimeout(() => setHighlightOldCells(new Set()), 10000); }
          if (hasNew) { setHighlightNewCells(new Set(ctx.newCells)); setTimeout(() => setHighlightNewCells(new Set()), 10000); }

          // Auto-scroll: start at old cells, then glide to new cells after 3s
          if (hasOld && hasNew && oldDates[0] !== newDates[0]) {
            // Scroll to old cells first
            setTimeout(() => {
              if (!gridRef.current) return;
              const oldIdx = allDays.findIndex(d => d.iso === oldDates[0]);
              if (oldIdx >= 0) gridRef.current.scrollTo({ left: Math.max(0, dayX[oldIdx] - 40), behavior: 'auto' });
            }, 200);
            // After 3s, smooth-scroll to new cells
            setTimeout(() => {
              if (!gridRef.current) return;
              const newIdx = allDays.findIndex(d => d.iso === newDates[0]);
              if (newIdx >= 0) gridRef.current.scrollTo({ left: Math.max(0, dayX[newIdx] - 40), behavior: 'smooth' });
            }, 3500);
          } else {
            // Single set of cells — just scroll to them
            const targetDate = (oldDates[0] || newDates[0]);
            if (targetDate) {
              setTimeout(() => {
                if (!gridRef.current) return;
                const idx = allDays.findIndex(d => d.iso === targetDate);
                if (idx >= 0) gridRef.current.scrollTo({ left: Math.max(0, dayX[idx] - 40), behavior: 'auto' });
              }, 200);
            }
          }
        }
      }

      // Scroll to specific staff row (e.g., from PTO View button)
      if (ctx.scrollToStaffId) {
        setTimeout(() => {
          const el = rowRefs.current?.[ctx.scrollToStaffId];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 600);
      }
    } catch { /* ignore */ }
  }, [loading, staff, showToast]);

  // Check for pendingReassign (from conflicts tab → edit modal with new staff)
  useEffect(() => {
    if (loading || !staff.length) return;
    const raw = sessionStorage.getItem('pendingReassign');
    if (!raw) return;
    sessionStorage.removeItem('pendingReassign');
    try {
      const ctx = JSON.parse(raw);
      const jobBlocks = (blocksByPerson[ctx.blockStaffId] || []).filter(b => b.jobId === ctx.blockJobId);
      const block = (ctx.anchorDate && jobBlocks.find(b => (b.dates || []).some(d => toISO(d.date) === ctx.anchorDate)))
        || jobBlocks[0];
      if (!block) { showToast('Could not find the schedule entry', 'error'); return; }
      // Filter to show the original staff
      setStaffFilter([ctx.blockStaffId]);
      setDeptFilter(['all']);
      // Open edit modal for this block
      openEditModal(block);
      // After modal state settles, switch to new staff + optimize
      setTimeout(() => {
        const newPerson = staff.find(s => s.StaffId === ctx.newStaffId);
        if (newPerson) {
          setModalPerson(newPerson);
          setModalAllocType('daily');
          pendingOptimizeRef.current = true;
        }
      }, 300);
    } catch { /* ignore */ }
  }, [loading, staff, blocksByPerson]);

  // ── Cache my saved filters ───────────────────────────────────────────
  //
  // ONE writer, deliberately. Five call sites used to each persist by hand after
  // mutating the list, and the newest of them did it INSIDE a setSavedFilters
  // updater — updaters must stay pure, because StrictMode double-invokes them.
  // Deriving the cache from the state that actually rendered removes both the
  // duplication and the impurity, and no caller can now forget to persist.
  //
  // cacheableFilters drops anything that isn't mine: shared and global filters
  // belong to someone else and can be edited or revoked at any time, so a cached
  // copy could outlive access to it.
  useEffect(() => {
    if (!username) return;
    try {
      localStorage.setItem(`sched-grid-filters-${username}`,
        JSON.stringify(cacheableFilters(savedFilters)));
    } catch { /* quota / private mode — the DB is the real store */ }
  }, [savedFilters, username]);

  // ── Sync saved filters from DB (background, on mount) ────────────────
  //
  // A failure here is REPORTED, not swallowed. It used to be `.catch(() => {})`,
  // which made a broken read indistinguishable from having no saved filters —
  // see SavedFilterSyncWarning for what that cost. Extracted from the effect so
  // the warning's Retry can call it again without a page reload.
  const syncSavedFilters = useCallback(() => {
    if (!username) return Promise.resolve();
    const email = username + '@bmss.com';
    return fetchSavedFilters(email).then(dbFilters => {
      setSavedFiltersSyncFailed(false);
      if (!Array.isArray(dbFilters) || dbFilters.length === 0) return;
      setSavedFilters(prev => {
        // Keyed on kind + name, not name alone: a staff group and a client group
        // may share a name ("Construction Team" as people and as clients), and the
        // DB's unique index now allows exactly that.
        const seen = new Set(prev.map(savedFilterKey));
        const merged = [...prev];
        for (const df of dbFilters) {
          const nf = normalizeSavedFilter(df);
          if (nf && !seen.has(savedFilterKey(nf))) { seen.add(savedFilterKey(nf)); merged.push(nf); }
        }
        return merged;
      });
    }).catch(err => {
      setSavedFiltersSyncFailed(true);
      // Breadcrumb for the bug-report buffer — this is precisely the class of
      // handled-away failure that left all 68 reports with empty EventsJson.
      recordEvent('savedFilters.syncFailed', { message: err?.message || 'unknown' });
    });
  }, [username]);

  useEffect(() => { syncSavedFilters(); }, [syncSavedFilters]);

  // ── Filter modal handlers ───────────────────────────────────────────────
  // One save path for both kinds. `name` arrives from SaveFilterRow, which owns
  // the text so typing it does not re-render the grid.
  const savePreset = useCallback((kind, members, name, opts) => {
    const trimmed = (name || '').trim();
    if (!trimmed || members.length === 0) return;
    const visibility = opts?.visibility || 'private';
    // Only meaningful for 'shared'. Cleared otherwise so switching a filter back
    // to private can't leave a stale audience behind — same rule the API applies.
    const sharedWith = visibility === 'shared' ? (opts?.sharedWith || []) : [];
    const newFilter = normalizeSavedFilter({
      id: 'f' + Date.now(),
      name: trimmed,
      kind,
      memberIds: [...members],
      visibility,
      sharedWith,
      createdAt: new Date().toISOString().slice(0, 10),
    });
    // Replace by kind + name so saving over a name updates in place, matching
    // what the server's MERGE does. Never drops someone else's shared filter that
    // happens to share a name.
    const updated = [
      ...savedFilters.filter(f => !(f.isMine && f.kind === newFilter.kind && f.name === newFilter.name)),
      newFilter,
    ];
    setSavedFilters(updated);
    // Ownership is resolved server-side from the auth principal -- `email` is
    // only a fallback for a login SWA hands us masked.
    saveFilterAPI({
      email: username + '@bmss.com',
      name: newFilter.name,
      kind: newFilter.kind,
      memberIds: newFilter.memberIds,
      visibility: newFilter.visibility,
      sharedWith: newFilter.sharedWith,
    }).then(res => {
      // Adopt the server's FilterId. Without this the row keeps its local
      // 'f<timestamp>' id for the rest of the session, so deleting it only ever
      // removes it from this tab -- delete_filter is skipped and the filter
      // reappears on the next reload.
      if (!res?.filterId) return;
      const adopt = (x) => (x.id === newFilter.id
        ? { ...x, id: res.filterId, filterId: res.filterId }
        : x);
      setSavedFilters(cur => cur.map(adopt));
    }).catch(() => {});
    const audience = visibility === 'global' ? ' — visible to everyone'
      : visibility === 'shared' ? ` — shared with ${sharedWith.length} ${sharedWith.length === 1 ? 'person' : 'people'}`
      : '';
    showToast(`${kind === 'client' ? 'Client group' : 'Filter'} "${newFilter.name}" saved${audience}`, 'success');
  }, [savedFilters, username, showToast]);

  // `ids` come up from the panel that owns the selection.
  const handleSaveFilterPreset = useCallback(
    (name, ids, opts) => savePreset('staff', ids, name, opts), [savePreset]);
  const handleSaveClientFilterPreset = useCallback(
    (name, ids, opts) => savePreset('client', ids, name, opts), [savePreset]);

  // ── Manage-screen edits (rename / duplicate / re-share) ─────────────────
  //
  // These go through update_filter, which edits by FilterId. save_filter is keyed
  // on NAME, so it can create and it can overwrite, but it cannot rename — saving
  // under a new name leaves the original behind as a duplicate.
  //
  // All three apply locally first and reconcile on failure, matching how save and
  // delete already behave: the modal stays responsive, and a rejected write (a name
  // clash, someone else's filter) surfaces as a toast rather than a frozen row.
  const patchLocalFilter = useCallback((filterId, patch) => {
    setSavedFilters(prev => prev.map(x => (x.filterId === filterId ? { ...x, ...patch } : x)));
  }, []);

  const handleRenameFilterPreset = useCallback((filter, newName) => {
    const name = (newName || '').trim();
    if (!name || name === filter.name) return;
    // A filter that never reached the server has no id to edit by. Can only happen
    // if the original save failed, in which case re-saving is the right recovery.
    if (!filter.filterId) { showToast('That filter has not saved yet — try again in a moment', 'error'); return; }
    const before = filter.name;
    patchLocalFilter(filter.filterId, { name });
    updateFilterAPI({ filterId: filter.filterId, name })
      .then(res => {
        if (res?.success) { showToast(`Renamed to "${name}"`, 'success'); return; }
        patchLocalFilter(filter.filterId, { name: before });
        showToast(res?.reason || 'Could not rename that filter', 'error');
      })
      .catch(err => {
        patchLocalFilter(filter.filterId, { name: before });
        showToast(err?.message || 'Could not rename that filter', 'error');
      });
  }, [patchLocalFilter, showToast]);

  const handleSetFilterSharing = useCallback((filter, opts) => {
    if (!filter.filterId) { showToast('That filter has not saved yet — try again in a moment', 'error'); return; }
    const visibility = opts?.visibility || 'private';
    const sharedWith = visibility === 'shared' ? (opts?.sharedWith || []) : [];
    const before = { visibility: filter.visibility, sharedWith: filter.sharedWith };
    patchLocalFilter(filter.filterId, { visibility, sharedWith });
    updateFilterAPI({ filterId: filter.filterId, visibility, sharedWith })
      .then(res => {
        if (res?.success) {
          showToast(visibility === 'global' ? `"${filter.name}" is now visible to everyone`
            : visibility === 'shared' ? `"${filter.name}" shared with ${sharedWith.length} ${sharedWith.length === 1 ? 'person' : 'people'}`
            : `"${filter.name}" is now private`, 'success');
          return;
        }
        patchLocalFilter(filter.filterId, before);
        showToast(res?.reason || 'Could not change sharing', 'error');
      })
      .catch(err => {
        patchLocalFilter(filter.filterId, before);
        showToast(err?.message || 'Could not change sharing', 'error');
      });
  }, [patchLocalFilter, showToast]);

  // Duplicating always produces a PRIVATE copy owned by the duplicator — including
  // when the source is someone else's shared filter. Inheriting their audience
  // would re-share their list under your name without either of you choosing to.
  const handleDuplicateFilterPreset = useCallback((filter) => {
    const base = `${filter.name} (copy)`;
    // Avoid colliding with the unique index if a copy already exists.
    const taken = new Set(savedFilters.filter(x => x.isMine && x.kind === filter.kind).map(x => x.name));
    let name = base;
    for (let n = 2; taken.has(name); n++) name = `${filter.name} (copy ${n})`;
    savePreset(filter.kind, filter.memberIds || [], name, { visibility: 'private' });
  }, [savedFilters, savePreset]);

  const handleDeleteFilterPreset = useCallback((filter) => {
    // The homeroom filter is generated, and a shared filter belongs to whoever
    // made it -- the server refuses both, this just keeps the UI honest.
    if (filter.isSystem || filter.isMine === false) return;
    const updated = savedFilters.filter(f => !(f.id === filter.id && f.kind === filter.kind));
    setSavedFilters(updated);
    // Fire-and-forget DB delete
    if (filter.filterId) deleteFilterAPI(filter.filterId).catch(() => {});
  }, [savedFilters]);

  const handleApplyClientFilter = useCallback((ids) => {
    setClientFilter(ids);
    closeClientFilterModal();
    // Same reason the staff filter clears the dept scope (bug #10): a client group's
    // jobs routinely span departments, and leaving a dept tab active would silently
    // hide most of what was just selected.
    if (ids.length > 0) setDeptFilter(['all']);
  }, [closeClientFilterModal, setClientFilter, setDeptFilter]);

  const handleApplyFilter = useCallback((ids) => {
    setStaffFilter(ids);
    setStaffSearch('');
    closeFilterModal();
    if (ids.length > 0) setDeptFilter(['all']);   // ARRAY — see bug #10
  }, [closeFilterModal, setStaffFilter, setStaffSearch, setDeptFilter]);

  // Auto-run optimize ONCE on next state-settle when pendingOptimizeRef is
  // set (e.g., reassign-from-conflict handoff that opens the edit modal for
  // a new staffer + wants optimize to run). Manual edits no longer auto-fire
  // optimize — that's now an explicit button click.
  useEffect(() => {
    if (!pendingOptimizeRef.current || !modalPerson || !modalStart || !showModal) return;
    // Reuse the single-staff Optimize handler (no overflow) so the reassign-
    // from-conflict handoff and the manual Optimize button share one path.
    const origTotal = Object.values(modalOrigDailyHours).reduce((s, v) => s + (v || 0), 0);
    const total = origTotal > 0 ? origTotal : (parseFloat(modalHours) || 0);
    if (total <= 0) { pendingOptimizeRef.current = false; return; }
    runOptimizeWith(false, false);
    pendingOptimizeRef.current = false;
  }, [modalStart, modalEnd, modalPerson, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save entry (new or edit) ──────────────────────────────────────────
  // Item 1 — placeholder save handler. Called when the new-entry modal is in
  // placeholder mode. Skips per-day allocation, capacity warnings, etc.
  // A placeholder can hold a real job OR a custom job (migrate-074). The guard used
  // to require modalJob, which is EMPTY for a custom job — switching the picker to
  // Custom calls setModalJob(''). So with a custom job selected this returned
  // silently: button enabled, no alert, no request, nothing (bug #285).
  const handleSavePlaceholder = useCallback(async () => {
    const isCustom = modalJobKind === 'custom';
    const jobRef = isCustom
      ? { customJobId: parseInt(modalCustomJobId, 10) }
      : { jobId: parseInt(modalJob, 10) };
    const hasJob = isCustom ? !!modalCustomJobId : !!modalJob;
    if (!hasJob || !modalDept || !modalLevel || !modalStart || !modalEnd || !modalHours) return;
    if (modalEnd < modalStart) { alert('End date must be on or after start date.'); return; }
    setModalSaving(true);
    try {
      await upsertPlaceholder({
        ...jobRef,
        departmentCode: modalDept,
        level: modalLevel,
        hours: parseFloat(modalHours),
        startDate: modalStart,
        endDate: modalEnd,
        // Optional label. The column and API always accepted it; only this create
        // path dropped it. It is how two otherwise-identical stacked placeholders
        // are told apart, and where "holding this for <client>, pending setup" goes.
        notes: modalNotesRef.current?.trim() || null,
        createdBy: username || null,
      });
      // Reload placeholders so the new block appears in the grid.
      try {
        const rows = await fetchPlaceholders();
        setPlaceholders(rows || []);
      } catch (_) { /* ignore */ }
      setShowModal(false);
    } catch (e) {
      alert('Failed to save placeholder: ' + e.message);
    } finally {
      setModalSaving(false);
    }
  }, [modalJob, modalJobKind, modalCustomJobId, modalDept, modalLevel, modalStart, modalEnd, modalHours, username]);

  // Reverse of placeholder→staff: convert the active edit block's schedule
  // entries back to a (dept, level) placeholder for the same job. Merges into
  // any existing same-(job, dept, level) placeholder whose dates overlap.
  const handleConvertToPlaceholder = useCallback(async () => {
    if (!modalEditBlock) return;
    if (modalEditBlock.customJobId) {
      alert('Custom-job entries cannot be converted to placeholders.');
      return;
    }
    if (!modalEditBlock.jobId || !modalDept || !modalLevel) {
      alert('Missing job, department, or level — cannot convert.');
      return;
    }
    const dates = modalEditBlock.dates || [];
    if (!dates.length) return;
    const startDate = toISO(dates[0].date);
    const endDate = toISO(dates[dates.length - 1].date);
    setModalSaving(true);
    try {
      const result = await convertToPlaceholder({
        staffId: modalEditBlock.staffId,
        jobId: modalEditBlock.jobId || undefined,
        customJobId: modalEditBlock.customJobId || undefined,
        startDate,
        endDate,
        departmentCode: modalDept,
        level: modalLevel,
        notes: modalNotesRef.current || null,
        createdBy: username || null,
      });
      // Refresh both placeholders + schedule so the staff block disappears
      // and the new placeholder block appears at the top of the (dept, level)
      // group.
      try {
        const [phRows, schedData] = await Promise.all([
          fetchPlaceholders(),
          refreshEntries(),
        ]);
        setPlaceholders(phRows || []);
        // refreshEntries() already setEntries internally, but Promise.all
        // gives us a single await point.
        void schedData;
      } catch (_) { /* ignore — next manual refresh will pick up */ }
      setShowModal(false);
      showToast(
        result?.merged
          ? `Merged ${(result.hoursMoved || 0).toFixed(2)}h into existing placeholder.`
          : `Moved ${(result?.hoursMoved || 0).toFixed(2)}h to ${modalDept} ${modalLevel} placeholder.`,
        'success'
      );
      refreshNotifications?.();
    } catch (e) {
      alert('Convert failed: ' + (e.message || e));
    } finally {
      setModalSaving(false);
    }
  }, [modalEditBlock, modalDept, modalLevel, username]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveEntry = useCallback(async () => {
    // Chooser-originated placeholders (dept+level, no staffId) — create them first via
    // upsertPlaceholder, independent of the staff schedule. Runs BEFORE the staff guard
    // so a placeholders-only pick still works; real staff (if any) fall through to schedule.
    if (pendingPlaceholders.length > 0 && modalJob) {
      const hasRealStaff = (modalIsMultiStaff && modalStaffList.length > 0) || (!modalIsMultiStaff && !!modalPerson);
      setModalSaving(true);
      try {
        for (const ph of pendingPlaceholders) {
          if (!ph.dept || !ph.level) continue;
          await upsertPlaceholder({
            jobId: parseInt(modalJob, 10), departmentCode: ph.dept, level: ph.level,
            hours: ph.hours, startDate: ph.startDate, endDate: ph.endDate, createdBy: username || null,
          });
        }
        try { const rows = await fetchPlaceholders(); setPlaceholders(rows || []); } catch { /* ignore */ }
      } catch (e) {
        alert('Failed to create placeholder: ' + e.message);
        setModalSaving(false);
        return;
      }
      const phCount = pendingPlaceholders.length;
      setPendingPlaceholders([]);
      if (!hasRealStaff) {
        setShowMultiPreview(false); setShowModal(false); setSelectedBlock(null); modalNotesRef.current = '';
        showToast(`Created ${phCount} placeholder${phCount === 1 ? '' : 's'}`, 'success');
        setModalSaving(false);
        return;
      }
      // else: real staff remain → fall through to schedule them (modalSaving stays true).
    }
    // Item 2 — either a real job OR a custom job must be selected.
    const isCustom = modalJobKind === 'custom';
    if (!modalPerson || !modalStart || !modalHours) return;
    if (!isCustom && !modalJob) return;
    if (isCustom && !modalCustomJobId) return;
    setModalSaving(true);
    try {
      const staffId = modalPerson.StaffId;
      const jobId = isCustom ? null : parseInt(modalJob);
      const customJobId = isCustom ? parseInt(modalCustomJobId) : null;
      const isEdit = !!modalEditBlock?.dates?.length;

      // Get batchId for grouping if multi-day operation
      const batchResult = await getBatchId();
      const batchId = batchResult.batchId;
      const source = isEdit ? 'edit' : modalSource || 'ui';
      const notes = modalNotesRef.current.trim() || null;

      // Multi-staff create-only branch: fan out across selected staff in a single
      // atomic transaction via bulk_move_multi. Each tab is independent — resolve
      // EACH staffer's own { range, total, allocType, dailyHours } through the SAME
      // computeProposedDaily the single-staff preview + save use (one resolver, no
      // fork). The ACTIVE tab reads the live scalars (flushed via buildActiveSettings);
      // every other tab reads its stored settings entry.
      if (modalIsMultiStaff && modalStaffList.length > 0) {
        const activeSettings = buildActiveSettings();
        const perStaff = [];
        for (const s of modalStaffList) {
          const st = s.StaffId === modalActiveStaffId
            ? activeSettings
            : (modalSettingsByStaff[s.StaffId] || activeSettings);
          const proposed = computeProposedDaily({
            startISO: st.start,
            endISO: st.end || st.start,
            total: parseFloat(st.hours) || 0,
            allocType: st.allocType || 'total',
            dailyMap: st.dailyHours,
            staffId: s.StaffId,
            getDayCap,
          });
          const inserts = [];
          for (const [date, hrs] of Object.entries(proposed)) {
            const h = Number(hrs) || 0;
            if (h > 0) inserts.push({ date, hours: h });
          }
          if (inserts.length > 0) perStaff.push({ staffId: s.StaffId, inserts });
        }
        if (perStaff.length === 0) {
          setModalSaving(false);
          alert('No hours to save — every selected staff resolved to 0 entries.');
          return;
        }
        const multiRes = await bulkMoveMulti({
          jobId, customJobId, batchId, source, movedBy: username, notes, perStaff,
        });
        await refreshEntries();
        refreshNotifications?.();
        setShowModal(false);
        setSelectedBlock(null);
        modalNotesRef.current = '';
        const totalRows = perStaff.reduce((sv, p) => sv + p.inserts.length, 0);
        showToast(`Created ${totalRows} entr${totalRows === 1 ? 'y' : 'ies'} across ${perStaff.length} staff${mergeNote(multiRes)}`, 'success');
        setModalSaving(false);
        return;
      }

      // Build the proposed day→hours map from the SAME single source of truth
      // the Before/After preview uses (getProposedDailyHours → computeProposedDaily),
      // keeping only >0-hour days for the write. This guarantees the preview and
      // the save can never diverge — the extend-range bug was exactly such a
      // divergence (Total mode: preview even-split the new range, save reused the
      // stale 0-filled daily map and wrote nothing). By Day mode still returns the
      // custom/Optimize/Fill map verbatim; Total mode even-splits across the range.
      const proposed = getProposedDailyHours();
      const newDays = {};
      for (const [iso, h] of Object.entries(proposed)) {
        const hrs = Number(h) || 0;
        if (hrs > 0) newDays[iso] = hrs;
      }

      // Item 2 — Custom-job save path. Uses bulkEntries (the simpler, polymorphic-aware
      // path) instead of bulkMove. For edits, delete the old entries first then
      // bulk-insert the new ones. No reassign/job-change audit semantics — custom
      // jobs don't have client context where those distinctions matter.
      if (isCustom) {
        // ONE call, one transaction: the edited block's old days are removed and the new days
        // written together (bulk_entries' deleteEntryIds), under one batchId so History reads
        // it as one event. It used to be bulk_delete_entries then bulk_entries — if the second
        // call failed, the block was simply gone. Removing ids that are already gone is a no-op.
        const staleIds = isEdit ? (modalEditBlock.dates || []).map(d => d.entryId).filter(Boolean) : [];
        const inserts = Object.entries(newDays).map(([date, hours]) => ({
          staffId, customJobId, date, hours,
        }));
        let res = null;
        if (inserts.length || staleIds.length) {
          res = await bulkEntries(inserts, username, { deleteEntryIds: staleIds, batchId, source });
        }
        await refreshEntries();
        refreshNotifications?.();
        setShowModal(false);
        setSelectedBlock(null);
        modalNotesRef.current = '';
        showToast(`${isEdit ? 'Custom-job entries updated' : 'Custom-job entries added'}${mergeNote(res)}`, 'success');
        setModalSaving(false);
        return;
      }

      // Track whether an actual write ran, so a no-effect edit-save reports
      // "No changes to save" instead of a misleading "Entry updated" success.
      let didWrite = false;
      let saveRes = null; // bulk_move result, for the "combined with existing hours" note
      if (isEdit) {
        const oldStaffId = modalEditBlock.staffId;
        const oldJobId = modalEditBlock.jobId;
        const staffChanged = staffId !== oldStaffId;
        const jobChanged = jobId !== oldJobId;
        const editSource = source; // always 'edit' — the Action column captures REASSIGN/JOB_CHANGE

        if (staffChanged || jobChanged) {
          // Staff or job changed — use moves (not delete+insert) for proper REASSIGN audit
          const oldByDate = {};
          for (const d of modalEditBlock.dates) oldByDate[toISO(d.date)] = d;
          const remainingNewDays = { ...newDays };
          const bmMoves = [];
          const bmDeletes = [];
          const bmInserts = [];

          // Match old entries to new days by date
          for (const d of modalEditBlock.dates) {
            const oldISO = toISO(d.date);
            if (remainingNewDays[oldISO] != null) {
              // Entry persists — move to new staff/job
              const m = { entryId: d.entryId };
              if (staffChanged) m.newStaffId = staffId;
              if (jobChanged) m.newJobId = jobId;
              if (remainingNewDays[oldISO] !== d.hours) m.newHours = remainingNewDays[oldISO];
              bmMoves.push(m);
              delete remainingNewDays[oldISO];
            } else {
              bmDeletes.push(d.entryId);
            }
          }
          // Remaining new days are additions (extended range)
          for (const [date, hours] of Object.entries(remainingNewDays)) {
            bmInserts.push({ date, hours, _staffId: staffId, _jobId: jobId });
          }

          // Merge any overlap with existing entries on the DESTINATION (staff, job)
          // additively — a reassign move keeps its date, so its landing day comes from
          // oldByEntryId. Prevents a UQ_ScheduleEntry collision when the destination
          // already has hours on a landing day.
          const bmOldById = {};
          for (const d of modalEditBlock.dates) bmOldById[d.entryId] = { iso: toISO(d.date), hours: d.hours };
          const bmResolved = resolveOverlapMerges(
            { moves: bmMoves, inserts: bmInserts, deletes: bmDeletes },
            staffId, jobId, modalEditBlock.dates.map(d => d.entryId), bmOldById
          );

          saveRes = await bulkMove({
            jobId: oldJobId, staffId: oldStaffId, batchId, source: editSource, movedBy: username, notes,
            moves: bmResolved.moves, deletes: bmResolved.deletes, inserts: bmResolved.inserts,
            // Note only the surviving block entries — not entries merged into (they keep their own note).
            noteEntryIds: bmResolved.moves.map(m => m.entryId).filter(id => bmOldById[id]),
          });
          didWrite = true;
        } else {
          // Same staff + job — single bulkMove: diff-based (moves for changed hours, deletes for removed, inserts for added)
          const oldByDate = {};
          for (const d of modalEditBlock.dates) oldByDate[toISO(d.date)] = d;

          const deletes = [];
          const moves = [];
          const inserts = [];

          // Days removed
          for (const oldISO of Object.keys(oldByDate)) {
            if (!newDays[oldISO]) deletes.push(oldByDate[oldISO].entryId);
          }
          // Days changed or added
          for (const [dayISO, hrs] of Object.entries(newDays)) {
            const old = oldByDate[dayISO];
            if (old && old.hours === hrs) continue; // unchanged
            if (old) {
              moves.push({ entryId: old.entryId, newHours: hrs }); // hours change only
            } else {
              inserts.push({ date: dayISO, hours: hrs });
            }
          }

          // Merge any insert that lands on an existing out-of-block entry for this
          // (staff, job) additively into that entry (absolute total → idempotent),
          // instead of duplicating the key. This is the common edit-modal collision:
          // extending a block onto a day another block already occupies.
          const sjOldById = {};
          for (const d of modalEditBlock.dates) sjOldById[d.entryId] = { iso: toISO(d.date), hours: d.hours };
          const sjResolved = resolveOverlapMerges(
            { moves, inserts, deletes }, staffId, jobId,
            (modalEditBlock.dates || []).map(d => d.entryId), sjOldById
          );

          // Block-level note: apply to all kept BLOCK entries (changed + unchanged), so a
          // notes-only edit still persists even when nothing else changed. Entries merged
          // into (not part of this block) keep their own note.
          const persistIds = (modalEditBlock.dates || []).map(d => d.entryId).filter(id => !sjResolved.deletes.includes(id));
          const noteChanged = notes !== (modalEditBlock.notes || null);

          if (sjResolved.deletes.length || sjResolved.moves.length || sjResolved.inserts.length || noteChanged) {
            saveRes = await bulkMove({ jobId, staffId, batchId, source, movedBy: username, notes, deletes: sjResolved.deletes, moves: sjResolved.moves, inserts: sjResolved.inserts, noteEntryIds: persistIds });
            didWrite = true;
          }
        }
      } else {
        // New entry — single bulkMove: all inserts. Merge any day that overlaps an
        // existing same-(staff, job) entry additively (so the write matches the
        // preview's merged total) instead of a duplicate insert that would collide.
        const inserts = Object.entries(newDays).map(([date, hours]) => ({ date, hours }));
        const created = resolveOverlapMerges({ moves: [], inserts, deletes: [] }, staffId, jobId, []);
        if (created.moves.length || created.inserts.length) {
          saveRes = await bulkMove({ jobId, staffId, batchId, source, movedBy: username, notes, deletes: created.deletes, moves: created.moves, inserts: created.inserts });
          didWrite = true;
        }
      }

      await refreshEntries();
      refreshNotifications?.();
      setShowModal(false);
      setSelectedBlock(null);
      modalNotesRef.current = '';
      if (isEdit && !didWrite) {
        showToast('No changes to save', 'info');
      } else {
        const staffChanged = isEdit && modalPerson.StaffId !== modalEditBlock.staffId;
        const jobChanged = isEdit && parseInt(modalJob) !== modalEditBlock.jobId;
        const msg = staffChanged ? 'Staff reassigned' : jobChanged ? 'Job changed' : isEdit ? 'Entry updated' : 'Entry created';
        showToast(`${msg}${mergeNote(saveRes)}`, 'success');
      }
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setModalSaving(false);
    }
  }, [modalJob, modalJobKind, modalCustomJobId, modalPerson, modalStart, modalHours, modalEditBlock, modalSource, username, refreshEntries, refreshNotifications, showToast, getProposedDailyHours, getDayCap, modalIsMultiStaff, modalStaffList, modalActiveStaffId, modalSettingsByStaff, buildActiveSettings, pendingPlaceholders, resolveOverlapMerges]);

  // ── Drag-drop (optimistic UI) ─────────────────────────────────────────
  const handleDragStart = useCallback((block) => {
    setDragging(block);
    // Imperatively add class so blocks become pointer-events:none without re-render
    setTimeout(() => gridBodyRef.current?.classList.add('sched-grid-body--dragging'), 0);
  }, []);

  const handleDrop = useCallback((targetStaffId, targetDayIdx, targetJobId) => {
    if (!dragging) { setDragging(null); return; }
    const movedBlock = dragging;
    const sameStaff = movedBlock.staffId === targetStaffId;
    // Jobs view can drop onto a different job (reassign) — undefined targetJobId means
    // "keep the block's job" (People view + same-job day-shift), so it never regresses People.
    const sameJob = targetJobId == null || targetJobId === movedBlock.jobId;

    // Departed staff are view-only. Gated on !sameStaff deliberately: day-shifting a
    // block WITHIN an inactive person's own row is editing existing work and stays
    // allowed — only moving someone else's block ONTO them is new assignment.
    if (!sameStaff && !isAssignableStaffId(targetStaffId)) {
      const name = staff.find(s => s.StaffId === targetStaffId)?.Name || 'That staffer';
      showToast(`${name} is inactive — can't schedule new work onto them`, 'error');
      setDragging(null);
      return;
    }

    let dayShift = 0;
    if (targetDayIdx != null && movedBlock.sIdx != null) {
      dayShift = targetDayIdx - movedBlock.sIdx;
    }

    if (sameStaff && dayShift === 0 && sameJob) { setDragging(null); return; }

    // Build proposed new dates (shift mode) — skip Sundays, include Saturdays with capacity
    const oldEntries = movedBlock.dates;
    const targetStartDate = targetDayIdx != null && allDays[targetDayIdx]
      ? new Date(allDays[targetDayIdx].date) : null;

    const newDates = [];
    if (targetStartDate) {
      const cursor = new Date(targetStartDate);
      for (let i = 0; i < oldEntries.length; i++) {
        // Skip Sunday always; skip Saturday if no capacity for target staff
        while (cursor.getDay() === 0 || (cursor.getDay() === 6 && getDayCap(targetStaffId, toISO(cursor)) <= 0)) {
          cursor.setDate(cursor.getDate() + 1);
        }
        newDates.push(toISO(new Date(cursor)));
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    // Build per-day hours for shift mode (preserve original daily hours at new positions)
    const dailyHours = {};
    for (let i = 0; i < oldEntries.length; i++) {
      const targetDate = newDates[i] || oldEntries[i].date;
      dailyHours[targetDate] = oldEntries[i].hours;
    }

    // Build warnings
    const warnings = [];
    const targetPerson = staff.find(s => s.StaffId === targetStaffId);
    for (const iso of newDates) {
      const dayCap = getDayCap(targetStaffId, iso);
      const existingSched = (dayHoursByStaff[targetStaffId]?.[iso] || 0);
      // Exclude the block being moved if same staff (don't double-count)
      const selfHours = sameStaff ? oldEntries.filter(d => toISO(d.date) === iso).reduce((s, d) => s + d.hours, 0) : 0;
      const dayTotal = existingSched - selfHours + (dailyHours[iso] || 0);
      if (dayTotal > dayCap && dayCap > 0) {
        warnings.push({ date: iso, scheduled: dayTotal, capacity: dayCap });
      }
    }

    // Cross-job drop (Jobs view): the destination job for the confirm/label.
    const targetJobMeta = (!sameJob && targetJobId) ? jobInfoMap[targetJobId] : null;
    const targetJobName = targetJobMeta ? `${targetJobMeta.ClientName || ''}${targetJobMeta.Name ? ' · ' + targetJobMeta.Name : ''}`.trim() : null;
    // Off-office advisory: the target staffer isn't in the (resulting) on-site job's office.
    const resJob = jobInfoMap[targetJobId ?? movedBlock.jobId];
    const officeWarn = offOffice(targetStaffId, resJob?.IsOnSite, resJob?.OfficeCode)
      ? { office: resJob.Office || resJob.OfficeCode, staffOffice: staffOfficeById[targetStaffId] || 'another office' }
      : null;

    setDragging(null);
    setSelectedBlock(null);
    setDropOverflowStart(false);
    setDropOverflowEnd(false);
    setDropPreview({
      block: movedBlock,
      targetStaffId,
      targetStaffName: targetPerson?.Name || 'Unknown',
      targetJobId: sameJob ? null : targetJobId,
      targetJobName,
      officeWarn,
      sameStaff,
      sameJob,
      dayShift,
      mode: 'shift',
      dailyHours,
      newDates,
      originalNewDates: [...newDates], // preserve for optimize revert
      oldEntries,
      warnings,
    });
  }, [dragging, allDays, staff, getDayCap, dayHoursByStaff, jobInfoMap, offOffice, staffOfficeById, isAssignableStaffId, showToast]);

  // ── Drop onto a (dept, level) placeholder row ──────────────────────────
  // Opens a confirmation preview; on confirm the staff entries collapse into
  // a single placeholder via the convert_to_placeholder API.
  const handleDropToPlaceholderRow = useCallback((deptCode, level, targetDayIdx) => {
    if (!dragging) return;
    const block = dragging;
    setDragging(null);
    setSelectedBlock(null);
    if (block.isPlaceholder) return; // no-op
    // Only allow drops onto the placeholder row matching the staff's own
    // (dept, level). Drops onto other levels are rejected to keep behavior
    // predictable — power users who want to move levels can do it via the
    // edit modal (and the dept/level still come from the staff for v1).
    const sourcePerson = staff.find(s => s.StaffId === block.staffId);
    const srcDept = sourcePerson?.DepartmentCode;
    const srcLevel = sourcePerson?.Level;
    if (srcDept !== deptCode || srcLevel !== level) {
      showToast(`Drop onto the placeholder row matching ${sourcePerson?.Name || 'the staff'}'s level (${srcDept} ${srcLevel}).`, 'error');
      return;
    }
    const dates = block.dates || [];
    if (!dates.length) return;
    // block.dates[i].date is a local-midnight Date (built in allDays via
    // addDays(baseMonday, i) — see line ~278). Use those directly; do NOT
    // round-trip through `new Date(isoString)` since that parses as UTC
    // midnight and shifts a day backward in local timezone (CST/CDT).
    const origStartLocal = dates[0].date;
    const origEndLocal = dates[dates.length - 1].date;
    const origMinDate = toISO(origStartLocal);
    const origMaxDate = toISO(origEndLocal);
    const totalHours = dates.reduce((s, d) => s + (Number(d.hours) || 0), 0);

    // Compute the shifted range based on the drop cell. If the drop landed on
    // a weekend cell, snap forward to the following Monday. End date is
    // computed to preserve the original range's workday count.
    let minDate = origMinDate;
    let maxDate = origMaxDate;
    let dayShift = 0;
    let snappedFromWeekend = false;
    if (targetDayIdx != null && allDays[targetDayIdx]) {
      const dropCellDate = new Date(allDays[targetDayIdx].date);
      const snappedDate = snapForwardToWorkday(dropCellDate);
      snappedFromWeekend = snappedDate.getTime() !== dropCellDate.getTime();
      const origWeekdayCount = countWeekdaysInclusive(origStartLocal, origEndLocal);
      const newEnd = addNWorkdaysInclusive(snappedDate, Math.max(origWeekdayCount, 1));
      minDate = toISO(snappedDate);
      maxDate = toISO(newEnd);
      // dayShift in calendar days from original-block-start to (post-snap)
      // new-start. Both Dates are local-midnight (or local 12:00) so the diff
      // in ms / 86400000 is the day count, DST-edge-safe via Math.round.
      dayShift = Math.round((snappedDate.getTime() - origStartLocal.getTime()) / 86400000);
    }

    // Re-run merge detection against the SHIFTED range so the preview is
    // accurate after a shift.
    //
    // Custom-job blocks never merge — mirrors findMergeablePlaceholder on the server,
    // which returns null for them. Several placeholders for one custom job at one
    // level is the only way to split that work across people, so absorbing them would
    // defeat the point.
    const willMergeWith = block.customJobId ? undefined : placeholders.find(p =>
      p.JobId === block.jobId &&
      p.DepartmentCode === deptCode &&
      p.Level === level &&
      String(p.StartDate).slice(0, 10) <= maxDate &&
      String(p.EndDate).slice(0, 10) >= minDate
    );
    setConvertPreview({
      block,
      departmentCode: deptCode,
      level,
      totalHours,
      minDate,
      maxDate,
      originalMinDate: origMinDate,
      originalMaxDate: origMaxDate,
      dayShift,
      snappedFromWeekend,
      willMergeWith: willMergeWith || null,
    });
  }, [dragging, staff, placeholders, allDays]);

  const handleConfirmConvertDrop = useCallback(async () => {
    if (!convertPreview) return;
    const cp = convertPreview;
    setConvertSaving(true);
    try {
      const result = await convertToPlaceholder({
        staffId: cp.block.staffId,
        jobId: cp.block.jobId || undefined,
        customJobId: cp.block.customJobId || undefined,
        // Destination = where the placeholder goes (post-shift if dragged).
        startDate: cp.minDate,
        endDate: cp.maxDate,
        // Source = where the original entries live (pre-shift). When the
        // destination differs (DND shift), the API needs both ranges so it
        // SUMs/DELETEs the right entries and INSERTs at the right place.
        sourceStartDate: cp.originalMinDate || cp.minDate,
        sourceEndDate:   cp.originalMaxDate || cp.maxDate,
        departmentCode: cp.departmentCode,
        level: cp.level,
        createdBy: username || null,
      });
      try {
        const [phRows] = await Promise.all([
          fetchPlaceholders(),
          refreshEntries(),
        ]);
        setPlaceholders(phRows || []);
      } catch (_) { /* ignore */ }
      setConvertPreview(null);
      showToast(
        result?.merged
          ? `Merged ${(result.hoursMoved || 0).toFixed(2)}h into existing placeholder.`
          : `Moved ${(result?.hoursMoved || 0).toFixed(2)}h to ${cp.departmentCode} ${cp.level} placeholder.`,
        'success'
      );
      refreshNotifications?.();
    } catch (e) {
      alert('Convert failed: ' + (e.message || e));
    } finally {
      setConvertSaving(false);
    }
  }, [convertPreview, username, refreshEntries, refreshNotifications, showToast]);

  // ── Confirm drop preview ────────────────────────────────────────────────
  const handleConfirmDrop = useCallback(async () => {
    if (!dropPreview) return;
    const { block, targetStaffId, targetJobId, sameStaff, dailyHours, oldEntries, newDates } = dropPreview;
    const batchResult = await getBatchId();
    const batchId = batchResult.batchId;

    const notes = dropNotesRef.current.trim() || null;
    setDropPreview(null);
    dropNotesRef.current = '';
    showToast('Moving schedule...', 'info', true);

    try {
      // Build moves/deletes/inserts for single transactional bulk_move call.
      // Topologically sorted on server → collision-proof. Transaction → all-or-nothing.

      // Moves: entries that persist, possibly with new date/hours/staff
      const moves = oldEntries.slice(0, newDates.length).map((old, i) => {
        const m = { entryId: old.entryId };
        const oldISO = toISO(old.date);
        const newDate = newDates[i];
        if (oldISO !== newDate) m.newDate = newDate;
        const newHrs = dailyHours[newDate] ?? old.hours;
        if (newHrs !== old.hours) m.newHours = newHrs;
        if (!sameStaff) m.newStaffId = targetStaffId;
        if (targetJobId) m.newJobId = targetJobId; // cross-job reassign (Jobs view); null when same job
        return m;
      }).filter(m => m.newDate || m.newHours != null || m.newStaffId || m.newJobId); // skip unchanged

      // Deletes: excess old entries (optimize squeezed into fewer days)
      const deletes = oldEntries.slice(newDates.length).map(e => e.entryId);

      // Inserts: overflow extension days (optimize extended beyond old entry count)
      const inserts = newDates.slice(oldEntries.length)
        .filter(iso => (dailyHours[iso] || 0) > 0)
        .map(iso => ({ date: iso, hours: dailyHours[iso] }));

      // Item 2 — custom-job DnD passes customJobId instead of jobId.
      // Cross-job drop (Jobs view): top-level jobId is the destination so overflow
      // inserts attach to the new job (mirrors how staffId is the destination).
      const resultJobId = block.isCustom ? null : (targetJobId ?? block.jobId);

      // Merge any move/insert that lands on an existing entry for the RESULT
      // (target staff, destination job/custom job) additively into that entry — covers a
      // block dragged onto another block's dates AND a reassign drop onto a day the
      // destination job already has hours. Custom-job blocks resolve on customJobId;
      // skipping them here was what made a custom-job drag onto an occupied day 500.
      const resultCustomJobId = block.isCustom ? (block.customJobId ?? null) : null;
      const dragOldById = {};
      for (const o of oldEntries) dragOldById[o.entryId] = { iso: toISO(o.date), hours: o.hours };
      const dragResolved = resolveOverlapMerges(
        { moves, inserts, deletes },
        targetStaffId, resultJobId, oldEntries.map(o => o.entryId), dragOldById, resultCustomJobId
      );
      const absorbed = new Set(dragResolved.deletes);

      const dropRes = await bulkMove({
        jobId: resultJobId,
        customJobId: resultCustomJobId,
        staffId: targetStaffId,
        batchId, source: 'drag-drop', movedBy: username,
        moves: dragResolved.moves, deletes: dragResolved.deletes, inserts: dragResolved.inserts, notes,
        // Persisting entries (the rest are deletes) get the block note even if a
        // particular day was otherwise unchanged by the drag — minus any absorbed by a merge.
        noteEntryIds: oldEntries.slice(0, newDates.length).map(o => o.entryId).filter(id => !absorbed.has(id)),
      });

      // Reassignment notification is real-job-only (custom jobs have no client/job context to surface).
      if (!sameStaff && !block.isCustom) {
        fetch('/api/schedWrite?action=notify_move', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ oldStaffId: block.staffId, newStaffId: targetStaffId, jobId: block.jobId, movedBy: username }),
        }).catch(() => {});
      }

      showToast(`${sameStaff ? 'Schedule moved' : 'Schedule reassigned'}${mergeNote(dropRes)}`, 'success');
      await refreshEntries();
      refreshNotifications?.();
    } catch (e) {
      showToast('Move failed: ' + e.message, 'error');
      await refreshEntries();
    }
  }, [dropPreview, refreshEntries, showToast, username, resolveOverlapMerges]);

  // Handler to switch drop preview to optimize mode
  const handleDropOptimize = useCallback(() => {
    if (!dropPreview) return;
    const { targetStaffId, sameStaff, oldEntries, originalNewDates } = dropPreview;

    const editEntryIds = new Set(sameStaff ? oldEntries.map(d => d.entryId) : []);
    const totalOriginal = oldEntries.reduce((s, d) => s + d.hours, 0);

    // Always start from the original shifted dates (not the currently expanded set)
    let allCandidateDates = [...originalNewDates];

    if (dropOverflowStart) {
      // Add working days before the start date (up to 5 days back, never before today)
      const firstDate = new Date(originalNewDates[0] + 'T12:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let added = 0;
      for (let i = 1; i <= 10 && added < 5; i++) {
        const d = new Date(firstDate);
        d.setDate(d.getDate() - i);
        if (d < today) break; // hard cutoff at today
        if (d.getDay() === 0) continue;
        if (d.getDay() === 6 && getDayCap(targetStaffId, toISO(d)) <= 0) continue;
        allCandidateDates.unshift(toISO(d));
        added++;
      }
    }

    if (dropOverflowEnd) {
      // Add working days after the end date (up to 5 days forward)
      const lastDate = new Date(originalNewDates[originalNewDates.length - 1] + 'T12:00:00');
      let added = 0;
      for (let i = 1; i <= 7 && added < 5; i++) {
        const d = new Date(lastDate);
        d.setDate(d.getDate() + i);
        if (d.getDay() === 0) continue;
        if (d.getDay() === 6 && getDayCap(targetStaffId, toISO(d)) <= 0) continue;
        allCandidateDates.push(toISO(d));
        added++;
      }
    }

    // Use shared optimize algorithm
    const result = computeOptimizedHours({
      staffId: targetStaffId, candidateDates: allCandidateDates, totalHours: totalOriginal,
      excludeEntryIds: editEntryIds, getDayCapFn: getDayCap, entriesArr: entries, dayHoursMap: dayHoursByStaff,
    });

    const expandedDates = allCandidateDates.filter(iso => (result.dailyHours[iso] || 0) > 0);
    setDropPreview(prev => ({ ...prev, mode: 'optimize', dailyHours: result.dailyHours, newDates: expandedDates, warnings: result.warnings }));
  }, [dropPreview, getDayCap, dayHoursByStaff, entries, dropOverflowStart, dropOverflowEnd]);

  // Re-run optimize when overflow flags change
  useEffect(() => {
    if (dropPreview?.mode === 'optimize') handleDropOptimize();
  }, [dropOverflowStart, dropOverflowEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drop on job row (Job View: reassign staff's entries to a different job) ──
  const handleDropJob = useCallback(async (targetJobId) => {
    if (!dragging || dragging.jobId === targetJobId) {
      setDragging(null);
      return;
    }
    const movedBlock = dragging;
    setDragging(null);
    showToast('Reassigning to new job...', 'info');

    try {
      const batchResult = await getBatchId();
      const batchId = batchResult.batchId;

      // One atomic bulkMove instead of a delete+upsert pair per day. The old loop cost
      // 2N HTTP requests (~7 DB round trips each) and fired N Outlook reconciles, and it
      // recreated every row, so EntryIds churned and the audit read as DELETE+INSERT
      // pairs rather than a job change. Moving with newJobId preserves EntryIds, records
      // JOB_CHANGE, and matches the edit modal's own job-change path.
      //
      // Resolve overlaps against the DESTINATION (staff, targetJob) first: a reassign
      // keeps its date, so any day the destination job already occupies would otherwise
      // collide on the uniqueness index. Same treatment as the modal path.
      const oldById = {};
      for (const d of movedBlock.dates) oldById[d.entryId] = { iso: toISO(d.date), hours: d.hours };
      const resolved = resolveOverlapMerges(
        { moves: movedBlock.dates.map(d => ({ entryId: d.entryId, newJobId: targetJobId })), inserts: [], deletes: [] },
        movedBlock.staffId, targetJobId, movedBlock.dates.map(d => d.entryId), oldById
      );

      const jobDropRes = await bulkMove({
        // Source job, matching the modal reassign call — the audit row's NewJobId still
        // records the destination.
        jobId: movedBlock.jobId,
        staffId: movedBlock.staffId,
        batchId, source: 'job-reassign', movedBy: username,
        moves: resolved.moves, deletes: resolved.deletes, inserts: resolved.inserts,
      });
      showToast(`Staff reassigned to new job${mergeNote(jobDropRes)}`, 'success');
      await refreshEntries();
      refreshNotifications?.();
    } catch (e) {
      showToast('Reassignment failed — reverting', 'error');
      await refreshEntries();
    }
  }, [dragging, refreshEntries, showToast, username]);

  // ── Drop onto a specific day-cell in a Jobs-view row ───────────────────
  // Same job → pure day-shift (job preserved); different job → reassign + shift.
  // Both route through the People drop pipeline (preview/optimize modal). Placeholder/
  // custom/global blocks aren't day-shiftable here.
  const handleDropJobCell = useCallback((targetJobId, targetDayIdx) => {
    if (!dragging) { setDragging(null); return; }
    if (dragging.isPlaceholder || dragging.isCustom || dragging.isGlobal || !dragging.jobId) { setDragging(null); return; }
    const sameJob = dragging.jobId === targetJobId;
    handleDrop(dragging.staffId, targetDayIdx, sameJob ? undefined : targetJobId);
  }, [dragging, handleDrop]);

  // ── Remove entry ──────────────────────────────────────────────────────
  const handleRemoveEntry = useCallback(async (block) => {
    if (!block?.dates?.length) return;
    showToast('Removing entries...', 'info');
    try {
      // One batched call. Promise.all still fired N HTTP requests (4 DB round trips each)
      // and N Outlook reconciles for what is a single user action; the reconciles are the
      // expensive part, since each one is a full calendar reconcile.
      await bulkDeleteEntries(block.dates.map(d => d.entryId).filter(Boolean), { deletedBy: username });
      // Send single removal notification (not per-entry).
      // Only fire for real staff-job assignments — placeholders, custom jobs,
      // and global assignments have no staffId/jobId to notify against.
      if (block.staffId && block.jobId) {
        notifyRemoval(block.staffId, block.jobId, username).catch(() => {});
      }
      setSelectedBlock(null);
      showToast('Entries removed', 'success');
      await refreshEntries();
      refreshNotifications?.();
    } catch (e) {
      showToast('Remove failed: ' + e.message, 'error');
    }
  }, [refreshEntries, showToast, username]);

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading scheduler...</div></div>;
  }
  if (error) {
    return <div className="sched-placeholder"><h2>Error</h2><p style={{ color: '#DC2626' }}>{error}</p></div>;
  }

  return (
    <div className="sched-grid-root">
      {/* ── Toolbar ── */}
      <div className="sched-grid-toolbar">
        {/* People / Jobs view toggle — leftmost: it switches the entire view */}
        <div className="sched-grid-viewtoggle">
          {[{ id: 'people', label: 'People' }, { id: 'jobs', label: 'Jobs' }].map(v => (
            <button key={v.id}
              className={`sched-grid-viewtoggle-btn${viewMode === v.id ? ' sched-grid-viewtoggle-btn--active' : ''}`}
              onClick={() => {
                if (v.id === viewMode) return;
                setViewMode(v.id);
                setSelectedBlock(null);
                setJobParam(null);
                if (v.id === 'jobs') {
                  // Dept carries over from the view you were in; the FIRST Jobs entry gets a
                  // one-time default landing — leads on jobs they lead (All + the composable
                  // "Me"), others on jobs they're scheduled on.
                  let nextDept = deptFilter, nextLead = jobLead;
                  if (!jobsDefaultedRef.current) {
                    jobsDefaultedRef.current = true;
                    const iLead = !!myStaffId && allJobs.some(j => j.ManagerId === myStaffId || j.PartnerId === myStaffId);
                    if (iLead) { nextDept = ['all']; nextLead = String(myStaffId); setJobLead(nextLead); }
                    else { nextDept = ['mine']; }
                    setDeptFilter(nextDept);
                  }
                  // Retain "Scheduled only" across view switches; only force it on when the
                  // jobs view would otherwise be unbounded (All depts, no lead/staff narrowing).
                  if (nextDept.length === 1 && nextDept[0] === 'all' && !nextLead && !staffFilter.length) {
                    setHideEmpty(true);
                  }
                }
                // → People: deptFilter carries over unchanged.
              }}>
              {v.label}
            </button>
          ))}
        </div>

        <div className="sched-grid-toolbar-divider" />

        {/* Dept tabs — 3 rows: My Schedule/All on top, then department groups 4-per-row */}
        <div className="sched-grid-dept-tabs">
          <div className="sched-grid-dept-tabs-row">
            {[
              { id: 'mine', label: 'My Schedule', color: BRAND.oceanBlue },
              { id: 'all', label: 'All' },
              // Jobs view only — swaps the row set to firm-internal custom jobs (EBP Team,
              // Tax Review, Training…). Inert in People view, where custom work already
              // shows on each staffer's own row.
              ...(viewMode === 'jobs' ? [{ id: 'custom', label: 'Custom', color: BRAND.deepTeal }] : []),
              // Job-only dept groups (AI) sit here too: Jobs view only, since no staffer's home
              // department is AI. Unlike Custom they are a normal dept toggle, not a scope.
              ...(viewMode === 'jobs' ? DEPT_GROUPS.filter(g => g.jobOnly) : []),
              // "Jobs I lead" is now the composable "Me" toggle in the JM/JP picklist, not a dept scope.
            ].map(g => (
              <button
                key={g.id}
                className={`sched-grid-dept-tab${deptFilter.includes(g.id) ? ' sched-grid-dept-tab--active' : ''}`}
                style={deptFilter.includes(g.id) ? { background: g.color || BRAND.deepTeal, color: '#fff' } : {}}
                onClick={() => selectDeptScope(g.id)}
              >
                {g.label}
              </button>
            ))}
          </div>
          {/* Steering + Admin are hidden from the dept tabs for now (still used elsewhere
              for coloring — DEPT_GROUPS itself is unchanged). */}
          <div className="sched-grid-dept-tabs-grid">
            {STAFF_DEPT_GROUPS.filter(g => g.id !== 'STEERING' && g.id !== 'ADMIN').map(g => (
              <button
                key={g.id}
                className={`sched-grid-dept-tab${deptFilter.includes(g.id) ? ' sched-grid-dept-tab--active' : ''}`}
                style={deptFilter.includes(g.id) ? { background: g.color || BRAND.deepTeal, color: '#fff' } : {}}
                onClick={() => selectDeptScope(g.id)}
              >
                {g.label?.split(' ')[0] || g.id}
              </button>
            ))}
          </div>
        </div>

        <div className="sched-grid-toolbar-divider" />

        {/* Filter Staff group — saved-group picker (button) stacked on the free-text search */}
        <div className="sched-grid-filter-group">
          <button className="sched-grid-filter-btn" onClick={() => {
            // Nothing to seed: the modal is conditionally rendered, so the panel
            // mounts fresh from staffFilter and its search box / name field start
            // empty for free.
            setShowFilterModal(true);
          }}>Filter Staff Groups</button>
          {/* Free-text name filter (?q). Isolated into StaffSearchInput so keystrokes
              re-render only the input, not the whole grid; it debounces the committed
              value up into ?q (which drives the re-filter). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <StaffSearchInput value={staffSearch} onChange={setStaffSearch} />
            {/* Explicit staff selection from the Filter Staff modal (?staff=ids). */}
            {staffFilter.length > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#EFF6FF', color: '#1D4ED8', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                {staffFilter.length} staff
                <button style={{ background: 'none', border: 'none', color: '#1D4ED8', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}
                  onClick={() => { setStaffFilter([]); setHighlightStaffIds(new Set()); }}
                  title="Clear filter"
                >&times;</button>
              </span>
            )}
          </div>
        </div>

        {/* Client filters — saved client-group picker (button) stacked on the free-text
            search, mirroring the staff group above. ?client is the id-list filter,
            ?cq the free-text one; both compose with the staff search + dept/office
            scope in both views (People hides non-matching blocks + empty rows, Jobs
            filters job rows). */}
        <div className="sched-grid-filter-group">
          <button
            className={`sched-grid-filter-btn${clientGroupActive ? ' sched-grid-filter-btn--active' : ''}`}
            onClick={() => setShowClientFilterModal(true)}
            title="Pick a saved client group, or build one from specific clients"
          >Filter Client Groups</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ClientSearchInput value={clientSearch} onChange={setClientSearch} />
            {clientFilter.length > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#EFF6FF', color: '#1D4ED8', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                {clientFilter.length} client{clientFilter.length === 1 ? '' : 's'}
                <button style={{ background: 'none', border: 'none', color: '#1D4ED8', cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}
                  onClick={() => setClientFilter([])}
                  title="Clear client group filter"
                >&times;</button>
              </span>
            )}
          </div>
        </div>

        {/* More filters (suggestion #17) — return type + prep budget. Behind a popover
            because the toolbar already wraps; anything ACTIVE surfaces as a chip beside
            the button so a narrowed grid is never silently narrowed. */}
        <div className="sched-grid-morefilters-wrap" ref={moreFiltersRef}>
          <button
            className={`sched-grid-filter-btn${moreFiltersActive ? ' sched-grid-filter-btn--active' : ''}`}
            onClick={() => setShowMoreFilters(o => !o)}
            title="Filter jobs by return type or budgeted prep hours"
          >
            More filters{moreFiltersActive ? ` (${returnTypes.length + (minPrepHours > 0 ? 1 : 0)})` : ''} &#9662;
          </button>
          {moreFiltersActive && (
            <div className="sched-grid-morefilters-chips">
              {returnTypes.map(rt => (
                <span key={rt} className="sched-grid-morefilters-chip">
                  {rt === RETURN_TYPE_OTHER ? 'Non-return' : rt}
                  <button onClick={() => setReturnTypes(returnTypes.filter(x => x !== rt))}
                    title={`Remove ${rt} filter`}>&times;</button>
                </span>
              ))}
              {minPrepHours > 0 && (
                <span className="sched-grid-morefilters-chip">
                  Prep &ge; {minPrepHours}h
                  <button onClick={() => setMinPrepHours(0)} title="Remove prep-hours filter">&times;</button>
                </span>
              )}
            </div>
          )}
          {showMoreFilters && (
            <div className="sched-grid-morefilters-panel">
              <div className="sched-grid-morefilters-title">Return type</div>
              <div className="sched-grid-morefilters-grid">
                {[...RETURN_TYPES.map(t => ({ id: t.id, label: t.label })),
                  { id: RETURN_TYPE_OTHER, label: 'Non-return' }].map(t => {
                  const on = returnTypes.includes(t.id);
                  return (
                    <button key={t.id}
                      className={`sched-grid-morefilters-opt${on ? ' sched-grid-morefilters-opt--on' : ''}`}
                      onClick={() => setReturnTypes(on
                        ? returnTypes.filter(x => x !== t.id)
                        : [...returnTypes, t.id])}
                      title={t.id === RETURN_TYPE_OTHER
                        ? 'Audits, financial statements, valuations — anything with no form number'
                        : `${t.label} returns`}>
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="sched-grid-morefilters-title" style={{ marginTop: 10 }}>Prep budget</div>
              <label className="sched-grid-morefilters-prep"
                title="Budgeted Preparation hours in PE (Report Preparation on audit jobs). Finds work that should be scheduled but might not be.">
                <span>At least</span>
                <input type="number" min="0" step="1" value={minPrepHours || ''}
                  placeholder="15"
                  onChange={e => {
                    const n = parseFloat(e.target.value);
                    setMinPrepHours(Number.isFinite(n) && n > 0 ? n : 0);
                  }} />
                <span>hrs</span>
              </label>

              <div className="sched-grid-morefilters-actions">
                <button onClick={() => { setReturnTypes([]); setMinPrepHours(0); }}
                  disabled={!moreFiltersActive}>Clear all</button>
                <button className="sched-grid-morefilters-done"
                  onClick={() => setShowMoreFilters(false)}>Done</button>
              </div>
            </div>
          )}
        </div>

        <div className="sched-grid-toolbar-divider" />

        {/* Selectors: Scheduled/Free · Mgr & below · Offices / JM-JP */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontSize: 10, lineHeight: 1.2, userSelect: 'none' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#6B7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={hideEmpty} onChange={e => { setHideEmpty(e.target.checked); if (e.target.checked) setShowFreeOnly(false); }} style={{ width: 12, height: 12 }} />
              Scheduled only
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#6B7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={showFreeOnly} onChange={e => { setShowFreeOnly(e.target.checked); if (e.target.checked) setHideEmpty(false); }} style={{ width: 12, height: 12 }} />
              Free only
            </label>
          </div>
          {/* Mgr & below is People-only (filters the staff list); inert in Jobs view */}
          {viewMode !== 'jobs' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6B7280', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
              title="Hide Members, Principals, and Senior Managers">
              <input type="checkbox" checked={hideLeadership && !deptFilter.includes('mine')} onChange={e => setHideLeadership(e.target.checked)} disabled={deptFilter.includes('mine')} />
              Mgr &amp; below
            </label>
          )}
          {/* Offices + (Jobs view) JM/JP filter stacked vertically to save toolbar width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <select value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}
              style={{ fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid #E5E7EB', color: '#374151', background: '#fff' }}>
              <option value="all">All Offices</option>
              <option value="BHM">Birmingham</option>
              <option value="HSV">Huntsville</option>
              <option value="GAD">Gadsden</option>
              <option value="RGD">Ridgeland</option>
              <option value="MOB">Mobile</option>
              <option value="FHP">Fairhope</option>
            </select>
            {viewMode === 'jobs' && (
              <span className="sched-grid-lead-filter">
                {myStaffId && (
                  <button
                    className={`sched-grid-zoom-btn${jobLead === String(myStaffId) ? ' sched-grid-zoom-btn--active' : ''}`}
                    style={{ fontSize: 10, padding: '2px 6px', marginRight: 3 }}
                    title="Jobs I lead (JM/JP) — combines with the department filter"
                    onClick={() => {
                      const next = jobLead === String(myStaffId) ? '' : String(myStaffId);
                      setJobLead(next);
                      // Turning "Me" off while at All depts would render the full firm list — guard it.
                      if (!next && deptFilter.length === 1 && deptFilter[0] === 'all') setHideEmpty(true);
                    }}>Me</button>
                )}
                <select value={jobLead} onChange={e => setJobLead(e.target.value)}
                  title="Filter jobs by Job Manager / Partner"
                  style={{ fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid #E5E7EB', color: '#374151', background: '#fff', maxWidth: 170 }}>
                  <option value="">All JM / JP</option>
                  {jobLeadOptions.map(o => (
                    <option key={o.id} value={String(o.id)}>
                      {o.name}{o.isJM && o.isJP ? ' (JM/JP)' : o.isJM ? ' (JM)' : ' (JP)'}
                    </option>
                  ))}
                </select>
                {jobLead && (
                  <span className="sched-grid-zoom" style={{ marginLeft: 1 }}>
                    {[['both', 'Both'], ['jm', 'JM'], ['jp', 'JP']].map(([id, label]) => (
                      <button key={id}
                        className={`sched-grid-zoom-btn${jobLeadRole === id ? ' sched-grid-zoom-btn--active' : ''}`}
                        style={{ fontSize: 10, padding: '2px 6px' }}
                        onClick={() => setJobLeadRole(id)}>{label}</button>
                    ))}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* Color legend */}
        <div className="sched-grid-legend-wrap" data-tour="grid-legend">
          <button className={`sched-grid-zoom-btn${showLegend ? ' sched-grid-zoom-btn--active' : ''}`}
            onClick={() => setShowLegend(s => !s)} title="Color key">
            Legend
          </button>
          {showLegend && (
            <div className="sched-grid-legend" onMouseLeave={() => setShowLegend(false)}>
              {/* Department + seniority */}
              <div className="sched-grid-legend-section">
                <div className="sched-grid-legend-title">Department <span>(staff &amp; jobs)</span></div>
                <div className="sched-grid-legend-grid">
                  {DEPT_GROUPS.map(g => (
                    <span key={g.id} className="sched-grid-legend-item">
                      <span className="sched-grid-legend-chip" style={{ background: g.color }} />{g.label}
                    </span>
                  ))}
                </div>
                <div className="sched-grid-legend-note">Staff show a seniority rank meter (more / taller bars = more senior):</div>
                <div className="sched-grid-legend-grid sched-grid-legend-grid--levels">
                  {LEVEL_BANDS.map(b => (
                    <span key={b.band} className="sched-grid-legend-item">
                      <SeniorityBars band={b.band} color="#374151" />
                      <span>{b.label} <span style={{ color: '#9CA3AF' }}>({b.levels.join(', ')})</span></span>
                    </span>
                  ))}
                </div>
                <div className="sched-grid-legend-note">A staff block matching a job&apos;s dept pill = in that department.</div>
              </div>
              {/* Service line / work */}
              <div className="sched-grid-legend-section">
                <div className="sched-grid-legend-title">Service line <span>(the work)</span></div>
                <div className="sched-grid-legend-grid">
                  {Object.keys(SERVICE_TO_DEPT).map(svc => (
                    <span key={svc} className="sched-grid-legend-item">
                      <span className="sched-grid-legend-chip" style={{ background: (JOB_TYPE_COLORS[svc] || {}).bg || '#6B7280' }} />{svc}
                    </span>
                  ))}
                </div>
              </div>
              {/* Status & days */}
              <div className="sched-grid-legend-section">
                <div className="sched-grid-legend-title">Status &amp; days</div>
                <div className="sched-grid-legend-grid">
                  {[
                    { c: '#F59E0B', t: 'FINANCIAL (stop work)' },
                    { c: '#DC2626', t: 'LOST client' },
                    { c: '#94a3b8', t: 'PTO / leave' },
                    { c: '#7C3AED', t: 'Holiday' },
                    { c: '#EF4444', t: 'Over capacity' },
                    { c: '#475569', t: 'Custom / internal (✦)' },
                    { c: '#DBE7EE', t: 'Placeholder (unassigned)' },
                  ].map(s => (
                    <span key={s.t} className="sched-grid-legend-item">
                      <span className="sched-grid-legend-chip" style={{ background: s.c }} />{s.t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Zoom */}
        <div className="sched-grid-zoom">
          {[{ w: 1, label: '1 Wk' }, { w: 5, label: '5 Wks' }].map(z => (
            <button
              key={z.w}
              className={`sched-grid-zoom-btn${zoom === z.w ? ' sched-grid-zoom-btn--active' : ''}`}
              onClick={() => setZoom(z.w)}
            >
              {z.label}
            </button>
          ))}
          <select
            value={zoom}
            onChange={e => setZoom(parseInt(e.target.value, 10))}
            style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid #D1D5DB', color: '#374151', cursor: 'pointer' }}
            title="Custom week range"
          >
            {[...new Set([1, 2, 3, 4, 5, 6, 8, 10, 12, 26, 52, zoom])].sort((a, b) => a - b).map(w => (
              <option key={w} value={w}>{w} wk{w > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>

        {/* Week nav. The date input is the "jump" control (suggestion #22): reaching a
            week months out was one ‹ / › click per week. Stacked ABOVE the arrows
            rather than beside them — inline it widened the toolbar enough to wrap
            "+ Schedule" onto a second row, and the nav column is dead space anyway.
            Picking any DAY snaps to that day's Monday (the grid is week-anchored, so
            landing mid-week is never what you want); the value is the visible start
            Monday, so it doubles as a position indicator while you click the arrows. */}
        <div className="sched-grid-nav">
          <WeekJumpPicker value={toISO(baseMonday)} onCommit={jumpToWeek} />
          <div className="sched-grid-nav-row">
            <button onClick={() => setWeekOffset(w => w - 1)} title="Go back one week"
              aria-label="Previous week">&lsaquo;</button>
            <button onClick={() => setWeekOffset(0)} className="sched-grid-today-btn"
              title="Jump back to the current week">Today</button>
            <button onClick={() => setWeekOffset(w => w + 1)} title="Go forward one week"
              aria-label="Next week">&rsaquo;</button>
          </div>
        </div>

        {/* + Schedule — start-from-scratch button; no staff prepop */}
        <button className="sched-assign-btn" onClick={() => openNewModal(null, null)}>
          + Schedule
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="sched-tabs">
        <button
          className={`sched-tab ${tab === 'calendar' ? 'sched-tab--active' : ''}`}
          onClick={() => setTab('calendar')}
        >
          Calendar View
        </button>
        <button
          className={`sched-tab ${tab === 'alerts' ? 'sched-tab--active' : ''}`}
          onClick={() => setTab('alerts')}
        >
          Conflicts
          {conflicts.length > 0 && (
            <span className="sched-tab-badge sched-tab-badge--red">{conflicts.length}</span>
          )}
        </button>
      </div>

      {/* ── Alerts Tab ── */}
      {tab === 'alerts' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ display: 'flex', gap: 20, maxWidth: conflictSelectedJob ? 1400 : 1000, margin: '0 auto' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>Scheduling Conflicts</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 16 }}>
              Staff members overbooked beyond their weekly capacity.
            </div>
            {conflicts.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>&#9989;</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>All clear &mdash; no conflicts detected.</div>
              </div>
            ) : (
              conflicts.map(c => {
                const isFixOpen = fixConflict?.staffId === c.staffId && fixConflict?.weekIdx === c.weekIdx;
                return (
                  <div key={`${c.staffId}-${c.weekIdx}`}
                    data-conflict-key={`${c.staffId}-${weeks[c.weekIdx]?.days?.[0] ? toISO(weeks[c.weekIdx].days[0].date) : c.weekIdx}`}>
                    <div className="sched-alert" style={{ border: `1px solid ${isFixOpen ? '#356371' : c.hasGlobalAssignment ? '#A5F3FC' : c.hasHoliday ? '#DDD6FE' : c.hasPTO ? '#FDE68A' : '#FECACA'}`, borderRadius: 10 }}>
                      <div className="sched-alert-severity" style={{ background: c.hasGlobalAssignment ? '#0891B2' : c.hasHoliday ? '#7C3AED' : c.hasPTO ? '#F59E0B' : '#EF4444' }} />
                      <span className="sched-alert-icon">{c.hasGlobalAssignment ? '\uD83D\uDCC5' : c.hasHoliday ? '\uD83C\uDF84' : c.hasPTO ? '\uD83C\uDFD6\uFE0F' : '\u26A0\uFE0F'}</span>
                      <div className="sched-alert-body">
                        <div className="sched-alert-title">
                          {c.staffName} &mdash; {c.level}
                        </div>
                        <div className="sched-alert-text">
                          {c.hasDailyViolation ? (
                            <><strong>{Math.round(c.violatingHours * 100) / 100}h</strong> scheduled on {c.violatingDays.length} day{c.violatingDays.length > 1 ? 's' : ''} exceeding daily capacity during {c.weekLabel}{c.hasPTO ? ` (${c.ptoHours}h PTO)` : ''}{c.hasHoliday ? ` (${c.holidayNames.join(', ')})` : ''}{c.hasGlobalAssignment ? ` (${c.globalAssignmentNames.join(', ')})` : ''}</>
                          ) : c.hasGlobalAssignment && !c.hasPTO && !c.hasHoliday ? (
                            <>Has <strong>{c.globalAssignmentNames.join(', ')}</strong> during {c.weekLabel} &mdash; {c.scheduled}h still scheduled ({c.capacity}h available capacity)</>
                          ) : c.hasHoliday && !c.hasPTO ? (
                            <>Has work scheduled on <strong>{c.holidayNames.join(', ')}</strong> during {c.weekLabel} &mdash; {c.scheduled}h scheduled ({c.capacity}h available capacity){c.hasGlobalAssignment ? <> + <strong>{c.globalAssignmentNames.join(', ')}</strong></> : ''}</>
                          ) : c.hasPTO ? (
                            <>Has <strong>{c.ptoHours}h PTO</strong> during {c.weekLabel} &mdash; {c.scheduled}h still scheduled ({c.capacity}h available capacity){c.hasHoliday ? <> + <strong>{c.holidayNames.join(', ')}</strong></> : ''}{c.hasGlobalAssignment ? <> + <strong>{c.globalAssignmentNames.join(', ')}</strong></> : ''}</>
                          ) : (
                            <>Overbooked by <strong>{c.overage}h</strong> during {c.weekLabel} ({c.scheduled}h scheduled / {c.capacity}h capacity){c.hasGlobalAssignment ? <> ({c.globalAssignmentNames.join(', ')})</> : ''}</>
                          )}
                        </div>
                        {c.hasGlobalAssignment && (
                          <div style={{ fontSize: 10, color: '#0891B2', fontWeight: 600, marginTop: 4 }}>
                            &#9889; Firm-wide event &mdash; optimize to shift hours off this day
                          </div>
                        )}
                        {(c.hasDailyViolation && c.overage === 0) || (c.hasPTO && c.overage === 0) ? (
                          <div style={{ fontSize: 10, color: '#059669', fontWeight: 600, marginTop: 4 }}>
                            &#9889; Optimize Schedule will redistribute daily hours to fix this
                          </div>
                        ) : null}
                      </div>
                      <div className="sched-alert-actions" style={{ display: 'flex', gap: 6 }}>
                        <button className="sched-alert-btn" onClick={() => scrollToStaff(c.staffId, c.weekIdx)}>View in Grid</button>
                        <button className="sched-alert-btn" style={{ background: BRAND.brightGreen, color: BRAND.deepTeal }}
                          onClick={() => {
                            const weekDays = allDays.slice(c.weekIdx * 7, (c.weekIdx + 1) * 7);
                            const weekIsos = new Set(weekDays.map(d => d.iso));
                            const staffBlocks = (blocksByPerson[c.staffId] || [])
                              .filter(b => !b.isGlobal && b.dates.some(d => weekIsos.has(typeof d.date === 'string' ? d.date.slice(0, 10) : toISO(d.date))))
                              .sort((a, b) => a.dates.length - b.dates.length); // shortest blocks first
                            if (staffBlocks.length > 0) {
                              // Always show job picker so user sees job info before choosing
                              const isOpen = optimizeConflict?.staffId === c.staffId && optimizeConflict?.weekIdx === c.weekIdx;
                              setOptimizeConflict(isOpen ? null : { staffId: c.staffId, weekIdx: c.weekIdx, blocks: staffBlocks });
                              if (isOpen) setConflictSelectedJob(null);
                            }
                          }}>
                          {optimizeConflict?.staffId === c.staffId && optimizeConflict?.weekIdx === c.weekIdx ? 'Close' : 'Optimize Schedule'}
                        </button>
                        <button className="sched-alert-btn" style={{
                            background: isFixOpen ? BRAND.deepTeal : '#356371', color: '#fff',
                            opacity: (!isFixOpen && (c.overage === 0 || c.hasGlobalAssignment)) ? 0.5 : 1,
                          }}
                          title={c.hasGlobalAssignment ? 'All staff have this event \u2014 try optimizing to shift hours off this day' : c.overage === 0 ? 'Optimization recommended for daily capacity issues' : undefined}
                          onClick={() => handleSuggestFix(c)}>
                          {isFixOpen ? 'Close' : 'Suggest Reassignment'}
                        </button>
                      </div>
                    </div>

                    {/* Inline optimize job picker */}
                    {optimizeConflict?.staffId === c.staffId && optimizeConflict?.weekIdx === c.weekIdx && (
                      <div className="sched-expand-enter" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 16, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                          Select a job to optimize ({optimizeConflict.blocks.length} jobs this week)
                        </div>
                        {optimizeConflict.blocks.map(b => {
                          const weekDays = allDays.slice(c.weekIdx * 7, (c.weekIdx + 1) * 7);
                          const weekIsos = new Set(weekDays.map(d => d.iso));
                          const weekEntries = b.dates.filter(d => weekIsos.has(typeof d.date === 'string' ? d.date.slice(0, 10) : toISO(d.date)));
                          const weekHours = weekEntries.reduce((s, d) => s + (d.hours || 0), 0);
                          const isSelected = conflictSelectedJob?.jobId === b.jobId;
                          const bi = getBlockInfo(c.staffId, b.jobId);
                          const nonStandaloneBlocks = optimizeConflict.blocks.filter(bl => { const bii = getBlockInfo(c.staffId, bl.jobId); return !bii.isStandalone; });
                          const isShortest = !bi.isStandalone && nonStandaloneBlocks.length > 1 && nonStandaloneBlocks[0].jobId === b.jobId;
                          return (
                            <div key={b.jobId}
                              onClick={() => {
                                const jobObj = { jobId: b.jobId, jobName: b.jobName, clientName: b.clientName, staffName: c.staffName, weekEntries };
                                setConflictSelectedJob(isSelected ? null : jobObj);
                                if (!isSelected) {
                                  fetchJobsWIP({ jobId: b.jobId }).then(d => setConflictJobWip(d || [])).catch(() => setConflictJobWip([]));
                                  handleSelectFixJob(b.jobId);
                                } else {
                                  setFixSelectedJob(null); setFixCandidates([]);
                                }
                              }}
                              style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 4, cursor: 'pointer', border: `1.5px solid ${isSelected ? BRAND.brightGreen : '#E5E7EB'}`, background: isSelected ? '#F0FDF4' : '#fff', transition: 'border-color 0.15s' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{b.clientName}</div>
                                  <div style={{ fontSize: 11, color: '#6B7280' }}>{b.jobName} &middot; {b.serviceDesc || b.jobType}</div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.deepTeal, marginTop: 2 }}>{Math.round(weekHours * 100) / 100}h this week</div>
                                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                                    {bi.isStandalone
                                      ? 'Standalone block (this week only)'
                                      : `Part of block: ${fmtBlockDate(bi.startISO)} \u2013 ${fmtBlockDate(bi.endISO)} (${bi.totalHours}h, ${bi.weekCount} wk${bi.weekCount > 1 ? 's' : ''})`}
                                  </div>
                                  {bi.isStandalone && (
                                    <div style={{ fontSize: 9, color: '#059669', fontWeight: 600, marginTop: 2 }}>&#10003; Standalone &mdash; easiest to adjust</div>
                                  )}
                                  {isShortest && (
                                    <div style={{ fontSize: 9, color: '#3B82F6', fontWeight: 600, marginTop: 2 }}>Shortest block &mdash; easier to reschedule than longer blocks</div>
                                  )}
                                </div>
                                {isSelected && (
                                  <button className="sched-alert-btn" style={{ background: BRAND.brightGreen, color: BRAND.deepTeal, fontSize: 10, padding: '4px 8px', whiteSpace: 'nowrap' }}
                                    onClick={(e) => { e.stopPropagation(); setOptimizeConflict(null); setConflictSelectedJob(null); setSelectedBlock(b); scrollToStaff(c.staffId, c.weekIdx); setTimeout(() => openOptimizeModal(b), 400); }}>
                                    Optimize &rarr;
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Inline fix panel */}
                    {isFixOpen && (
                      <div className="sched-expand-enter" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 16, marginBottom: 8 }}>
                        {fixLoading && !fixEntries.length ? (
                          <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 12 }}>Loading entries...</div>
                        ) : (
                          <div style={{ display: 'flex', gap: 20 }}>
                            {/* Left: entries to move */}
                            <div style={{ width: 280, flexShrink: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                                Entries this week ({fixEntries.reduce((s, e) => s + e.totalHours, 0)}h total)
                              </div>
                              {fixEntries.length === 0 && <div style={{ fontSize: 11, color: '#9CA3AF' }}>No entries found.</div>}
                              {[...fixEntries].sort((a, b) => {
                                const biA = getBlockInfo(fixConflict?.staffId, a.jobId);
                                const biB = getBlockInfo(fixConflict?.staffId, b.jobId);
                                return (biA.weekCount || 1) - (biB.weekCount || 1);
                              }).map((e, idx, arr) => {
                                const bi = getBlockInfo(fixConflict?.staffId, e.jobId);
                                const nonStandaloneArr = arr.filter(ae => { const abi = getBlockInfo(fixConflict?.staffId, ae.jobId); return !abi.isStandalone; });
                                const isShortest = !bi.isStandalone && nonStandaloneArr.length > 1 && nonStandaloneArr[0].jobId === e.jobId;
                                return (
                                <div key={e.jobId}
                                  onClick={() => handleSelectFixJob(e.jobId)}
                                  style={{
                                    padding: '8px 10px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                                    border: `1px solid ${fixSelectedJob === e.jobId ? BRAND.deepTeal : '#E5E7EB'}`,
                                    background: fixSelectedJob === e.jobId ? '#EFF6FF' : '#fff',
                                  }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{e.clientName}</div>
                                  <div style={{ fontSize: 11, color: '#6B7280' }}>{e.jobName} &middot; {e.serviceDesc}</div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.deepTeal, marginTop: 2 }}>{e.totalHours}h this week</div>
                                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                                    {bi.isStandalone
                                      ? 'Standalone block (this week only)'
                                      : `Part of block: ${fmtBlockDate(bi.startISO)} \u2013 ${fmtBlockDate(bi.endISO)} (${bi.totalHours}h, ${bi.weekCount} wk${bi.weekCount > 1 ? 's' : ''})`}
                                  </div>
                                  {bi.isStandalone && (
                                    <div style={{ fontSize: 9, color: '#059669', fontWeight: 600, marginTop: 2 }}>&#10003; Standalone &mdash; easiest to move</div>
                                  )}
                                  {isShortest && (
                                    <div style={{ fontSize: 9, color: '#3B82F6', fontWeight: 600, marginTop: 2 }}>Shortest block &mdash; easier to reschedule than longer blocks</div>
                                  )}
                                </div>
                                );
                              })}
                            </div>

                            {/* Right: replacement candidates */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: '#374151' }}>
                                  {fixSelectedJob ? 'Replacement Candidates' : 'Select a job to see candidates'}
                                  {fixSelectedJob && <NicheGapFlag missing={fixNicheMissing} />}
                                  {fixSelectedJob && (
                                    <OnSiteFilterNote isOnSite={fixOnSite.isOnSite}
                                      officeCode={fixOnSite.officeCode} office={fixOnSite.office} />
                                  )}
                                </div>
                                {fixSelectedJob && (
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#6B7280', cursor: 'pointer' }}
                                    title={fixExactLevel ? 'Showing exact level matches only' : 'Showing ±1 level (e.g. Senior Accountant also shows Supervisor and Staff B)'}>
                                    <input type="checkbox" checked={fixExactLevel}
                                      onChange={e => { const v = e.target.checked; setFixExactLevel(v); }}
                                      style={{ accentColor: BRAND.deepTeal }} />
                                    Exact level only
                                  </label>
                                )}
                              </div>
                              {/* Scope toggle */}
                              {fixSelectedJob && (() => {
                                const entry = fixEntries.find(e => e.jobId === fixSelectedJob);
                                const weekHrs = entry ? entry.totalHours : 0;
                                // Full contiguous block is window-INDEPENDENT (fetched on job-select), not zoom-scoped.
                                const blockLoading = fixFullBlock === null;
                                const totalHrs = fixFullBlock ? Math.round(fixFullBlock.totalHours * 100) / 100 : weekHrs;
                                const staffName = staff.find(s => s.StaffId === fixConflict?.staffId)?.Name || '';
                                const dateRange = (fixFullBlock && fixFullBlock.startISO)
                                  ? `${fixFullBlock.startISO} \u2013 ${fixFullBlock.endISO}`
                                  : '';
                                return (
                                  <div style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', gap: 2, background: '#F3F4F6', borderRadius: 5, padding: 2, marginBottom: 6 }}>
                                      <button type="button" onClick={() => setReassignScope('week')}
                                        style={{ flex: 1, padding: '4px 8px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                                          background: reassignScope === 'week' ? BRAND.deepTeal : 'transparent', color: reassignScope === 'week' ? '#fff' : '#6B7280' }}>
                                        This week ({weekHrs}h)
                                      </button>
                                      <button type="button" onClick={() => setReassignScope('all')}
                                        style={{ flex: 1, padding: '4px 8px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                                          background: reassignScope === 'all' ? BRAND.deepTeal : 'transparent', color: reassignScope === 'all' ? '#fff' : '#6B7280' }}>
                                        Entire block ({blockLoading ? '…' : `${totalHrs}h`})
                                      </button>
                                    </div>
                                    <div style={{ fontSize: 10, color: '#6B7280', lineHeight: 1.4 }}>
                                      {reassignScope === 'week'
                                        ? `Reassign ${weekHrs}h for ${entry?.jobName || ''} on ${c.weekLabel} from ${staffName}`
                                        : `Reassign ${totalHrs}h for entire block for ${entry?.jobName || ''} (${dateRange}) from ${staffName}`
                                      }
                                    </div>
                                    {reassignScope === 'week' && fixFullBlock && totalHrs > weekHrs && (
                                      <div style={{ fontSize: 10, color: '#D97706', marginTop: 4, padding: '4px 8px', background: '#FFFBEB', borderRadius: 4 }}>
                                        Note: Only this week's entries ({weekHrs}h) will be reassigned. The remaining {Math.round((totalHrs - weekHrs) * 100) / 100}h across other weeks will stay on {staffName}.
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              {fixLoading && fixSelectedJob && <div style={{ fontSize: 11, color: '#9CA3AF' }}>Loading suggestions...</div>}
                              {!fixSelectedJob && !fixLoading && (
                                <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>Click a job entry on the left to find available replacements.</div>
                              )}
                              {fixSelectedJob && !fixLoading && fixCandidates.length === 0 && (
                                <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                                  No candidates at a similar level with capacity this week
                                  {fixOnSite.isOnSite && fixOnSite.officeCode
                                    ? ` in ${fixOnSite.officeCode} (this job is on-site, so other offices are excluded)`
                                    : ''}.
                                </div>
                              )}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: `repeat(${Math.ceil(fixCandidates.length / 2)}, auto)`, gridAutoFlow: 'column', gap: 6 }}>
                              {fixCandidates.map((cand, idx) => (
                                <div key={cand.StaffId} style={{
                                  padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff',
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  opacity: cand.hasCapacity ? 1 : 0.5,
                                }}>
                                  {/* Rank icon */}
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF', minWidth: 20, textAlign: 'center' }}>
                                    {idx === 0 ? '\uD83E\uDD47' : idx === 1 ? '\uD83E\uDD48' : idx === 2 ? '\uD83E\uDD49' : `#${idx + 1}`}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cand.Name}</div>
                                    <div style={{ fontSize: 10, color: '#6B7280' }}>
                                      {cand.Level} &middot; {cand.DepartmentCode} &middot;
                                      <span style={{ color: cand.hasCapacity ? '#065F46' : '#DC2626', fontWeight: 600 }}>
                                        {' '}{cand.freeHours}h free
                                      </span>
                                    </div>
                                    {fixGenTrackFlagFor(cand)}
                                    {(() => {
                                      const ov = fixOverlapsFor(cand.StaffId);
                                      if (!ov.length) return null;
                                      return (
                                        <div style={{ fontSize: 10, color: '#92400E', marginTop: 2, lineHeight: 1.35 }}
                                          title={`${cand.Name} already has hours on this job on these days. Reassigning combines them into one entry per day.`}>
                                          Already on this job: {ov.slice(0, 3).map(o => `${o.label} ${o.existing}h → ${o.combined}h`).join(', ')}
                                          {ov.length > 3 ? ` +${ov.length - 3} more` : ''} &middot; hours will be combined
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div style={{ textAlign: 'center', minWidth: 32 }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.oceanBlue }}>{Math.round(cand.totalScore || 0)}</div>
                                    <div style={{ fontSize: 8, color: '#9CA3AF', textTransform: 'uppercase' }}>fit</div>
                                  </div>
                                  <button
                                    className="sched-alert-btn"
                                    style={{ background: cand.hasCapacity ? BRAND.brightGreen : '#F59E0B', color: cand.hasCapacity ? '#fff' : '#92400E', fontSize: 11, padding: '4px 10px' }}
                                    disabled={fixReassigning}
                                    title={!cand.hasCapacity ? 'Will exceed their capacity \u2014 optimize after reassignment' : undefined}
                                    onClick={() => handleReassignRef.current?.(cand.StaffId, fixSelectedJob)}
                                  >
                                    {fixReassigning ? '...' : 'Reassign'}
                                  </button>
                                </div>
                              ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {/* ── Job Detail Sidebar ── */}
          {conflictSelectedJob && (() => {
            const cj = conflictSelectedJob;
            const jobMeta = allJobs.find(j => j.JobId === cj.jobId);
            const util = jobBudgetUtil(cj.jobId);
            const jobBlocks = (blocksByJob[cj.jobId]?.blocks || []).sort((a, b) => b.totalHours - a.totalHours);
            // Scheduled totals come from the window-independent today-on read (conflictJobSched), not the zoom-windowed blocks.
            const schedTotalTodayOn = conflictJobSched.reduce((s, r) => s + (r.TotalHours || 0), 0);
            const schedPctTodayOn = util.budget > 0 ? Math.round((schedTotalTodayOn / util.budget) * 100) : 0;
            const typeColor = JOB_TYPE_COLORS[jobMeta?.ServiceDesc] || JOB_TYPE_COLORS[jobMeta?.JobType] || { bg: '#E5E7EB', text: '#374151' };
            // Move conflicted staff to top of list
            const conflictStaffId = fixConflict?.staffId || optimizeConflict?.staffId;
            const sortedBlocks = conflictStaffId
              ? [
                  ...jobBlocks.filter(b => b.staffId === conflictStaffId),
                  ...jobBlocks.filter(b => b.staffId !== conflictStaffId),
                ]
              : jobBlocks;
            // WIP per staff from conflictJobWip
            const wipByStaff = {};
            for (const w of conflictJobWip) wipByStaff[w.StaffId] = w.TotalHours || 0;
            return (
              <div style={{ width: 340, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                      {jobMeta?.ServiceDesc || 'General'}
                    </span>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#9CA3AF', lineHeight: 1 }}
                      onClick={() => setConflictSelectedJob(null)}>&times;</button>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                    {jobMeta?.ClientName || cj.clientName}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
                    {jobMeta?.Name || cj.jobName}{jobMeta?.ClientCode ? ` \u00B7 ${jobMeta.ClientCode}` : ''}
                  </div>

                  {/* Budget / Scheduled / WIP */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>Budget</div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{util.budget}h</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>Scheduled</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: schedPctTodayOn > 100 ? '#DC2626' : '#374151' }}>{Math.round(schedTotalTodayOn * 100) / 100}h ({schedPctTodayOn}%)</div>
                    </div>
                    {conflictJobWip.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>Total WIP</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{Math.round(conflictJobWip.reduce((s, w) => s + (w.TotalHours || 0), 0) * 100) / 100}h</div>
                    </div>
                    )}
                  </div>

                  {/* Deadline / Status */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    {jobMeta?.Deadline && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>Deadline</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{new Date(jobMeta.Deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                    )}
                    {jobMeta?.StatusDetail && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>Status</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{jobMeta.StatusDetail}</div>
                    </div>
                    )}
                  </div>

                  {/* This week for conflicted staff */}
                  {cj.weekEntries && cj.weekEntries.length > 0 && (
                    <div style={{ marginBottom: 12, padding: '8px 10px', background: '#EFF6FF', borderRadius: 6 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>This Week — {cj.staffName}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1D4ED8' }}>
                        {Math.round(cj.weekEntries.reduce((s, e) => s + (e.hours || 0), 0) * 100) / 100}h
                        <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 400, marginLeft: 6 }}>
                          ({cj.weekEntries.map(e => {
                            const raw = e.date;
                            const d = raw instanceof Date ? raw : new Date(String(raw).slice(0, 10) + 'T12:00:00');
                            const label = !isNaN(d) ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '?';
                            return `${label} ${e.hours}h`;
                          }).join(', ')})
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Staff on this job (merged: scheduled blocks + WIP) */}
                  {(() => {
                    // Build merged staff list from scheduled blocks + WIP data
                    const merged = {};
                    for (const sb of conflictJobSched) {
                      merged[sb.StaffId] = { staffId: sb.StaffId, name: sb.StaffName || `Staff #${sb.StaffId}`, initials: sb.Initials || '??', department: sb.DepartmentCode || '', level: sb.Level || '', schedHrs: sb.TotalHours || 0, wipHrs: 0 };
                    }
                    for (const w of conflictJobWip) {
                      if (merged[w.StaffId]) {
                        merged[w.StaffId].wipHrs = w.TotalHours || 0;
                      } else {
                        merged[w.StaffId] = { staffId: w.StaffId, name: w.StaffName || `Staff #${w.StaffId}`, initials: w.StaffInitials || '??', department: w.DepartmentCode || '', level: w.StaffLevel || '', schedHrs: 0, wipHrs: w.TotalHours || 0 };
                      }
                    }
                    // Sort: conflict staff first, then by WIP desc, then scheduled desc
                    const staffList = Object.values(merged).sort((a, b) => {
                      if (a.staffId === conflictStaffId) return -1;
                      if (b.staffId === conflictStaffId) return 1;
                      if (b.wipHrs !== a.wipHrs) return b.wipHrs - a.wipHrs;
                      return b.schedHrs - a.schedHrs;
                    });
                    return (
                      <>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
                          Staff on this Job ({staffList.length})
                        </div>
                        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                          {staffList.map(s => {
                            const isConflicted = s.staffId === conflictStaffId;
                            const deptColor = staffColor(s.department);
                            return (
                              <div key={s.staffId} style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #F3F4F6', background: isConflicted ? '#EFF6FF' : 'transparent', borderRadius: isConflicted ? 4 : 0, padding: isConflicted ? '6px 6px' : '6px 0' }}>
                                <div className="sched-grid-avatar" style={{ width: 24, height: 24, fontSize: 8, background: deptColor, flexShrink: 0 }}>{s.initials}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: '#111827' }}>
                                    {s.name}
                                    <SeniorityBars level={s.level} color={deptColor} />
                                    {isConflicted && <span style={{ fontSize: 9, color: '#3B82F6', marginLeft: 3 }}>(conflict)</span>}
                                  </div>
                                  <div style={{ fontSize: 10, color: '#6B7280' }}>
                                    {s.schedHrs > 0 ? `${Math.round(s.schedHrs * 100) / 100}h sched` : ''}{s.schedHrs > 0 && s.wipHrs > 0 ? ' \u00B7 ' : ''}{s.wipHrs > 0 ? `${Math.round(s.wipHrs * 100) / 100}h WIP` : ''}{s.schedHrs === 0 && s.wipHrs === 0 ? '\u2014' : ''}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}

                  {/* PE link */}
                  {jobMeta?.PEJobIndex && (
                    <button className="sched-grid-action-btn sched-grid-action-pe" style={{ marginTop: 10, width: '100%', fontSize: 11 }}
                      onClick={() => window.open(`https://bmss.pehosted.com/PE/Job/Job/${jobMeta.PEJobIndex}?panel=0`, '_blank')}>
                      &#8599; View Job in PE
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          </div>
        </div>
      )}

      {/* ── Grid Area ── */}
      {tab === 'calendar' && <div className="sched-grid-container">
        {/* Left: staff labels */}
        <div className="sched-grid-left" style={viewMode === 'jobs' ? { width: 280, minWidth: 280 } : undefined}>
          {/* Corner */}
          <div className="sched-grid-corner">
            <span className="sched-grid-corner-label">{viewMode === 'people' ? 'Staff Member' : customScope ? 'Custom Job' : 'Client / Job'}</span>
          </div>
          {/* Left body rows */}
          <div className="sched-grid-left-body" ref={leftRef}>
            {viewMode === 'people' ? (
              /* People View: staff rows */
              visibleStaff.map(person => {
                // Item 1 — placeholder sidebar row: thin, italic, no avatar/utilization.
                if (person.isPlaceholder) {
                  // Row height scales with the max stack track so overlapping
                  // placeholders never visually collide. Same algorithm regular
                  // staff rows use; 56 is the floor to fit the title text.
                  const PH_ROW_H = Math.max(56, BLOCK_TOP + (person.phMaxTrack || 1) * (BLOCK_H + BLOCK_GAP) + 8);
                  const deptColor = staffColor(person.DepartmentCode);
                  const phCount = (person.placeholders || []).length;
                  return (
                    <div key={person.StaffId}
                      className="sched-grid-label-row sched-grid-label-row--placeholder"
                      style={{ height: PH_ROW_H }}>
                      <div className="sched-grid-dept-bar" style={{ background: deptColor }} />
                      <div className="sched-grid-person-info" style={{ paddingLeft: 6 }}>
                        <div className="sched-grid-placeholder-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{person.Level} · {person.Department || person.DepartmentCode}</span>
                          <SeniorityBars level={person.Level} color={deptColor} />
                        </div>
                        <div style={{ fontSize: 10, color: '#6B7280' }}>
                          {phCount > 0
                            ? `${phCount} placeholder${phCount === 1 ? '' : 's'}`
                            : 'Click row to add'}
                        </div>
                      </div>
                    </div>
                  );
                }

                const uPct = overallUtil(person.StaffId);
                const deptColor = staffColor(person.DepartmentCode);
                const hasBlocks = (blocksByPerson[person.StaffId] || []).length > 0;
                // Departed staff still holding scheduled work — amber + ⚠, matching the
                // convention in ClientView's PY-team list. Red stays reserved for
                // conflicts / over-capacity on this grid.
                const isInactive = !!person.ScheduledInactive;
                const inactiveNote = isInactive ? inactiveNoteFor(person.StaffId) : '';
                return (
                  <div key={person.StaffId}
                    className={`sched-grid-label-row${conflictStaffIds.has(person.StaffId) ? ' sched-grid-label--conflict' : ''}`}
                    style={{ height: rowHeight(person.StaffId) }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={() => handleDrop(person.StaffId, null)}>
                    <div className="sched-grid-dept-bar" style={{ background: deptColor }} />
                    <div className="sched-grid-avatar" style={{ background: deptColor }}>{person.Initials || '??'}</div>
                    <div className="sched-grid-person-info">
                      <div className="sched-grid-person-name-row">
                        <div className={`sched-grid-person-name sched-grid-person-link${isInactive ? ' sched-grid-person-name--inactive' : ''}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${person.StaffId}`); }}
                          title={isInactive ? inactiveNote : 'View employee profile'}>
                          {isInactive && <span className="sched-grid-person-inactive-icon">&#9888;</span>}
                          {person.Name || ''}
                        </div>
                        {isInactive && (
                          <span className="sched-grid-person-inactive-pill" title={inactiveNote}>INACTIVE</span>
                        )}
                        {person.OfficeCode && (
                          <span className="sched-grid-person-office" title={person.Office || person.OfficeCode}>{person.OfficeCode}</span>
                        )}
                      </div>
                      <div className="sched-grid-person-title">{person.Department || person.DepartmentCode}</div>
                      <div className="sched-grid-person-title" style={{ fontSize: 10, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 6 }}>{person.Level}<SeniorityBars level={person.Level} color={deptColor} /></div>
                      <div className="sched-grid-util-row">
                        <div className="sched-grid-util-bar">
                          <div className="sched-grid-util-fill" style={{ width: `${Math.min(uPct, 100)}%`, background: utilColor(Math.min(uPct, 100)) }} />
                          {uPct > 100 && <div className="sched-grid-util-overflow" style={{ width: `${Math.min(uPct - 100, 100)}%` }} />}
                        </div>
                        <span className="sched-grid-util-pct" style={{ color: uPct > 100 ? '#DC2626' : utilColor(uPct) }}>{uPct}%</span>
                      </div>
                    </div>
                    {!hasBlocks && <span className="sched-gantt-row-badge sched-gantt-row-badge--free">FREE</span>}
                    {/* Job Pools presence (Trello #257). Pinned to the bottom-right
                        corner of the staff card rather than sitting inline after the
                        name, where it competed with the name for width and truncated
                        to "…". Absolutely placed, like the FREE badge above it, so it
                        costs the name column nothing.

                        Self-clearing by construction: it reflects a live check-in,
                        which ends on checkout, claim, or assignment — so unlike a
                        "working" badge there is nothing that could get stuck on.
                        Purely informational; affects no capacity or utilization
                        number. */}
                    {poolAvailByStaff[person.StaffId] && (() => {
                      const avail = poolAvailByStaff[person.StaffId];
                      const hrs = avail.hours == null
                        ? null
                        : Math.round(Number(avail.hours) * 100) / 100;
                      return (
                        <span
                          className="sched-grid-person-poolavail"
                          // Hover target for the day-cell link — see setPoolChipPulse.
                          data-pool-chip={person.StaffId}
                          title={`Marked available in the job pools${hrs != null ? ` — says they have ${hrs}h free` : ''}\n(today's cell is outlined to match)`}
                        >
                          POOL{hrs != null ? ` ${hrs}h` : ''}
                        </span>
                      );
                    })()}
                    {/* Trello #332 — a staffer already on the grid (so they can be
                        scheduled forward) who hasn't actually started yet. Their day cells
                        are struck through to their start date; this chip is the row-level
                        cue so you don't have to scroll to notice. */}
                    {!isEmployedOn(person, todayISO) && person.StartDate && (
                      <span
                        className="sched-grid-person-notstarted"
                        title={`Starts ${String(person.StartDate).slice(0, 10)} — 0h capacity until then. Can be scheduled from that date onward.`}
                      >
                        NOT STARTED
                      </span>
                    )}
                    {/* Contract (PT / Flex / Leave) — the same bottom-right corner, so it
                        never depends on how long the level title is. The temporary states
                        above (POOL check-in, NOT STARTED) take the corner when present. */}
                    {!poolAvailByStaff[person.StaffId] && !(!isEmployedOn(person, todayISO) && person.StartDate) && (
                      <ContractChip contract={getContract(person.StaffId, toISO(baseMonday))}
                        className="sched-grid-person-contract" />
                    )}
                  </div>
                );
              })
            ) : customScope ? (
              /* Custom scope: one label row per firm-internal custom job. A custom job has
                 no client, budget, JM/JP, niche, office or PE record — rprs.CustomJobs is
                 only Name/Description/Category/IsActive — so the client link, dept pill,
                 budget bar, status chip and in-pool chip all have nothing to render. What
                 replaces them is the one figure that IS meaningful here: how much work is
                 parked on this job and how many people are carrying it. */
              visibleCustomJobs.map(c => {
                const catColor = CUSTOM_JOB_CATEGORY_COLORS[c.category] || CUSTOM_JOB_DEFAULT_COLOR;
                const shown = customShownTracks(c);
                const hiddenTracks = c.maxTrack - shown;
                const expanded = expandedCustomRows.has(c.customJobId);
                const staffCount = new Set(c.blocks.map(b => b.staffId)).size;
                const winHours = Math.round(c.blocks.reduce((s, b) => s + b.totalHours, 0) * 100) / 100;
                return (
                  <div key={c.customJobId} ref={el => { customJobRowRefs.current[c.customJobId] = el; }}
                    className={`sched-grid-label-row${c.customJobId === focusCustomJobId ? ' sched-grid-label-row--focus' : ''}`}
                    style={{ height: customRowHeight(c), cursor: 'pointer' }}
                    title="Click for custom-job details"
                    onClick={() => setCustomJobParam(String(c.customJobId))}>
                    <div className="sched-grid-dept-bar" style={{ background: catColor.bg, width: 5 }} />
                    <div className="sched-grid-person-info">
                      <div className="sched-grid-person-name">{c.name}</div>
                      <div className="sched-grid-person-title">Firm-internal</div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                        <span className="sched-type-tag" style={{ background: catColor.bg, color: catColor.text, fontSize: 9, padding: '1px 6px' }}>
                          {c.category}
                        </span>
                        {!c.isActive && (
                          <span className="sched-grid-job-chip" style={{ background: '#F3F4F6', color: '#6B7280' }}
                            title="Deactivated in Admin — no longer schedulable, but it still holds scheduled time">
                            <span className="sched-grid-job-chip-dot" style={{ background: '#9CA3AF' }} />
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="sched-grid-util-row">
                        <span className="sched-grid-util-pct" style={{ fontSize: 10, color: '#6B7280' }}
                          title="Scheduled hours and people in the weeks currently in view. There is no budget to measure against — a custom job is firm-internal, not client work. Open the row for the full today-onward team.">
                          In view: {winHours}h · {staffCount} {staffCount === 1 ? 'person' : 'people'}
                          {c.placeholderHours > 0 ? ` · ${c.placeholderHours}h placeholder` : ''}
                        </span>
                      </div>
                      {hiddenTracks > 0 || expanded ? (
                        <button type="button" className="sched-grid-custom-expand"
                          onClick={(e) => { e.stopPropagation(); toggleCustomRow(c.customJobId); }}>
                          {expanded ? '− show less' : `+ ${hiddenTracks} more scheduled`}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })
            ) : (
              /* Jobs View: job rows */
              visibleJobs.map(job => {
                // Item 4 — progress = WIP (logged) + future-scheduled (incl. placeholders)
                // vs budget. Uses the shared jobProgressProps rather than re-deriving from
                // progressByJob/jobBudgetUtil, so the row chip, the detail sidebar and the
                // modal budget bar can never disagree about the same job.
                const { budget: pBudget, noBudget, wipTotal: pWip, scheduledTotal: pFutureSched, placeholderHours: pPlaceholder } = jobProgressProps(job.jobId);
                const pUsed = Math.round((pWip + pFutureSched) * 100) / 100;
                const pWipPct = pBudget > 0 ? Math.min(100, Math.round((pWip / pBudget) * 100)) : 0;
                const pSchedPct = pBudget > 0 ? Math.min(100 - pWipPct, Math.round((pFutureSched / pBudget) * 100)) : 0;
                const pStatus = SCHED_STATUS[computeSchedStatus(pBudget, pFutureSched, pWip)];
                const deptColor = jobDeptColor(job.jobType);
                const deptId = jobDept(job.jobType); // service-implied dept code (shares the staff dept palette)
                const serviceColor = JOB_TYPE_COLORS[job.jobType] || { bg: '#6B7280', text: '#fff' };
                return (
                  <div key={job.jobId} ref={el => { jobRowRefs.current[job.jobId] = el; }}
                    data-tour={job.jobId === SENTINEL_JOB_ID ? 'demo-job-row' : undefined}
                    className={`sched-grid-label-row${job.jobId === focusJobId ? ' sched-grid-label-row--focus' : ''}`}
                    style={{ height: jobRowHeight(job.jobId), cursor: 'pointer' }}
                    title="Click for job details"
                    onClick={() => setJobParam(String(job.jobId))}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={() => handleDropJob(job.jobId)}>
                    <div className="sched-grid-dept-bar" style={{ background: deptColor, width: 5 }} />
                    <div className="sched-grid-person-info">
                      <div className="sched-grid-person-name sched-mgr-client-link"
                        onClick={(e) => { e.stopPropagation(); if (job.clientId) navigate(`/schedule/client/${job.clientId}`); }}>
                        {job.clientName}
                      </div>
                      <div className="sched-grid-person-title sched-grid-job-detail-link" style={{ cursor: 'pointer' }} title="View job details"
                        onClick={(e) => { e.stopPropagation(); setJobParam(String(job.jobId)); }}>{job.jobName}</div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
                        {deptId && (
                          <span className="sched-grid-dept-pill" style={{ background: deptColor }} title="Department (matches staff in this dept)">
                            {deptId}
                          </span>
                        )}
                        <span className="sched-type-tag" style={{ background: serviceColor.bg, color: serviceColor.text, fontSize: 9, padding: '1px 6px' }}>
                          {job.jobType || 'Other'}
                        </span>
                        {noBudget ? (
                          <span className="sched-grid-job-chip" style={{ background: '#F3F4F6', color: '#6B7280' }} title="No budget hours set for this job">
                            <span className="sched-grid-job-chip-dot" style={{ background: '#9CA3AF' }} />
                            No budget
                          </span>
                        ) : pStatus && (
                          <span className="sched-grid-job-chip" style={{ background: pStatus.bg, color: pStatus.text }} title="WIP + future-scheduled vs budget">
                            <span className="sched-grid-job-chip-dot" style={{ background: pStatus.dot }} />
                            {pStatus.label}
                          </span>
                        )}
                        {/* In-pool chip (Trello #257). Pooled jobs deliberately STAY
                            visible in the grid — they're still real work someone has
                            to do. Only the manager's My Jobs list hides them, to cut
                            the clutter this feature exists to remove. */}
                        {poolJobsById[job.jobId] && (
                          <span
                            className="sched-pool-chip sched-pool-chip--sm"
                            style={{ background: POOL_META[poolJobsById[job.jobId].code]?.color || '#6B7280' }}
                            title={`Waiting in the ${POOL_META[poolJobsById[job.jobId].code]?.label || poolJobsById[job.jobId].code} pool — any eligible staffer can claim it`}
                          >
                            In pool
                          </span>
                        )}
                      </div>
                      {noBudget ? (
                        <div className="sched-grid-util-row">
                          <span className="sched-grid-util-pct" style={{ fontSize: 10, color: '#9CA3AF' }}>{pUsed}h scheduled · no budget</span>
                        </div>
                      ) : (
                        <div className="sched-grid-util-row" title={`WIP ${Math.round(pWip * 100) / 100}h + future-scheduled ${Math.round(pFutureSched * 100) / 100}h${pPlaceholder > 0 ? ` (incl. ${Math.round(pPlaceholder * 100) / 100}h placeholder)` : ''} of ${pBudget}h budget`}>
                          <div className="sched-grid-jobbar">
                            <div className="sched-grid-jobbar-seg" style={{ width: `${pWipPct}%`, background: BRAND.deepTeal }} />
                            <div className="sched-grid-jobbar-seg" style={{ width: `${pSchedPct}%`, background: BRAND.sageGreen }} />
                          </div>
                          <span className="sched-grid-util-pct" style={{ fontSize: 10 }}>{pUsed}/{pBudget}h</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: timeline grid */}
        <div className="sched-grid-right" ref={gridRef} onScroll={handleGridScroll}>
          {/* Week + day headers */}
          <div className="sched-grid-header" style={{ width: totalW }}>
            {/* Week banners */}
            <div className="sched-grid-week-row">
              {weeks.map(wk => (
                <div key={wk.idx} className="sched-grid-week-banner" style={{ width: wk.w }}>
                  {wk.label}
                </div>
              ))}
            </div>
            {/* Day labels */}
            <div className="sched-grid-day-row">
              {allDays.map((d, i) => (
                <div
                  key={i}
                  className={`sched-grid-day-cell${d.today ? ' sched-grid-day--today' : ''}${d.isWE ? ' sched-grid-day--we' : ''}`}
                  style={{ width: d.w }}
                >
                  <div className="sched-grid-day-label">{d.label}</div>
                  <div className="sched-grid-day-num">{d.dn}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline rows with blocks */}
          <div className="sched-grid-body" ref={gridBodyRef} style={{ width: totalW }}>
            {viewMode === 'people' ? (
              /* ── People View rows ── */
              visibleStaff.map(person => {
                // Item 1 — placeholder synthetic row: thin row that acts as a
                // visual level break AND a place to park placeholder blocks.
                // Clicking an empty cell opens the create modal in placeholder
                // mode with dept+level pre-filled (same UX as clicking a real
                // staff cell to schedule for that person). Clicking a block
                // opens the assign modal.
                if (person.isPlaceholder) {
                  // Row height matches the sidebar — scales with overlap stack
                  // (phMaxTrack assigned in visibleStaff useMemo).
                  const PH_ROW_H = Math.max(56, BLOCK_TOP + (person.phMaxTrack || 1) * (BLOCK_H + BLOCK_GAP) + 8);
                  const phBlocks = (person.placeholders || []).map(ph => {
                    const startIso = String(ph.StartDate).slice(0, 10);
                    const endIso   = String(ph.EndDate).slice(0, 10);
                    const sIdx = allDays.findIndex(d => d.iso === startIso);
                    const eIdx = allDays.findIndex(d => d.iso === endIso);
                    return { ph, sIdx, eIdx };
                  });
                  return (
                    <div key={person.StaffId}
                      className="sched-grid-row sched-grid-row--placeholder"
                      style={{ height: PH_ROW_H }}>
                      {allDays.map((d, i) => (
                        <div key={i}
                          className={`sched-grid-cell-bg${d.today ? ' sched-grid-cell--today' : ''}${d.isWE ? ' sched-grid-cell--we' : ''}`}
                          style={{ left: dayX[i], width: d.w, height: PH_ROW_H, cursor: d.isWE ? 'default' : 'pointer' }}
                          onClick={() => {
                            if (d.isWE) return;
                            openNewModal(null, i, { dept: person.DepartmentCode, level: person.Level });
                          }}
                          onDragOver={e => {
                            // Per-cell DND target — accept staff-block drops onto
                            // this (dept, level) placeholder row to convert the
                            // entry to a placeholder. Custom-job blocks are accepted
                            // now that PlaceholderAssignments carries CustomJobId
                            // (migrate-074). Blocks that are already placeholders are
                            // still rejected — dragging placeholders is not a gesture
                            // here, they are edited in the modal.
                            if (!dragging || dragging.isPlaceholder) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            e.currentTarget.classList.add('sched-grid-cell--dragover');
                          }}
                          onDragLeave={e => e.currentTarget.classList.remove('sched-grid-cell--dragover')}
                          onDrop={e => {
                            e.currentTarget.classList.remove('sched-grid-cell--dragover');
                            handleDropToPlaceholderRow(person.DepartmentCode, person.Level, i);
                          }} />
                      ))}
                      {phBlocks.map(({ ph, sIdx, eIdx }) => {
                        // Clip to visible window: if both ends outside, skip; if one end outside, clamp.
                        if (sIdx < 0 && eIdx < 0) return null;
                        const safeS = sIdx < 0 ? 0 : sIdx;
                        const safeE = eIdx < 0 ? allDays.length - 1 : eIdx;
                        if (safeE < safeS) return null;
                        const x = dayX[safeS] + 2;
                        const xEnd = dayX[safeE] + allDays[safeE].w - 2;
                        const w = Math.max(xEnd - x, 30);
                        // Build a regular-block-shaped object for the shared rich tooltip.
                        // staffName='Unassigned' + isPlaceholder triggers the greyed-out Staff row.
                        // A placeholder holds a real job or a custom job. Custom rows have
                        // no client/job context at all, so the label falls back to the
                        // custom job's own name — without this the bar renders blank.
                        const phIsCustom = ph.CustomJobId != null;
                        const phLabel = phIsCustom ? (ph.CustomJobName || 'Custom job') : (ph.ClientName || '');
                        const phTooltipBlock = {
                          // Mirror the regular-block tooltip's fallback chain (see line ~402).
                          // ServiceDesc is the human-readable service line ("Audit") that
                          // JOB_TYPE_COLORS is keyed by; raw JobType is a legacy classification.
                          jobType: ph.ServiceDesc || ph.JobType || ph.JobClassDesc || '',
                          clientName: phLabel,
                          clientStatus: ph.ClientStatus,
                          jobName: phIsCustom ? (ph.CustomJobName || 'Custom job') : ph.JobName,
                          jobCode: ph.JobCode,
                          totalHours: Number(ph.Hours),
                          staffName: 'Unassigned',
                          managerName: ph.JobManagerName,
                          serviceDesc: ph.ServiceDesc,
                          level: ph.Level,
                          departmentCode: ph.DepartmentCode,
                          isPlaceholder: true,
                          isCustom: phIsCustom,
                          customJobCategory: ph.CustomJobCategory,
                          // Tooltip-only on purpose: several placeholders for the same
                          // custom job/level/window are legal and otherwise identical on
                          // screen, and the bar is too narrow to carry a label.
                          notes: ph.Notes || null,
                        };
                        const top = BLOCK_TOP + (ph._track || 0) * (BLOCK_H + BLOCK_GAP);
                        const phc = phContBlock(ph);
                        const geom = barGeom(phc, x, w, true); // placeholder bars are border-box
                      const { barX, barW, contentW } = geom;
                        return (
                          <Fragment key={ph.PlaceholderId}>
                          {phc && contMarker(phc, 'left', '#003D4C', geom, top)}
                          <div
                            className="sched-grid-block sched-grid-block--placeholder"
                            style={{ left: barX, top, width: barW }}
                            onClick={e => { e.stopPropagation(); setPlaceholderAssignTarget(ph); }}
                            onMouseEnter={e => setTooltip({ block: phTooltipBlock, x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setTooltip(null)}>
                            <span className="sched-grid-block-text">
                              {contentW > 60 ? phLabel : phLabel.split(' ')[0]}
                            </span>
                            {contentW > 90 && (
                              <span className="sched-grid-block-hours">{Number(ph.Hours)}h</span>
                            )}
                          </div>
                          {phc && contMarker(phc, 'right', '#003D4C', geom, top)}
                          </Fragment>
                        );
                      })}
                    </div>
                  );
                }

                let blocks = blocksByPerson[person.StaffId] || [];
                // Client filter (?cq): show only this staffer's blocks on the matching client.
                // visibleStaff already dropped rows with no match; util bars/row height keep the full set.
                if (clientSearch.trim()) {
                  const cq = clientSearch.trim().toLowerCase();
                  blocks = blocks.filter(b =>
                    (b.clientName || '').toLowerCase().includes(cq) ||
                    (b.clientCode || '').toLowerCase().includes(cq));
                }
                // Client group (?client) — same treatment as the client search.
                if (clientGroupActive) {
                  blocks = blocks.filter(b => b.jobId && jobMatchesClientFilter(b.jobId));
                }
                // Return type / prep budget (?ret, ?prep) — same treatment: show only
                // the blocks that match, on rows visibleStaff already kept.
                if (moreFiltersActive) {
                  blocks = blocks.filter(b => b.jobId && jobMatchesMoreFilters(b.jobId));
                }
                return (
                  <div key={person.StaffId}
                    ref={el => { rowRefs.current[person.StaffId] = el; }}
                    data-tour={person.StaffId === myStaffId ? 'my-row' : undefined}
                    className={`sched-grid-row${conflictStaffIds.has(person.StaffId) ? ' sched-grid-row--conflict' : ''}${highlightStaffIds.size > 0 && !highlightStaffIds.has(person.StaffId) ? ' sched-grid-row--dimmed' : ''}${highlightStaffIds.has(person.StaffId) && !highlightConflictCells ? ' sched-grid-row--highlighted' : ''}`}
                    style={{ height: rowHeight(person.StaffId) }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={() => handleDrop(person.StaffId, null)}>
                    {allDays.map((d, i) => {
                      const daySched = !d.isWE ? (dayHoursByStaff[person.StaffId]?.[d.iso] || 0) : 0;
                      const dayCap = !d.isWE ? getDayCap(person.StaffId, d.iso) : 0;
                      const dayPTO = !d.isWE ? (ptoByStaffDay[person.StaffId]?.[d.iso] || 0) : 0;
                      const dayPendingPTO = !d.isWE ? (pendingPtoByStaffDay[person.StaffId]?.[d.iso] || 0) : 0;
                      const isPTOConflict = !d.isWE && dayPTO > 0 && daySched > 0;
                      // Trello #332 — capacity is 0 outside a staffer's employment window,
                      // so mark those days visibly instead of leaving an ordinary-looking
                      // empty cell that happens to reject work. 11 staff are in this state
                      // today (Sept interns), 10 of them with work already booked.
                      const notEmployedDay = !d.isWE && !isEmployedOn(person, d.iso);
                      const isOverDay = !d.isWE && daySched > 0 && daySched > dayCap;
                      const dayWeekIdx = Math.floor(i / 7);
                      const isConflictHighlight = highlightConflictCells && highlightConflictCells.staffId === person.StaffId && highlightConflictCells.weekIdx === dayWeekIdx;
                      const cellKey = `${person.StaffId}_${d.iso}`;
                      const isNotifOld = highlightOldCells.size > 0 && highlightOldCells.has(cellKey);
                      const isNotifNew = highlightNewCells.size > 0 && highlightNewCells.has(cellKey);
                      const holiday = !d.isWE ? holidayByDate[d.iso] : null;
                      const isHoliday = !!holiday;
                      const isHolidayConflict = isHoliday && daySched > 0;
                      const globalEntry = !d.isWE ? getGlobalEntry(person.StaffId, d.iso) : null;
                      // Mirror the onClick precedence below: an admin's approve/reject on a
                      // pending cell outranks self-cancel, so the tooltip must agree with it.
                      const adminPtoCell = isSuperUser && !!pendingPtoLookup[cellKey];
                      const myPtoReq = !adminPtoCell ? myPtoLookup[cellKey] : null;
                      const myPtoHours = dayPTO || dayPendingPTO;
                      // Job Pools: mark TODAY's cell when this person is currently
                      // marked available in a pool. Read-only, like the POOL chip —
                      // it enters no capacity, utilization or conflict math.
                      const isPoolAvailToday = !!d.today && !!poolAvailByStaff[person.StaffId];
                      return (
                        <div key={i} className={`sched-grid-cell-bg${d.today ? ' sched-grid-cell--today' : ''}${d.isWE ? ' sched-grid-cell--we' : ''}${isHolidayConflict ? ' sched-grid-cell--holiday-conflict' : isHoliday ? ' sched-grid-cell--holiday' : ''}${isOverDay ? ' sched-grid-cell--overday' : ''}${isPTOConflict ? ' sched-grid-cell--pto-conflict' : ''}${dayPTO > 0 && !isPTOConflict ? ' sched-grid-cell--pto' : ''}${dayPendingPTO > 0 ? ' sched-grid-cell--pto-pending' : ''}${notEmployedDay ? ' sched-grid-cell--notstarted' : ''}${isConflictHighlight ? ' sched-grid-cell--conflict-highlight' : ''}${isNotifOld ? ' sched-grid-cell--notif-old' : ''}${isNotifNew ? ' sched-grid-cell--notif-new' : ''}${isPoolAvailToday ? ' sched-grid-cell--poolavail' : ''}`}
                          style={{ left: dayX[i], width: d.w, height: rowHeight(person.StaffId) }}
                          onMouseEnter={isPoolAvailToday ? () => setPoolChipPulse(person.StaffId, true) : undefined}
                          onMouseLeave={isPoolAvailToday ? () => setPoolChipPulse(person.StaffId, false) : undefined}
                          title={adminPtoCell ? `Pending PTO (${dayPendingPTO}h) \u2014 Click to approve/reject`
                            : myPtoReq ? (myPtoReq.isPast ? `Your PTO (${myPtoHours}h) \u2014 already taken` : `Your PTO (${myPtoHours}h) \u2014 Click to cancel or schedule work`)
                            : dayPendingPTO > 0 ? `Pending PTO (${dayPendingPTO}h)`
                            : globalEntry ? `${globalEntry.name} (${globalEntry.hours}h)` : isHoliday ? holiday.name : undefined}
                          onClick={(e) => {
                            const cellKey = `${person.StaffId}_${d.iso}`;
                            const ptoReq = pendingPtoLookup[cellKey];
                            if (ptoReq && isSuperUser) {
                              // Admins keep approve/reject on pending cells, including their
                              // own \u2014 self-cancel takes over only once it's approved.
                              setPtoCellPopover({ x: e.clientX, y: e.clientY, req: ptoReq });
                            } else if (myPtoLookup[cellKey]) {
                              setMyPtoPopover({ x: e.clientX, y: e.clientY, req: myPtoLookup[cellKey], person, dayIdx: i });
                            } else {
                              openNewModal(person, i);
                            }
                          }}
                          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.classList.add('sched-grid-cell--dragover'); showDragTip(e.clientX, e.clientY, `New start: ${d.mn} ${d.dn}`); }}
                          onDragLeave={e => { e.currentTarget.classList.remove('sched-grid-cell--dragover'); hideDragTip(); }}
                          onDrop={e => { e.stopPropagation(); e.currentTarget.classList.remove('sched-grid-cell--dragover'); hideDragTip(); handleDrop(person.StaffId, i); }}>
                          {isOverDay && (
                            <span style={{ position: 'absolute', bottom: (dayPTO > 0 || dayPendingPTO > 0 || isHoliday) ? 11 : 1, left: 0, right: 0, fontSize: 8, textAlign: 'center', color: isPTOConflict ? '#D97706' : isHoliday ? '#7C3AED' : '#DC2626', fontWeight: 700, lineHeight: 1, pointerEvents: 'none' }}>
                              {daySched % 1 === 0 ? daySched : Math.round(daySched * 100) / 100}/{dayCap}h
                            </span>
                          )}
                          {dayPendingPTO > 0 && !notEmployedDay && (
                            <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 7, textAlign: 'center', color: '#3B82F6', fontWeight: 700, lineHeight: 1, pointerEvents: 'none' }}>
                              PTO?{dayPendingPTO < 8 ? ` ${dayPendingPTO}h` : ''}
                            </span>
                          )}
                          {dayPTO > 0 && !dayPendingPTO && !notEmployedDay && (
                            <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 8, textAlign: 'center', color: '#9CA3AF', fontWeight: 700, lineHeight: 1, pointerEvents: 'none' }}>
                              PTO {dayPTO < 8 ? dayPTO + 'h' : ''}
                            </span>
                          )}
                          {/* Trello #332 — outranks PTO/holiday labels: if they aren't
                              employed yet, that is the reason the day is unavailable, and
                              a PTO or holiday marker on top would just be noise. */}
                          {notEmployedDay && (
                            <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 6.5, textAlign: 'center', color: '#C2620E', fontWeight: 700, lineHeight: 1, pointerEvents: 'none', letterSpacing: -0.1 }}>
                              NOT STARTED
                            </span>
                          )}
                          {isHoliday && !dayPTO && !dayPendingPTO && !notEmployedDay && (
                            <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 7, textAlign: 'center', color: '#7C3AED', fontWeight: 700, lineHeight: 1, pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 1px' }}>
                              {holiday.name}
                            </span>
                          )}
                          {isNotifOld && <span className="sched-grid-cell-notif-label" style={{ color: '#D97706' }}>Old</span>}
                          {isNotifNew && <span className="sched-grid-cell-notif-label" style={{ color: '#2563EB' }}>New</span>}
                          {/* ── Job Pools event stamp (Trello #257) ──
                              A ~4px bar marking a day this person claimed or was
                              assigned pool work. It is an EVENT, not state: nothing
                              needs to clear it and it simply scrolls out of the
                              window. Deliberately inert — no hours, excluded from
                              every calculation above, and pointer-events:none so it
                              can't interfere with drag/drop on the cell.
                              Bottom offset reuses the SAME rule the PTO/holiday
                              labels use so the two never overlap. */}
                          {(() => {
                            const ev = poolEventsByStaffDay[`${person.StaffId}|${d.iso}`];
                            if (!ev || !ev.length) return null;
                            const anyPtoText = dayPTO > 0 || dayPendingPTO > 0 || isHoliday || isOverDay;
                            const tipFor = (e) =>
                              `${e.ClientName} — ${e.JobName}\n${e.ClaimKind === 'assigned'
                                ? `Assigned${e.AssignedByName ? ` by ${e.AssignedByName}` : ''}`
                                : 'Claimed'} from the ${POOL_META[e.PoolCode]?.label || e.PoolCode} pool`;
                            const tip = ev.map(tipFor).join('\n\n');
                            return (
                              <span
                                className="sched-grid-pool-event"
                                style={{ bottom: anyPtoText ? 11 : 2 }}
                                title={tip}
                              >
                                {/* Each segment carries its OWN tooltip so a multi-event
                                    day names the job you're actually pointing at; the
                                    container's combined tip covers the ×N count. */}
                                {ev.map((e, k) => (
                                  <span
                                    key={e.PoolJobId || k}
                                    className="sched-grid-pool-event-seg"
                                    style={{ background: POOL_META[e.PoolCode]?.color || '#6B7280' }}
                                    title={tipFor(e)}
                                  />
                                ))}
                                {ev.length > 1 && (
                                  <span className="sched-grid-pool-event-count">&times;{ev.length}</span>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {weeks.map(wk => {
                      const u = weekUtil(person.StaffId, wk.idx);
                      const pct = u.cap > 0 ? Math.round((u.sch / u.cap) * 100) : (u.sch > 0 ? 999 : 0);
                      const isOver = pct > 100;
                      const x = dayX[wk.idx * 7] || 0;
                      const barW = wk.w - 28;
                      return (
                        <div key={wk.idx} className="sched-grid-week-util" style={{ left: x, width: wk.w }}>
                          <div style={{ width: barW, height: 4, background: '#E5E7EB', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                            <div style={{ width: `${Math.min(pct, 100)}%`, height: 4, background: isOver ? '#EF4444' : utilColor(pct) }} />
                            {isOver && <div className="sched-grid-util-overflow" style={{ width: `${Math.min(pct - 100, 100)}%`, height: 4 }} />}
                          </div>
                          <span className="sched-grid-week-util-pct" style={{ color: isOver ? '#DC2626' : utilColor(pct) }}>
                            {u.sch}/{u.cap}h
                          </span>
                        </div>
                      );
                    })}
                    {blocks.map(b => {
                      const x = dayX[b.sIdx] + 2;
                      const xEnd = dayX[b.eIdx] + allDays[b.eIdx].w - 2;
                      const w = Math.max(xEnd - x, 4);
                      const top = BLOCK_TOP + (b.track || 0) * (BLOCK_H + BLOCK_GAP);
                      if (b.isGlobal) {
                        return (
                          <div key={b.key}
                            className="sched-grid-block sched-grid-block--global"
                            style={{ left: x, top, width: w }}
                            onMouseEnter={e => setTooltip({ block: b, x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setTooltip(null)}>
                            <span className="sched-grid-block-text">{w > 60 ? b.clientName : (b.clientName || '').split(' ')[0]}</span>
                            {w > 90 && <span className="sched-grid-block-hours">{b.totalHours}h</span>}
                          </div>
                        );
                      }
                      const isPTO = b.entryType === 'pto' || b.clientName === 'PTO';
                      // Item 2 — custom-job blocks use the category palette; real jobs
                      // use JOB_TYPE_COLORS keyed on ServiceDesc.
                      const typeColor = isPTO ? { bg: '#94a3b8' }
                        : b.isCustom ? (CUSTOM_JOB_CATEGORY_COLORS[b.customJobCategory] || CUSTOM_JOB_DEFAULT_COLOR)
                        : (JOB_TYPE_COLORS[b.jobType] || { bg: '#6B7280', text: '#fff' });
                      const isDragging = dragging?.key === b.key;
                      const isBlockSelected = selectedBlock?.key === b.key;
                      const statusMod = b.clientStatus === 'FINANCIAL'
                        ? ' sched-grid-block--client-financial'
                        : b.clientStatus === 'LOST'
                          ? ' sched-grid-block--client-lost'
                          : '';
                      const geom = barGeom(b, x, w);
                      const { barX, barW, contentW } = geom;
                      // The off-office badge is flex-shrink:0 and sits before the hours, so it
                      // spends the same budget; charge it or the hours are what gets clipped.
                      const roomForHours = contentW
                        - (offOffice(b.staffId, jobInfoMap[b.jobId]?.IsOnSite, jobInfoMap[b.jobId]?.OfficeCode) ? BADGE_W.offOffice : 0);
                      return (
                        <Fragment key={b.key}>
                        {contMarker(b, 'left', typeColor.bg, geom, top)}
                        <div
                          data-tour={b.jobId === SENTINEL_JOB_ID ? 'demo-block' : undefined}
                          className={`sched-grid-block${isPTO ? ' sched-grid-block--pto' : ''}${b.isCustom ? ' sched-grid-block--custom' : ''}${isDragging ? ' sched-grid-block--dragging' : ''}${b.isEmpire ? ' sched-grid-block--empire' : ''}${b.isClosedInPE ? ' sched-grid-block--closed' : ''}${isBlockSelected ? ' sched-grid-block--selected' : ''}${statusMod}`}
                          style={{ left: barX, top, width: barW, background: typeColor.bg, color: typeColor.text || '#fff' }}
                          draggable onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; handleDragStart(b); }}
                          onDragEnd={() => { setDragging(null); hideDragTip(); gridBodyRef.current?.classList.remove('sched-grid-body--dragging'); }}
                          onClick={e => { e.stopPropagation(); setTooltip(null); if (jobParam) setJobParam(null); if (selectedBlock?.key === b.key) closeSidebar(); else setSelectedBlock(entryWithJobMeta(b)); }}
                          onMouseEnter={e => setTooltip({ block: b, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setTooltip(null)}>
                          <span className="sched-grid-block-text">{
                            b.isCustom
                              ? (contentW > 60 ? b.jobName : (b.jobName || '').split(' ')[0])
                              : (contentW > 60 ? b.clientName : (b.clientName || '').split(' ')[0])
                          }</span>
                          {offOffice(b.staffId, jobInfoMap[b.jobId]?.IsOnSite, jobInfoMap[b.jobId]?.OfficeCode) && <span className="sched-offoffice-badge" title={`On-site job in ${jobInfoMap[b.jobId]?.Office || jobInfoMap[b.jobId]?.OfficeCode} — ${b.staffName || 'this staffer'} is from ${staffOfficeById[b.staffId] || 'another office'}`}>&#128205; off-office</span>}
                          {roomForHours > HOURS_MIN_W && <span className="sched-grid-block-hours">{b.totalHours}h</span>}
                          {b.clientStatus === 'FINANCIAL' && <span className="sched-grid-block-warn sched-grid-block-warn--financial" title="Client status: FINANCIAL (Stop Work)">⚠</span>}
                          {b.clientStatus === 'LOST' && <span className="sched-grid-block-warn sched-grid-block-warn--lost" title="Client status: LOST">✕</span>}
                        </div>
                        {contMarker(b, 'right', typeColor.bg, geom, top)}
                        </Fragment>
                      );
                    })}
                  </div>
                );
              })
            ) : customScope ? (
              /* ── Custom scope rows ── */
              /* Deliberately NOT drop targets. bulkMove has no newCustomJobId, so a block
                 dropped here could not be re-pointed at the custom job server-side; a row
                 that looks like it accepts a drop and then silently does nothing is worse
                 than an inert one. Blocks stay draggable so they can still be moved OUT. */
              visibleCustomJobs.map(c => {
                const shown = customShownTracks(c);
                const expanded = expandedCustomRows.has(c.customJobId);
                const hiddenTracks = c.maxTrack - shown;
                // Repeat the label so it is on screen wherever the timeline is scrolled — a
                // single centred label would need scroll state, and handleGridScroll is
                // deliberately imperative (no re-render on scroll).
                const bandLabels = Math.max(2, Math.min(40, Math.ceil(totalW / 220)));
                return (
                  <div key={c.customJobId} className="sched-grid-row" style={{ height: customRowHeight(c) }}>
                    {allDays.map((d, i) => {
                      // Parity with the job rows: firm holidays + firm-wide events shade the column.
                      const holiday = !d.isWE ? holidayByDate[d.iso] : null;
                      const fw = (!d.isWE && !holiday) ? (globalAssignmentByDate[d.iso] || []).filter(g => !g.departments) : [];
                      const fwName = fw.length ? (fw[0].name || fw[0].description || 'Firm-wide') : null;
                      return (
                        <div key={i} className={`sched-grid-cell-bg${d.today ? ' sched-grid-cell--today' : ''}${d.isWE ? ' sched-grid-cell--we' : ''}${holiday ? ' sched-grid-cell--holiday' : ''}${fwName ? ' sched-grid-cell--firmwide' : ''}`}
                          style={{ left: dayX[i], width: d.w, height: customRowHeight(c), cursor: d.isWE ? 'default' : 'pointer' }}
                          title={holiday ? holiday.name : (fwName || undefined)}
                          onClick={d.isWE ? undefined : () => openNewModalForCustomJob(c.customJobId, i)}>
                          {holiday && <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 7, textAlign: 'center', color: '#7C3AED', fontWeight: 700, lineHeight: 1, pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 1px' }}>{holiday.name}</span>}
                          {!holiday && fwName && <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 7, textAlign: 'center', color: '#0891B2', fontWeight: 700, lineHeight: 1, pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 1px' }}>{fwName}</span>}
                        </div>
                      );
                    })}
                    {c.blocks.map(b => {
                      if ((b.track || 0) >= shown) return null; // collapsed away — "+N more" reveals it
                      const x = dayX[b.sIdx] + 2;
                      const xEnd = dayX[b.eIdx] + allDays[b.eIdx].w - 2;
                      const w = Math.max(xEnd - x, 4);
                      const top = BLOCK_TOP + (b.track || 0) * (BLOCK_H + BLOCK_GAP);
                      const deptColor = staffColor(b.department);
                      const isDragging = dragging?.key === b.key;
                      // Per-staffer attention flags carry over unchanged — they are properties of
                      // the PERSON's day, not of the job, so they apply to custom work identically.
                      // Client-status / closed-in-PE / off-office do not: no client, no job record.
                      const ptoWarn = (b.dates || []).some(d => (ptoByStaffDay[b.staffId]?.[toISO(d.date)] || 0) > 0);
                      const capWarn = (b.dates || []).some(d => { const iso = toISO(d.date); const cap = getDayCap(b.staffId, iso); return cap > 0 && (dayHoursByStaff[b.staffId]?.[iso] || 0) > cap; });
                      const staffGone = scheduledInactiveIds.has(b.staffId);
                      const geom = barGeom(b, x, w);
                      const { barX, barW, contentW } = geom;
                      // Priority once the bar runs short: the name ellipsises, then the
                      // seniority bars drop, then the hours — decoration yields to data.
                      const roomForHours = contentW
                        - (staffGone ? BADGE_W.staffGone : 0)
                        - (capWarn ? BADGE_W.capWarn : 0)
                        - (ptoWarn ? BADGE_W.ptoWarn : 0);
                      const showSeniority = roomForHours > HOURS_MIN_W + SENIORITY_W;
                      return (
                        <Fragment key={b.key}>
                        {contMarker(b, 'left', deptColor, geom, top)}
                        <div
                          className={`sched-grid-block${isDragging ? ' sched-grid-block--dragging' : ''}${staffGone ? ' sched-grid-block--inactive' : ''}${selectedBlock?.key === b.key ? ' sched-grid-block--selected' : ''}`}
                          style={{ left: barX, top, width: barW, background: deptColor, color: '#fff' }}
                          draggable
                          onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; handleDragStart(b); }}
                          onDragEnd={() => { setDragging(null); hideDragTip(); gridBodyRef.current?.classList.remove('sched-grid-body--dragging'); }}
                          onClick={e => {
                            e.stopPropagation();
                            setTooltip(null);
                            if (customJobParam) setCustomJobParam(null);
                            if (selectedBlock?.key === b.key) closeSidebar(); else setSelectedBlock(b);
                          }}
                          onMouseEnter={e => setTooltip({ block: b, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setTooltip(null)}>
                          <span className="sched-grid-block-text">{contentW > 60 ? b.staffName : b.initials}</span>
                          {staffGone && <span title={inactiveNoteFor(b.staffId)}
                            style={{ marginLeft: 4, fontSize: 9, fontWeight: 800, lineHeight: 1, color: '#92400E', flexShrink: 0, pointerEvents: 'none' }}>&#9888;</span>}
                          {capWarn && <span title="Over capacity on a scheduled day — click to open & Quick Optimize" style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, lineHeight: 1, color: '#fff', background: 'rgba(220,38,38,0.92)', borderRadius: 3, padding: '1px 4px', flexShrink: 0, whiteSpace: 'nowrap', pointerEvents: 'none' }}>!</span>}
                          {ptoWarn && <span title="Scheduled during PTO — click to open & Quick Optimize" style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, lineHeight: 1, color: '#fff', background: 'rgba(217,119,6,0.95)', borderRadius: 3, padding: '1px 4px', flexShrink: 0, whiteSpace: 'nowrap', pointerEvents: 'none' }}>PTO</span>}
                          {showSeniority && <SeniorityBars level={b.level} color="#fff" onFill style={{ marginLeft: 6, marginRight: 4 }} />}
                          {roomForHours > HOURS_MIN_W && <span className="sched-grid-block-hours">{b.totalHours}h</span>}
                        </div>
                        {contMarker(b, 'right', deptColor, geom, top)}
                        </Fragment>
                      );
                    })}
                    {/* Custom-job placeholders (migrate-074) — stacked below the staff blocks,
                        below the CAP rather than below maxTrack so they stay visible while the
                        row is collapsed. Click to assign to a real staffer. */}
                    {(placeholdersByCustomJob[c.customJobId]?.phBlocks || []).map(({ ph, sIdx, eIdx, track }) => {
                      if (sIdx < 0 && eIdx < 0) return null;
                      const safeS = sIdx < 0 ? 0 : sIdx;
                      const safeE = eIdx < 0 ? allDays.length - 1 : eIdx;
                      if (safeE < safeS) return null;
                      const x = dayX[safeS] + 2;
                      const xEnd = dayX[safeE] + allDays[safeE].w - 2;
                      const w = Math.max(xEnd - x, 30);
                      const top = BLOCK_TOP + (shown + (track || 0)) * (BLOCK_H + BLOCK_GAP);
                      const phc = phContBlock(ph);
                      const geom = barGeom(phc, x, w, true); // placeholder bars are border-box
                      const { barX, barW, contentW } = geom;
                      return (
                        <Fragment key={`ph_${ph.PlaceholderId}`}>
                        {phc && contMarker(phc, 'left', '#003D4C', geom, top)}
                        <div
                          className="sched-grid-block sched-grid-block--placeholder"
                          style={{ left: barX, top, width: barW }}
                          onClick={e => { e.stopPropagation(); setPlaceholderAssignTarget(ph); }}
                          onMouseEnter={e => setTooltip({ block: { jobType: c.category, clientName: c.name, jobName: ph.Notes || 'Firm-internal', totalHours: Number(ph.Hours), staffName: 'Unassigned', level: ph.Level, departmentCode: ph.DepartmentCode, isPlaceholder: true }, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setTooltip(null)}>
                          <span className="sched-grid-block-text">{contentW > 70 ? `${ph.Level || 'Staff'} · placeholder` : (ph.Level || 'PH')}</span>
                          {contentW > 90 && <span className="sched-grid-block-hours">{Number(ph.Hours)}h</span>}
                        </div>
                          {phc && contMarker(phc, 'right', '#003D4C', geom, top)}
                          </Fragment>
                      );
                    })}
                    {customHasBand(c) && (
                      <div className="sched-grid-expand-band"
                        style={{ bottom: 0, width: totalW }}
                        title={expanded
                          ? 'Collapse this row'
                          : `${hiddenTracks} more scheduled staff hidden — click to show everyone`}
                        onClick={() => toggleCustomRow(c.customJobId)}>
                        {Array.from({ length: bandLabels }, (_, li) => (
                          <span key={li}>{expanded ? '\u2303 show less' : `\u2304 ${hiddenTracks} more scheduled`}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              /* ── Jobs View rows ── */
              visibleJobs.map(job => {
                const blocks = job.blocks || [];
                return (
                  <div key={job.jobId} className="sched-grid-row" style={{ height: jobRowHeight(job.jobId) }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={() => handleDropJob(job.jobId)}>
                    {allDays.map((d, i) => {
                      // Parity with People view: firm holidays + firm-wide events (globals with no dept filter)
                      // shade the whole column. Per-person states (PTO / over-cap) live on the blocks, not here.
                      const holiday = !d.isWE ? holidayByDate[d.iso] : null;
                      const fw = (!d.isWE && !holiday) ? (globalAssignmentByDate[d.iso] || []).filter(g => !g.departments) : [];
                      const fwName = fw.length ? (fw[0].name || fw[0].description || 'Firm-wide') : null;
                      return (
                      <div key={i} className={`sched-grid-cell-bg${d.today ? ' sched-grid-cell--today' : ''}${d.isWE ? ' sched-grid-cell--we' : ''}${holiday ? ' sched-grid-cell--holiday' : ''}${fwName ? ' sched-grid-cell--firmwide' : ''}`}
                        style={{ left: dayX[i], width: d.w, height: jobRowHeight(job.jobId), cursor: d.isWE ? 'default' : 'pointer' }}
                        title={holiday ? holiday.name : (fwName || undefined)}
                        onClick={d.isWE ? undefined : () => openNewModalForJob(job.jobId, i, jobDept(job.jobType))}
                        onDragOver={d.isWE ? undefined : e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.classList.add('sched-grid-cell--dragover'); showDragTip(e.clientX, e.clientY, `${dragging && dragging.jobId !== job.jobId ? `${job.clientName || 'New job'} · ` : ''}New start: ${d.mn} ${d.dn}`); }}
                        onDragLeave={d.isWE ? undefined : e => { e.currentTarget.classList.remove('sched-grid-cell--dragover'); hideDragTip(); }}
                        onDrop={d.isWE ? undefined : e => { e.stopPropagation(); e.currentTarget.classList.remove('sched-grid-cell--dragover'); hideDragTip(); handleDropJobCell(job.jobId, i); }}>
                        {holiday && <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 7, textAlign: 'center', color: '#7C3AED', fontWeight: 700, lineHeight: 1, pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 1px' }}>{holiday.name}</span>}
                        {!holiday && fwName && <span style={{ position: 'absolute', bottom: 1, left: 0, right: 0, fontSize: 7, textAlign: 'center', color: '#0891B2', fontWeight: 700, lineHeight: 1, pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 1px' }}>{fwName}</span>}
                      </div>
                      );
                    })}
                    {blocks.map(b => {
                      const x = dayX[b.sIdx] + 2;
                      const xEnd = dayX[b.eIdx] + allDays[b.eIdx].w - 2;
                      const w = Math.max(xEnd - x, 4);
                      const top = BLOCK_TOP + (b.track || 0) * (BLOCK_H + BLOCK_GAP);
                      const deptColor = staffColor(b.department);
                      const isDragging = dragging?.key === b.key;
                      const statusMod = job.clientStatus === 'FINANCIAL'
                        ? ' sched-grid-block--client-financial'
                        : job.clientStatus === 'LOST'
                          ? ' sched-grid-block--client-lost'
                          : '';
                      // Per-person attention flags (parity with People view's PTO / over-cap cell shading,
                      // surfaced per-block here): staffer scheduled over their PTO, or over capacity on a
                      // covered day (cross-job aware via dayHoursByStaff). Click → entry sidebar → Quick Optimize.
                      const ptoWarn = (b.dates || []).some(d => (ptoByStaffDay[b.staffId]?.[toISO(d.date)] || 0) > 0);
                      const capWarn = (b.dates || []).some(d => { const iso = toISO(d.date); const cap = getDayCap(b.staffId, iso); return cap > 0 && (dayHoursByStaff[b.staffId]?.[iso] || 0) > cap; });
                      // Departed staffer still holding this work. People view shows this on the
                      // staff row; Jobs view has no staff row, so it goes on the block.
                      const staffGone = scheduledInactiveIds.has(b.staffId);
                      const geom = barGeom(b, x, w);
                      const { barX, barW, contentW } = geom;
                      // Priority once the bar runs short: the name ellipsises, then the
                      // seniority bars drop, then the hours — decoration yields to data.
                      const roomForHours = contentW
                        - (staffGone ? BADGE_W.staffGone : 0)
                        - (capWarn ? BADGE_W.capWarn : 0)
                        - (ptoWarn ? BADGE_W.ptoWarn : 0)
                        - (offOffice(b.staffId, job.isOnSite, job.officeCode) ? BADGE_W.offOffice : 0);
                      const showSeniority = roomForHours > HOURS_MIN_W + SENIORITY_W;
                      return (
                        <Fragment key={b.key}>
                        {contMarker(b, 'left', deptColor, geom, top)}
                        <div
                          className={`sched-grid-block${isDragging ? ' sched-grid-block--dragging' : ''}${b.isEmpire ? ' sched-grid-block--empire' : ''}${job.isClosedInPE ? ' sched-grid-block--closed' : ''}${staffGone ? ' sched-grid-block--inactive' : ''}${selectedBlock?.key === b.key ? ' sched-grid-block--selected' : ''}${statusMod}`}
                          style={{ left: barX, top, width: barW, background: deptColor, color: '#fff' }}
                          draggable
                          onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; handleDragStart({ ...b, clientName: job.clientName, clientCode: job.clientCode, jobName: job.jobName, jobCode: job.jobCode, jobType: job.jobType }); }}
                          onDragEnd={() => { setDragging(null); hideDragTip(); gridBodyRef.current?.classList.remove('sched-grid-body--dragging'); }}
                          onClick={e => {
                            e.stopPropagation();
                            setTooltip(null);
                            if (jobParam) setJobParam(null);
                            const enriched = { ...b, clientName: job.clientName, clientCode: job.clientCode, jobName: job.jobName, jobCode: job.jobCode, jobType: job.jobType, clientId: job.clientId, clientStatus: job.clientStatus, isClosedInPE: job.isClosedInPE, peJobIndex: job.peJobIndex, managerName: job.managerName, managerId: job.managerId, partnerName: job.partnerName, partnerId: job.partnerId, nicheName: job.nicheName, isOnSite: !!job.isOnSite, office: job.office, officeCode: job.officeCode };
                            if (selectedBlock?.key === b.key) closeSidebar(); else setSelectedBlock(enriched);
                          }}
                          onMouseEnter={e => setTooltip({ block: { ...b, clientName: job.clientName, clientCode: job.clientCode, jobName: job.jobName, jobType: job.jobType, clientStatus: job.clientStatus }, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setTooltip(null)}>
                          <span className="sched-grid-block-text">{contentW > 60 ? b.staffName : b.initials}</span>
                          {staffGone && <span title={inactiveNoteFor(b.staffId)}
                            style={{ marginLeft: 4, fontSize: 9, fontWeight: 800, lineHeight: 1, color: '#92400E', flexShrink: 0, pointerEvents: 'none' }}>&#9888;</span>}
                          {capWarn && <span title="Over capacity on a scheduled day — click to open & Quick Optimize" style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, lineHeight: 1, color: '#fff', background: 'rgba(220,38,38,0.92)', borderRadius: 3, padding: '1px 4px', flexShrink: 0, whiteSpace: 'nowrap', pointerEvents: 'none' }}>!</span>}
                          {ptoWarn && <span title="Scheduled during this staffer's PTO — click to open & Quick Optimize" style={{ marginLeft: 4, fontSize: 8, fontWeight: 800, lineHeight: 1, color: '#fff', background: 'rgba(217,119,6,0.95)', borderRadius: 3, padding: '1px 4px', flexShrink: 0, whiteSpace: 'nowrap', pointerEvents: 'none' }}>PTO</span>}
                          {offOffice(b.staffId, job.isOnSite, job.officeCode) && <span className="sched-offoffice-badge" title={`On-site job in ${job.office || job.officeCode} — ${b.staffName} is from ${staffOfficeById[b.staffId] || 'another office'}`}>&#128205; off-office</span>}
                          {showSeniority && <SeniorityBars level={b.level} color="#fff" onFill style={{ marginLeft: 6, marginRight: 4 }} />}
                          {roomForHours > HOURS_MIN_W && <span className="sched-grid-block-hours">{b.totalHours}h</span>}
                          {job.clientStatus === 'FINANCIAL' && <span className="sched-grid-block-warn sched-grid-block-warn--financial" title="Client status: FINANCIAL (Stop Work)">⚠</span>}
                          {job.clientStatus === 'LOST' && <span className="sched-grid-block-warn sched-grid-block-warn--lost" title="Client status: LOST">✕</span>}
                        </div>
                        {contMarker(b, 'right', deptColor, geom, top)}
                        </Fragment>
                      );
                    })}
                    {/* Placeholder blocks for this job — stacked below the staff blocks. Click to assign to a real staffer. */}
                    {(placeholdersByJob[job.jobId]?.phBlocks || []).map(({ ph, sIdx, eIdx, track }) => {
                      if (sIdx < 0 && eIdx < 0) return null;
                      const safeS = sIdx < 0 ? 0 : sIdx;
                      const safeE = eIdx < 0 ? allDays.length - 1 : eIdx;
                      if (safeE < safeS) return null;
                      const x = dayX[safeS] + 2;
                      const xEnd = dayX[safeE] + allDays[safeE].w - 2;
                      const w = Math.max(xEnd - x, 30);
                      const top = BLOCK_TOP + ((job.maxTrack || 0) + (track || 0)) * (BLOCK_H + BLOCK_GAP);
                      const phc = phContBlock(ph);
                      const geom = barGeom(phc, x, w, true); // placeholder bars are border-box
                      const { barX, barW, contentW } = geom;
                      return (
                        <Fragment key={`ph_${ph.PlaceholderId}`}>
                        {phc && contMarker(phc, 'left', '#003D4C', geom, top)}
                        <div
                          data-tour={ph.PlaceholderId === SENTINEL_JOB_ID ? 'demo-placeholder' : undefined}
                          className="sched-grid-block sched-grid-block--placeholder"
                          style={{ left: barX, top, width: barW }}
                          onClick={e => { e.stopPropagation(); setPlaceholderAssignTarget(ph); }}
                          onMouseEnter={e => setTooltip({ block: { jobType: job.jobType, clientName: job.clientName, clientCode: job.clientCode, clientStatus: job.clientStatus, jobName: job.jobName, jobCode: job.jobCode, totalHours: Number(ph.Hours), staffName: 'Unassigned', level: ph.Level, departmentCode: ph.DepartmentCode, isPlaceholder: true }, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setTooltip(null)}>
                          <span className="sched-grid-block-text">{contentW > 70 ? `${ph.Level || 'Staff'} · placeholder` : (ph.Level || 'PH')}</span>
                          {contentW > 90 && <span className="sched-grid-block-hours">{Number(ph.Hours)}h</span>}
                        </div>
                          {phc && contMarker(phc, 'right', '#003D4C', geom, top)}
                          </Fragment>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Detail Panel ── */}
        {selectedBlock && (() => {
          const b = selectedBlock;

          // Custom-job detail. A slim panel by necessity, not by preference: rprs.CustomJobs
          // is Name/Description/Category/IsActive, so there is no budget to chart, no JM/JP
          // or niche to list, no client to link and no PE record to open. What remains is the
          // question DJB actually asked — who is on this? — so the roster is the panel.
          //
          // No Scheduled/WIP toggle either: PE has no concept of a custom job, so WIP is
          // empty by construction rather than merely absent.
          if (b._viewMode === 'customjob' && b._customJob) {
            const c = b._customJob;
            const catColor = CUSTOM_JOB_CATEGORY_COLORS[c.category] || CUSTOM_JOB_DEFAULT_COLOR;
            const roster = detailCustomSched.customJobId === c.customJobId ? detailCustomSched.byStaff : [];
            const rosterTotal = Math.round(roster.reduce((s, r) => s + (r.hours || 0), 0) * 100) / 100;
            return (
              <div ref={sidebarRef} className="sched-grid-detail">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span className="sched-type-tag" style={{ background: catColor.bg, color: catColor.text }}>{c.category}</span>
                  {!c.isActive && (
                    <span className="sched-grid-job-chip" style={{ background: '#F3F4F6', color: '#6B7280' }}
                      title="Deactivated in Admin — no longer schedulable, but it still holds scheduled time">
                      <span className="sched-grid-job-chip-dot" style={{ background: '#9CA3AF' }} />
                      Inactive
                    </span>
                  )}
                </div>
                <div className="sched-grid-detail-header">
                  <h3>{c.name}</h3>
                  <button onClick={closeSidebar}>&times;</button>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Firm-internal &#183; no budget</div>

                {c.description && (
                  <div style={{ marginBottom: 14 }}>
                    <div className="sched-grid-detail-label">ABOUT</div>
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 2, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{c.description}</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
                  <div>
                    <div className="sched-grid-detail-label">SCHEDULED, TODAY ON</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{rosterTotal}h</div>
                  </div>
                  {c.placeholderHours > 0 && (
                    <div>
                      <div className="sched-grid-detail-label">PLACEHOLDER</div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{c.placeholderHours}h</div>
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 16 }}>
                  <div className="sched-grid-detail-label" style={{ marginBottom: 6 }}>TEAM ({roster.length})</div>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {roster.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#9CA3AF', padding: '6px 0' }}>No scheduled (today on) staff.</div>
                    ) : roster.map(sb => {
                      const dept = sb.department || (staff.find(s => s.StaffId === sb.staffId)?.DepartmentCode) || '';
                      const deptColor = staffColor(dept);
                      return (
                        <div key={sb.staffId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                          <div className="sched-grid-avatar" style={{ width: 26, height: 26, fontSize: 9, background: deptColor }}>{sb.initials}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>
                              {sb.staffId
                                ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${sb.staffId}`); }}>{sb.name}</span>
                                : sb.name}
                            </div>
                            <div style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 }}>{sb.level}<SeniorityBars level={sb.level} color={deptColor} /></div>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{Math.round((sb.hours || 0) * 100) / 100}h</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }

          // Jobs View detail: show all staff on this job
          if (b._viewMode === 'jobs' && b._job) {
            const job = b._job;
            const typeColor = JOB_TYPE_COLORS[job.jobType] || { bg: BRAND.oceanBlue, text: '#fff' };
            return (
              <div ref={sidebarRef} className="sched-grid-detail">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>{job.jobType || 'Other'}</span>
                  <OnSiteToggle isOnSite={!!job.isOnSite} office={job.office} officeCode={job.officeCode}
                    canEdit={canSetOnSite(job)} onChange={(next) => handleSetOnSite(job.jobId, next)} />
                </div>
                <div className="sched-grid-detail-header">
                  <h3>
                    {job.clientId ? (
                      <span className="sched-grid-detail-link" onClick={() => navigate(`/schedule/client/${job.clientId}`)}>
                        {job.clientName} &#8599;
                      </span>
                    ) : job.clientName}
                  </h3>
                  <button onClick={closeSidebar}>&times;</button>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>{job.jobName}{job.clientCode ? ` \u00B7 ${job.clientCode}` : ''}</div>
                {job.clientStatus === 'LOST' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '5px 8px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6 }}>
                    <span style={{ fontSize: 14, color: '#991B1B' }}>&#10005;</span>
                    <span style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>Client is LOST &#8212; remove scheduled work</span>
                  </div>
                )}
                {job.clientStatus === 'FINANCIAL' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '5px 8px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
                    <span style={{ fontSize: 14, color: '#B45309' }}>&#9888;</span>
                    <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>Client is FINANCIAL &#8212; Stop Work until status changes</span>
                  </div>
                )}
                {job.isClosedInPE && renderClosedJobBanner(job.jobId, { closedAt: job.closedAt, jobManagerId: job.jobManagerId, jobPartnerId: job.jobPartnerId })}

                {renderJobProgress(job.jobId)}

                {(job.nicheName || job.clientIndustry) && (
                  <div style={{ marginBottom: 14 }}>
                    <div className="sched-grid-detail-label">NICHE</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{job.nicheName || job.clientIndustry}</div>
                  </div>
                )}

                {job.managerName && (
                  <div style={{ marginBottom: 14 }}>
                    <div className="sched-grid-detail-label">MANAGER</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {job.managerId
                        ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${job.managerId}`); }}>{job.managerName}</span>
                        : job.managerName}
                    </div>
                  </div>
                )}

                {job.partnerName && (
                  <div style={{ marginBottom: 14 }}>
                    <div className="sched-grid-detail-label">PARTNER</div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {job.partnerId
                        ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${job.partnerId}`); }}>{job.partnerName}</span>
                        : job.partnerName}
                    </div>
                  </div>
                )}

                {renderJobStaffList(job.jobId)}

                {job.peJobIndex && (
                  <button className="sched-grid-action-btn sched-grid-action-pe" style={{ marginTop: 12, width: '100%' }}
                    onClick={() => window.open(`https://bmss.pehosted.com/PE/Job/Job/${job.peJobIndex}?panel=0`, '_blank')}>
                    &#8599; View Job in PE
                  </button>
                )}
              </div>
            );
          }

          const typeColor = JOB_TYPE_COLORS[b.jobType] || { bg: '#E5E7EB', text: '#374151' };
          const startD = b.dates?.[0]?.date;
          const endD = b.dates?.[b.dates.length - 1]?.date;
          const fmtD = (d) => d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014';
          // Assignee identity (People blocks omit level/dept/initials \u2014 fall back to the staff record).
          const asg = b.staffId ? staff.find(s => s.StaffId === b.staffId) : null;
          const asgName = b.staffName || asg?.Name || '';
          const asgLevel = b.level || asg?.Level || '';
          const asgDept = b.department || asg?.DepartmentCode || '';
          const asgInitials = b.initials || asg?.Initials || (asgName ? asgName.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() : '??');
          const asgColor = staffColor(asgDept);
          return (
            <div ref={sidebarRef} className="sched-grid-detail">
              {/* Type badge + on-site toggle (same row, space-efficient) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                  {b.jobType || 'Other'}
                </span>
                {b.jobId && !b.isCustom && (
                  <OnSiteToggle isOnSite={!!b.isOnSite} office={b.office} officeCode={b.officeCode}
                    canEdit={canSetOnSite(b)} onChange={(next) => handleSetOnSite(b.jobId, next)} />
                )}
              </div>

              <div className="sched-grid-detail-header">
                <h3>
                  {b.clientId ? (
                    <span className="sched-grid-detail-link" onClick={() => navigate(`/schedule/client/${b.clientId}`)}>
                      {b.clientName} &#8599;
                    </span>
                  ) : (b.isCustom ? b.jobName : b.clientName)}
                </h3>
                <button onClick={closeSidebar}>&times;</button>
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                {b.isCustom ? 'Firm-internal \u00B7 no budget' : `${b.jobName}${b.clientCode ? ` \u00B7 ${b.clientCode}` : ''}`}
              </div>
              {offOffice(b.staffId, b.isOnSite, b.officeCode) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '5px 8px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
                  <span style={{ fontSize: 14 }}>&#128205;</span>
                  <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                    On-site job in {b.office || b.officeCode} — {asgName || 'this staffer'} is from {staffOfficeById[b.staffId] || 'another office'}. Consider reassigning or Quick Optimize.
                  </span>
                </div>
              )}
              {b.clientStatus === 'LOST' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '5px 8px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6 }}>
                  <span style={{ fontSize: 14, color: '#991B1B' }}>&#10005;</span>
                  <span style={{ fontSize: 11, color: '#991B1B', fontWeight: 600 }}>Client is LOST &#8212; remove scheduled work</span>
                </div>
              )}
              {b.clientStatus === 'FINANCIAL' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '5px 8px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
                  <span style={{ fontSize: 14, color: '#B45309' }}>&#9888;</span>
                  <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>Client is FINANCIAL &#8212; Stop Work until status changes</span>
                </div>
              )}
              {/* staffId/staffName: a People-view block belongs to one person, so the
                  single-staffer clear targets THEM, not whoever is signed in. */}
              {b.isClosedInPE && !b.isCustom && renderClosedJobBanner(b.jobId, { closedAt: b.closedAt, jobManagerId: b.jobManagerId, jobPartnerId: b.jobPartnerId, staffId: b.staffId, staffName: asgName || b.staffName })}
              {b.isEmpire && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '5px 8px', background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 6 }}>
                  <span style={{ fontSize: 12 }}>&#8635;</span>
                  <span style={{ fontSize: 11, color: '#C2410C', fontWeight: 600 }}>
                    Synced from Empire Suite{b.empireSyncedAt ? ` \u00B7 ${new Date(b.empireSyncedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                  </span>
                </div>
              )}

              {/* Assigned staffer */}
              {asgName && (
                <div style={{ marginBottom: 14 }}>
                  <div className="sched-grid-detail-label">ASSIGNED TO</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                    <div className="sched-grid-avatar" style={{ width: 26, height: 26, fontSize: 9, background: asgColor }}>{asgInitials}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>
                        {b.staffId
                          ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${b.staffId}`); }}>{asgName}</span>
                          : asgName}
                      </div>
                      {asgLevel && <div style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 }}>{asgLevel}<SeniorityBars level={asgLevel} color={asgColor} /></div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Note (block-level — scoped to this clicked entry; editable via Edit Entry) */}
              {b.notes && (
                <div style={{ marginBottom: 14 }}>
                  <div className="sched-grid-detail-label">NOTE</div>
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 2, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{b.notes}</div>
                </div>
              )}

              {/* Start / End */}
              <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
                <div>
                  <div className="sched-grid-detail-label">START{b.span?.clippedLeft ? ' (IN VIEW)' : ''}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: b.span?.clippedLeft ? '#B45309' : undefined }}>{fmtD(startD)}</div>
                </div>
                <div>
                  <div className="sched-grid-detail-label">END{b.span?.clippedRight ? ' (IN VIEW)' : ''}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: b.span?.clippedRight ? '#B45309' : undefined }}>{fmtD(endD)}</div>
                </div>
              </div>

              {/* Hours \u00b7 Job Manager \u00b7 Job Partner (compact) */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div>
                  <div className="sched-grid-detail-label">HOURS{b.span ? ' (IN VIEW)' : ''}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: b.span ? '#B45309' : undefined }}>{b.totalHours}h</div>
                </div>
                {b.managerName && (
                  <div>
                    <div className="sched-grid-detail-label">JM</div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      {b.managerId
                        ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${b.managerId}`); }}>{b.managerName}</span>
                        : b.managerName}
                    </div>
                  </div>
                )}
                {b.partnerName && (
                  <div>
                    <div className="sched-grid-detail-label">JP</div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>
                      {b.partnerId
                        ? <span className="sched-grid-detail-link" onClick={(e) => { e.stopPropagation(); navigate(`/schedule/employee/${b.partnerId}`); }}>{b.partnerName}</span>
                        : b.partnerName}
                    </div>
                  </div>
                )}
              </div>

              {/* The grid fetches entries for exactly the visible window, so every figure
                  above describes only the slice on screen. When that is not the whole
                  assignment, say what the whole one is rather than leaving the reader to
                  widen the zoom and re-add it themselves. */}
              {b.span && (
                <div style={{ marginBottom: 14, padding: '7px 9px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
                  <div className="sched-grid-detail-label" style={{ color: '#92400E' }}>FULL ASSIGNMENT</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginTop: 2 }}>
                    {fmtShort(b.span.fullMin)} &ndash; {fmtShort(b.span.fullMax)} &middot; {b.span.fullHours}h
                  </div>
                  <div style={{ fontSize: 10, color: '#B45309', marginTop: 2 }}>
                    This entry runs past the {b.span.clippedLeft && b.span.clippedRight ? 'weeks' : b.span.clippedRight ? 'last week' : 'first week'} on screen.
                  </div>
                </div>
              )}

              {/* Job progress (budget context for this entry) */}
              {b.jobId && !b.isCustom && renderJobProgress(b.jobId)}

              {/* Daily hours chart — week-by-week grid rows. Built from b.dates, so it
                  shows only the in-window days; the note above already says so. */}
              {b.dates && b.dates.length > 0 && (() => {
                const maxH = Math.max(...b.dates.map(d => d.hours || 0), 8);
                // Group dates into weeks (Mon-Fri rows)
                const weeks = [];
                let currentWeek = null;
                for (const d of b.dates) {
                  const dt = d.date;
                  const mon = new Date(dt);
                  mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7)); // snap to Monday
                  const monKey = toISO(mon);
                  if (!currentWeek || currentWeek.monKey !== monKey) {
                    currentWeek = { monKey, monDate: mon, days: {} };
                    weeks.push(currentWeek);
                  }
                  const dow = (dt.getDay() + 6) % 7; // 0=Mon, 4=Fri
                  currentWeek.days[dow] = d;
                }
                // Check if any week has Saturday data (dow=5)
                const hasSat = weeks.some(wk => wk.days[5]);
                const DOW = hasSat ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
                const dayIndices = hasSat ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4];
                const colCount = DOW.length;
                return (
                  <div style={{ marginBottom: 14 }}>
                    <div className="sched-grid-detail-label" style={{ marginBottom: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}
                      onClick={() => setEntryHistOpen(o => !o)} title={entryHistOpen ? 'Collapse' : 'Expand'}>
                      <span style={{ fontSize: 9 }}>{entryHistOpen ? '▾' : '▸'}</span>DAILY BREAKDOWN
                    </div>
                    {entryHistOpen && (<>
                    {/* Day headers */}
                    <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(${colCount}, 1fr)`, gap: 3, marginBottom: 2 }}>
                      <div />
                      {DOW.map(d => (
                        <div key={d} style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'center' }}>{d}</div>
                      ))}
                    </div>
                    {/* Week rows */}
                    {weeks.map((wk, wi) => {
                      const weekLabel = wk.monDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return (
                        <div key={wi} style={{ display: 'grid', gridTemplateColumns: `56px repeat(${colCount}, 1fr)`, gap: 3, marginBottom: 4 }}>
                          <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                            {weekLabel}
                          </div>
                          {dayIndices.map(dow => {
                            const d = wk.days[dow];
                            const h = d?.hours || 0;
                            const barH = h > 0 ? Math.max(4, Math.round((h / maxH) * 40)) : 0;
                            return (
                              <div key={dow} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                                {h > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: '#374151', marginBottom: 2 }}>{h}h</span>}
                                <div style={{
                                  width: '100%', height: barH, borderRadius: 4, minHeight: h > 0 ? 4 : 2,
                                  background: h > 8 ? '#F59E0B' : h > 0 ? BRAND.oceanBlue : '#F3F4F6',
                                }} title={h > 0 ? `${DOW[dow]}: ${h}h` : ''} />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    </>)}
                  </div>
                );
              })()}

              {/* Job Work Status */}
              {b.statusDetail && (
                <div style={{ marginBottom: 14 }}>
                  <div className="sched-grid-detail-label">JOB WORK STATUS</div>
                  <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>{b.statusDetail}</div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                <button
                  className="sched-grid-action-btn sched-grid-action-edit"
                  onClick={() => openEditModal(b)}
                >
                  &#9998; Edit Entry
                </button>
                <button
                  className="sched-grid-action-btn"
                  onClick={() => openOptimizeModal(b)}
                >
                  &#9889; Quick Optimize
                </button>
                {b.peJobIndex && (
                  <button
                    className="sched-grid-action-btn sched-grid-action-pe"
                    onClick={() => window.open(`https://bmss.pehosted.com/PE/Job/Job/${b.peJobIndex}?panel=0`, '_blank')}
                  >
                    &#8599; View Job in PE
                  </button>
                )}
                <button
                  className="sched-grid-action-btn sched-grid-action-remove"
                  onClick={() => handleRemoveEntry(b)}
                >
                  &#128465; Remove Entry
                </button>
                {/* Both open in place (this used to navigate to the Client page). Job history is
                    the whole job; person history is everything that happened to this person's
                    schedule — including custom jobs like CPE, which have no job history. */}
                {b.jobId && !b.isCustom && (
                  <button
                    className="sched-grid-action-btn"
                    onClick={() => setHistoryModal({ jobId: b.jobId, jobName: b.jobName || b.serviceDesc || 'Job', clientName: b.clientName || '' })}
                  >
                    &#128203; View Job History
                  </button>
                )}
                {b.staffId && (
                  <button
                    className="sched-grid-action-btn"
                    onClick={() => setHistoryModal({ staffId: b.staffId, staffName: asgName || b.staffName || '' })}
                  >
                    &#128203; View {(asgName || b.staffName || 'Staff').split(/\s+/)[0]}&rsquo;s History
                  </button>
                )}
              </div>

              {/* Other team members on this job — clickable + Scheduled/WIP toggle (shared) */}
              {renderJobStaffList(b.jobId)}
            </div>
          );
        })()}
      </div>}

      {/* ── Drop Preview Modal ── */}
      {dropPreview && (() => {
        const dp = dropPreview;
        const block = dp.block;
        const totalHours = Object.values(dp.dailyHours).reduce((s, h) => s + (h || 0), 0);
        // Group dates by week for the grid display
        const weekGroups = [];
        let currentWeek = null;
        const hasSat = dp.newDates.some(iso => new Date(iso + 'T12:00:00').getDay() === 6);
        const DOW = hasSat ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

        // Build old hours lookup by ISO date
        const oldHoursByDate = {};
        for (const d of dp.oldEntries) oldHoursByDate[toISO(d.date)] = d.hours;
        const oldStartISO = dp.oldEntries.length > 0 ? toISO(dp.oldEntries[0].date) : null;
        const oldEndISO = dp.oldEntries.length > 0 ? toISO(dp.oldEntries[dp.oldEntries.length - 1].date) : null;
        const newStartISO = dp.newDates[0] || null;
        const newEndISO = dp.newDates[dp.newDates.length - 1] || null;

        for (const iso of dp.newDates) {
          const dt = new Date(iso + 'T12:00:00');
          const mon = new Date(dt);
          mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
          const monKey = toISO(mon);
          if (!currentWeek || currentWeek.monKey !== monKey) {
            currentWeek = { monKey, monLabel: mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), days: {} };
            weekGroups.push(currentWeek);
          }
          const dowIdx = (dt.getDay() + 6) % 7;
          currentWeek.days[dowIdx] = { iso, hours: dp.dailyHours[iso] || 0 };
        }

        return (
          <div ref={dndModalRef} className="sched-mgr-modal-backdrop" {...dndBackdrop}>
            <div className="sched-mgr-modal" style={{ width: 850, maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ background: BRAND.deepTeal, color: '#fff', margin: '-24px -24px 16px', padding: '14px 24px', borderRadius: '14px 14px 0 0' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {dp.targetJobId ? 'Reassign to job' : (dp.sameStaff ? 'Reschedule' : 'Reassign')}: {block.clientName}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                  {block.jobName}
                  {dp.targetJobId && dp.targetJobName && <> &rarr; <strong>{dp.targetJobName}</strong></>}
                  {!dp.sameStaff && <> &middot; {block.staffName} &rarr; <strong>{dp.targetStaffName}</strong></>}
                </div>
                {/* Date range diff */}
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ padding: '3px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.15)', fontSize: 11 }}>
                    <span style={{ opacity: 0.6 }}>
                      {oldStartISO && new Date(oldStartISO + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' '}&ndash;{' '}{oldEndISO && new Date(oldEndISO + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {(oldStartISO !== newStartISO || oldEndISO !== newEndISO) && (
                    <>
                      <span style={{ fontSize: 12 }}>&rarr;</span>
                      <div style={{ padding: '3px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.25)', fontSize: 11, fontWeight: 700 }}>
                        {newStartISO && new Date(newStartISO + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {' '}&ndash;{' '}{newEndISO && new Date(newEndISO + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </>
                  )}
                  <span style={{ opacity: 0.7, fontSize: 11, marginLeft: 'auto' }}>{Math.round(totalHours * 100) / 100}h total</span>
                </div>
              </div>

              {/* Mode toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280' }}>MODE:</span>
                <div style={{ display: 'flex', gap: 2, background: '#F3F4F6', borderRadius: 5, padding: 2 }}>
                  <button type="button" onClick={() => {
                    // Rebuild shift mode from original entries using pristine originalNewDates
                    const shiftDates = dp.originalNewDates;
                    const dailyHours = {};
                    for (let i = 0; i < dp.oldEntries.length; i++) {
                      dailyHours[shiftDates[i]] = dp.oldEntries[i].hours;
                    }
                    const warnings = [];
                    for (const iso of shiftDates) {
                      const dayCap = getDayCap(dp.targetStaffId, iso);
                      const existing = (dayHoursByStaff[dp.targetStaffId]?.[iso] || 0);
                      const self = dp.sameStaff ? dp.oldEntries.filter(d => toISO(d.date) === iso).reduce((s, d) => s + d.hours, 0) : 0;
                      if (existing - self + (dailyHours[iso] || 0) > dayCap && dayCap > 0) {
                        warnings.push({ date: iso, scheduled: existing - self + dailyHours[iso], capacity: dayCap });
                      }
                    }
                    setDropPreview(prev => ({ ...prev, mode: 'shift', dailyHours, warnings, newDates: [...prev.originalNewDates] }));
                  }}
                    style={{ padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                      background: dp.mode === 'shift' ? BRAND.deepTeal : 'transparent', color: dp.mode === 'shift' ? '#fff' : '#6B7280' }}>
                    Shift
                  </button>
                  <button type="button" onClick={handleDropOptimize}
                    style={{ padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none', cursor: 'pointer',
                      background: dp.mode === 'optimize' ? BRAND.deepTeal : 'transparent', color: dp.mode === 'optimize' ? '#fff' : '#6B7280' }}>
                    Optimize
                  </button>
                </div>
                <span style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 4 }}>
                  {dp.mode === 'shift' ? 'Keep original daily hours pattern' : 'Fill each day up to remaining capacity'}
                </span>
              </div>

              {/* Overflow options (optimize mode only) */}
              {dp.mode === 'optimize' && (
                <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 10, color: '#6B7280' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={dropOverflowStart}
                      onChange={e => setDropOverflowStart(e.target.checked)}
                      style={{ accentColor: BRAND.deepTeal }} />
                    Extend before start date
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={dropOverflowEnd}
                      onChange={e => setDropOverflowEnd(e.target.checked)}
                      style={{ accentColor: BRAND.deepTeal }} />
                    Extend after end date
                  </label>
                </div>
              )}

              {/* Per-day grid */}
              <div style={{ marginBottom: 12 }}>
                {/* Day headers */}
                <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${DOW.length}, 1fr)`, gap: 4, marginBottom: 4 }}>
                  <div />
                  {DOW.map(d => (
                    <div key={d} style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'center' }}>{d}</div>
                  ))}
                </div>
                {/* Week rows */}
                {weekGroups.map((wk, wi) => {
                  return (
                    <div key={wi} style={{ display: 'grid', gridTemplateColumns: `60px repeat(${DOW.length}, 1fr)`, gap: 4, marginBottom: 6 }}>
                      <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        {wk.monLabel}
                      </div>
                      {DOW.map((_, di) => {
                        const d = wk.days[di];
                        if (!d) return <div key={di} />;
                        const dayCap = getDayCap(dp.targetStaffId, d.iso);
                        const existing = (dayHoursByStaff[dp.targetStaffId]?.[d.iso] || 0);
                        const self = dp.sameStaff ? dp.oldEntries.filter(oe => toISO(oe.date) === d.iso).reduce((s, oe) => s + oe.hours, 0) : 0;
                        const otherHours = existing - self; // hours from other jobs on this day
                        const dayTotal = otherHours + d.hours;
                        const isOver = dayTotal > dayCap && dayCap > 0;
                        const oldH = oldHoursByDate[d.iso]; // hours this block had on this date originally
                        const isChanged = oldH == null || oldH !== d.hours;
                        return (
                          <div key={di} style={{ textAlign: 'center' }}>
                            {/* Cell: show old→new inline for changes, gray for unchanged */}
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                              padding: '3px 2px', borderRadius: 4, minHeight: 28,
                              border: `1.5px solid ${isOver ? '#EF4444' : isChanged ? '#F59E0B' : '#E5E7EB'}`,
                              background: isOver ? '#FEF2F2' : !isChanged ? '#F9FAFB' : '#fff',
                            }}>
                              {isChanged && oldH != null && (
                                <span style={{ fontSize: 10, color: '#9CA3AF', textDecoration: 'line-through' }}>{oldH}</span>
                              )}
                              {isChanged && oldH != null && (
                                <span style={{ fontSize: 8, color: '#9CA3AF' }}>&rarr;</span>
                              )}
                              <input
                                type="number" min="0" max="24" step="0.5"
                                value={d.hours}
                                onChange={e => {
                                  const val = parseFloat(e.target.value) || 0;
                                  setDropPreview(prev => ({
                                    ...prev,
                                    dailyHours: { ...prev.dailyHours, [d.iso]: val },
                                  }));
                                }}
                                style={{
                                  width: isChanged && oldH != null ? 40 : '100%', padding: '0 2px', borderRadius: 2, fontSize: 12,
                                  textAlign: 'center', fontWeight: 700, border: 'none', outline: 'none',
                                  background: 'transparent',
                                  color: isOver ? '#DC2626' : !isChanged ? '#9CA3AF' : '#374151',
                                }}
                              />
                            </div>
                            <div style={{ fontSize: 8, color: isOver ? '#DC2626' : '#9CA3AF', marginTop: 1, fontWeight: isOver ? 600 : 400 }}>
                              {dayTotal % 1 === 0 ? dayTotal : Math.round(dayTotal * 100) / 100}/{dayCap}h
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Off-office advisory (on-site job, target staffer from another office) */}
              {dp.officeWarn && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 12, padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8 }}>
                  <span style={{ fontSize: 13 }}>&#128205;</span>
                  <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600, lineHeight: 1.5 }}>
                    On-site job in {dp.officeWarn.office} — {dp.targetStaffName} is from {dp.officeWarn.staffOffice}. You can still proceed; consider a same-office staffer.
                  </span>
                </div>
              )}

              {/* Summary + warnings */}
              {dp.warnings.length > 0 ? (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 13 }}>&#9888;&#65039;</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#DC2626' }}>
                      {dp.warnings.length} day{dp.warnings.length > 1 ? 's' : ''} over capacity
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#991B1B', lineHeight: 1.6 }}>
                    {dp.mode === 'shift'
                      ? 'Try Optimize mode to redistribute hours within capacity, or manually adjust the values above.'
                      : 'Total hours exceed available capacity across these days. Adjust values above or accept the overage.'}
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13 }}>&#9989;</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#065F46' }}>
                      All days within capacity
                    </span>
                  </div>
                </div>
              )}

              {/* Before / After View */}
              {(() => {
                const origHours = {};
                for (const e of dp.oldEntries) {
                  const iso = e.date instanceof Date ? toISO(e.date) : String(e.date).slice(0, 10);
                  origHours[iso] = e.hours;
                }
                return (
                  <BeforeAfterSummary
                    originalDailyHours={origHours}
                    proposedDailyHours={dp.dailyHours}
                    getDayCap={getDayCap}
                    ptoByStaffDay={ptoByStaffDay}
                    holidayByDate={holidayByDate}
                    globalAssignmentByDate={globalAssignmentByDate}
                    staffDeptCode={staff.find(s => s.StaffId === dp.targetStaffId)?.DepartmentCode}
                    staffId={dp.targetStaffId}
                    originalStaff={dp.block.staffName}
                    newStaff={dp.targetStaffName}
                    originalJob={dp.block.jobName}
                    newJob={dp.block.jobName}
                    isNewEntry={false}
                    displayTotal={totalHours}
                    dayHoursByStaff={dayHoursByStaff}
                    editEntryIds={new Set(dp.sameStaff ? dp.oldEntries.map(d => d.entryId) : [])}
                    sameJobDailyHours={sameJobHoursFor(dp.targetStaffId, dp.targetJobId || dp.block.jobId, dp.sameStaff ? dp.oldEntries.map(d => d.entryId) : [])}
                  />
                );
              })()}

              {/* Notes */}
              <div style={{ marginBottom: 8 }}>
                <input type="text" placeholder="Add a note (optional)" defaultValue="" onChange={e => { dropNotesRef.current = e.target.value; }}
                  style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' }} />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="sched-mgr-pill" onClick={() => { closeDndModal(); dropNotesRef.current = ''; }} style={{ padding: '8px 16px' }}>
                  Cancel
                </button>
                <button className="sched-assign-btn" onClick={handleConfirmDrop} style={{ padding: '8px 20px' }}>
                  Confirm {dp.sameStaff ? 'Move' : 'Reassign'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Tooltip (enhanced) ── */}
      {/* Drag hover tooltip */}
      <DragHoverTooltip ref={dragTipRef} />

      {/* Schedule History — job or person mode, opened in place from the block sidebar */}
      {historyModal && (
        <ScheduleHistoryModal
          jobId={historyModal.jobId}
          jobName={historyModal.jobName}
          clientName={historyModal.clientName}
          staffId={historyModal.staffId}
          staffName={historyModal.staffName}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* Placeholder assign modal (Item 1) */}
      {placeholderAssignTarget && (
        <PlaceholderAssignModal
          placeholder={placeholderAssignTarget}
          currentUser={username}
          // Only consumed for CUSTOM-job placeholders, which have no job to score
          // candidates against, so the modal builds the (level, dept) pool itself.
          staffList={gridEligibleStaff}
          getDayCap={getDayCap}
          entries={entries}
          dayHoursByStaff={dayHoursByStaff}
          ptoByStaffDay={ptoByStaffDay}
          holidayByDate={holidayByDate}
          globalAssignmentByDate={globalAssignmentByDate}
          onClose={() => setPlaceholderAssignTarget(null)}
          onAssigned={async () => {
            // Reload placeholders + schedule so the placeholder block disappears
            // and the new ScheduleEntries show under the staffer's row.
            try {
              const [phRows, schedData] = await Promise.all([
                fetchPlaceholders(),
                fetchSchedule({ startDate: toISO(baseMonday), endDate: toISO(endDate) }),
              ]);
              setPlaceholders(phRows || []);
              setEntries(schedData || []);
            } catch (_) { /* ignore — next manual refresh will pick up */ }
            setPlaceholderAssignTarget(null);
          }}
          // An edit may have MOVED the placeholder to another level, or (real jobs
          // only) merged it into an existing row that this one no longer is. Refetch
          // and close rather than trying to reconcile the open modal's copy.
          onEdited={async () => {
            try {
              const rows = await fetchPlaceholders();
              setPlaceholders(rows || []);
            } catch (_) { /* ignore — next manual refresh will pick up */ }
            setPlaceholderAssignTarget(null);
          }}
          onDeleted={async () => {
            try {
              const phRows = await fetchPlaceholders();
              setPlaceholders(phRows || []);
            } catch (_) { /* ignore */ }
            setPlaceholderAssignTarget(null);
          }}
        />
      )}

      {/* ── Convert-to-placeholder preview (DND path) ─────────────────────── */}
      {convertPreview && (
        <div className="sched-mgr-modal-backdrop" onClick={() => !convertSaving && setConvertPreview(null)}>
          <div className="sched-mgr-modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <div className="sched-mgr-modal-header">
              <h3>Convert to placeholder</h3>
              <button className="sched-mgr-sidebar-close sched-mgr-modal-btn sched-mgr-x-btn" onClick={() => !convertSaving && setConvertPreview(null)}>&times;</button>
            </div>
            <div className="sched-mgr-modal-field" style={{
              background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8,
              padding: '12px 14px', fontSize: 12, lineHeight: 1.5,
            }}>
              <div style={{ color: '#374151' }}>
                <div><strong>From:</strong> {convertPreview.block.staffName || '(staff)'}</div>
                <div><strong>Job:</strong> {convertPreview.block.jobName || '(job)'}</div>
                <div>
                  <strong>Range:</strong>{' '}
                  {convertPreview.dayShift ? (
                    <>
                      <span style={{ color: '#9CA3AF', textDecoration: 'line-through', marginRight: 6 }}>
                        {convertPreview.originalMinDate} → {convertPreview.originalMaxDate}
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        {convertPreview.minDate} → {convertPreview.maxDate}
                      </span>
                    </>
                  ) : (
                    <span>{convertPreview.minDate} → {convertPreview.maxDate}</span>
                  )}
                </div>
                {convertPreview.dayShift !== 0 && (
                  <div style={{ fontSize: 11, color: '#0F766E', marginTop: 2 }}>
                    Shifted by {Math.abs(convertPreview.dayShift)} day{Math.abs(convertPreview.dayShift) === 1 ? '' : 's'}
                    {convertPreview.dayShift > 0 ? ' forward' : ' backward'}
                    {convertPreview.snappedFromWeekend ? ' (snapped from weekend to Monday)' : ''}
                    {' · workday count preserved'}
                  </div>
                )}
                <div><strong>Total hours:</strong> {convertPreview.totalHours.toFixed(2)}h</div>
                <div style={{ marginTop: 6, color: '#6B7280', fontStyle: 'italic' }}>
                  The current daily breakdown will be discarded. When re-assigned,
                  Optimize will recompute daily values for the new staffer.
                </div>
                <div style={{ marginTop: 6 }}>
                  <strong>Going to:</strong> {convertPreview.departmentCode} · {convertPreview.level} placeholder pool for this job.
                </div>
                {convertPreview.willMergeWith && (
                  <div style={{
                    marginTop: 8, padding: '6px 8px', borderRadius: 4,
                    background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D',
                  }}>
                    ⚠ Will merge with existing placeholder (#{convertPreview.willMergeWith.PlaceholderId}, currently
                    {' '}{Number(convertPreview.willMergeWith.Hours).toFixed(2)}h from
                    {' '}{String(convertPreview.willMergeWith.StartDate).slice(0, 10)} to
                    {' '}{String(convertPreview.willMergeWith.EndDate).slice(0, 10)}). Hours will sum and the date
                    range will envelope both.
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button
                type="button"
                className="sched-mgr-modal-btn sched-mgr-stepper-btn"
                disabled={convertSaving}
                onClick={() => setConvertPreview(null)}
                style={{
                  flex: 1, padding: '8px 12px', fontSize: 13, fontWeight: 600,
                  borderRadius: 6, border: '1px solid #D1D5DB',
                  background: '#fff', color: '#374151',
                }}
              >Cancel</button>
              <button
                type="button"
                className="sched-assign-btn sched-mgr-modal-btn sched-mgr-action-btn"
                disabled={convertSaving}
                onClick={handleConfirmConvertDrop}
                style={{ flex: 2 }}
              >{convertSaving ? 'Converting...' : 'Convert to Placeholder'}</button>
            </div>
          </div>
        </div>
      )}

      {/* PTO Approve/Reject popover (admin clicks pending PTO cell) */}
      {ptoCellPopover && (
        <div className="sched-mgr-modal-backdrop" style={{ background: 'transparent' }}
          onClick={() => setPtoCellPopover(null)}>
          <div style={{
            position: 'fixed', left: Math.min(ptoCellPopover.x, window.innerWidth - 280), top: Math.min(ptoCellPopover.y, window.innerHeight - 200),
            background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', border: '1px solid #E5E7EB',
            padding: 16, width: 260, zIndex: 400,
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{ptoCellPopover.req.staffName}</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 2 }}>
              {(() => {
                const r = ptoCellPopover.req;
                const fmtD = iso => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?';
                return r.startDate === r.endDate ? fmtD(r.startDate) : `${fmtD(r.startDate)} \u2013 ${fmtD(r.endDate)}`;
              })()}
              {' \u00B7 '}{ptoCellPopover.req.totalHours}h ({ptoCellPopover.req.days.length} day{ptoCellPopover.req.days.length !== 1 ? 's' : ''})
            </div>
            {ptoCellPopover.req.notes && ptoCellPopover.req.notes !== 'New Leave Request' && ptoCellPopover.req.notes !== 'Leave Request' && (
              <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', marginBottom: 6 }}>
                {ptoCellPopover.req.notes.replace(/^New Leave Request\s*[-\u2013]\s*/i, '')}
              </div>
            )}
            <div style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
              background: '#DBEAFE', color: '#1D4ED8', marginBottom: 10 }}>PENDING</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={ptoPopoverActioning}
                onClick={async () => {
                  if (!window.confirm(`Approve PTO for ${ptoCellPopover.req.staffName}?`)) return;
                  setPtoPopoverActioning(true);
                  try {
                    const approvedReq = ptoCellPopover.req;
                    await approvePTO(approvedReq.leaveReqId);
                    setPtoCellPopover(null);
                    // Check if staff has scheduled work on the approved PTO days
                    const conflictDays = (approvedReq.days || []).filter(d => (dayHoursByStaff[approvedReq.staffId]?.[d] || 0) > 0);
                    if (conflictDays.length > 0) {
                      const totalConflict = conflictDays.reduce((s, d) => s + (dayHoursByStaff[approvedReq.staffId]?.[d] || 0), 0);
                      showToast(`PTO approved \u2014 ${approvedReq.staffName} has ${totalConflict}h scheduled on ${conflictDays.length} PTO day${conflictDays.length > 1 ? 's' : ''}. Consider reassigning or optimizing.`, 'info', true);
                    } else {
                      showToast('PTO approved', 'info');
                    }
                    const refreshed = await fetchPTO({ startDate: toISO(baseMonday), endDate: toISO(addDays(baseMonday, zoom * 7 - 1)) });
                    setPtoData(refreshed || []);
                  } catch (e) { alert('Approve failed: ' + e.message); }
                  setPtoPopoverActioning(false);
                }}
                style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: '#10B981', color: '#fff', fontSize: 12, fontWeight: 700, transition: 'opacity 0.15s',
                  opacity: ptoPopoverActioning ? 0.5 : 1 }}>
                {ptoPopoverActioning ? '...' : '\u2713 Approve'}
              </button>
              <button disabled={ptoPopoverActioning}
                onClick={async () => {
                  if (!window.confirm(`Reject PTO for ${ptoCellPopover.req.staffName}?`)) return;
                  setPtoPopoverActioning(true);
                  try {
                    await rejectPTO(ptoCellPopover.req.leaveReqId);
                    setPtoCellPopover(null);
                    showToast('PTO rejected', 'info');
                    const refreshed = await fetchPTO({ startDate: toISO(baseMonday), endDate: toISO(addDays(baseMonday, zoom * 7 - 1)) });
                    setPtoData(refreshed || []);
                  } catch (e) { alert('Reject failed: ' + e.message); }
                  setPtoPopoverActioning(false);
                }}
                style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: '#EF4444', color: '#fff', fontSize: 12, fontWeight: 700, transition: 'opacity 0.15s',
                  opacity: ptoPopoverActioning ? 0.5 : 1 }}>
                {ptoPopoverActioning ? '...' : '\u2717 Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Own-PTO cancel popover (staffer clicks their own approved/pending PTO cell).
          PE has no partial cancel, so this always acts on the whole leave request —
          the range and total are spelled out for that reason. */}
      {myPtoPopover && (() => {
        const req = myPtoPopover.req;
        const fmtD = iso => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?';
        const range = req.startDate === req.endDate ? fmtD(req.startDate) : `${fmtD(req.startDate)} – ${fmtD(req.endDate)}`;
        const isPending = req.status === 'pending';
        const note = req.notes && req.notes !== 'New Leave Request' && req.notes !== 'Leave Request'
          ? req.notes.replace(/^New Leave Request\s*[-–]\s*/i, '') : null;
        return (
          <div className="sched-mgr-modal-backdrop" style={{ background: 'transparent' }}
            onClick={() => setMyPtoPopover(null)}>
            <div style={{
              position: 'fixed', left: Math.min(myPtoPopover.x, window.innerWidth - 280), top: Math.min(myPtoPopover.y, window.innerHeight - 220),
              background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.18)', border: '1px solid #E5E7EB',
              padding: 16, width: 260, zIndex: 400,
            }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Your PTO</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 2 }}>
                {range}{' · '}{req.totalHours}h ({req.days.length} day{req.days.length !== 1 ? 's' : ''})
              </div>
              {note && (
                <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', marginBottom: 6 }}>{note}</div>
              )}
              <div style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                background: isPending ? '#DBEAFE' : '#D1FAE5', color: isPending ? '#1D4ED8' : '#065F46', marginBottom: 10 }}>
                {isPending ? 'PENDING' : 'APPROVED'}
              </div>
              {req.isPast ? (
                <div style={{ fontSize: 11, color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB',
                  borderRadius: 6, padding: '8px 10px', marginBottom: 8, lineHeight: 1.45 }}>
                  Already taken — this PTO has been posted to your timesheet and can no
                  longer be cancelled here. Ask a PTO admin if it needs reversing.
                </div>
              ) : (
                <button disabled={ptoPopoverActioning}
                  onClick={async () => {
                    const label = isPending ? 'pending' : 'approved';
                    if (!window.confirm(`Cancel this ${label} PTO request (${range}, ${req.totalHours}h)?${isPending ? '' : '\n\nPractice Engine will post a reversing entry to your timesheet.'}`)) return;
                    setPtoPopoverActioning(true);
                    try {
                      await cancelPTO({ leaveReqId: req.leaveReqId, staffId: req.staffId });
                      setMyPtoPopover(null);
                      showToast('PTO cancelled', 'info');
                      const refreshed = await fetchPTO({ startDate: toISO(baseMonday), endDate: toISO(addDays(baseMonday, zoom * 7 - 1)) });
                      setPtoData(refreshed || []);
                    } catch (e) { alert('Cancel failed: ' + e.message); }
                    setPtoPopoverActioning(false);
                  }}
                  style={{ width: '100%', padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: '#EF4444', color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 8,
                    transition: 'opacity 0.15s', opacity: ptoPopoverActioning ? 0.5 : 1 }}>
                  {ptoPopoverActioning ? '...' : '✗ Cancel this PTO'}
                </button>
              )}
              {/* The cell click used to open the create-entry modal; keep that reachable. */}
              <button disabled={ptoPopoverActioning}
                onClick={() => { const { person, dayIdx } = myPtoPopover; setMyPtoPopover(null); openNewModal(person, dayIdx); }}
                style={{ width: '100%', padding: '7px 0', borderRadius: 6, cursor: 'pointer',
                  background: '#fff', color: '#374151', border: '1px solid #D1D5DB', fontSize: 12, fontWeight: 600,
                  transition: 'opacity 0.15s', opacity: ptoPopoverActioning ? 0.5 : 1 }}>
                + Schedule work here
              </button>
            </div>
          </div>
        );
      })()}

      {tooltip && !dragging && (() => {
        const t = tooltip.block;
        // Item 2 \u2014 custom blocks color the chip from the category palette and
        // omit the client/manager/service-desc rows (those have no meaning here).
        const typeColor = t.isCustom
          ? (CUSTOM_JOB_CATEGORY_COLORS[t.customJobCategory] || CUSTOM_JOB_DEFAULT_COLOR)
          : (JOB_TYPE_COLORS[t.jobType] || { bg: '#6B7280', text: '#fff' });
        return (
          <div className="sched-tooltip" style={{
            left: Math.min(tooltip.x + 12, window.innerWidth - 300),
            top: Math.min(tooltip.y + 12, window.innerHeight - 220),
          }}>
            <div style={{ marginBottom: 6 }}>
              <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text, fontSize: 10 }}>
                {t.isCustom ? (t.customJobCategory || 'Custom') : (t.jobType || 'Other')}
              </span>
              {t.isCustom && <span style={{ marginLeft: 6, fontSize: 9, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>Internal</span>}
            </div>
            {!t.isCustom && (
              <div className="sched-tooltip-client">
                {t.clientName}
                {t.clientStatus === 'FINANCIAL' && <span className="sched-client-status-badge sched-client-status-badge--financial" style={{ marginLeft: 6 }}>&#9888; Financial</span>}
                {t.clientStatus === 'LOST' && <span className="sched-client-status-badge sched-client-status-badge--lost" style={{ marginLeft: 6 }}>&#10005; Lost</span>}
              </div>
            )}
            {t.isCustom ? (
              <div className="sched-tooltip-client">{t.jobName}</div>
            ) : (
              <div className="sched-tooltip-meta">{t.jobName}{t.clientCode ? ` \u00B7 ${t.clientCode}` : ''}</div>
            )}
            {t.isCustom && t.customJobDescription && (
              <div className="sched-tooltip-meta" style={{ fontStyle: 'italic' }}>{t.customJobDescription}</div>
            )}
            {/* Placeholder label. Tooltip-only by design: several placeholders for the
                same custom job, level and window are legal and otherwise identical on
                screen, so this is what tells them apart. */}
            {t.isPlaceholder && t.notes && (
              <div className="sched-tooltip-meta" style={{ fontStyle: 'italic' }}>&ldquo;{t.notes}&rdquo;</div>
            )}
            {t.isClosedInPE && (
              <div className="sched-tooltip-meta" style={{ color: '#FCD34D' }}>
                &#9888; Completed in PE{t.closedAt ? ` · ${new Date(t.closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
              </div>
            )}
            <div className="sched-tooltip-row">
              <span className="sched-tooltip-label">Hours</span>
              <span className="sched-tooltip-value">{t.totalHours}h</span>
            </div>
            {scheduledInactiveIds.has(t.staffId) && (
              <div className="sched-tooltip-meta" style={{ color: '#FCD34D' }}>
                &#9888; No longer active &mdash; these hours are still on the books
              </div>
            )}
            {(() => {
              // The off-office badge on the block cannot show its own title (pointer-events:
              // none, so the block keeps drag + this tooltip), so the reason lives here.
              const ji = jobInfoMap[t.jobId];
              if (!ji?.IsOnSite || !ji?.OfficeCode) return null;
              const so = staffOfficeById[t.staffId];
              const off = so && so !== ji.OfficeCode;
              return (
                <div className="sched-tooltip-row">
                  <span className="sched-tooltip-label">On-site</span>
                  <span className="sched-tooltip-value" style={off ? { color: '#FCD34D' } : undefined}>
                    {ji.Office || ji.OfficeCode}{off ? ` · staffer is ${so}` : ''}
                  </span>
                </div>
              );
            })()}
            <div className="sched-tooltip-row">
              <span className="sched-tooltip-label">Staff</span>
              <span
                className="sched-tooltip-value"
                style={t.isPlaceholder ? { color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' } : undefined}
              >
                {t.staffName}
              </span>
            </div>
            {t.isPlaceholder && (t.level || t.departmentCode) && (
              <div className="sched-tooltip-row">
                <span className="sched-tooltip-label">Open for</span>
                <span className="sched-tooltip-value">
                  {t.level}{t.level && t.departmentCode ? ' · ' : ''}{t.departmentCode}
                </span>
              </div>
            )}
            {t.managerName && (
              <div className="sched-tooltip-row">
                <span className="sched-tooltip-label">Manager</span>
                <span className="sched-tooltip-value">{t.managerName}</span>
              </div>
            )}
            {t.notes && (
              <div className="sched-tooltip-row">
                <span className="sched-tooltip-label">Note</span>
                <span className="sched-tooltip-value">{t.notes}</span>
              </div>
            )}
            {t.isEmpire && (
              <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                Synced from Empire{t.empireSyncedAt ? ` \u00B7 ${new Date(t.empireSyncedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Toast ── */}
      {toast && (
        <div className={`sched-grid-toast sched-grid-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* ── Schedule Entry Modal (New + Edit) ── */}
      {showModal && (() => {
        const isEdit = !!modalEditBlock;

        // Re-seed the By-Day editor from the block's original per-day hours. Used
        // whenever the staffer OR the job changes in edit mode (same block in both
        // spots) — single source so the two can't drift.
        const resetToOrigDailyByDay = () => {
          if (!isEdit || Object.keys(modalOrigDailyHours).length === 0) return;
          setModalAllocType('daily');
          setModalDailyHours({ ...modalOrigDailyHours });
          const sum = Object.values(modalOrigDailyHours).reduce((sv, v) => sv + (v || 0), 0);
          setModalHours(String(Math.round(sum * 100) / 100));
        };

        // Select a staffer into the modal (shared by the search list + the embedded
        // job-suggestions list). fromSuggestion drives the "Suggested" chip badge.
        const pickModalStaff = (s, fromSuggestion = false) => {
          setModalPerson(s);
          setModalStaffSearch('');
          setModalStaffFromSuggestion(!!fromSuggestion);
          resetToOrigDailyByDay();
          if (Object.keys(modalOrigDailyHours).length > 0) {
            let overDays = 0;
            for (const [iso, hrs] of Object.entries(modalOrigDailyHours)) {
              if (dayIsConflict({
                proposedHrs: hrs,
                cap: getDayCap(s.StaffId, iso),
                totalExistingHrs: dayHoursByStaff[s.StaffId]?.[iso] || 0,
                // Subtract this entry's own hours ONLY when re-picking the SAME
                // staffer — else dayHoursByStaff double-counts the entry against
                // itself and false-warns. (Picking a different staffer → 0.)
                ownPreEditHrs: modalEditBlock?.staffId === s.StaffId ? (Number(modalOrigDailyHours[iso]) || 0) : 0,
              })) overDays++;
            }
            setModalCapacityWarning(overDays > 0
              ? `${s.Name} has less availability on ${overDays} day${overDays > 1 ? 's' : ''}. Click Optimize to redistribute.`
              : null);
          } else { setModalCapacityWarning(null); }
        };

        // Round-trip to the full StaffSuggestions page: stash the modal draft, then
        // navigate. The page returns to the grid (draft restored, staffer populated).
        const openFullSuggestions = () => {
          if (!modalJob || modalJobKind !== 'real') return;
          const draft = {
            jobId: parseInt(modalJob, 10), jobKind: 'real', mode: modalMode,
            isMultiStaff: modalIsMultiStaff,
            staffId: modalPerson?.StaffId || null,
            // Multi mode: carry the team + which were suggestion-sourced so the page can
            // pre-select them and the return ★'s only suggestion-derived staff (F1).
            priorTeamIds: modalIsMultiStaff ? modalStaffList.map(s => s.StaffId) : undefined,
            priorStarIds: modalIsMultiStaff ? [...modalSuggestedStaffIds] : undefined,
            start: modalStart, end: modalEnd, hours: modalHours,
            allocType: modalAllocType, dailyHours: modalDailyHours,
          };
          try { sessionStorage.setItem('pendingScheduleDraft', JSON.stringify(draft)); } catch { /* ignore */ }
          navigate(`/schedule/suggestions/${parseInt(modalJob, 10)}?return=grid`);
        };

        // Count conflicts in the current proposal — PTO overlaps and over-cap
        // days for the assigned staff. Drives the Optimize button's alert
        // styling. Subtracts this entry's pre-edit hours from `totalExisting`
        // so we don't double-count the entry itself.
        // Count conflicts per-staff. In single-staff mode this is a 1-element map
        // keyed on modalPerson.StaffId. In multi-staff mode it covers every tab
        // (active tab reads the live scalars; others read their settings entry's
        // dailyHours). Drives the active Optimize button's alert state + each tab's ⚠ N badge.
        const conflictCountByStaff = {};
        const _staffListForConflicts = modalIsMultiStaff
          ? modalStaffList
          : (modalPerson ? [modalPerson] : []);
        for (const s of _staffListForConflicts) {
          const isActive = modalIsMultiStaff ? s.StaffId === modalActiveStaffId : true;
          // Evaluate the PROPOSED distribution for EVERY tab (same resolver the
          // preview + save use) so ⚠ badges + the "Optimize all" alert reflect real
          // conflicts on ANY tab, not just the one you're viewing. The active tab
          // reads live scalars; others derive from their settings (a Total-mode
          // tab's stored dailyHours is stale — the real distribution is hours split).
          let dailyMap;
          if (isActive) {
            dailyMap = getProposedDailyHours();
          } else {
            const st = modalSettingsByStaff[s.StaffId];
            dailyMap = st ? computeProposedDaily({
              startISO: st.start, endISO: st.end || st.start, total: parseFloat(st.hours) || 0,
              allocType: st.allocType || 'total', dailyMap: st.dailyHours, staffId: s.StaffId, getDayCap,
            }) : {};
          }
          let n = 0;
          for (const [iso, hrs] of Object.entries(dailyMap || {})) {
            if (dayIsConflict({
              proposedHrs: hrs,
              cap: getDayCap(s.StaffId, iso),
              totalExistingHrs: dayHoursByStaff[s.StaffId]?.[iso] || 0,
              // Only the editing-original entry's pre-edit hours are subtracted —
              // and only in single-staff edit mode. Multi-staff is create-only.
              ownPreEditHrs: modalIsMultiStaff ? 0 : (Number(modalOrigDailyHours[iso]) || 0),
              ptoHrs: ptoByStaffDay[s.StaffId]?.[iso] || 0,
              ptoAsConflict: true,
            })) n++;
          }
          conflictCountByStaff[s.StaffId] = n;
        }
        const conflictCount = conflictCountByStaff[modalPerson?.StaffId] || 0;
        const hasConflicts = conflictCount > 0;
        const anyConflicts = Object.values(conflictCountByStaff).some(n => n > 0);
        const totalConflicts = Object.values(conflictCountByStaff).reduce((s, n) => s + n, 0);
        // "Fill all" is ON only when EVERY tab is Fill-on (active reads live scalar,
        // others read their settings). Individually toggling one tab off flips it OFF.
        const fillAllOn = modalIsMultiStaff && modalStaffList.length > 0 && modalStaffList.every(s =>
          s.StaffId === modalActiveStaffId ? modalFillMode : !!modalSettingsByStaff[s.StaffId]?.fillMode);

        // Compute weekday dates for the selected range (for per-day inputs)
        // Weekday dates for the range (skip Sun + no-cap Sat) — same enumeration
        // as workdaysInRange. Pass getDayCap only when a staffer is selected so
        // the "no person → exclude all Saturdays" behavior is preserved.
        const rangeDays = (modalStart
          ? workdaysInRange(modalStart, modalEnd || modalStart, modalPerson?.StaffId, modalPerson ? getDayCap : undefined)
          : []
        ).map(iso => ({
          iso,
          label: new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        }));

        // Search dropdowns — computed in memos at component level (see
        // modalFilteredJobs / modalFilteredCustomJobs). Hooks can't live in this IIFE.
        const filteredJobs = modalFilteredJobs;
        const filteredCustomJobs = modalFilteredCustomJobs;
        // The AI list replaces the real-job search list while picking; once an AI job is
        // chosen, the shared "selected job" chip below shows it like any real job.
        const pickingAiJob = modalJobKind === 'real' && modalJobScope === 'ai' && (!!modalJobSearch || !modalJob);

        return (
          <div ref={modalBackdropRef} className="sched-mgr-modal-backdrop" {...modalBackdrop}>
            <div className="sched-mgr-modal" style={{ width: (() => {
              if (modalAllocType !== 'daily' || !modalDailyHours) return 850;
              const satCount = Object.keys(modalDailyHours).filter(d => new Date(d + 'T12:00:00').getDay() === 6).length;
              const satWeeks = Math.min(satCount, 3);
              return Math.min(850 + satWeeks * 50, window.innerWidth * 0.95);
            })() }} onClick={e => e.stopPropagation()}>
              <div className="sched-mgr-modal-header">
                <h3>{isEdit ? 'Edit Schedule Entry' : 'New Schedule Entry'}</h3>
                <button className="sched-mgr-sidebar-close sched-mgr-modal-btn sched-mgr-x-btn" onClick={closeModal}>&times;</button>
              </div>
              {/* Without a boundary a throw anywhere in this ~4,000-line body unmounts
                  the entire grid, which the user experiences as the modal closing on
                  its own with no message (bug #12). Scoped INSIDE the modal shell so
                  the header and its close button survive the error — otherwise the
                  fallback would be trapped with no way out. Reopening already gives a
                  clean boundary (the whole modal is behind `showModal &&`); resetKeys
                  covers switching job or staff tab WITHOUT closing. */}
              <ErrorBoundary label="grid-entry-modal" resetKeys={[modalJob, modalActiveStaffId]}>

              {/* Item 1 — assign mode toggle (Person | Dept + Level).
                  - New entries: choose person or placeholder before saving.
                  - Edit: flipping to placeholder converts the entry back to a
                    (dept, level) placeholder.
                  Always shown. It used to be hidden for custom-job edits, because
                  PlaceholderAssignments had no CustomJobId column — the one place the
                  old limitation was handled honestly rather than as a dead-end click.
                  migrate-074 removed the limitation. */}
                <div className="sched-mgr-modal-field" style={{ display: 'flex', gap: 6 }}>
                  <button type="button"
                    className={`sched-mgr-modal-btn sched-mgr-mode-btn sched-mgr-mode-btn--${modalMode === 'person' ? 'selected' : 'unselected'}`}
                    onClick={() => setModalMode('person')}
                    style={{
                      flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                      borderRadius: 6, border: modalMode === 'person' ? '1.5px solid #003D4C' : '1px solid #D1D5DB',
                      background: modalMode === 'person' ? '#ECFDF5' : '#fff',
                      color: modalMode === 'person' ? '#065F46' : '#374151',
                    }}>{isEdit ? 'Keep with person' : 'Assign to person'}</button>
                  <button type="button"
                    data-tour="entry-placeholder-mode"
                    className={`sched-mgr-modal-btn sched-mgr-mode-btn sched-mgr-mode-btn--${modalMode === 'placeholder' ? 'selected' : 'unselected'}`}
                    onClick={() => setModalMode('placeholder')}
                    style={{
                      flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                      borderRadius: 6, border: modalMode === 'placeholder' ? '1.5px solid #003D4C' : '1px solid #D1D5DB',
                      background: modalMode === 'placeholder' ? '#ECFDF5' : '#fff',
                      color: modalMode === 'placeholder' ? '#065F46' : '#374151',
                    }}>{isEdit ? 'Convert to placeholder' : 'Assign placeholder to dept + level'}</button>
                </div>

              {/* Multi-staff toggle: visible only on create-new in person mode.
                  Edit + placeholder modes don't support multi-staff. Toggle ON
                  promotes the staff picker to multi-select + shows tabs below. */}
              {!isEdit && modalMode === 'person' && (
                <div className="sched-mgr-modal-field" style={{ display: 'flex', gap: 6 }}>
                  <button type="button"
                    className={`sched-mgr-modal-btn sched-mgr-mode-btn sched-mgr-mode-btn--${!modalIsMultiStaff ? 'selected' : 'unselected'}`}
                    onClick={() => {
                      if (!modalIsMultiStaff) return;
                      // Single → flush multi state, keep first selected staff (or current modalPerson)
                      const keep = modalActiveStaffId
                        ? modalStaffList.find(s => s.StaffId === modalActiveStaffId)
                        : (modalStaffList[0] || modalPerson);
                      setModalIsMultiStaff(false);
                      setModalStaffList([]);
                      setModalActiveStaffId(null);
                      setModalSettingsByStaff({});
                      setModalPerson(keep || null);
                      setModalStaffFromSuggestion(!!(keep && modalSuggestedStaffIds.has(keep.StaffId)));
                      setModalSuggestedStaffIds(new Set());
                    }}
                    style={{
                      flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                      borderRadius: 6, border: !modalIsMultiStaff ? '1.5px solid #003D4C' : '1px solid #D1D5DB',
                      background: !modalIsMultiStaff ? '#ECFDF5' : '#fff',
                      color: !modalIsMultiStaff ? '#065F46' : '#374151',
                    }}>Single staff</button>
                  <button type="button"
                    data-tour="entry-multistaff"
                    className={`sched-mgr-modal-btn sched-mgr-mode-btn sched-mgr-mode-btn--${modalIsMultiStaff ? 'selected' : 'unselected'}`}
                    onClick={() => {
                      if (modalIsMultiStaff) return;
                      // Single → multi: seed the list with the current modalPerson (if any).
                      const seed = modalPerson ? [modalPerson] : [];
                      setModalIsMultiStaff(true);
                      setModalStaffList(seed);
                      setModalActiveStaffId(modalPerson?.StaffId || null);
                      // Carry the current editor state over as the seeded staff's settings.
                      setModalSettingsByStaff(modalPerson ? { [modalPerson.StaffId]: {
                        start: modalStart, end: modalEnd, hours: modalHours, allocType: modalAllocType,
                        dailyHours: { ...modalDailyHours }, fillMode: modalFillMode,
                        overflowStart: modalOverflowStart, overflowEnd: modalOverflowEnd,
                      } } : {});
                      // If the seeded staffer was picked from suggestions, keep the ★.
                      setModalSuggestedStaffIds(new Set(modalPerson && modalStaffFromSuggestion ? [modalPerson.StaffId] : []));
                    }}
                    style={{
                      flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                      borderRadius: 6, border: modalIsMultiStaff ? '1.5px solid #003D4C' : '1px solid #D1D5DB',
                      background: modalIsMultiStaff ? '#ECFDF5' : '#fff',
                      color: modalIsMultiStaff ? '#065F46' : '#374151',
                    }}>Multi-staff</button>
                </div>
              )}

              {modalMode === 'placeholder' && isEdit ? (
                /* Convert-to-placeholder summary — replaces the editor body since
                   conversion is a one-click action: dept+level come from the staff,
                   range + hours come from the block. */
                (() => {
                  const dates = modalEditBlock?.dates || [];
                  const minDate = dates.length ? toISO(dates[0].date) : modalStart;
                  const maxDate = dates.length ? toISO(dates[dates.length - 1].date) : modalEnd;
                  const totalHours = dates.reduce((s, d) => s + (Number(d.hours) || 0), 0);
                  const weekdayCount = dates.length;
                  // Merge detection: existing placeholder w/ same (job, dept, level)
                  // AND date-range overlap → merge; otherwise new row.
                  const willMergeWith = placeholders.find(p =>
                    p.JobId === modalEditBlock?.jobId &&
                    p.DepartmentCode === modalDept &&
                    p.Level === modalLevel &&
                    String(p.StartDate).slice(0, 10) <= maxDate &&
                    String(p.EndDate).slice(0, 10) >= minDate
                  );
                  return (
                    <div className="sched-mgr-modal-field" style={{
                      background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8,
                      padding: '12px 14px', fontSize: 12, lineHeight: 1.5,
                    }}>
                      <div style={{ fontWeight: 700, color: '#065F46', fontSize: 13, marginBottom: 6 }}>
                        Convert to placeholder
                      </div>
                      <div style={{ color: '#374151' }}>
                        <div><strong>From:</strong> {modalEditBlock?.staffName || modalPerson?.Name || '(staff)'}</div>
                        <div><strong>Job:</strong> {modalEditBlock?.jobName || '(job)'}</div>
                        <div><strong>Range:</strong> {minDate} → {maxDate} ({weekdayCount} day{weekdayCount === 1 ? '' : 's'})</div>
                        <div><strong>Total hours:</strong> {totalHours.toFixed(2)}h</div>
                        <div style={{ marginTop: 6, color: '#6B7280', fontStyle: 'italic' }}>
                          The current daily breakdown will be discarded. When re-assigned,
                          Optimize will recompute daily values for the new staffer.
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <strong>Going to:</strong> {modalDept || '(dept)'} · {modalLevel || '(level)'} placeholder pool for this job.
                        </div>
                        {willMergeWith && (
                          <div style={{
                            marginTop: 8, padding: '6px 8px', borderRadius: 4,
                            background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D',
                          }}>
                            ⚠ Will merge with existing placeholder (#{willMergeWith.PlaceholderId}, currently
                            {' '}{Number(willMergeWith.Hours).toFixed(2)}h from
                            {' '}{String(willMergeWith.StartDate).slice(0, 10)} to
                            {' '}{String(willMergeWith.EndDate).slice(0, 10)}). Hours will sum and the date
                            range will envelope both.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : modalMode === 'placeholder' ? (
                /* Placeholder (new entry): dept + level dropdowns instead of staff picker */
                <div style={{ display: 'flex', gap: 12 }}>
                  <div className="sched-mgr-modal-field" style={{ flex: 1 }}>
                    <label className="sched-mgr-modal-label">Department</label>
                    <select value={modalDept} onChange={e => setModalDept(e.target.value)}
                      style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #E5E7EB' }}>
                      <option value="">Select department...</option>
                      {STAFF_DEPT_GROUPS.map(d => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sched-mgr-modal-field" style={{ flex: 1 }}>
                    <label className="sched-mgr-modal-label">Level</label>
                    <select value={modalLevel} onChange={e => setModalLevel(e.target.value)}
                      style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #E5E7EB' }}>
                      <option value="">Select level...</option>
                      {PLACEHOLDER_LEVELS.map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : modalIsMultiStaff ? (
                /* Multi-staff picker: chips for selected + search input below to add more */
                <>
                <div className="sched-mgr-modal-field">
                  <label className="sched-mgr-modal-label">Staff (multiple)</label>
                  {modalStaffList.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                      {modalStaffList.map(s => (
                        <span key={s.StaffId} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', fontSize: 11, fontWeight: 600,
                          background: '#ECFDF5', color: '#065F46',
                          border: '1px solid #A7F3D0', borderRadius: 12,
                        }}>
                          {modalSuggestedStaffIds.has(s.StaffId) && <span title="Added from suggestions" style={{ color: '#B45309' }}>★</span>}
                          {s.Name}
                          <button type="button"
                            className="sched-mgr-modal-btn sched-mgr-x-btn"
                            onClick={() => removeStaffFromModal(s.StaffId)}
                            title={`Remove ${s.Name}`}
                            style={{ background: 'none', border: 'none', color: '#047857', fontSize: 13, padding: '0 2px', borderRadius: 4, lineHeight: 1 }}>&times;</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <DebouncedSearchInput placeholder="Search to add staff..." value={modalStaffSearch}
                    onChange={setModalStaffSearch}
                    className="sched-modal-job-search" />
                  {(() => {
                    // Reuses the shared memo, then drops anyone already picked — the
                    // exclusion depends on modalStaffList, which changes as you add
                    // people, so it stays out of the memo's key.
                    const filtered = modalFilteredStaff
                      .filter(s => !modalStaffList.some(sel => sel.StaffId === s.StaffId));
                    // Only say "No staff found" once something has actually been typed —
                    // an empty search legitimately yields nothing and shouldn't read as a
                    // failed lookup.
                    const hasQuery = modalStaffSearch.trim().length > 0;
                    return filtered.length > 0 ? (
                      <div className="sched-modal-job-list">
                        {filtered.map(s => (
                          <div key={s.StaffId} className="sched-modal-job-option"
                            onClick={() => { addStaffToModal(s); setModalStaffSearch(''); }}>
                            <div className="sched-modal-job-option-client">{s.Name}</div>
                            <div className="sched-modal-job-option-name">
                              {s.Level}
                              <span className="sched-modal-job-option-type">{s.Department || ''}</span>
                              <OffOfficeFlag staffOffice={s.OfficeCode} staffName={s.Name}
                                isOnSite={!!jobInfoMap[parseInt(modalJob, 10)]?.IsOnSite}
                                jobOfficeCode={jobInfoMap[parseInt(modalJob, 10)]?.OfficeCode}
                                jobOffice={jobInfoMap[parseInt(modalJob, 10)]?.Office} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : hasQuery ? <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No staff found</div> : null;
                  })()}
                  {/* Item 4 — quick-add picklist: ★ Suggested | Staff with WIP toggle (shown when not searching) */}
                  {(() => {
                    const jobId = parseInt(modalJob, 10);
                    const forThisJob = modalJobKind === 'real' && modalJob && modalSuggestions.jobId === jobId;
                    if (!forThisJob || modalStaffSearch.trim()) return null;
                    const sug = modalSuggestions.list.filter(s => !modalStaffList.some(sel => sel.StaffId === s.StaffId));
                    const wip = (modalWipStaff.jobId === jobId ? modalWipStaff.list : []).filter(w => !modalStaffList.some(sel => sel.StaffId === w.StaffId));
                    const hasWip = wip.length > 0;
                    if (modalSuggestions.loading && !sug.length && !hasWip) return <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading suggestions…</div>;
                    if (!sug.length && !hasWip) return null;
                    const mode = (modalPicklistMode === 'wip' && hasWip) ? 'wip' : 'suggest';
                    return (
                      <div className="sched-modal-job-list" data-tour="staff-suggestions-list">
                        <PicklistToggle mode={mode} onMode={setModalPicklistMode} hasWip={hasWip} wipCount={wip.length} />
                        {mode === 'suggest' && (modalSuggestions.onSiteOffice || modalSuggestions.nicheMissing) && (
                          <div className="sched-modal-picklist-note">
                            <OnSiteFilterNote isOnSite={!!jobInfoMap[parseInt(modalJob, 10)]?.IsOnSite}
                              officeCode={jobInfoMap[parseInt(modalJob, 10)]?.OfficeCode}
                              office={jobInfoMap[parseInt(modalJob, 10)]?.Office} />
                            {modalSuggestions.nicheMissing && <NicheGapFlag missing style={{ marginLeft: 8, verticalAlign: 'middle' }} />}
                          </div>
                        )}
                        {mode === 'wip' ? (
                          wip.map(s => (
                            <PicklistStaffRow key={s.StaffId} staff={s} mode="wip"
                              right={`${Math.round((s.wipHours || 0) * 100) / 100}h WIP`} rightColor="#1D4ED8"
                              badge={picklistBadgesFor(s)}
                              onClick={() => { addStaffToModal(s); setModalStaffSearch(''); }} />
                          ))
                        ) : (
                          sug.length ? sug.map(s => (
                            <PicklistStaffRow key={s.StaffId} staff={s} mode="suggest"
                              right={`${modalSuggestionFreeHours[s.StaffId] ?? 0}h free`}
                              rightColor={(modalSuggestionFreeHours[s.StaffId] || 0) > 0 ? '#065F46' : '#DC2626'}
                              badge={picklistBadgesFor(s)}
                              onClick={() => { addStaffToModal(s); setModalSuggestedStaffIds(prev => new Set(prev).add(s.StaffId)); setModalStaffSearch(''); }} />
                          )) : <div className="sched-modal-picklist-empty">No suggestions for this job</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                </>
              ) : (
              /* Staff: searchable picker */
              <div className="sched-mgr-modal-field" data-tour="modal-staff-picker">
                <label className="sched-mgr-modal-label">Staff</label>
                {modalStaffSearch || !modalPerson ? (
                  <>
                    <DebouncedSearchInput placeholder="Search staff..." value={modalStaffSearch}
                      onChange={setModalStaffSearch}
                      className="sched-modal-job-search" />
                    {(() => {
                      const q = modalStaffSearch.toLowerCase().trim();
                      // Typing → search all staff (minus departed staff, who are view-only).
                      if (q) {
                        const filtered = modalFilteredStaff;
                        return filtered.length > 0 ? (
                          <div className="sched-modal-job-list">
                            {filtered.map(s => (
                              <div key={s.StaffId}
                                className={`sched-modal-job-option${modalPerson?.StaffId === s.StaffId ? ' sched-modal-job-option--selected' : ''}`}
                                onClick={() => pickModalStaff(s)}>
                                <div className="sched-modal-job-option-client">{s.Name}</div>
                                <div className="sched-modal-job-option-name">
                                  {s.Level}
                                  <span className="sched-modal-job-option-type">{s.Department || ''}</span>
                                  <OffOfficeFlag staffOffice={s.OfficeCode} staffName={s.Name}
                                    isOnSite={!!jobInfoMap[parseInt(modalJob, 10)]?.IsOnSite}
                                    jobOfficeCode={jobInfoMap[parseInt(modalJob, 10)]?.OfficeCode}
                                    jobOffice={jobInfoMap[parseInt(modalJob, 10)]?.Office} />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No staff found</div>;
                      }
                      // No query: ★ Suggested | Staff with WIP toggle for the selected real job.
                      const jobId = parseInt(modalJob, 10);
                      const forThisJob = modalJobKind === 'real' && modalJob && modalSuggestions.jobId === jobId;
                      const sugList = forThisJob ? modalSuggestions.list : [];
                      const wipList = (modalWipStaff.jobId === jobId ? modalWipStaff.list : []);
                      const hasWip = wipList.length > 0;
                      if (modalJobKind === 'real' && modalJob && modalSuggestions.loading && !forThisJob && !hasWip) {
                        return <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading suggestions…</div>;
                      }
                      if (sugList.length > 0 || hasWip) {
                        const mode = (modalPicklistMode === 'wip' && hasWip) ? 'wip' : 'suggest';
                        return (
                          <div className="sched-modal-job-list">
                            <PicklistToggle mode={mode} onMode={setModalPicklistMode} hasWip={hasWip} wipCount={wipList.length} />
                            {mode === 'suggest' && (modalSuggestions.onSiteOffice || modalSuggestions.nicheMissing) && (
                              <div className="sched-modal-picklist-note">
                                <OnSiteFilterNote isOnSite={!!jobInfoMap[parseInt(modalJob, 10)]?.IsOnSite}
                                  officeCode={jobInfoMap[parseInt(modalJob, 10)]?.OfficeCode}
                                  office={jobInfoMap[parseInt(modalJob, 10)]?.Office} />
                                {modalSuggestions.nicheMissing && <NicheGapFlag missing style={{ marginLeft: 8, verticalAlign: 'middle' }} />}
                              </div>
                            )}
                            {mode === 'wip' ? (
                              wipList.map(s => (
                                <PicklistStaffRow key={s.StaffId} staff={s} mode="wip"
                                  right={`${Math.round((s.wipHours || 0) * 100) / 100}h WIP`} rightColor="#1D4ED8"
                                  selected={modalPerson?.StaffId === s.StaffId}
                                  badge={picklistBadgesFor(s)}
                                  onClick={() => pickModalStaff(s)} />
                              ))
                            ) : (
                              sugList.length ? sugList.map(s => (
                                <PicklistStaffRow key={s.StaffId} staff={s} mode="suggest"
                                  right={`${modalSuggestionFreeHours[s.StaffId] ?? 0}h free`}
                                  rightColor={(modalSuggestionFreeHours[s.StaffId] || 0) > 0 ? '#065F46' : '#DC2626'}
                                  selected={modalPerson?.StaffId === s.StaffId}
                                  badge={picklistBadgesFor(s)}
                                  onClick={() => pickModalStaff(s, true)} />
                              )) : <div className="sched-modal-picklist-empty">No suggestions for this job</div>
                            )}
                          </div>
                        );
                      }
                      if (modalJobKind === 'real' && modalJob) return null; // job set, nothing to show
                      return <div style={{ padding: '10px 12px', fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>Pick a job to see suggested staff, or type to search.</div>;
                    })()}
                  </>
                ) : (
                  <div className="sched-modal-job-selected">
                    <div>
                      <div style={{ fontWeight: 600, color: '#065F46', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {modalPerson.Name}
                        {modalStaffFromSuggestion && <span className="sched-modal-suggest-tag" title="Selected from staff suggestions">★ Suggested</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#047857' }}>{modalPerson.Level} &middot; {modalPerson.Department}</div>
                    </div>
                    <button
                      className="sched-mgr-modal-btn sched-mgr-x-btn"
                      onClick={() => { setModalStaffSearch(' '); setModalStaffFromSuggestion(false); }}
                      title="Clear staff"
                      style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 14, padding: '2px 6px', borderRadius: 4 }}>&times;</button>
                  </div>
                )}
              </div>
              )}

              {/* Full staff analysis link + on-site toggle on one row (real job) to save vertical space. */}
              {modalJobKind === 'real' && modalJob && (() => {
                const mJob = jobInfoMap[parseInt(modalJob, 10)];
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    {modalMode === 'person' && (
                      <button type="button" className="sched-modal-fullanalysis-link" style={{ margin: 0 }}
                        onClick={openFullSuggestions}
                        title="Open the full staff suggestions page (availability + score breakdowns), then return here">
                        View detailed staff suggestions ↗
                      </button>
                    )}
                    {mJob && (
                      <OnSiteToggle isOnSite={!!mJob.IsOnSite} office={mJob.Office} officeCode={mJob.OfficeCode}
                        canEdit={canSetOnSite(mJob)} onChange={(next) => handleSetOnSite(parseInt(modalJob, 10), next)} />
                    )}
                  </div>
                );
              })()}

              {/* Off-office advisory: selected staffer(s) not in an on-site job's office. */}
              {modalJobKind === 'real' && modalJob && (() => {
                const mJob = jobInfoMap[parseInt(modalJob, 10)];
                if (!mJob?.IsOnSite || !mJob?.OfficeCode) return null;
                const picks = modalIsMultiStaff ? modalStaffList : (modalPerson ? [modalPerson] : []);
                const offenders = picks.filter(s => offOffice(s.StaffId, mJob.IsOnSite, mJob.OfficeCode));
                if (!offenders.length) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8, padding: '6px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6 }}>
                    <span style={{ fontSize: 13 }}>&#128205;</span>
                    <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                      On-site job in {mJob.Office || mJob.OfficeCode}. {offenders.map(s => s.Name).join(', ')} {offenders.length > 1 ? 'are' : 'is'} from a different office — you can still assign, but consider same-office staff.
                    </span>
                  </div>
                );
              })()}

              {/* In edit→placeholder (convert) mode, the job/dates/hours are
                  fixed by the source block — skip the editor body entirely.
                  The convert-summary above is the whole UI; the footer Save
                  button kicks off the conversion. */}
              {!(isEdit && modalMode === 'placeholder') && (<>
              {/* Job: searchable picker + Real/AI/Custom toggle (Item 2) */}
              <div className="sched-mgr-modal-field">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <label className="sched-mgr-modal-label" style={{ margin: 0 }}>
                    {modalJobKind === 'custom' ? 'Custom Job' : modalJobScope === 'ai' ? 'AI Project' : 'Client / Job'}
                  </label>
                  {/* Compact Real/AI/Custom toggle. Editing an existing block fixes the KIND
                      (real vs custom), so a real-job edit can still flip Real <-> AI (both are
                      real jobs) while Custom stays locked, and a custom-job edit locks all three. */}
                  {(() => {
                    const seg = modalJobKind === 'custom' ? 'custom' : modalJobScope === 'ai' ? 'ai' : 'real';
                    const locked = (id) => isEdit && (modalJobKind === 'custom' || id === 'custom');
                    const segBtn = (id, label, activeBg, onClick) => (
                      <button type="button" disabled={locked(id)}
                        className={`sched-mgr-modal-btn sched-mgr-toggle-btn sched-mgr-toggle-btn--${seg === id ? 'selected' : 'unselected'}`}
                        onClick={onClick}
                        style={{ padding: '2px 8px', fontSize: 10, fontWeight: 700, borderRadius: 3, border: 'none',
                          background: seg === id ? activeBg : 'transparent',
                          color: seg === id ? '#fff' : '#6B7280',
                          opacity: locked(id) && seg !== id ? 0.5 : 1, cursor: locked(id) ? 'default' : 'pointer' }}>
                        {label}
                      </button>
                    );
                    return (
                      <div style={{ display: 'flex', gap: 1, background: '#F3F4F6', borderRadius: 4, padding: 1, marginLeft: 'auto' }}>
                        {segBtn('real', 'Real', '#003D4C', () => {
                          setModalJobKind('real');
                          setModalJobScope('all');
                          setModalCustomJobId('');
                          setModalJobSearch('');
                        })}
                        {segBtn('ai', 'AI', jobDeptColor('AI Development'), () => {
                          setModalJobKind('real');
                          setModalJobScope('ai');
                          setModalCustomJobId('');
                          setModalJobSearch('');
                          // A selected non-AI job doesn't belong to this list; clear it so the list shows.
                          if (modalJob && !isAiJob(demoAllJobs.find(j => String(j.JobId) === modalJob))) setModalJob('');
                        })}
                        {segBtn('custom', 'Custom', '#475569', () => {
                          setModalJobKind('custom');
                          setModalJob('');
                          setModalJobSearch('');
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* AI-project picker: the real-job picker filtered to BMSS AI, listed without
                    typing, ranked for whoever is being scheduled (utils/aiJobPicker.js). */}
                {pickingAiJob && (
                  <>
                    <DebouncedSearchInput
                      placeholder="Filter AI projects by name, code, or JM..."
                      value={modalJobSearch}
                      onChange={v => { setModalJobSearch(v); if (modalJob) setModalJob(''); }}
                      className="sched-modal-job-search"
                    />
                    {modalAiJobSections.length > 0 ? (
                      <div className="sched-modal-job-list sched-modal-job-list--grouped">
                        {modalAiJobSections.map(sec => (
                          <Fragment key={sec.key}>
                            <div className="sched-modal-job-group">{sec.label}</div>
                            {sec.items.map(({ job: j, hint }) => (
                              <div key={j.JobId}
                                className={`sched-modal-job-option${String(j.JobId) === modalJob ? ' sched-modal-job-option--selected' : ''}`}
                                onClick={() => {
                                  setModalJob(String(j.JobId)); setModalJobSearch('');
                                  resetToOrigDailyByDay();
                                }}>
                                <div className="sched-modal-job-option-client" style={{ display: 'flex', gap: 8 }}>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{j.Name}</span>
                                  {hint && <span className="sched-modal-job-option-hint">{hint}</span>}
                                </div>
                                <div className="sched-modal-job-option-name">
                                  {[j.StatusDetail, j.ManagerName && `JM ${j.ManagerName}`].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            ))}
                          </Fragment>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                        {modalJobSearch.trim() ? `No AI projects matching "${modalJobSearch}"` : 'No open AI projects.'}
                      </div>
                    )}
                  </>
                )}

                {/* Real-job picker */}
                {modalJobKind === 'real' && !pickingAiJob && (modalJobSearch || !modalJob ? (
                  <>
                    <DebouncedSearchInput
                      placeholder="Search by client or job name..."
                      value={modalJobSearch}
                      onChange={v => { setModalJobSearch(v); if (modalJob) setModalJob(''); }}
                      className="sched-modal-job-search"
                    />
                    {filteredJobs.length > 0 && (
                      <div className="sched-modal-job-list">
                        {filteredJobs.map(j => (
                          <div key={j.JobId}
                            className={`sched-modal-job-option${String(j.JobId) === modalJob ? ' sched-modal-job-option--selected' : ''}`}
                            onClick={() => {
                              setModalJob(String(j.JobId)); setModalJobSearch('');
                              // Auto-switch to By Day when job changes in edit mode
                              resetToOrigDailyByDay();
                            }}>
                            <div className="sched-modal-job-option-client">{j.ClientName}</div>
                            <div className="sched-modal-job-option-name">
                              {j.Name}
                              <span className="sched-modal-job-option-type">{j.ServiceDesc || j.JobType || 'General'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {filteredJobs.length === 0 && modalJobSearch.trim() && (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                        No jobs found matching "{modalJobSearch}"
                      </div>
                    )}
                  </>
                ) : (() => {
                  const sel = demoAllJobs.find(j => String(j.JobId) === modalJob);
                  return sel ? (
                    <div className="sched-modal-job-selected">
                      {isAiJob(sel) ? (
                        // Every AI project shares one client, so lead with the project instead.
                        <div>
                          <div style={{ fontWeight: 600, color: '#065F46' }}>{sel.Name}</div>
                          <div style={{ fontSize: 11, color: '#047857' }}>{[sel.ClientName, sel.StatusDetail].filter(Boolean).join(' · ')}</div>
                        </div>
                      ) : (
                      <div>
                        <div style={{ fontWeight: 600, color: '#065F46' }}>{sel.ClientName}</div>
                        <div style={{ fontSize: 11, color: '#047857' }}>{sel.Name} ({sel.ServiceDesc || sel.JobType || 'General'})</div>
                      </div>
                      )}
                      <button
                        className="sched-mgr-modal-btn sched-mgr-x-btn"
                        onClick={() => { setModalJob(''); setModalJobSearch(''); }}
                        title="Clear job"
                        style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 14, padding: '2px 6px', borderRadius: 4 }}>&times;</button>
                    </div>
                  ) : null;
                })())}

                {/* Custom-job picker (Item 2) */}
                {modalJobKind === 'custom' && (modalJobSearch || !modalCustomJobId ? (
                  <>
                    <input
                      type="text"
                      placeholder="Search custom jobs..."
                      value={modalJobSearch}
                      onChange={e => { setModalJobSearch(e.target.value); if (modalCustomJobId) setModalCustomJobId(''); }}
                      className="sched-modal-job-search"
                    />
                    {filteredCustomJobs.length > 0 && (
                      <div className="sched-modal-job-list">
                        {filteredCustomJobs.map(c => {
                          const catColor = CUSTOM_JOB_CATEGORY_COLORS[c.Category] || CUSTOM_JOB_DEFAULT_COLOR;
                          return (
                            <div key={c.CustomJobId}
                              className={`sched-modal-job-option${String(c.CustomJobId) === modalCustomJobId ? ' sched-modal-job-option--selected' : ''}`}
                              onClick={() => { setModalCustomJobId(String(c.CustomJobId)); setModalJobSearch(''); }}>
                              <div className="sched-modal-job-option-client" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {c.Name}
                                <span style={{ background: catColor.bg, color: catColor.text, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, letterSpacing: 0.3 }}>
                                  {c.Category}
                                </span>
                              </div>
                              {c.Description && (
                                <div className="sched-modal-job-option-name" style={{ fontSize: 11, color: '#9CA3AF' }}>{c.Description}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {filteredCustomJobs.length === 0 && (
                      <div style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
                        {modalJobSearch.trim() ? `No custom jobs matching "${modalJobSearch}"` : 'No active custom jobs configured.'}
                      </div>
                    )}
                  </>
                ) : (() => {
                  const sel = allCustomJobs.find(c => String(c.CustomJobId) === modalCustomJobId);
                  if (!sel) return null;
                  const catColor = CUSTOM_JOB_CATEGORY_COLORS[sel.Category] || CUSTOM_JOB_DEFAULT_COLOR;
                  return (
                    <div className="sched-modal-job-selected">
                      <div>
                        <div style={{ fontWeight: 600, color: '#065F46', display: 'flex', alignItems: 'center', gap: 8 }}>
                          {sel.Name}
                          <span style={{ background: catColor.bg, color: catColor.text, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, letterSpacing: 0.3 }}>
                            {sel.Category}
                          </span>
                        </div>
                        {sel.Description && <div style={{ fontSize: 11, color: '#047857' }}>{sel.Description}</div>}
                      </div>
                      <button
                        className="sched-mgr-modal-btn sched-mgr-x-btn"
                        onClick={() => { setModalCustomJobId(''); setModalJobSearch(''); }}
                        title="Clear custom job"
                        style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 14, padding: '2px 6px', borderRadius: 4 }}>&times;</button>
                    </div>
                  );
                })())}
              </div>

              {/* Multi-staff switcher tabs — ABOVE the date range so each tab owns its
                  own dates + hours + distribution. The header carries the "All staff"
                  batch cluster (acts on every tab); everything below the tabs (dates,
                  hours, distribution) edits only the ACTIVE tab. */}
              {modalIsMultiStaff && modalStaffList.length > 0 && (
                <div className="sched-mgr-staff-tabs-wrapper">
                  <div className="sched-mgr-staff-tabs-header">
                    <span className="sched-mgr-staff-tabs-label">Per-staff view — each tab has its own dates &amp; hours</span>
                    {modalStaffList.length > 1 && (
                      <div className="sched-mgr-allstaff-cluster">
                        <span className="sched-mgr-allstaff-label">All staff:</span>
                        <button type="button"
                          disabled={modalFillMode || modalSaving}
                          className={`sched-mgr-modal-btn sched-mgr-allstaff-btn${anyConflicts && !modalFillMode ? ' sched-mgr-action-btn--alert' : ''}`}
                          onClick={runOptimizeAll}
                          title={anyConflicts
                            ? `Optimize every tab against its own range (resolve ${totalConflicts} conflict${totalConflicts > 1 ? 's' : ''})`
                            : 'Optimize every tab against its own range'}>
                          {anyConflicts ? `⚠ Optimize all (${totalConflicts})` : 'Optimize all'}
                        </button>
                        <button type="button"
                          disabled={modalSaving}
                          className={`sched-mgr-modal-btn sched-mgr-allstaff-btn${fillAllOn ? ' sched-mgr-allstaff-btn--on' : ''}`}
                          onClick={toggleFillAll}
                          title={fillAllOn
                            ? 'Turn Fill off for every tab (restore prior hours)'
                            : 'Fill every tab to its own capacity over its own range'}>
                          {fillAllOn ? '✓ Fill all' : 'Fill all'}
                        </button>
                        <button type="button"
                          disabled={modalSaving}
                          className="sched-mgr-modal-btn sched-mgr-allstaff-btn"
                          onClick={copyActiveToAll}
                          title="Copy this tab's date range + hours onto every other staffer">
                          Copy active &rarr; all
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="sched-mgr-staff-tabs">
                    <button type="button"
                      className="sched-mgr-modal-btn sched-mgr-staff-tabs__scrollbtn"
                      onClick={() => { staffTabScrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' }); }}
                      title="Scroll left">‹</button>
                    <div ref={staffTabScrollRef} className="sched-mgr-staff-tabs__scroll">
                      {modalStaffList.map(s => {
                        const isActive = s.StaffId === modalActiveStaffId;
                        const cnt = conflictCountByStaff[s.StaffId] || 0;
                        const nameShort = (s.Name || '').split(/\s+/).slice(0, 2).map((p, i) => i === 1 ? p[0] + '.' : p).join(' ');
                        return (
                          <button key={s.StaffId} type="button"
                            className={`sched-mgr-modal-btn sched-mgr-staff-tab sched-mgr-staff-tab--${isActive ? 'selected' : 'unselected'}${copyPulsing && !isActive ? ' sched-mgr-staff-tab--copypulse' : ''}`}
                            onClick={() => switchActiveStaff(s.StaffId)}
                            title={s.Name + (cnt > 0 ? ` — ${cnt} conflict${cnt > 1 ? 's' : ''}` : '')}>
                            {nameShort || s.Name}
                            {cnt > 0 && <span className="sched-mgr-staff-tab__badge">⚠ {cnt}</span>}
                          </button>
                        );
                      })}
                    </div>
                    <button type="button"
                      className="sched-mgr-modal-btn sched-mgr-staff-tabs__scrollbtn"
                      onClick={() => { staffTabScrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' }); }}
                      title="Scroll right">›</button>
                  </div>
                </div>
              )}

              {/* Date range + stepper shifts (‹‹ ‹  Start  ·mid-week hint·  End  › ››)
                  Date inputs are fixed at 160px; the middle flex-1 slot holds the
                  "Starts mid-week" nudge so it costs zero vertical space. When a NEW
                  entry's start isn't a Monday the four steppers pulse amber (live —
                  clears the moment the start lands on a Monday). Steppers shift both
                  endpoints by 1 workday / 1 week (weekend-respecting) + rekey dailyHours. */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', gap: 2, paddingBottom: 7 }}>
                  <button type="button" title="Shift back 1 week (5 workdays)"
                    disabled={modalSaving || !modalStart || !modalEnd}
                    onClick={() => shiftBy(-5)}
                    className={`sched-mgr-modal-btn sched-mgr-stepper-btn${shiftNudge ? ' sched-mgr-stepper-btn--pulse' : ''}`}
                    style={{ padding: '6px 8px', fontSize: 12, fontWeight: 700, borderRadius: 5, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', minWidth: 28 }}>‹‹</button>
                  <button type="button" title="Shift back 1 workday"
                    disabled={modalSaving || !modalStart || !modalEnd}
                    onClick={() => shiftBy(-1)}
                    className={`sched-mgr-modal-btn sched-mgr-stepper-btn${shiftNudge ? ' sched-mgr-stepper-btn--pulse' : ''}`}
                    style={{ padding: '6px 8px', fontSize: 12, fontWeight: 700, borderRadius: 5, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', minWidth: 24 }}>‹</button>
                </div>
                <div className="sched-mgr-modal-field" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                  <label className="sched-mgr-modal-label">Start Date</label>
                  <input type="date" value={modalStart}
                    onChange={e => handleStartChange(e.target.value)}
                    style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #E5E7EB', boxSizing: 'border-box' }} />
                </div>
                {/* Inline mid-week nudge — points at the pulsing steppers on either side. */}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 9, minWidth: 0 }}>
                  {shiftNudge && (
                    <span className="sched-midweek-hint"
                      title="This entry starts mid-week — use the ‹‹ ‹ › ›› buttons to shift it onto a cleaner week boundary.">
                      ‹ Starts mid-week ›
                    </span>
                  )}
                </div>
                <div className="sched-mgr-modal-field" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                  <label className="sched-mgr-modal-label">End Date</label>
                  <input type="date" value={modalEnd}
                    onChange={e => handleEndChange(e.target.value)}
                    style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #E5E7EB', boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', gap: 2, paddingBottom: 7 }}>
                  <button type="button" title="Shift forward 1 workday"
                    disabled={modalSaving || !modalStart || !modalEnd}
                    onClick={() => shiftBy(1)}
                    className={`sched-mgr-modal-btn sched-mgr-stepper-btn${shiftNudge ? ' sched-mgr-stepper-btn--pulse' : ''}`}
                    style={{ padding: '6px 8px', fontSize: 12, fontWeight: 700, borderRadius: 5, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', minWidth: 24 }}>›</button>
                  <button type="button" title="Shift forward 1 week (5 workdays)"
                    disabled={modalSaving || !modalStart || !modalEnd}
                    onClick={() => shiftBy(5)}
                    className={`sched-mgr-modal-btn sched-mgr-stepper-btn${shiftNudge ? ' sched-mgr-stepper-btn--pulse' : ''}`}
                    style={{ padding: '6px 8px', fontSize: 12, fontWeight: 700, borderRadius: 5, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', minWidth: 28 }}>››</button>
                </div>
              </div>

              {modalOutsideLoaded && (
                <div className="sched-modal-unloaded" role="status">
                  <span>
                    &#9888; These dates run past the weeks loaded on the grid, so free hours, Fill and
                    Optimize can&rsquo;t see work already booked there.
                  </span>
                  <button type="button" className="sched-modal-unloaded-btn" onClick={showModalRangeOnGrid}>
                    Load these weeks
                  </button>
                </div>
              )}

              {/* ── Compact Mode Toolbar ── */}
              <div data-tour="dist-modes" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #F3F4F6', borderBottom: '1px solid #F3F4F6', marginBottom: 8, marginTop: 10 }}>
                {/* Total / By Day toggle */}
                <div style={{ display: 'flex', gap: 2, background: '#F3F4F6', borderRadius: 5, padding: 2, opacity: modalFillMode ? 0.5 : 1 }}>
                  <button type="button"
                    disabled={modalFillMode}
                    className={`sched-mgr-modal-btn sched-mgr-toggle-btn sched-mgr-toggle-btn--${modalAllocType === 'total' ? 'selected' : 'unselected'}`}
                    onClick={() => setModalAllocType('total')}
                    style={{ padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none',
                      background: modalAllocType === 'total' ? '#003D4C' : 'transparent',
                      color: modalAllocType === 'total' ? '#fff' : '#6B7280' }}>
                    Total
                  </button>
                  <button type="button"
                    disabled={modalFillMode}
                    className={`sched-mgr-modal-btn sched-mgr-toggle-btn sched-mgr-toggle-btn--${modalAllocType === 'daily' ? 'selected' : 'unselected'}`}
                    onClick={() => {
                      setModalAllocType('daily');
                      if (rangeDays.length > 0 && Object.keys(modalDailyHours).length === 0) {
                        const dailyArr = distributeEvenlyQuarters(parseFloat(modalHours || 0), rangeDays.length);
                        const daily = {};
                        rangeDays.forEach((d, i) => { daily[d.iso] = dailyArr[i]; });
                        setModalDailyHours(daily);
                      }
                    }}
                    style={{ padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: 'none',
                      background: modalAllocType === 'daily' ? '#003D4C' : 'transparent',
                      color: modalAllocType === 'daily' ? '#fff' : '#6B7280' }}>
                    By Day
                  </button>
                </div>

                {/* Optimize button + overflow toggles. Shown whenever the
                    modal has a staffer + a valid range, regardless of Total
                    vs By Day. Clicking Optimize auto-switches to By Day via
                    runOptimize. When conflicts are present, the button uses
                    the --alert variant (amber + pulse + ⚠ count suffix).
                    In Fill mode, Optimize fills to capacity instead of
                    distributing the entered total. In multi-staff mode, a
                    parallel "Optimize all" button applies across every tab. */}
                {modalPerson && rangeDays.length > 0 && (<>
                  <div style={{ width: 1, height: 18, background: '#E5E7EB' }} />
                  {/* Fill mode toggle — flipping ON snapshots the current daily/
                      hours state then immediately fills to capacity; flipping OFF
                      restores from the snapshot. Available in both create AND
                      edit modes. While ON, the Total/By Day/Optimize/Extend
                      controls below are disabled. */}
                  <label className="sched-mgr-modal-btn sched-mgr-checkbox-label"
                    style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: modalFillMode ? '#065F46' : '#6B7280', fontWeight: modalFillMode ? 700 : 400 }}
                    title="Fill each day to that staff's available capacity. Total hours becomes the output. Toggle off to restore your prior distribution.">
                    <input type="checkbox" checked={modalFillMode}
                      onChange={e => {
                        const nextOn = e.target.checked;
                        if (nextOn) {
                          // Snapshot the ACTIVE tab's pre-Fill state so toggling off
                          // restores it. Fill now only affects the active tab, so no
                          // per-staff map needs snapshotting.
                          modalFillSnapshotRef.current = {
                            dailyHours: { ...modalDailyHours },
                            hours: modalHours,
                            allocType: modalAllocType,
                          };
                          setModalFillMode(true);
                          // Defer so setModalFillMode + setModalAllocType('daily')
                          // inside runFillTrigger don't fight over state in the
                          // same render cycle.
                          setTimeout(() => runFillTrigger(), 0);
                        } else {
                          const snap = modalFillSnapshotRef.current;
                          setModalFillMode(false);
                          if (snap) {
                            setModalDailyHours(snap.dailyHours);
                            setModalHours(snap.hours);
                            setModalAllocType(snap.allocType);
                            modalFillSnapshotRef.current = null;
                          }
                        }
                      }}
                      style={{ accentColor: '#003D4C' }} />
                    Fill mode
                  </label>
                  <button
                    type="button"
                    data-tour="entry-optimize"
                    disabled={modalFillMode || modalSaving}
                    className={`sched-mgr-modal-btn sched-mgr-action-btn${hasConflicts && !modalFillMode ? ' sched-mgr-action-btn--alert' : ''}`}
                    onClick={runOptimize}
                    title={hasConflicts
                      ? `Resolve ${conflictCount} conflict${conflictCount > 1 ? 's' : ''} (PTO overlap or over-capacity)`
                      : 'Redistribute hours across the date range, respecting capacity'}
                    style={{ padding: '3px 12px', fontSize: 11, fontWeight: 700, borderRadius: 4, border: '1px solid #003D4C', background: '#003D4C', color: '#fff' }}>
                    {hasConflicts ? `⚠ Optimize (${conflictCount})` : 'Optimize'}
                  </button>
                  {/* (Multi-staff "Optimize all"/"Fill all"/"Copy active → all" live in
                      the per-staff tab strip's All-staff cluster, not here — this
                      toolbar acts only on the ACTIVE tab.) */}
                  <label data-tour="entry-extend-before" className="sched-mgr-modal-btn sched-mgr-checkbox-label"
                    style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#6B7280', opacity: modalFillMode ? 0.5 : 1, pointerEvents: modalFillMode ? 'none' : 'auto' }}>
                    <input type="checkbox" checked={modalOverflowStart}
                      disabled={modalFillMode}
                      onChange={e => {
                        const v = e.target.checked;
                        setModalOverflowStart(v);
                        runOptimizeWith(v, modalOverflowEnd);
                      }}
                      style={{ accentColor: '#003D4C' }} />
                    Extend before
                  </label>
                  <label data-tour="entry-extend-after" className="sched-mgr-modal-btn sched-mgr-checkbox-label"
                    style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#6B7280', opacity: modalFillMode ? 0.5 : 1, pointerEvents: modalFillMode ? 'none' : 'auto' }}>
                    <input type="checkbox" checked={modalOverflowEnd}
                      disabled={modalFillMode}
                      onChange={e => {
                        const v = e.target.checked;
                        setModalOverflowEnd(v);
                        runOptimizeWith(modalOverflowStart, v);
                      }}
                      style={{ accentColor: '#003D4C' }} />
                    Extend after
                  </label>
                  {optimizeHasOverCap && !modalOverflowStart && !modalOverflowEnd && (
                    <span style={{ fontSize: 10, color: '#D97706', fontWeight: 600 }}>
                      &#9888; Some days still exceed capacity &mdash; try extending the date range
                    </span>
                  )}
                </>)}

                {/* Reset-to-original link — edit mode only, only when dirty. */}
                {isEdit && modalOrigStart && (() => {
                  const origTotal = Object.values(modalOrigDailyHours).reduce((s, v) => s + (Number(v) || 0), 0);
                  const isDirty = modalStart !== modalOrigStart
                    || modalEnd !== modalOrigEnd
                    || JSON.stringify(modalDailyHours) !== JSON.stringify(modalOrigDailyHours)
                    || Math.abs((parseFloat(modalHours) || 0) - origTotal) > 0.01  // total edited alone (Total mode)
                    || modalAllocType !== modalOrigAllocType                        // Total/By Day toggled
                    || modalFillMode;                                               // Fill turned on
                  if (!isDirty) return null;
                  return (
                    <button type="button"
                      className="sched-mgr-modal-btn sched-mgr-link-btn"
                      onClick={resetToOriginal}
                      style={{ marginLeft: 'auto', padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 4, color: '#6B7280' }}
                      title="Restore original dates + daily hours">
                      ↶ Reset to original
                    </button>
                  );
                })()}

              </div>

              {/* ── Hours Section ── */}
              <div className="sched-mgr-modal-field">

                {/* Capacity warning (from staff/date change). The link runs
                    Optimize on the current range with current overflow flags. */}
                {modalCapacityWarning && (
                  <div style={{ marginBottom: 6, padding: '5px 8px', background: '#FFFBEB', borderRadius: 6, fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                    {modalCapacityWarning}{' '}
                    <button type="button"
                      className="sched-mgr-modal-btn sched-mgr-link-btn"
                      onClick={() => { runOptimize(); setModalCapacityWarning(null); }}
                      style={{ color: '#92400E', fontWeight: 600, fontSize: 11, padding: 0 }}>
                      Run Optimize
                    </button>
                  </div>
                )}

                {/* Item 1 — live budget progress bar (shown in BOTH alloc modes). Reads the
                    live progressByJob map (not the stale modalJobWip); hidden for custom /
                    no-budget jobs. The projection (header "X% → Y%") + ghost segment are
                    CUMULATIVE across every staffer being scheduled on this job in multi-staff
                    mode — they all draw down the same budget. */}
                {modalJobKind === 'real' && modalJob && (() => {
                  const p = jobProgressProps(parseInt(modalJob, 10));
                  if (p.noBudget) return null;
                  const sumHours = (allocType, hours, dailyHours) => allocType === 'daily'
                    ? Object.values(dailyHours || {}).reduce((a, v) => a + (parseFloat(v) || 0), 0)
                    : (parseFloat(hours) || 0);
                  let proposedHours, origEntryHours;
                  if (modalIsMultiStaff && modalStaffList.length > 0) {
                    // Cumulative across the team — active tab live, others from their saved settings.
                    proposedHours = modalStaffList.reduce((sum, s) => {
                      if (s.StaffId === modalActiveStaffId) return sum + sumHours(modalAllocType, modalHours, modalDailyHours);
                      const st = modalSettingsByStaff[s.StaffId];
                      return sum + (st ? sumHours(st.allocType, st.hours, st.dailyHours) : 0);
                    }, 0);
                    origEntryHours = 0; // multi-staff is add-mode
                  } else {
                    proposedHours = sumHours(modalAllocType, modalHours, modalDailyHours);
                    origEntryHours = (modalEditBlock?.dates || []).reduce((s, d) => s + (d.hours || 0), 0);
                  }
                  const delta = Math.round((proposedHours - origEntryHours) * 100) / 100;
                  const afterUsed = Math.round((p.used + delta) * 100) / 100;
                  const afterPct = p.budget > 0 ? Math.round((afterUsed / p.budget) * 100) : 0;
                  const forThisJob = modalWipStaff.jobId === parseInt(modalJob, 10);
                  return (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span className="sched-grid-detail-label" style={{ marginBottom: 0, lineHeight: 1 }}>BUDGET</span>
                        <span style={{ fontSize: 11, color: '#6B7280', lineHeight: 1 }}>{p.budget}h</span>
                      </div>
                      <JobProgressBar budget={p.budget} wipTotal={p.wipTotal} scheduledTotal={p.scheduledTotal}
                        placeholderHours={p.placeholderHours}
                        remaining={p.remaining} overBudget={p.overBudget} overage={p.overage} pctComplete={p.pctComplete}
                        wipByStaff={forThisJob ? modalWipStaff.wipByStaff : []}
                        scheduledByStaff={forThisJob ? modalWipStaff.schedByStaff : []}
                        pctAfter={delta !== 0 ? afterPct : null}
                        ghostHours={delta > 0 ? delta : 0}
                        showLegend={false} tipDirection="down" />
                    </div>
                  );
                })()}

                {modalAllocType === 'total' ? (
                  <div>
                    <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 4 }}>
                      {modalIsMultiStaff
                        ? `${(modalStaffList.find(s => s.StaffId === modalActiveStaffId)?.Name || 'This staffer').split(/\s+/)[0]}: distributed evenly across ${rangeDays.length} weekday${rangeDays.length !== 1 ? 's' : ''} (each tab is independent). Switch to By Day to edit per-day or run Optimize.`
                        : `Hours distributed evenly across ${rangeDays.length} weekday${rangeDays.length !== 1 ? 's' : ''}. Switch to By Day to edit per-day or run Optimize.`}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" data-tour="entry-hours" min="0" max="200" step="0.5" value={modalHours}
                        onChange={e => setModalHours(e.target.value)}
                        readOnly={modalFillMode}
                        className="sched-mgr-hours-input"
                        style={modalFillMode ? { background: '#F3F4F6', color: '#6B7280' } : undefined} />
                      {modalFillMode && (
                        <span style={{ fontSize: 10, color: '#9CA3AF' }}>(computed by Fill schedule)</span>
                      )}
                    </div>
                    {/* Budget warning — reads the live progressByJob (WIP + future-scheduled),
                        consistent with the budget bar above (no longer the stale modalJobWip). */}
                    {(() => {
                      if (modalJobKind !== 'real' || !modalJob) return null;
                      const p = jobProgressProps(parseInt(modalJob, 10));
                      if (p.noBudget) return null;
                      const budget = p.budget;
                      const currentTotal = p.used;
                      // Delta: difference between proposed hours and original entry hours
                      const origEntryHours = (modalEditBlock?.dates || []).reduce((s, d) => s + (d.hours || 0), 0);
                      const proposedHours = parseFloat(modalHours) || 0;
                      const delta = proposedHours - origEntryHours;
                      const afterChange = currentTotal + delta;
                      const wipNote = p.wipTotal > 0 ? `${Math.round(p.wipTotal * 100) / 100}h WIP + ` : '';
                      if (currentTotal >= budget && delta > 0) {
                        return <div style={{ marginTop: 6, padding: '5px 8px', background: '#FEF2F2', borderRadius: 6, fontSize: 11, color: '#DC2626', fontWeight: 600 }}>
                          This job is at budget ({wipNote}{Math.round(p.scheduledTotal * 100) / 100}h scheduled = {Math.round(currentTotal * 100) / 100}h of {budget}h).{delta > 0 ? ` Adding ${Math.round(delta * 100) / 100}h will push it to ${Math.round(afterChange / budget * 100)}%.` : ''}
                        </div>;
                      }
                      if (afterChange > budget) {
                        return <div style={{ marginTop: 6, padding: '5px 8px', background: '#FFFBEB', borderRadius: 6, fontSize: 11, color: '#92400E', fontWeight: 600 }}>
                          {delta > 0 ? `Adding ${Math.round(delta * 100) / 100}h` : delta < 0 ? `Reducing by ${Math.round(Math.abs(delta) * 100) / 100}h` : 'Current hours'} will {delta >= 0 ? 'put' : 'leave'} this job at {Math.round(afterChange / budget * 100)}% of budget ({wipNote}{Math.round(p.scheduledTotal * 100) / 100}h scheduled{delta !== 0 ? ` ${delta > 0 ? '+' : ''}${Math.round(delta * 100) / 100}h` : ''} = {Math.round(afterChange * 100) / 100}h of {budget}h).
                        </div>;
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Collapsible daily grid toggle */}
                    <button type="button" onClick={() => setShowDailyGrid(p => !p)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: 11, fontWeight: 700, color: '#6B7280', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ transform: showDailyGrid ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
                      Daily Hours ({Object.values(modalDailyHours).filter(v => v > 0).length} days, {Math.round(Object.values(modalDailyHours).reduce((s, v) => s + (v || 0), 0) * 100) / 100}h total)
                    </button>
                    {/* Per-day inputs — week-row grid layout */}
                    {showDailyGrid && (() => {
                      const isEdit = !!modalEditBlock?.dates?.length;
                      // Collect all date keys (from modalDailyHours + rangeDays)
                      const allIsos = [...new Set([
                        ...Object.keys(modalDailyHours).filter(iso => modalDailyHours[iso] != null),
                        ...rangeDays.map(d => d.iso),
                      ])].sort();
                      // Check if any day is a Saturday with capacity
                      const hasSat = allIsos.some(iso => {
                        const dt = new Date(iso + 'T12:00:00');
                        return dt.getDay() === 6 && (modalPerson ? getDayCap(modalPerson.StaffId, iso) > 0 : false);
                      });
                      const DOW = hasSat ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
                      // Group into week rows
                      const weekGroups = [];
                      let currentWeek = null;
                      for (const iso of allIsos) {
                        const dt = new Date(iso + 'T12:00:00');
                        if (dt.getDay() === 0) continue; // skip Sundays
                        const mon = new Date(dt); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
                        const monKey = toISO(mon);
                        if (!currentWeek || currentWeek.monKey !== monKey) {
                          currentWeek = { monKey, monLabel: mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), days: {} };
                          weekGroups.push(currentWeek);
                        }
                        const dowIdx = (dt.getDay() + 6) % 7;
                        currentWeek.days[dowIdx] = iso;
                      }
                      return (
                        <div className="sched-expand-enter" style={{ marginBottom: 8 }}>
                          {/* Day headers */}
                          <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${DOW.length}, 1fr)`, gap: 4, marginBottom: 4 }}>
                            <div />
                            {DOW.map(d => (
                              <div key={d} style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'center' }}>{d}</div>
                            ))}
                          </div>
                          {/* Week rows */}
                          {weekGroups.map((wk, wi) => (
                            <div key={wi} style={{ display: 'grid', gridTemplateColumns: `60px repeat(${DOW.length}, 1fr)`, gap: 4, marginBottom: 6 }}>
                              <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, display: 'flex', alignItems: 'center' }}>{wk.monLabel}</div>
                              {DOW.map((_, di) => {
                                const iso = wk.days[di];
                                if (!iso) return <div key={di} />;
                                const hrs = parseFloat(modalDailyHours[iso]) || 0;
                                const dayCap = modalPerson ? getDayCap(modalPerson.StaffId, iso) : 8;
                                // Over-cap uses the SAME inputs as the banner/tab-badges: dayHoursByStaff
                                // (which INCLUDES global-assignment hours) minus this entry's own pre-edit
                                // hours, via the shared dayIsConflict predicate — so the per-cell indicator
                                // can never contradict the banner (and 0-cap days now flag too).
                                const totalExisting = dayHoursByStaff[modalPerson?.StaffId]?.[iso] || 0;
                                const ownPreEdit = isEdit ? (Number(modalOrigDailyHours[iso]) || 0) : 0;
                                const existingOther = Math.max(0, totalExisting - ownPreEdit);
                                const dayTotal = existingOther + hrs;
                                const isOver = dayIsConflict({ proposedHrs: hrs, cap: dayCap, totalExistingHrs: totalExisting, ownPreEditHrs: ownPreEdit });
                                const oldH = isEdit ? modalOrigDailyHours[iso] : undefined;
                                const isChanged = isEdit ? (oldH == null ? hrs > 0 : oldH !== hrs) : hrs > 0;
                                return (
                                  <div key={di} style={{ textAlign: 'center' }}>
                                    <div style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                                      padding: '3px 2px', borderRadius: 4, minHeight: 28,
                                      border: `1.5px solid ${isOver ? '#EF4444' : isChanged ? '#F59E0B' : '#E5E7EB'}`,
                                      background: isOver ? '#FEF2F2' : !isChanged ? '#F9FAFB' : '#fff',
                                    }}>
                                      {isEdit && isChanged && oldH != null && (
                                        <span style={{ fontSize: 10, color: '#9CA3AF', textDecoration: 'line-through' }}>{oldH}</span>
                                      )}
                                      {isEdit && isChanged && oldH != null && (
                                        <span style={{ fontSize: 8, color: '#9CA3AF' }}>&rarr;</span>
                                      )}
                                      <input type="number" min="0" max="24" step="0.5"
                                        value={modalDailyHours[iso] ?? ''}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setModalDailyHours(prev => {
                                            const updated = { ...prev, [iso]: val === '' ? '' : parseFloat(val) };
                                            const sum = Object.values(updated).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                                            setModalHours(String(Math.round(sum * 100) / 100));
                                            return updated;
                                          });
                                        }}
                                        style={{
                                          width: isEdit && isChanged && oldH != null ? 40 : '100%', padding: '0 2px', borderRadius: 2, fontSize: 12,
                                          textAlign: 'center', fontWeight: 700, border: 'none', outline: 'none',
                                          background: 'transparent',
                                          color: isOver ? '#DC2626' : !isChanged ? '#9CA3AF' : '#374151',
                                        }}
                                      />
                                    </div>
                                    <div style={{ fontSize: 8, color: isOver ? '#DC2626' : '#9CA3AF', marginTop: 1, fontWeight: isOver ? 600 : 400 }}>
                                      {dayTotal % 1 === 0 ? dayTotal : Math.round(dayTotal * 100) / 100}/{dayCap}h
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#003D4C', marginTop: 4 }}>
                            Total: {modalHours}h
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Visual change summary (Before/After View) — shown in both Total and By Day modes */}
              {modalPerson && rangeDays.length > 0 && (() => {
                // Same single source of truth the save uses (getProposedDailyHours
                // → computeProposedDaily) so the Before/After preview can never
                // diverge from what's actually written.
                const summaryProposedHours = getProposedDailyHours();
                return Object.keys(summaryProposedHours).length > 0 ? (
                <div data-tour="modal-before-after">
                <BeforeAfterSummary
                  originalDailyHours={modalOrigDailyHours}
                  proposedDailyHours={summaryProposedHours}
                  getDayCap={getDayCap}
                  ptoByStaffDay={ptoByStaffDay}
                  holidayByDate={holidayByDate}
                  globalAssignmentByDate={globalAssignmentByDate}
                  staffDeptCode={modalPerson?.DepartmentCode}
                  staffId={modalPerson?.StaffId}
                  originalStaff={modalEditBlock?.staffName}
                  newStaff={modalPerson?.Name}
                  originalJob={modalEditBlock?.jobName}
                  newJob={allJobs.find(j => j.JobId === parseInt(modalJob))?.Name}
                  isNewEntry={!modalEditBlock}
                  displayTotal={parseFloat(modalHours) || 0}
                  dayHoursByStaff={dayHoursByStaff}
                  editEntryIds={new Set((modalEditBlock?.dates || []).map(d => d.entryId))}
                  sameJobDailyHours={sameJobHoursFor(modalPerson?.StaffId, parseInt(modalJob, 10), (modalEditBlock?.dates || []).map(d => d.entryId))}
                  rangeDays={rangeDays.map(d => d.iso)}
                />
                </div>
                ) : null;
              })()}

              <div style={{ marginTop: 8 }}>
                <input type="text" placeholder="Add a note (optional)"
                  key={modalEditBlock?.key || 'new'}
                  defaultValue={modalEditBlock?.notes || ''}
                  onChange={e => { modalNotesRef.current = e.target.value; }}
                  style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box' }} />
              </div>
              </>)}

              <button
                className="sched-assign-btn sched-mgr-modal-add sched-mgr-modal-btn sched-mgr-action-btn"
                disabled={
                  // Convert mode (edit→placeholder): only needs the source block.
                  // Person mode: real-or-custom job + start + hours.
                  // New placeholder mode: dept + level + end.
                  modalSaving ||
                  (isEdit && modalMode === 'placeholder'
                    ? !modalEditBlock?.jobId || !modalDept || !modalLevel
                    : (((modalJobKind === 'custom') ? !modalCustomJobId : !modalJob) ||
                       !modalStart || !modalHours ||
                       (modalMode === 'placeholder' && (!modalDept || !modalLevel || !modalEnd))))
                }
                onClick={
                  isEdit && modalMode === 'placeholder'
                    ? handleConvertToPlaceholder
                    : (modalMode === 'placeholder'
                        ? handleSavePlaceholder
                        // Multi-staff create → confirm-gate: open the per-staff gantt
                        // preview first; single-staff + edits save directly.
                        : (((modalIsMultiStaff && modalStaffList.length > 0) || pendingPlaceholders.length > 0)
                            ? () => setShowMultiPreview(true)
                            : handleSaveEntry))
                }
              >
                {modalSaving
                  ? 'Saving...'
                  : (isEdit && modalMode === 'placeholder'
                      ? 'Convert to Placeholder'
                      : (modalMode === 'placeholder'
                          ? 'Create Placeholder'
                          : (isEdit ? 'Apply Changes'
                              : (((modalIsMultiStaff && modalStaffList.length > 0) || pendingPlaceholders.length > 0) ? 'Review & Create →' : 'Create Entry'))))}
              </button>
              {isEdit && modalEditBlock && modalMode !== 'placeholder' && (
                <button
                  className="sched-mgr-modal-btn"
                  disabled={modalSaving}
                  style={{ width: '100%', marginTop: 8, background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 8, padding: '8px', fontWeight: 600, cursor: modalSaving ? 'default' : 'pointer' }}
                  onClick={async () => { const blk = modalEditBlock; closeModal(); await handleRemoveEntry(blk); }}
                >
                  &#128465; Remove entry
                </button>
              )}
              </ErrorBoundary>
            </div>
          </div>
        );
      })()}

      {/* ── Multi-staff create preview (confirm-gate) ──
          A per-staff day-level gantt shown when "Review & Create" is clicked in
          multi-staff mode. Bars use the grid's JOB_TYPE_COLORS so they read like
          the grid; over-cap / PTO days get a red outline (dayIsConflict). */}
      {showModal && showMultiPreview && ((modalIsMultiStaff && modalStaffList.length > 0) || pendingPlaceholders.length > 0) && (() => {
        const jobMetaReal = allJobs.find(j => j.JobId === parseInt(modalJob, 10));
        const customMeta = modalJobKind === 'custom' ? allCustomJobs.find(c => String(c.CustomJobId) === modalCustomJobId) : null;
        const jobColor = modalJobKind === 'custom'
          ? (CUSTOM_JOB_CATEGORY_COLORS[customMeta?.Category] || CUSTOM_JOB_DEFAULT_COLOR)
          : (JOB_TYPE_COLORS[jobMetaReal?.ServiceDesc || jobMetaReal?.JobType || jobMetaReal?.JobClassDesc] || { bg: '#6B7280', text: '#fff' });
        const jobTitle = modalJobKind === 'custom'
          ? (customMeta?.Name || 'Custom job')
          : (jobMetaReal ? `${jobMetaReal.ClientName} — ${jobMetaReal.Name}` : 'Job');

        // Resolve each staffer's proposed distribution via the SAME resolver the save uses.
        const rows = modalStaffList.map(s => {
          const st = s.StaffId === modalActiveStaffId ? buildActiveSettings() : (modalSettingsByStaff[s.StaffId] || {});
          const proposed = computeProposedDaily({
            startISO: st.start, endISO: st.end || st.start, total: parseFloat(st.hours) || 0,
            allocType: st.allocType || 'total', dailyMap: st.dailyHours, staffId: s.StaffId, getDayCap,
          });
          const byDay = {}; let total = 0; let conflictDays = 0;
          for (const [iso, h] of Object.entries(proposed)) {
            const hrs = Number(h) || 0;
            if (hrs <= 0) continue;
            const conflict = dayIsConflict({
              proposedHrs: hrs, cap: getDayCap(s.StaffId, iso),
              totalExistingHrs: dayHoursByStaff[s.StaffId]?.[iso] || 0, ownPreEditHrs: 0,
              ptoHrs: ptoByStaffDay[s.StaffId]?.[iso] || 0, ptoAsConflict: true,
            });
            byDay[iso] = { hours: hrs, conflict };
            total += hrs; if (conflict) conflictDays++;
          }
          const isos = Object.keys(byDay).sort();
          return { s, byDay, total: Math.round(total * 100) / 100, conflictDays, firstISO: isos[0], lastISO: isos[isos.length - 1] };
        });

        // Contiguous weekday columns across the union range (skip Sun; Sat only if used).
        const filled = new Set();
        rows.forEach(r => Object.keys(r.byDay).forEach(iso => filled.add(iso)));
        const sortedFilled = [...filled].sort();
        const columns = [];
        if (sortedFilled.length) {
          const c = parseLocalISODate(sortedFilled[0]);
          const end = parseLocalISODate(sortedFilled[sortedFilled.length - 1]);
          while (c && end && c <= end) {
            const iso = toISO(c); const dow = c.getDay();
            if (dow !== 0 && !(dow === 6 && !filled.has(iso))) columns.push(iso);
            c.setDate(c.getDate() + 1);
          }
        }
        const grandTotal = Math.round(rows.reduce((sv, r) => sv + r.total, 0) * 100) / 100;
        const anyConflict = rows.some(r => r.conflictDays > 0);
        const fmtCol = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
        const fmtMD = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

        return (
          <div className="sched-mgr-modal-backdrop" style={{ zIndex: 1200 }}>
            <div className="sched-mgr-modal" style={{ maxWidth: 900, width: '94%' }}>
              <div className="sched-mgr-modal-header">
                <span>Review schedule &mdash; {jobTitle}</span>
                <button className="sched-mgr-modal-btn sched-mgr-x-btn" onClick={() => setShowMultiPreview(false)} title="Back to edit">&times;</button>
              </div>
              <div style={{ padding: 16 }}>
                {rows.length > 0 && (
                  <div style={{ fontSize: 12, color: '#374151', marginBottom: 10, fontWeight: 600 }}>
                    {rows.length} staff &middot; {grandTotal}h total
                    {anyConflict && <span style={{ color: '#B45309', marginLeft: 8, fontWeight: 700 }}>&#9888; some days exceed capacity / hit PTO</span>}
                  </div>
                )}
                {rows.length > 0 && (columns.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No hours to schedule &mdash; every staffer resolved to 0.</div>
                ) : (
                  <div className="sched-preview-gantt">
                    <div className="sched-preview-gantt-header">
                      <div className="sched-preview-gantt-labelcol">Staff</div>
                      {columns.map(iso => <div key={iso} className="sched-preview-gantt-col">{fmtCol(iso)}</div>)}
                    </div>
                    {rows.map(r => (
                      <div key={r.s.StaffId} className="sched-preview-gantt-row">
                        <div className="sched-preview-gantt-labelcol">
                          <div className="sched-preview-gantt-name">{r.s.Name}</div>
                          <div className="sched-preview-gantt-sub">
                            {r.total}h &middot; {r.firstISO ? `${fmtMD(r.firstISO)}–${fmtMD(r.lastISO)}` : '—'}
                            {r.conflictDays > 0 && <span className="sched-preview-gantt-warn"> &#9888; {r.conflictDays}</span>}
                          </div>
                        </div>
                        {columns.map(iso => {
                          const cell = r.byDay[iso];
                          return (
                            <div key={iso} className="sched-preview-gantt-cell">
                              {cell && (
                                <div className="sched-preview-block"
                                  style={{ background: jobColor.bg, color: jobColor.text || '#fff', ...(cell.conflict ? { outline: '2px solid #EF4444', outlineOffset: -2 } : {}) }}
                                  title={cell.conflict ? `${cell.hours}h — over capacity / PTO` : `${cell.hours}h`}>
                                  {cell.hours}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
                {pendingPlaceholders.length > 0 && (
                  <div style={{ marginTop: rows.length > 0 ? 14 : 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Placeholders ({pendingPlaceholders.length})</div>
                    {pendingPlaceholders.map((ph, i) => (
                      <div key={i} className="sched-mgr-ph-preview-row">
                        <span className="sched-grid-placeholder-label">Placeholder</span>
                        <span style={{ fontSize: 12, color: '#374151' }}>{ph.dept} &middot; {ph.level}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6B7280' }}>{ph.hours}h &middot; {fmtMD(ph.startDate)}&ndash;{fmtMD(ph.endDate)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '0 16px 16px' }}>
                <button className="sched-mgr-modal-btn" onClick={() => setShowMultiPreview(false)} disabled={modalSaving}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontWeight: 600 }}>
                  &larr; Back to edit
                </button>
                <button className="sched-mgr-modal-btn sched-mgr-action-btn" onClick={() => handleSaveEntry()} disabled={modalSaving}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #003D4C', background: '#003D4C', color: '#fff', fontWeight: 700 }}>
                  {modalSaving ? 'Saving…' : 'Confirm & schedule'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Staff Filter Modal ── */}
      {showFilterModal && (() => {
        return (
          <div ref={filterModalRef} className="sched-mgr-modal-backdrop" {...filterBackdrop}>
            <div className="sched-mgr-modal sched-filter-modal" style={{ width: 520, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
              <div className="sched-mgr-modal-header">
                <h3>Filter Staff</h3>
                <button className="sched-mgr-sidebar-close" onClick={closeFilterModal}>&times;</button>
              </div>

              <StaffFilterPanel
                initialSelected={staffFilter}
                staffList={gridEligibleStaff}
                staffById={staffById}
                savedGroups={displayFilters}
                canShareGlobally={isSuperUser}
                syncFailed={savedFiltersSyncFailed}
                onRetrySync={syncSavedFilters}
                onApply={handleApplyFilter}
                onSaveGroup={handleSaveFilterPreset}
                onDeleteGroup={handleDeleteFilterPreset}
                onRenameGroup={handleRenameFilterPreset}
                onDuplicateGroup={handleDuplicateFilterPreset}
                onSetGroupSharing={handleSetFilterSharing}
                onClose={closeFilterModal}
              />
            </div>
          </div>
        );
      })()}

      {/* ── Filter Client Groups ────────────────────────────────────────────
          Mirrors the staff modal above, with one deliberate difference: the
          picker shows nothing until you type. See ClientFilterPicker. */}
      {showClientFilterModal && (
        <div ref={clientFilterModalRef} className="sched-mgr-modal-backdrop" {...clientFilterBackdrop}>
          <div className="sched-mgr-modal sched-filter-modal" style={{ width: 520, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="sched-mgr-modal-header">
              <h3>Filter Clients</h3>
              <button className="sched-mgr-sidebar-close" onClick={closeClientFilterModal}>&times;</button>
            </div>

            <ClientFilterPanel
              initialSelected={clientFilter}
              clients={gridClients}
              clientById={clientById}
              savedGroups={clientDisplayFilters}
              canShareGlobally={isSuperUser}
              shareStaffList={gridEligibleStaff}
              staffById={staffById}
              syncFailed={savedFiltersSyncFailed}
              onRetrySync={syncSavedFilters}
              onApply={handleApplyClientFilter}
              onSaveGroup={handleSaveClientFilterPreset}
              onDeleteGroup={handleDeleteFilterPreset}
              onRenameGroup={handleRenameFilterPreset}
              onDuplicateGroup={handleDuplicateFilterPreset}
              onSetGroupSharing={handleSetFilterSharing}
              onClose={closeClientFilterModal}
            />
          </div>
        </div>
      )}
    </div>
  );
}
